import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MessageCircle, AlertTriangle, Mail, Clock, CheckCircle, Send, Paperclip, Sparkles, X, Search, Phone, Video, VideoOff, MoreVertical, Mic, MicOff, ThumbsUp, Smile, Image as ImageIcon, Calendar, UserPlus, CalendarPlus, UserCheck, Trash2, Reply, Flag, Pin, Loader2, PhoneOff, Bot, RefreshCw, Link, Download, FileText, ChevronLeft, Check, FolderPlus, Plane, DollarSign, Plus, Pencil } from 'lucide-react';
import { apiCall, aiEmailAPI } from '../services/api';
import useVoiceClient from '../hooks/useVoiceClient';
import IncomingCallPopup from '../components/IncomingCallPopup';
import { toast } from 'sonner';

// New Call Modal Component - Fetches iCloud contacts and initiates Twilio calls
const NewCallModal = ({ isOpen, onClose, contactsList, loadingContacts, fetchICloudContacts, onCallInitiated, voiceMakeCall, voiceReady }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [manualNumber, setManualNumber] = useState('');
  const [callingContact, setCallingContact] = useState(null);
  const [callError, setCallError] = useState(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const [isCallingManual, setIsCallingManual] = useState(false);
  const [callMode, setCallMode] = useState('browser'); // 'browser' or 'phone'
  
  // Fetch contacts when modal opens
  useEffect(() => {
    if (isOpen && initialLoad) {
      fetchICloudContacts();
      setInitialLoad(false);
      setSearchQuery('');
      setManualNumber('');
      setCallError(null);
    }
  }, [isOpen]);
  
  // Reset initial load when modal closes
  useEffect(() => {
    if (!isOpen) {
      setInitialLoad(true);
      setCallingContact(null);
      setIsCallingManual(false);
    }
  }, [isOpen]);
  
  // Filter contacts based on search - filter those with phone numbers first
  const filteredContacts = contactsList
    .filter(contact => {
      // Must have a phone number to be callable
      if (!contact.phone) return false;
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        contact.name?.toLowerCase().includes(query) ||
        contact.phone?.toLowerCase().includes(query) ||
        contact.email?.toLowerCase().includes(query)
      );
    })
    .slice(0, 50); // Limit to 50 for performance
  
  // Initiate call - uses browser WebRTC if voice ready, otherwise REST API
  const handleCall = async (contact) => {
    if (!contact.phone) {
      setCallError('This contact has no phone number');
      return;
    }
    
    setCallingContact(contact);
    setCallError(null);
    
    try {
      // Use browser calling if voice client is ready
      if (voiceReady && voiceMakeCall && callMode === 'browser') {
        console.log('📞 Initiating browser call to:', contact.phone);
        await voiceMakeCall(contact.phone);
        
        // Don't call the REST API - the browser call handles everything
        // The call will be logged via webhook when Twilio processes it
        
        onCallInitiated({
          name: contact.name,
          phone: contact.phone,
          avatar: contact.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(contact.name)}&size=128&background=random&color=fff&bold=true`,
          isBrowserCall: true,
          isFaceTime: false
        });
      } else {
        // Fallback to REST API (phone bridge) - this calls both phones
        const response = await apiCall('/calls/make-call', {
          method: 'POST',
          body: JSON.stringify({
            to_phone: contact.phone,
            recipient_name: contact.name
          })
        });
        
        console.log('📞 Call initiated via phone bridge:', response);
        
        onCallInitiated({
          name: contact.name,
          phone: contact.phone,
          avatar: contact.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(contact.name)}&size=128&background=random&color=fff&bold=true`,
          callSid: response.call_sid,
          isFaceTime: false
        });
      }
      
    } catch (error) {
      console.error('Error initiating call:', error);
      setCallError(error.message || 'Failed to initiate call');
      setCallingContact(null);
    }
  };
  
  // Call a manually entered number
  const handleManualCall = async () => {
    const phoneNumber = manualNumber.trim();
    if (!phoneNumber) {
      setCallError('Please enter a phone number');
      return;
    }
    
    // Basic phone validation - allow digits, spaces, dashes, parentheses, plus sign
    const cleanNumber = phoneNumber.replace(/[\s\-\(\)]/g, '');
    if (!/^\+?[0-9]{10,15}$/.test(cleanNumber)) {
      setCallError('Please enter a valid phone number');
      return;
    }
    
    setIsCallingManual(true);
    setCallError(null);
    
    try {
      // Use browser calling if voice client is ready
      if (voiceReady && voiceMakeCall && callMode === 'browser') {
        console.log('📞 Initiating browser call to:', phoneNumber);
        await voiceMakeCall(phoneNumber);
        
        // Don't call REST API - browser call handles everything via TwiML
        
        onCallInitiated({
          name: phoneNumber,
          phone: phoneNumber,
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(phoneNumber.slice(-4))}&size=128&background=22c55e&color=fff&bold=true`,
          isBrowserCall: true,
          isFaceTime: false
        });
      } else {
        // Fallback to REST API if voice not ready
        const response = await apiCall('/calls/make-call', {
          method: 'POST',
          body: JSON.stringify({
            to_phone: phoneNumber,
            recipient_name: phoneNumber
          })
        });
        
        onCallInitiated({
          name: phoneNumber,
          phone: phoneNumber,
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(phoneNumber.slice(-4))}&size=128&background=22c55e&color=fff&bold=true`,
          callSid: response.call_sid,
          isFaceTime: false
        });
      }
      
    } catch (error) {
      console.error('Error initiating manual call:', error);
      setCallError(error.message || 'Failed to initiate call');
      setIsCallingManual(false);
    }
  };
  
  // Handle Enter key in manual number input
  const handleManualKeyPress = (e) => {
    if (e.key === 'Enter' && manualNumber.trim()) {
      handleManualCall();
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-400/20 rounded-2xl w-[500px] max-h-[750px] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="p-6 border-b border-gray-400/20">
          <h2 className="text-2xl font-bold text-white">New Call</h2>
          <p className="text-gray-400 text-sm mt-1">Dial a number or select a contact to call</p>
          
          {/* Call Mode Toggle */}
          {voiceReady && (
            <div className="flex items-center gap-2 mt-3 p-2 bg-gray-800 rounded-lg">
              <button
                onClick={() => setCallMode('browser')}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  callMode === 'browser' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-transparent text-gray-400 hover:text-white'
                }`}
              >
                <Mic size={16} />
                Browser Call
              </button>
              <button
                onClick={() => setCallMode('phone')}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  callMode === 'phone' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-transparent text-gray-400 hover:text-white'
                }`}
              >
                <Phone size={16} />
                Phone Bridge
              </button>
            </div>
          )}
        </div>
        
        {/* Manual Dial Section */}
        <div className="px-4 py-4 border-b border-gray-400/20 bg-gray-800/50">
          <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 block">
            Dial a Number
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Phone size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-green-500" />
              <input
                type="tel"
                placeholder="Enter phone number..."
                value={manualNumber}
                onChange={(e) => setManualNumber(e.target.value)}
                onKeyPress={handleManualKeyPress}
                className="w-full pl-10 pr-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors text-lg font-mono"
                data-testid="manual-dial-input"
              />
            </div>
            <button
              onClick={handleManualCall}
              disabled={!manualNumber.trim() || isCallingManual}
              className={`px-5 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
                !manualNumber.trim() || isCallingManual
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
              data-testid="manual-call-btn"
            >
              {isCallingManual ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <Phone size={20} />
              )}
              Call
            </button>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="px-4 py-3 border-b border-gray-400/20">
          <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 block">
            Or Select from Contacts
          </label>
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder="Search contacts by name or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors"
              autoFocus
              data-testid="new-call-search-input"
            />
          </div>
        </div>
        
        {/* Error Message */}
        {callError && (
          <div className="mx-4 mt-3 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-sm">{callError}</p>
          </div>
        )}

        {/* Contacts List */}
        <div className="flex-1 overflow-y-auto p-4" style={{ maxHeight: '400px' }}>
          {loadingContacts && filteredContacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 size={32} className="text-green-500 animate-spin mb-3" />
              <p className="text-gray-400 text-sm">Loading your iCloud contacts...</p>
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Phone size={48} className="text-gray-600 mb-4" />
              <h3 className="text-lg font-semibold text-gray-400 mb-2">
                {searchQuery ? 'No contacts found' : 'No contacts with phone numbers'}
              </h3>
              <p className="text-sm text-gray-500">
                {searchQuery ? 'Try a different search term' : 'Sync your iCloud contacts to see them here'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredContacts.map((contact, index) => (
                <div
                  key={contact.id || index}
                  className={`bg-gray-800 border border-gray-400/20 rounded-lg p-4 hover:bg-gray-700 transition-colors ${
                    callingContact?.id === contact.id ? 'ring-2 ring-green-500' : ''
                  }`}
                  data-testid={`contact-${contact.id || index}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {contact.avatar ? (
                        <img
                          src={contact.avatar}
                          alt={contact.name}
                          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold text-lg">
                            {contact.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{contact.name || 'Unknown'}</p>
                        <p className="text-sm text-gray-400 truncate">{contact.phone || 'No phone number'}</p>
                        {contact.email && (
                          <p className="text-xs text-gray-500 truncate">{contact.email}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0 ml-3">
                      {contact.phone ? (
                        <button
                          onClick={() => handleCall(contact)}
                          disabled={callingContact !== null}
                          className={`p-3 rounded-full transition-colors flex items-center justify-center ${
                            callingContact?.id === contact.id
                              ? 'bg-green-500 animate-pulse'
                              : callingContact !== null
                              ? 'bg-gray-700 cursor-not-allowed opacity-50'
                              : 'bg-green-600 hover:bg-green-700'
                          }`}
                          title={callingContact?.id === contact.id ? 'Calling...' : 'Call'}
                          data-testid={`call-btn-${contact.id || index}`}
                        >
                          {callingContact?.id === contact.id ? (
                            <Loader2 size={20} className="text-white animate-spin" />
                          ) : (
                            <Phone size={20} className="text-white" />
                          )}
                        </button>
                      ) : (
                        <div className="p-3 bg-gray-700 rounded-full opacity-50" title="No phone number">
                          <Phone size={20} className="text-gray-500" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with contact count */}
        <div className="p-4 border-t border-gray-400/20 flex items-center justify-between">
          <span className="text-sm text-gray-500">
            {filteredContacts.length} contact{filteredContacts.length !== 1 ? 's' : ''} 
            {searchQuery && ` matching "${searchQuery}"`}
          </span>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors text-white font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Call Detail Modal - Shows transcript, summary, lead capture, and outcome buttons
const CallDetailModal = ({ call, isOpen, onClose, onOutcomeUpdate, onLeadCapture }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [transcript, setTranscript] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [selectedOutcome, setSelectedOutcome] = useState(call?.outcome || null);
  const [outcomeNotes, setOutcomeNotes] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [savingOutcome, setSavingOutcome] = useState(false);
  const [capturingLead, setCapturingLead] = useState(false);
  const [pipelineStage, setPipelineStage] = useState('new_lead');
  
  const outcomeOptions = [
    { id: 'booked', label: 'Booked', color: 'bg-green-500', icon: '📅' },
    { id: 'follow_up', label: 'Follow-up', color: 'bg-blue-500', icon: '🔄' },
    { id: 'hot_lead', label: 'Hot Lead', color: 'bg-orange-500', icon: '🔥' },
    { id: 'qualified', label: 'Qualified', color: 'bg-purple-500', icon: '✓' },
    { id: 'not_interested', label: 'Not Interested', color: 'bg-gray-500', icon: '✗' },
    { id: 'voicemail', label: 'Voicemail', color: 'bg-yellow-500', icon: '📧' },
  ];
  
  const pipelineStages = [
    { id: 'new_lead', label: 'New Lead' },
    { id: 'contacted', label: 'Contacted' },
    { id: 'qualified', label: 'Qualified' },
    { id: 'proposal', label: 'Proposal' },
    { id: 'negotiation', label: 'Negotiation' },
    { id: 'closed', label: 'Closed' },
  ];
  
  // Fetch transcript and existing summary when modal opens
  useEffect(() => {
    if (isOpen && call?.call_sid) {
      fetchTranscript();
      fetchExistingSummary();
    }
  }, [isOpen, call?.call_sid]);
  
  const fetchTranscript = async () => {
    if (!call?.call_sid) return;
    setLoadingTranscript(true);
    try {
      const data = await apiCall(`/calls/${call.call_sid}/transcript`);
      setTranscript(data);
    } catch (error) {
      console.error('Failed to fetch transcript:', error);
    } finally {
      setLoadingTranscript(false);
    }
  };
  
  const fetchExistingSummary = async () => {
    if (!call?.call_sid) return;
    try {
      // Check if summary already exists by making a GET request or checking call data
      const callData = await apiCall(`/calls/${call.call_sid}`);
      if (callData?.ai_summary) {
        setSummary({
          summary: callData.ai_summary,
          action_items: callData.action_items || [],
          sentiment: callData.sentiment,
          lead_quality: callData.lead_quality
        });
      }
    } catch (error) {
      // Summary doesn't exist yet, that's fine
      console.log('No existing summary found');
    }
  };
  
  const transcribeCall = async () => {
    if (!call?.call_sid) return;
    setLoadingTranscript(true);
    try {
      const data = await apiCall(`/calls/${call.call_sid}/transcribe`, { method: 'POST' });
      setTranscript(prev => ({ ...prev, transcript: data.transcript, has_transcript: true }));
    } catch (error) {
      console.error('Failed to transcribe call:', error);
      alert('Failed to transcribe: ' + (error.message || 'Unknown error'));
    } finally {
      setLoadingTranscript(false);
    }
  };
  
  const generateSummary = async () => {
    if (!call?.call_sid) return;
    setLoadingSummary(true);
    try {
      const data = await apiCall(`/calls/${call.call_sid}/summary`, { method: 'POST' });
      setSummary(data);
    } catch (error) {
      console.error('Failed to generate summary:', error);
      alert('Failed to generate summary: ' + (error.message || 'Unknown error'));
    } finally {
      setLoadingSummary(false);
    }
  };
  
  const handleSaveOutcome = async () => {
    if (!selectedOutcome || !call?.call_sid) return;
    setSavingOutcome(true);
    try {
      await apiCall(`/calls/${call.call_sid}/outcome`, {
        method: 'PUT',
        body: JSON.stringify({
          outcome: selectedOutcome,
          notes: outcomeNotes,
          follow_up_date: followUpDate || null
        })
      });
      onOutcomeUpdate?.(call.call_sid, selectedOutcome);
      onClose();
    } catch (error) {
      console.error('Failed to save outcome:', error);
    } finally {
      setSavingOutcome(false);
    }
  };
  
  const handleCaptureLead = async () => {
    if (!call?.call_sid) return;
    setCapturingLead(true);
    try {
      const result = await apiCall(`/calls/${call.call_sid}/capture-lead`, {
        method: 'POST',
        body: JSON.stringify({
          call_sid: call.call_sid,
          add_to_pipeline: true,
          pipeline_stage: pipelineStage
        })
      });
      onLeadCapture?.(result);
      alert(`Lead captured successfully! ${result.is_new_contact ? 'New contact created.' : 'Existing contact updated.'}`);
    } catch (error) {
      console.error('Failed to capture lead:', error);
      alert('Failed to capture lead');
    } finally {
      setCapturingLead(false);
    }
  };
  
  if (!isOpen || !call) return null;
  
  const displayPhone = call.direction === 'inbound' ? call.from_phone : call.to_phone;
  const displayName = call.contact_name || displayPhone;
  const callDate = call.created_at ? new Date(call.created_at) : new Date();
  
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-400/20 rounded-2xl w-[700px] max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-gray-400/20 bg-gradient-to-r from-gray-900 to-gray-800">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                <Phone size={24} className="text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{displayName}</h2>
                <p className="text-gray-400 text-sm">{displayPhone}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    call.direction === 'inbound' 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-blue-500/20 text-blue-400'
                  }`}>
                    {call.direction === 'inbound' ? '↓ Inbound' : '↑ Outbound'}
                  </span>
                  <span className="text-xs text-gray-500">
                    {callDate.toLocaleDateString()} at {callDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </span>
                  {call.duration && (
                    <span className="text-xs text-gray-500">• {Math.floor(call.duration / 60)}m {call.duration % 60}s</span>
                  )}
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
              <X size={20} className="text-gray-400" />
            </button>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-gray-400/20">
          {['details', 'transcript', 'lead'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === tab 
                  ? 'text-white border-b-2 border-green-500' 
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              {tab === 'details' && 'Call Outcome'}
              {tab === 'transcript' && 'Transcript & Summary'}
              {tab === 'lead' && 'Lead Capture'}
            </button>
          ))}
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Call Outcome Tab */}
          {activeTab === 'details' && (
            <div className="space-y-6">
              <div>
                <label className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 block">
                  Call Outcome
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {outcomeOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setSelectedOutcome(option.id)}
                      className={`p-4 rounded-xl border-2 transition-all text-center ${
                        selectedOutcome === option.id
                          ? `${option.color} border-white text-white`
                          : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-500'
                      }`}
                    >
                      <span className="text-2xl block mb-1">{option.icon}</span>
                      <span className="text-sm font-medium">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {selectedOutcome === 'follow_up' && (
                <div>
                  <label className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2 block">
                    Follow-up Date
                  </label>
                  <input
                    type="datetime-local"
                    value={followUpDate}
                    onChange={(e) => setFollowUpDate(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-green-500"
                  />
                </div>
              )}
              
              <div>
                <label className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2 block">
                  Notes
                </label>
                <textarea
                  value={outcomeNotes}
                  onChange={(e) => setOutcomeNotes(e.target.value)}
                  placeholder="Add notes about this call..."
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-500 resize-none"
                />
              </div>
              
              <button
                onClick={handleSaveOutcome}
                disabled={!selectedOutcome || savingOutcome}
                className={`w-full py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${
                  selectedOutcome && !savingOutcome
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                }`}
              >
                {savingOutcome ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <>Save Outcome</>
                )}
              </button>
            </div>
          )}
          
          {/* Transcript Tab */}
          {activeTab === 'transcript' && (
            <div className="space-y-6">
              {/* VAPI AI Badge if handled by Klara */}
              {(call.vapi_handled || call.vapi_transcript) && (
                <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 rounded-lg p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Bot size={20} className="text-purple-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Handled by AI Receptionist (Klara)</p>
                    <p className="text-gray-400 text-sm">This call was answered by your VAPI AI assistant</p>
                  </div>
                </div>
              )}
              
              {/* Transcript Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-gray-400 uppercase tracking-wider">
                    Call Transcript
                  </label>
                  <div className="flex items-center gap-2">
                    {(transcript?.has_recording || call.recording_url || call.vapi_recording_url) && !transcript?.transcript && !call.vapi_transcript && (
                      <button
                        onClick={transcribeCall}
                        disabled={loadingTranscript}
                        className="text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white"
                      >
                        {loadingTranscript ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <Mic size={12} />
                        )}
                        Transcribe Recording
                      </button>
                    )}
                    {(transcript?.recording_url || call.recording_url || call.vapi_recording_url) && (
                      <a
                        href={transcript?.recording_url || call.vapi_recording_url || call.recording_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-white flex items-center gap-1"
                      >
                        <Mic size={12} /> Play Recording
                      </a>
                    )}
                  </div>
                </div>
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 min-h-[150px]">
                  {loadingTranscript ? (
                    <div className="flex flex-col items-center justify-center py-8">
                      <Loader2 size={24} className="animate-spin text-green-500 mb-2" />
                      <p className="text-gray-400 text-sm">Transcribing with AI... This may take a moment.</p>
                    </div>
                  ) : (transcript?.transcript || call.vapi_transcript) ? (
                    <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
                      {call.vapi_transcript || transcript.transcript}
                    </p>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-gray-500 text-sm italic mb-3">
                        {(transcript?.has_recording || call.recording_url || call.vapi_recording_url)
                          ? 'Recording available. Click "Transcribe Recording" to generate transcript using AI.'
                          : 'No recording available for this call. Make a call with recording enabled to transcribe.'}
                      </p>
                      {!(transcript?.has_recording || call.recording_url || call.vapi_recording_url) && (
                        <p className="text-gray-600 text-xs">
                          Tip: Recordings are automatically saved when calls are made from the dashboard.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* VAPI AI Summary (if available) */}
              {call.vapi_summary && (
                <div>
                  <label className="text-sm font-semibold text-purple-400 uppercase tracking-wider mb-3 block">
                    🤖 AI Receptionist Summary
                  </label>
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                    <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
                      {call.vapi_summary}
                    </p>
                  </div>
                </div>
              )}
              
              {/* AI Summary Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-gray-400 uppercase tracking-wider">
                    AI Analysis & Summary
                  </label>
                  <button
                    onClick={generateSummary}
                    disabled={loadingSummary || (!transcript?.transcript && !call.vapi_transcript)}
                    className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 ${
                      (transcript?.transcript || call.vapi_transcript) && !loadingSummary
                        ? 'bg-purple-600 hover:bg-purple-700 text-white'
                        : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {loadingSummary ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Sparkles size={12} />
                    )}
                    Generate Summary
                  </button>
                </div>
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                  {loadingSummary ? (
                    <div className="flex flex-col items-center justify-center py-4">
                      <Loader2 size={24} className="animate-spin text-purple-500 mb-2" />
                      <p className="text-gray-400 text-sm">Analyzing call with AI...</p>
                    </div>
                  ) : summary?.summary ? (
                    <div className="space-y-4">
                      {/* Summary */}
                      <div>
                        <h4 className="text-purple-400 font-semibold text-sm mb-2">Summary</h4>
                        <p className="text-gray-300 text-sm leading-relaxed">
                          {summary.summary}
                        </p>
                      </div>
                      
                      {/* Action Items */}
                      {summary.action_items && summary.action_items.length > 0 && (
                        <div>
                          <h4 className="text-purple-400 font-semibold text-sm mb-2">Action Items</h4>
                          <ul className="list-disc list-inside text-gray-300 text-sm space-y-1">
                            {summary.action_items.map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Sentiment & Lead Quality */}
                      <div className="flex gap-4">
                        {summary.sentiment && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-xs">Sentiment:</span>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              summary.sentiment === 'positive' ? 'bg-green-500/20 text-green-400' :
                              summary.sentiment === 'negative' ? 'bg-red-500/20 text-red-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {summary.sentiment}
                            </span>
                          </div>
                        )}
                        {summary.lead_quality && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-xs">Lead Quality:</span>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              summary.lead_quality === 'hot' ? 'bg-orange-500/20 text-orange-400' :
                              summary.lead_quality === 'warm' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-blue-500/20 text-blue-400'
                            }`}>
                              {summary.lead_quality}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm italic">
                      Click "Generate Summary" to create an AI-powered summary of this call.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Lead Capture Tab */}
          {activeTab === 'lead' && (
            <div className="space-y-6">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                  <UserPlus size={18} className="text-blue-400" />
                  Capture Lead from Call
                </h3>
                <p className="text-gray-400 text-sm">
                  Convert this caller into a contact and add them to your sales pipeline.
                </p>
              </div>
              
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <h4 className="text-white font-medium mb-3">Caller Information</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Name:</span>
                    <span className="text-white">{displayName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Phone:</span>
                    <span className="text-white">{displayPhone}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Call Date:</span>
                    <span className="text-white">{callDate.toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 block">
                  Pipeline Stage
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {pipelineStages.map((stage) => (
                    <button
                      key={stage.id}
                      onClick={() => setPipelineStage(stage.id)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        pipelineStage === stage.id
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                      }`}
                    >
                      {stage.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <button
                onClick={handleCaptureLead}
                disabled={capturingLead}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
              >
                {capturingLead ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <>
                    <UserPlus size={20} />
                    Capture Lead & Add to Pipeline
                  </>
                )}
              </button>
              
              {call.lead_captured && (
                <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-3 flex items-center gap-2">
                  <CheckCircle size={18} className="text-green-400" />
                  <span className="text-green-400 text-sm">Lead already captured from this call</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper function to format email time
const formatEmailTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  if (days < 30) return `${Math.floor(days / 7)}w`;
  return date.toLocaleDateString();
};

// Helper function to categorize emails
const categorizeEmail = (email) => {
  const subject = (email.subject || '').toLowerCase();
  const from = (email.from_email || '').toLowerCase();
  
  if (subject.includes('urgent') || subject.includes('asap') || subject.includes('important')) {
    return 'urgent';
  }
  if (subject.includes('meeting') || subject.includes('schedule') || subject.includes('calendar')) {
    return 'follow-up';
  }
  if (subject.includes('report') || subject.includes('statement') || subject.includes('invoice')) {
    return 'report';
  }
  return 'client';
};

const RealEstateManagingPartnerCommunications = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  
  // User ID for Nylas API calls
  const userId = 'dwoodson';
  const [messageInput, setMessageInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [activeTab, setActiveTab] = useState('messages'); // 'messages' or 'email'
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [mobileShowChat, setMobileShowChat] = useState(false); // Mobile: show chat view instead of list
  const [conversations, setConversations] = useState([]);
  const [calls, setCalls] = useState([]);
  const [messages, setMessages] = useState([]);
  const [previousMessageCount, setPreviousMessageCount] = useState(0);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [justOpenedConversation, setJustOpenedConversation] = useState(false);
  const [showAIDraft, setShowAIDraft] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [showEmailComposeModal, setShowEmailComposeModal] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [emailBcc, setEmailBcc] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeFolder, setActiveFolder] = useState('inbox');
  
  // Voice client hook for real-time incoming calls
  const {
    isReady: voiceReady,
    isConnected: voiceConnected,
    activeCall: voiceActiveCall,
    incomingCall,
    isMuted,
    acceptCall,
    rejectCall,
    makeCall: voiceMakeCall,
    endCall: endVoiceCall,
    toggleMute,
    ignoreCall,
    error: voiceError
  } = useVoiceClient('dashboard_user');
  // Restore activeEmailAccount from sessionStorage
  const [activeEmailAccount, setActiveEmailAccount] = useState(() => {
    try {
      return sessionStorage.getItem('activeEmailAccount') || 'outlook';
    } catch {
      return 'outlook';
    }
  });
  const [activeCallFilter, setActiveCallFilter] = useState('all');
  const [activeCallCategory, setActiveCallCategory] = useState('all'); // for Family, Friends, Business tabs
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduledMessages, setScheduledMessages] = useState([]);
  const [showScheduledTracker, setShowScheduledTracker] = useState(false);
  const [messageAttachment, setMessageAttachment] = useState(null);
  const [uploadingMessageAttachment, setUploadingMessageAttachment] = useState(false);
  const messageAttachmentInputRef = React.useRef(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Email pagination state
  const [emailCurrentPage, setEmailCurrentPage] = useState(1);
  const emailsPerPage = 100;
  
  // Email search state
  const [emailSearchQuery, setEmailSearchQuery] = useState('');
  
  // Outlook email state - initialize from sessionStorage for persistence across page navigation
  const [outlookEmails, setOutlookEmails] = useState(() => {
    try {
      const cached = sessionStorage.getItem('outlookEmails');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  const [outlookEmailsCache, setOutlookEmailsCache] = useState(() => {
    try {
      const cached = sessionStorage.getItem('outlookEmailsCache');
      return cached ? JSON.parse(cached) : {};
    } catch { return {}; }
  });
  const [outlookLoading, setOutlookLoading] = useState(false);
  const [outlookConnected, setOutlookConnected] = useState(() => {
    try {
      return sessionStorage.getItem('outlookConnected') === 'true';
    } catch { return false; }
  });
  const [hasInitiallyFetched, setHasInitiallyFetched] = useState(() => {
    try {
      return sessionStorage.getItem('outlookHasInitiallyFetched') === 'true';
    } catch { return false; }
  });
  
  // Gmail connection state
  const [gmailConnected, setGmailConnected] = useState(false);
  const [gmailLoading, setGmailLoading] = useState(false);
  const [gmailEmail, setGmailEmail] = useState('creatrhq@gmail.com');
  const [gmailEmails, setGmailEmails] = useState([]);
  // Restore gmailEmailsCache from sessionStorage on mount
  const [gmailEmailsCache, setGmailEmailsCache] = useState(() => {
    try {
      const cached = sessionStorage.getItem('gmailEmailsCache');
      return cached ? JSON.parse(cached) : {};
    } catch {
      return {};
    }
  });
  
  // Nylas universal email state
  const [nylasEmails, setNylasEmails] = useState([]);
  const [nylasLoading, setNylasLoading] = useState(false);
  const [nylasEmailsCache, setNylasEmailsCache] = useState({});
  
  // Email account rename state
  const [renamingAccountId, setRenamingAccountId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  
  const [conversationToDelete, setConversationToDelete] = useState(null);
  const [showAIDraftMessage, setShowAIDraftMessage] = useState(false);
  const [showAIDraftEmail, setShowAIDraftEmail] = useState(false);
  const [aiDraftEmailText, setAiDraftEmailText] = useState('');
  const [aiDraftMessageText, setAiDraftMessageText] = useState('');
  const [generatingAIReply, setGeneratingAIReply] = useState(false);
  const [openMessageDropdown, setOpenMessageDropdown] = useState(null);
  const [openEmailDropdown, setOpenEmailDropdown] = useState(null);
  const [swipeOffset, setSwipeOffset] = useState({});
  const [showEmojiPicker, setShowEmojiPicker] = useState(null);
  const [messageReactions, setMessageReactions] = useState({});
  const [showGroupMembers, setShowGroupMembers] = useState(false);
  const [showAddContactToast, setShowAddContactToast] = useState(null);
  const [flaggedEmails, setFlaggedEmails] = useState({});
  const [pinnedConversations, setPinnedConversations] = useState({});
  const [showCallModal, setShowCallModal] = useState(false);
  const [showFaceTimeModal, setShowFaceTimeModal] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [isCalling, setIsCalling] = useState(false);
  const [showNewCallModal, setShowNewCallModal] = useState(false);
  const [activeMessagesCategory, setActiveMessagesCategory] = useState('all');
  const [selectedCallDetail, setSelectedCallDetail] = useState(null);  // For expanded call detail view
  const [callOutcomes, setCallOutcomes] = useState({});  // Track call outcomes by call_sid
  const [scheduledCountdown, setScheduledCountdown] = useState(0); // For forcing re-render of countdown
  
  // Custom Category Management State
  const [messageCategories, setMessageCategories] = useState(() => {
    try {
      const saved = localStorage.getItem('message_categories');
      return saved ? JSON.parse(saved) : ['urgent', 'follow-up', 'client', 'report', 'meeting', 'travel', 'other'];
    } catch { return ['urgent', 'follow-up', 'client', 'report', 'meeting', 'travel', 'other']; }
  });
  const [callCategories, setCallCategories] = useState(() => {
    try {
      const saved = localStorage.getItem('call_categories');
      return saved ? JSON.parse(saved) : ['family', 'friends', 'business', 'favorites'];
    } catch { return ['family', 'friends', 'business', 'favorites']; }
  });
  const [emailFolders, setEmailFolders] = useState(() => {
    try {
      const saved = localStorage.getItem('email_folders');
      return saved ? JSON.parse(saved) : ['inbox', 'sent', 'drafts', 'flagged', 'archive', 'trash', 'spam'];
    } catch { return ['inbox', 'sent', 'drafts', 'flagged', 'archive', 'trash', 'spam']; }
  });
  const [emailCategories, setEmailCategories] = useState(() => {
    try {
      const saved = localStorage.getItem('email_categories');
      return saved ? JSON.parse(saved) : ['urgent', 'orders', 'meeting', 'travel', 'client', 'report', 'other'];
    } catch { return ['urgent', 'orders', 'meeting', 'travel', 'client', 'report', 'other']; }
  });
  
  // Custom keywords for categories (used for auto-categorization)
  const [customCategoryKeywords, setCustomCategoryKeywords] = useState(() => {
    try {
      const saved = localStorage.getItem('custom_category_keywords');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryModalType, setCategoryModalType] = useState('messages'); // 'messages', 'calls', 'emails', 'emailCategories'
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryValue, setEditCategoryValue] = useState('');
  
  // Generate keywords from category name using AI-like expansion
  const generateKeywordsFromCategory = (categoryName) => {
    const name = categoryName.toLowerCase().replace(/-/g, ' ');
    const baseKeywords = [name];
    
    // Common keyword expansions based on category patterns
    const keywordExpansions = {
      'finance': ['payment', 'bank', 'invoice', 'statement', 'transaction', 'credit', 'debit', 'account', 'balance', 'transfer', 'wire', 'ach', 'financial', 'budget', 'expense', 'tax', 'receipt'],
      'shopping': ['order', 'purchase', 'buy', 'cart', 'checkout', 'delivery', 'shipping', 'tracking', 'amazon', 'ebay', 'store', 'product', 'item', 'sale', 'discount'],
      'social': ['facebook', 'instagram', 'twitter', 'linkedin', 'tiktok', 'snapchat', 'friend', 'follow', 'like', 'comment', 'share', 'post', 'notification'],
      'work': ['project', 'task', 'deadline', 'assignment', 'report', 'presentation', 'office', 'team', 'manager', 'colleague', 'workplace', 'job', 'career'],
      'health': ['doctor', 'appointment', 'medical', 'prescription', 'pharmacy', 'insurance', 'hospital', 'clinic', 'wellness', 'fitness', 'healthcare', 'patient'],
      'education': ['school', 'university', 'course', 'class', 'assignment', 'homework', 'exam', 'grade', 'student', 'teacher', 'professor', 'lecture', 'degree'],
      'entertainment': ['movie', 'music', 'concert', 'show', 'ticket', 'event', 'streaming', 'netflix', 'spotify', 'youtube', 'game', 'play'],
      'food': ['restaurant', 'delivery', 'order', 'menu', 'reservation', 'doordash', 'ubereats', 'grubhub', 'recipe', 'dining'],
      'sports': ['game', 'match', 'score', 'team', 'player', 'league', 'championship', 'workout', 'gym', 'fitness', 'training'],
      'news': ['breaking', 'headline', 'update', 'alert', 'newsletter', 'digest', 'daily', 'weekly', 'report', 'announcement'],
      'promotions': ['sale', 'discount', 'offer', 'deal', 'coupon', 'promo', 'limited time', 'special', 'exclusive', 'save', 'off'],
      'bills': ['bill', 'due', 'payment', 'utility', 'electric', 'water', 'gas', 'internet', 'phone', 'rent', 'mortgage', 'subscription'],
      'personal': ['friend', 'family', 'birthday', 'party', 'invitation', 'wedding', 'anniversary', 'holiday', 'vacation', 'plans'],
      'legal': ['contract', 'agreement', 'legal', 'attorney', 'lawyer', 'court', 'law', 'lawsuit', 'settlement', 'document'],
      'real-estate': ['property', 'house', 'apartment', 'listing', 'rent', 'lease', 'mortgage', 'realtor', 'showing', 'closing'],
      'investment': ['stock', 'invest', 'portfolio', 'dividend', 'trade', 'market', 'fund', 'retirement', '401k', 'ira', 'crypto', 'bitcoin'],
      'insurance': ['policy', 'claim', 'coverage', 'premium', 'deductible', 'insurance', 'insured', 'benefits'],
      'automotive': ['car', 'vehicle', 'auto', 'service', 'maintenance', 'repair', 'oil change', 'tire', 'dealer', 'registration'],
      'subscription': ['subscribe', 'subscription', 'renew', 'renewal', 'membership', 'plan', 'upgrade', 'downgrade', 'cancel'],
      'security': ['security', 'password', 'login', 'verify', 'authentication', '2fa', 'alert', 'suspicious', 'breach', 'protection']
    };
    
    // Check if the category name matches any known expansion
    for (const [key, keywords] of Object.entries(keywordExpansions)) {
      if (name.includes(key) || key.includes(name)) {
        return [...baseKeywords, ...keywords];
      }
    }
    
    // For custom categories, generate related words
    const words = name.split(' ');
    const related = [];
    words.forEach(word => {
      if (word.length > 2) {
        related.push(word);
        // Add common variations
        if (word.endsWith('s')) related.push(word.slice(0, -1));
        if (word.endsWith('ing')) related.push(word.slice(0, -3));
        if (word.endsWith('ed')) related.push(word.slice(0, -2));
      }
    });
    
    return [...new Set([...baseKeywords, ...related])];
  };
  
  // Messaging Connection State
  const [isMessagingConnected, setIsMessagingConnected] = useState(() => {
    try {
      return localStorage.getItem('messaging_connected') === 'true';
    } catch { return false; }
  });
  const [messagingConnectionType, setMessagingConnectionType] = useState(() => {
    try {
      return localStorage.getItem('messaging_connection_type') || null;
    } catch { return null; }
  });
  const [showMessagingConnectionModal, setShowMessagingConnectionModal] = useState(false);
  const [twilioAccountSid, setTwilioAccountSid] = useState('');
  const [twilioAuthToken, setTwilioAuthToken] = useState('');
  const [twilioPhoneNumber, setTwilioPhoneNumber] = useState('');
  const [messagingConnectionError, setMessagingConnectionError] = useState('');
  const [connectingMessaging, setConnectingMessaging] = useState(false);
  
  // Handle Twilio connection
  const handleTwilioConnect = async () => {
    if (!twilioAccountSid || !twilioAuthToken || !twilioPhoneNumber) {
      setMessagingConnectionError('Please fill in all Twilio credentials');
      return;
    }
    
    setConnectingMessaging(true);
    setMessagingConnectionError('');
    
    try {
      // Test Twilio connection
      const response = await fetch(`${apiUrl}/api/twilio-sms/test`);
      const data = await response.json();
      
      // Store connection in localStorage
      localStorage.setItem('messaging_connected', 'true');
      localStorage.setItem('messaging_connection_type', 'twilio');
      localStorage.setItem('twilio_phone_number', twilioPhoneNumber);
      
      setIsMessagingConnected(true);
      setMessagingConnectionType('twilio');
      setShowMessagingConnectionModal(false);
    } catch (error) {
      setMessagingConnectionError('Failed to connect to Twilio. Please check your credentials.');
    } finally {
      setConnectingMessaging(false);
    }
  };
  
  // Handle iPhone Shortcuts connection
  const handleShortcutsConnect = () => {
    // For Shortcuts, we just mark as connected since messages sync via webhook
    localStorage.setItem('messaging_connected', 'true');
    localStorage.setItem('messaging_connection_type', 'shortcuts');
    
    setIsMessagingConnected(true);
    setMessagingConnectionType('shortcuts');
    setShowMessagingConnectionModal(false);
  };
  
  // Category Management Functions
  const openCategoryModal = (type) => {
    setCategoryModalType(type);
    setShowCategoryModal(true);
    setNewCategoryName('');
    setEditingCategory(null);
    setEditCategoryValue('');
  };
  
  const addCategory = () => {
    if (!newCategoryName.trim()) return;
    const categoryKey = newCategoryName.toLowerCase().replace(/\s+/g, '-');
    
    if (categoryModalType === 'messages') {
      if (!messageCategories.includes(categoryKey)) {
        const updated = [...messageCategories, categoryKey];
        setMessageCategories(updated);
        localStorage.setItem('message_categories', JSON.stringify(updated));
        
        // Generate keywords for the new category
        const keywords = generateKeywordsFromCategory(newCategoryName);
        const updatedKeywords = { ...customCategoryKeywords, [categoryKey]: keywords };
        setCustomCategoryKeywords(updatedKeywords);
        localStorage.setItem('custom_category_keywords', JSON.stringify(updatedKeywords));
        
        toast.success(`Added "${newCategoryName}" category with ${keywords.length} auto-scan keywords`);
      } else {
        toast.error('Category already exists');
      }
    } else if (categoryModalType === 'calls') {
      if (!callCategories.includes(categoryKey)) {
        const updated = [...callCategories, categoryKey];
        setCallCategories(updated);
        localStorage.setItem('call_categories', JSON.stringify(updated));
        toast.success(`Added "${newCategoryName}" category`);
      } else {
        toast.error('Category already exists');
      }
    } else if (categoryModalType === 'emails') {
      if (!emailFolders.includes(categoryKey)) {
        const updated = [...emailFolders, categoryKey];
        setEmailFolders(updated);
        localStorage.setItem('email_folders', JSON.stringify(updated));
        toast.success(`Added "${newCategoryName}" folder`);
      } else {
        toast.error('Folder already exists');
      }
    } else if (categoryModalType === 'emailCategories') {
      if (!emailCategories.includes(categoryKey)) {
        const updated = [...emailCategories, categoryKey];
        setEmailCategories(updated);
        localStorage.setItem('email_categories', JSON.stringify(updated));
        
        // Generate keywords for the new category
        const keywords = generateKeywordsFromCategory(newCategoryName);
        const updatedKeywords = { ...customCategoryKeywords, [categoryKey]: keywords };
        setCustomCategoryKeywords(updatedKeywords);
        localStorage.setItem('custom_category_keywords', JSON.stringify(updatedKeywords));
        
        toast.success(`Added "${newCategoryName}" category with ${keywords.length} auto-scan keywords`);
      } else {
        toast.error('Category already exists');
      }
    }
    setNewCategoryName('');
  };
  
  const updateCategory = (oldKey, newName) => {
    if (!newName.trim()) return;
    const newKey = newName.toLowerCase().replace(/\s+/g, '-');
    
    if (categoryModalType === 'messages') {
      const updated = messageCategories.map(c => c === oldKey ? newKey : c);
      setMessageCategories(updated);
      localStorage.setItem('message_categories', JSON.stringify(updated));
      if (activeCategory === oldKey) setActiveCategory(newKey);
      
      // Generate keywords for the new category and save
      const keywords = generateKeywordsFromCategory(newName);
      const updatedKeywords = { ...customCategoryKeywords, [newKey]: keywords };
      delete updatedKeywords[oldKey];
      setCustomCategoryKeywords(updatedKeywords);
      localStorage.setItem('custom_category_keywords', JSON.stringify(updatedKeywords));
      
      toast.success(`Category renamed to "${newName}" - ${keywords.length} keywords auto-generated for scanning`);
    } else if (categoryModalType === 'calls') {
      const updated = callCategories.map(c => c === oldKey ? newKey : c);
      setCallCategories(updated);
      localStorage.setItem('call_categories', JSON.stringify(updated));
      if (activeCallCategory === oldKey) setActiveCallCategory(newKey);
    } else if (categoryModalType === 'emails') {
      const updated = emailFolders.map(c => c === oldKey ? newKey : c);
      setEmailFolders(updated);
      localStorage.setItem('email_folders', JSON.stringify(updated));
      if (activeFolder === oldKey) setActiveFolder(newKey);
    } else if (categoryModalType === 'emailCategories') {
      const updated = emailCategories.map(c => c === oldKey ? newKey : c);
      setEmailCategories(updated);
      localStorage.setItem('email_categories', JSON.stringify(updated));
      if (activeCategory === oldKey) setActiveCategory(newKey);
      
      // Generate keywords for the new category and save
      const keywords = generateKeywordsFromCategory(newName);
      const updatedKeywords = { ...customCategoryKeywords, [newKey]: keywords };
      delete updatedKeywords[oldKey];
      setCustomCategoryKeywords(updatedKeywords);
      localStorage.setItem('custom_category_keywords', JSON.stringify(updatedKeywords));
      
      toast.success(`Category renamed to "${newName}" - ${keywords.length} keywords auto-generated for email scanning`);
    }
    setEditingCategory(null);
    setEditCategoryValue('');
  };
  
  const deleteCategory = (categoryKey) => {
    // Don't allow deleting default categories
    const defaultMessageCats = ['urgent', 'follow-up', 'client'];
    const defaultCallCats = ['family', 'friends', 'business'];
    const defaultEmailFolders = ['inbox', 'sent', 'drafts', 'trash'];
    const defaultEmailCategories = ['urgent', 'orders', 'meeting'];
    
    if (categoryModalType === 'messages') {
      if (defaultMessageCats.includes(categoryKey)) {
        toast.error('Cannot delete default category');
        return;
      }
      const updated = messageCategories.filter(c => c !== categoryKey);
      setMessageCategories(updated);
      localStorage.setItem('message_categories', JSON.stringify(updated));
      if (activeCategory === categoryKey) setActiveCategory('all');
    } else if (categoryModalType === 'calls') {
      if (defaultCallCats.includes(categoryKey)) {
        toast.error('Cannot delete default category');
        return;
      }
      const updated = callCategories.filter(c => c !== categoryKey);
      setCallCategories(updated);
      localStorage.setItem('call_categories', JSON.stringify(updated));
      if (activeCallCategory === categoryKey) setActiveCallCategory('all');
    } else if (categoryModalType === 'emails') {
      if (defaultEmailFolders.includes(categoryKey)) {
        toast.error('Cannot delete default folder');
        return;
      }
      const updated = emailFolders.filter(c => c !== categoryKey);
      setEmailFolders(updated);
      localStorage.setItem('email_folders', JSON.stringify(updated));
      if (activeFolder === categoryKey) setActiveFolder('inbox');
    } else if (categoryModalType === 'emailCategories') {
      if (defaultEmailCategories.includes(categoryKey)) {
        toast.error('Cannot delete default category');
        return;
      }
      const updated = emailCategories.filter(c => c !== categoryKey);
      setEmailCategories(updated);
      localStorage.setItem('email_categories', JSON.stringify(updated));
      if (activeCategory === categoryKey) setActiveCategory('all');
    }
    toast.success('Category deleted');
  };
  
  const formatCategoryName = (key) => {
    return key.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };
  
  // Calls Connection State
  const [isCallsConnected, setIsCallsConnected] = useState(() => {
    try {
      return localStorage.getItem('calls_connected') === 'true';
    } catch { return false; }
  });
  const [callsConnectionType, setCallsConnectionType] = useState(() => {
    try {
      return localStorage.getItem('calls_connection_type') || null;
    } catch { return null; }
  });
  const [showCallsConnectionModal, setShowCallsConnectionModal] = useState(false);
  const [twilioCallsAccountSid, setTwilioCallsAccountSid] = useState('');
  const [twilioCallsAuthToken, setTwilioCallsAuthToken] = useState('');
  const [twilioCallsPhoneNumber, setTwilioCallsPhoneNumber] = useState('');
  const [callsConnectionError, setCallsConnectionError] = useState('');
  const [connectingCalls, setConnectingCalls] = useState(false);
  
  // Handle Twilio connection for calls
  const handleTwilioCallsConnect = async () => {
    if (!twilioCallsAccountSid || !twilioCallsAuthToken || !twilioCallsPhoneNumber) {
      setCallsConnectionError('Please fill in all Twilio credentials');
      return;
    }
    
    setConnectingCalls(true);
    setCallsConnectionError('');
    
    try {
      // Test Twilio connection
      const response = await fetch(`${apiUrl}/api/twilio-sms/test`);
      const data = await response.json();
      
      // Store connection in localStorage
      localStorage.setItem('calls_connected', 'true');
      localStorage.setItem('calls_connection_type', 'twilio');
      localStorage.setItem('twilio_calls_phone_number', twilioCallsPhoneNumber);
      
      setIsCallsConnected(true);
      setCallsConnectionType('twilio');
      setShowCallsConnectionModal(false);
    } catch (error) {
      setCallsConnectionError('Failed to connect to Twilio. Please check your credentials.');
    } finally {
      setConnectingCalls(false);
    }
  };
  
  // Handle iPhone Shortcuts connection for calls
  const handleCallsShortcutsConnect = () => {
    // For Shortcuts, we just mark as connected since call logs sync via webhook
    localStorage.setItem('calls_connected', 'true');
    localStorage.setItem('calls_connection_type', 'shortcuts');
    
    setIsCallsConnected(true);
    setCallsConnectionType('shortcuts');
    setShowCallsConnectionModal(false);
  };
  
  // Email Accounts Management State
  const [emailAccounts, setEmailAccounts] = useState(() => {
    try {
      const stored = localStorage.getItem('email_accounts');
      if (stored) {
        return JSON.parse(stored);
      }
      // Default accounts - includes wdjwood Gmail
      return [
        { id: 'outlook', name: 'Twenty8', type: 'outlook', email: 'darren@twenty8holdings.com', connected: false },
        { id: 'gmail', name: 'CreatRHQ', type: 'gmail', email: 'creatrhq@gmail.com', connected: false },
        { id: 'wdjwood', name: 'WDJ', type: 'gmail', email: 'wdjwood@gmail.com', connected: false }
      ];
    } catch { 
      return [
        { id: 'outlook', name: 'Twenty8', type: 'outlook', email: 'darren@twenty8holdings.com', connected: false },
        { id: 'gmail', name: 'CreatRHQ', type: 'gmail', email: 'creatrhq@gmail.com', connected: false },
        { id: 'wdjwood', name: 'WDJ', type: 'gmail', email: 'wdjwood@gmail.com', connected: false }
      ];
    }
  });
  const [showAddEmailModal, setShowAddEmailModal] = useState(false);
  const [newEmailName, setNewEmailName] = useState('');
  const [newEmailAddress, setNewEmailAddress] = useState('');
  const [newEmailType, setNewEmailType] = useState('outlook');
  const [newEmailPassword, setNewEmailPassword] = useState('');
  const [addingEmail, setAddingEmail] = useState(false);
  const [addEmailError, setAddEmailError] = useState('');
  
  // Save email accounts to localStorage
  useEffect(() => {
    localStorage.setItem('email_accounts', JSON.stringify(emailAccounts));
  }, [emailAccounts]);
  
  // ============== SMS WebSocket for Real-time Notifications ==============
  const smsWebSocketRef = useRef(null);
  const smsReconnectTimeoutRef = useRef(null);
  const selectedConversationRef = useRef(selectedConversation);
  
  // Keep ref in sync with state
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);
  
  // Function to fetch conversations (will be used to refresh after new SMS)
  const fetchConversations = useCallback(async () => {
    try {
      const data = await apiCall('/conversations');
      if (data) {
        // Sort by timestamp/updated_at with most recent first
        const sortedConversations = data.sort((a, b) => {
          const dateA = new Date(a.updated_at || a.timestamp || 0);
          const dateB = new Date(b.updated_at || b.timestamp || 0);
          return dateB - dateA;
        });
        setConversations(sortedConversations);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  }, []);
  
  // Connect to SMS WebSocket for real-time notifications - ONLY ONCE on mount
  useEffect(() => {
    let isConnecting = false;
    
    const connectSMSWebSocket = () => {
      // Prevent multiple simultaneous connection attempts
      if (isConnecting || (smsWebSocketRef.current?.readyState === WebSocket.OPEN)) {
        return;
      }
      
      isConnecting = true;
      
      // Build WebSocket URL from current location
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/api/webhooks/sms/ws`;
      
      console.log('📱 Connecting to SMS WebSocket:', wsUrl);
      
      try {
        // Close any existing connection first
        if (smsWebSocketRef.current) {
          smsWebSocketRef.current.close();
        }
        
        smsWebSocketRef.current = new WebSocket(wsUrl);
        
        smsWebSocketRef.current.onopen = () => {
          console.log('📱 SMS WebSocket connected!');
          isConnecting = false;
        };
        
        smsWebSocketRef.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            // Ignore pong messages
            if (data.type === 'pong') return;
            
            console.log('📱 SMS WebSocket message:', data);
            
            if (data.type === 'new_sms') {
              // Show single toast notification for new SMS
              toast.success(
                <div className="flex flex-col">
                  <span className="font-semibold">{data.sender}</span>
                  <span className="text-sm text-gray-600">{data.preview}</span>
                </div>,
                {
                  duration: 5000,
                  icon: '💬',
                  position: 'top-right',
                  id: `sms-${data.conversation_id}`, // Unique ID prevents duplicates
                }
              );
              
              // Refresh conversations to show the new message
              fetchConversations();
              
              // If we're viewing this conversation, mark as read (unread = 0)
              const currentConv = selectedConversationRef.current;
              if (currentConv?.phone === data.phone) {
                setConversations(prev => {
                  const updated = prev.map(conv => {
                    if (conv.phone === data.phone) {
                      return {
                        ...conv,
                        lastMessage: data.preview,
                        unread: 0, // We're viewing it, so mark as read
                        timestamp: data.time_display
                      };
                    }
                    return conv;
                  });
                  return updated;
                });
              }
            }
          } catch (e) {
            console.error('📱 Error parsing SMS WebSocket message:', e);
          }
        };
        
        smsWebSocketRef.current.onclose = (event) => {
          console.log('📱 SMS WebSocket disconnected:', event.code, event.reason);
          isConnecting = false;
          
          // Only reconnect if not intentionally closed
          if (event.code !== 1000) {
            smsReconnectTimeoutRef.current = setTimeout(() => {
              console.log('📱 Attempting to reconnect SMS WebSocket...');
              connectSMSWebSocket();
            }, 5000);
          }
        };
        
        smsWebSocketRef.current.onerror = (error) => {
          console.error('📱 SMS WebSocket error:', error);
          isConnecting = false;
        };
        
      } catch (error) {
        console.error('📱 Failed to create SMS WebSocket:', error);
        isConnecting = false;
      }
    };
    
    // Connect to WebSocket
    connectSMSWebSocket();
    
    // Send ping every 30 seconds to keep connection alive
    const pingInterval = setInterval(() => {
      if (smsWebSocketRef.current?.readyState === WebSocket.OPEN) {
        smsWebSocketRef.current.send('ping');
      }
    }, 30000);
    
    // Cleanup on unmount
    return () => {
      clearInterval(pingInterval);
      if (smsReconnectTimeoutRef.current) {
        clearTimeout(smsReconnectTimeoutRef.current);
      }
      if (smsWebSocketRef.current) {
        smsWebSocketRef.current.close(1000, 'Component unmounting');
      }
    };
  }, []); // Empty dependency array - only connect once on mount
  
  // Handle adding new email account
  const handleAddEmailAccount = () => {
    if (!newEmailName.trim()) {
      setAddEmailError('Please enter a name for this email account');
      return;
    }
    if (!newEmailAddress.trim()) {
      setAddEmailError('Please enter an email address');
      return;
    }
    // For iCloud, require app-specific password
    if (newEmailType === 'icloud' && !newEmailPassword.trim()) {
      setAddEmailError('Please enter your app-specific password for iCloud');
      return;
    }
    
    const newAccount = {
      id: `${newEmailType}_${Date.now()}`,
      name: newEmailName.trim(),
      type: newEmailType,
      email: newEmailAddress.trim(),
      password: newEmailPassword.trim() || null, // Store password for iCloud
      connected: newEmailType === 'icloud' && newEmailPassword.trim() ? true : false
    };
    
    // Store credentials securely in localStorage for the account
    if (newEmailPassword.trim()) {
      localStorage.setItem(`email_creds_${newAccount.id}`, JSON.stringify({
        email: newEmailAddress.trim(),
        password: newEmailPassword.trim()
      }));
    }
    
    setEmailAccounts(prev => [...prev, newAccount]);
    setShowAddEmailModal(false);
    setNewEmailName('');
    setNewEmailAddress('');
    setNewEmailType('outlook');
    setNewEmailPassword('');
    setAddEmailError('');
  };
  
  // Handle removing email account
  const handleRemoveEmailAccount = (accountId) => {
    setEmailAccounts(prev => prev.filter(acc => acc.id !== accountId));
    // If the removed account was active, switch to first available
    if (activeEmailAccount === accountId) {
      setActiveEmailAccount(emailAccounts[0]?.id || 'outlook');
    }
  };

  // Rename email account function
  const handleRenameEmailAccount = (accountId, newName) => {
    if (!newName.trim()) return;
    setEmailAccounts(prev => prev.map(acc => 
      acc.id === accountId ? { ...acc, name: newName.trim() } : acc
    ));
    setRenamingAccountId(null);
    setRenameValue('');
  };

  // Start renaming an account
  const startRenamingAccount = (accountId, currentName) => {
    setRenamingAccountId(accountId);
    setRenameValue(currentName);
  };
  
  // Timer effect for scheduled messages countdown
  useEffect(() => {
    if (showScheduledTracker && scheduledMessages.some(m => m.status === 'pending')) {
      const interval = setInterval(() => {
        setScheduledCountdown(prev => prev + 1);
        
        // Check if any messages should be marked as sent
        setScheduledMessages(prev => prev.map(msg => {
          if (msg.status === 'pending' && new Date(msg.scheduledFor) <= new Date()) {
            return { ...msg, status: 'sent' };
          }
          return msg;
        }));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [showScheduledTracker, scheduledMessages]);
  
  // Gmail cache version - increment this when categorization logic changes
  const GMAIL_CACHE_VERSION = '6';
  
  // Clear cache if version changed (to re-apply new categorization)
  useEffect(() => {
    const storedVersion = sessionStorage.getItem('gmailCacheVersion');
    if (storedVersion !== GMAIL_CACHE_VERSION) {
      console.log('📧 Cache version changed, clearing Gmail cache for re-categorization...');
      sessionStorage.removeItem('gmailEmailsCache');
      sessionStorage.setItem('gmailCacheVersion', GMAIL_CACHE_VERSION);
      setGmailEmailsCache({});
    }
  }, []);
  
  // Persist Gmail emails cache to sessionStorage whenever it changes
  useEffect(() => {
    if (Object.keys(gmailEmailsCache).length > 0) {
      try {
        sessionStorage.setItem('gmailEmailsCache', JSON.stringify(gmailEmailsCache));
        console.log('📧 Gmail emails cache persisted to sessionStorage');
      } catch (e) {
        console.warn('Failed to persist Gmail cache to sessionStorage:', e);
      }
    }
  }, [gmailEmailsCache]);

  // Persist activeEmailAccount to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem('activeEmailAccount', activeEmailAccount);
    } catch (e) {
      // Ignore storage errors
    }
  }, [activeEmailAccount]);

  // Fetch connected Nylas accounts on mount and add them to emailAccounts
  useEffect(() => {
    const fetchNylasAccounts = async () => {
      try {
        const apiBase = window.location.origin;
        const response = await fetch(`${apiBase}/api/nylas/accounts?user_id=${userId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.accounts && data.accounts.length > 0) {
            console.log('📧 Fetched Nylas accounts:', data.accounts);
            
            // Add Nylas accounts to emailAccounts if not already present
            setEmailAccounts(prev => {
              const updatedAccounts = [...prev];
              
              data.accounts.forEach(nylasAccount => {
                // Check if this account already exists in emailAccounts
                const existingIndex = updatedAccounts.findIndex(acc => 
                  acc.email === nylasAccount.email || 
                  (acc.type === 'nylas' && acc.id === `nylas_${nylasAccount.grant_id}`)
                );
                
                if (existingIndex === -1) {
                  // Add new Nylas account
                  updatedAccounts.push({
                    id: `nylas_${nylasAccount.grant_id || nylasAccount.email}`,
                    name: nylasAccount.account_name || nylasAccount.email.split('@')[0],
                    type: 'nylas',
                    email: nylasAccount.email,
                    provider: nylasAccount.provider,
                    connected: true,
                    grant_id: nylasAccount.grant_id
                  });
                  console.log(`📧 Added Nylas account: ${nylasAccount.email}`);
                } else {
                  // Update existing account to mark as connected
                  updatedAccounts[existingIndex] = {
                    ...updatedAccounts[existingIndex],
                    connected: true,
                    type: 'nylas',
                    provider: nylasAccount.provider,
                    grant_id: nylasAccount.grant_id
                  };
                  console.log(`📧 Updated existing account: ${nylasAccount.email}`);
                }
              });
              
              return updatedAccounts;
            });
          }
        }
      } catch (error) {
        console.error('Error fetching Nylas accounts:', error);
      }
    };
    
    fetchNylasAccounts();
  }, []);

  // Check Gmail connection status on mount
  useEffect(() => {
    checkGmailConnectionStatus();
    checkAllGmailAccountsStatus(); // Check all Gmail accounts' status
    
    // Restore Gmail emails from cache if available
    try {
      const cached = sessionStorage.getItem('gmailEmailsCache');
      if (cached) {
        const parsedCache = JSON.parse(cached);
        if (parsedCache['inbox'] && parsedCache['inbox'].length > 0) {
          setGmailEmails(parsedCache['inbox']);
          console.log(`📧 Restored ${parsedCache['inbox'].length} Gmail inbox emails from sessionStorage`);
        }
      }
    } catch (e) {
      console.warn('Failed to restore Gmail cache from sessionStorage:', e);
    }
    
    // Check if we just returned from Gmail OAuth
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('gmail_connected') === 'true') {
      const connectedAccountId = urlParams.get('account') || 'creatrhq';
      
      // Map backend account_id to frontend emailAccounts id
      const accountIdMapping = {
        'creatrhq': 'gmail',
        'wdjwood': 'wdjwood',
        'darren': 'darren',
        'creatrverse': 'creatrverse',
        'kallie': 'kallie'
      };
      const frontendAccountId = accountIdMapping[connectedAccountId] || connectedAccountId;
      
      // Update the connected status for the correct account
      setEmailAccounts(prev => prev.map(acc => 
        acc.id === frontendAccountId ? { ...acc, connected: true } : acc
      ));
      
      setGmailConnected(true);
      setActiveTab('email');
      setActiveEmailAccount(frontendAccountId);
      
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      // Refresh Gmail status
      checkGmailConnectionStatus();
    } else if (urlParams.get('gmail_error') === 'true') {
      const errorAccount = urlParams.get('account') || 'unknown';
      alert(`Failed to connect Gmail account (${errorAccount}). Please try again.`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Check if we just returned from Nylas OAuth
    if (urlParams.get('nylas_connected') === 'true') {
      const email = urlParams.get('email') || '';
      const provider = urlParams.get('provider') || '';
      
      // Add the new Nylas account to emailAccounts
      const newAccountId = `nylas_${Date.now()}`;
      setEmailAccounts(prev => {
        // Check if account with this email already exists
        const existing = prev.find(acc => acc.email === email);
        if (existing) {
          return prev.map(acc => 
            acc.email === email ? { ...acc, connected: true, type: 'nylas', provider } : acc
          );
        }
        // Add new account
        return [...prev, {
          id: newAccountId,
          name: email.split('@')[0],
          type: 'nylas',
          email: email,
          provider: provider,
          connected: true
        }];
      });
      
      setActiveTab('email');
      setActiveEmailAccount(newAccountId);
      
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Show success message
      alert(`Successfully connected ${email} (${provider})`);
    } else if (urlParams.get('nylas_error') === 'true') {
      const errorMessage = urlParams.get('message') || 'Unknown error';
      alert(`Failed to connect email: ${errorMessage}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  
  // Map email addresses to backend account IDs
  const getGmailAccountId = (email) => {
    const emailToAccountId = {
      'creatrhq@gmail.com': 'creatrhq',
      'darren@creatrverse.com': 'darren',
      'wdjwood@gmail.com': 'wdjwood',
      'creatrverse@gmail.com': 'creatrverse',
      'kalliestockton@creatrverse.com': 'kallie'
    };
    return emailToAccountId[email?.toLowerCase()] || 'creatrhq';
  };

  // Get current Gmail account ID based on active account
  const getCurrentGmailAccountId = () => {
    const activeAccount = emailAccounts.find(acc => acc.id === activeEmailAccount);
    if (activeAccount && activeAccount.type === 'gmail') {
      return getGmailAccountId(activeAccount.email);
    }
    // Default to creatrhq for the default gmail account
    const defaultGmail = emailAccounts.find(acc => acc.id === 'gmail');
    return getGmailAccountId(defaultGmail?.email);
  };

  // Check Gmail connection status for all Gmail accounts
  const checkGmailConnectionStatus = async () => {
    try {
      // Check status for the currently active Gmail account
      const accountId = getCurrentGmailAccountId();
      const response = await fetch(`${window.location.origin}/api/gmail/status?account_id=${accountId}`);
      const data = await response.json();
      setGmailConnected(data.connected === true);
      if (data.email) {
        setGmailEmail(data.email);
      }
      console.log('📧 Gmail connection status:', data);
      
      // Also update the emailAccounts state with the connection status
      if (data.connected) {
        setEmailAccounts(prev => prev.map(acc => {
          if (acc.type === 'gmail' && getGmailAccountId(acc.email) === accountId) {
            return { ...acc, connected: true };
          }
          return acc;
        }));
      }
    } catch (error) {
      console.error('Failed to check Gmail status:', error);
      setGmailConnected(false);
    }
  };
  
  // Check all Gmail accounts' connection status on mount
  const checkAllGmailAccountsStatus = async () => {
    try {
      // Get status for all accounts
      const response = await fetch(`${window.location.origin}/api/gmail/status`);
      const data = await response.json();
      
      if (data.accounts) {
        setEmailAccounts(prev => prev.map(acc => {
          if (acc.type === 'gmail') {
            const backendAccountId = getGmailAccountId(acc.email);
            const accountStatus = data.accounts[backendAccountId];
            if (accountStatus) {
              return { ...acc, connected: accountStatus.connected };
            }
          }
          return acc;
        }));
      }
    } catch (error) {
      console.error('Failed to check all Gmail accounts status:', error);
    }
  };
  
  // Connect Gmail account - now supports any configured account
  const connectGmail = async (accountEmail = null) => {
    setGmailLoading(true);
    try {
      // Determine which account to connect
      let accountId;
      if (accountEmail) {
        accountId = getGmailAccountId(accountEmail);
      } else {
        // Use the currently active account or default
        const activeAccount = emailAccounts.find(acc => acc.id === activeEmailAccount);
        if (activeAccount && activeAccount.type === 'gmail') {
          accountId = getGmailAccountId(activeAccount.email);
        } else {
          const defaultGmail = emailAccounts.find(acc => acc.id === 'gmail');
          accountId = getGmailAccountId(defaultGmail?.email);
        }
      }
      
      console.log('🔗 Connecting Gmail account:', accountId);
      const response = await fetch(`${window.location.origin}/api/gmail/auth/url?account_id=${accountId}`);
      const data = await response.json();
      if (data.auth_url) {
        // Redirect to Google OAuth
        window.location.href = data.auth_url;
      } else {
        throw new Error('No auth URL returned');
      }
    } catch (error) {
      console.error('Failed to get Gmail auth URL:', error);
      alert('Failed to connect Gmail. Please try again.');
      setGmailLoading(false);
    }
  };
  
  // Sync Gmail emails - accepts optional accountId to avoid stale closure issues
  const syncGmailEmails = async (folder = 'inbox', overrideAccountId = null, frontendAccountId = null) => {
    if (gmailLoading) return;
    
    setGmailLoading(true);
    
    try {
      // Map folder names to Gmail labels
      const labelMap = {
        'inbox': 'INBOX',
        'sent': 'SENT',
        'drafts': 'DRAFT',
        'spam': 'SPAM',
        'trash': 'TRASH',
        'starred': 'STARRED',
        'flagged': 'STARRED',  // Gmail uses STARRED for flagged
        'archive': 'all',      // Gmail "All Mail" for archived emails
        'important': 'IMPORTANT'
      };
      
      const label = labelMap[folder] || 'INBOX';
      // Use override if provided, otherwise get from current state
      const accountId = overrideAccountId || getCurrentGmailAccountId();
      // Frontend account ID for caching (e.g., 'wdjwood' not 'gmail')
      const feAccountId = frontendAccountId || activeEmailAccount;
      
      console.log(`📧 Fetching Gmail emails for ${feAccountId}/${folder}`);
      const response = await fetch(`${window.location.origin}/api/gmail/messages?account_id=${accountId}&label_id=${label}&max_results=50`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch Gmail emails');
      }
      
      const data = await response.json();
      const messages = data.messages || [];
      
      // Helper function to parse "Name <email>" format
      const parseFromField = (fromStr) => {
        if (!fromStr) return { name: 'Unknown', email: '' };
        // Match "Name <email>" format
        const match = fromStr.match(/^(.+?)\s*<(.+?)>$/);
        if (match) {
          return { name: match[1].trim(), email: match[2].trim() };
        }
        // If it's just an email
        if (fromStr.includes('@')) {
          return { name: fromStr.split('@')[0], email: fromStr };
        }
        return { name: fromStr, email: '' };
      };
      
      // Transform Gmail messages to match the email structure used by Twenty8
      const transformedEmails = messages.map((msg, index) => {
        const fromParsed = parseFromField(msg.from);
        return {
          id: msg.id || `gmail-${index}`,
          from: fromParsed.name,
          email: fromParsed.email,
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(fromParsed.name || 'U')}&background=random&size=100`,
          subject: msg.subject || '(No Subject)',
          preview: msg.snippet || msg.body_preview || msg.body?.substring(0, 150) || '',
          body: msg.body || msg.snippet || '',
          time: formatGmailTime(msg.date),
          date: msg.date,
          category: categorizeEmail(msg),
          folder: folder,
          read: !msg.is_unread,
          starred: msg.is_starred,
          hasAttachments: msg.has_attachments,
          source: 'gmail',
          thread: [{
            from: fromParsed.name,
            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(fromParsed.name || 'U')}&background=random&size=100`,
            time: formatGmailTime(msg.date),
            body: msg.body || msg.snippet || ''
          }]
        };
      });
      
      console.log(`📧 Gmail: Fetched ${transformedEmails.length} emails for ${feAccountId}/${folder}`);
      
      // Update cache and state - use account-specific cache key
      const cacheKey = `${feAccountId}_${folder}`;
      setGmailEmailsCache(prev => ({
        ...prev,
        [cacheKey]: transformedEmails
      }));
      setGmailEmails(transformedEmails);
      setGmailConnected(true);
      
      // Also update account connected status
      setEmailAccounts(prev => prev.map(acc => {
        if (acc.id === feAccountId) {
          return { ...acc, connected: true };
        }
        return acc;
      }));
      
    } catch (error) {
      console.error('❌ Failed to sync Gmail emails:', error);
      // If token expired, mark as disconnected
      if (error.message.includes('invalid_grant') || error.message.includes('Token')) {
        setGmailConnected(false);
        // Mark the specific account as disconnected
        setEmailAccounts(prev => prev.map(acc => {
          if (acc.id === feAccountId) {
            return { ...acc, connected: false };
          }
          return acc;
        }));
      }
    } finally {
      setGmailLoading(false);
    }
  };
  
  // Sync Nylas emails for universal email provider support
  const syncNylasEmails = async (folder = 'inbox', accountEmail = null, frontendAccountId = null) => {
    if (nylasLoading) return;
    
    setNylasLoading(true);
    
    try {
      const userId = 'dwoodson';
      const emailParam = accountEmail ? `&email=${encodeURIComponent(accountEmail)}` : '';
      const feAccountId = frontendAccountId || activeEmailAccount;
      
      console.log(`📧 Fetching Nylas emails for ${feAccountId}/${folder}`);
      const response = await fetch(`${window.location.origin}/api/nylas/messages?user_id=${userId}&folder=${folder}&limit=50${emailParam}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch Nylas emails');
      }
      
      const data = await response.json();
      const messages = data.messages || [];
      
      // Transform to common email format
      const transformedEmails = messages.map(msg => ({
        id: msg.id,
        threadId: msg.thread_id,
        subject: msg.subject || '(No Subject)',
        snippet: msg.snippet || '',
        from: msg.from?.email || 'unknown',
        fromName: msg.from?.name || msg.from?.email?.split('@')[0] || 'Unknown',
        to: msg.to?.map(t => t.email) || [],
        date: msg.date ? new Date(msg.date * 1000).toISOString() : new Date().toISOString(),
        unread: msg.unread,
        starred: msg.starred,
        hasAttachments: msg.has_attachments,
        source: 'nylas',
        category: 'Other'
      }));
      
      console.log(`📧 Nylas: Fetched ${transformedEmails.length} emails for ${feAccountId}/${folder}`);
      
      // Update cache and state
      const cacheKey = `${feAccountId}_${folder}`;
      setNylasEmailsCache(prev => ({
        ...prev,
        [cacheKey]: transformedEmails
      }));
      setNylasEmails(transformedEmails);
      
    } catch (error) {
      console.error('❌ Failed to sync Nylas emails:', error);
    } finally {
      setNylasLoading(false);
    }
  };
  
  // Format Gmail timestamp
  const formatGmailTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 60) return `${diffMins}m`;
      if (diffHours < 24) return `${diffHours}h`;
      if (diffDays < 7) return `${diffDays}d`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)}w`;
      return `${Math.floor(diffDays / 30)}mo`;
    } catch {
      return '';
    }
  };
  
  // Helper function to parse email body and split quoted messages into separate parts
  const parseEmailThread = (body, originalFrom, originalEmail, originalTime) => {
    if (!body) return [{ from: originalFrom, email: originalEmail, time: originalTime, body: '', isQuoted: false }];
    
    const messages = [];
    
    // First try to split by Gmail's blockquote structure
    // Gmail wraps quotes in: <div class="hmail-attr">On ... wrote:</div><blockquote>...</blockquote>
    const gmailQuotePattern = /<div[^>]*class="[^"]*hmail-attr[^"]*"[^>]*>([^<]*(?:On[^<]*wrote:|From:[^<]*))<\/div>\s*<blockquote[^>]*class="[^"]*hmail-quote[^"]*"[^>]*>([\s\S]*?)(?=<div[^>]*class="[^"]*hmail-attr|$)/gi;
    
    // Also handle gmail_quote pattern
    const gmailQuote2Pattern = /<div[^>]*class="[^"]*gmail_attr[^"]*"[^>]*>([^<]*(?:On[^<]*wrote:|From:[^<]*))<\/div>\s*<div[^>]*class="[^"]*gmail_quote[^"]*"[^>]*>([\s\S]*?)(?=<div[^>]*class="[^"]*gmail_attr|$)/gi;
    
    let workingBody = body;
    let lastIndex = 0;
    let foundQuotes = false;
    
    // Try Gmail hmail-quote pattern first
    const quoteMatches = [];
    let match;
    
    // Find all quote containers
    const containerPattern = /<div[^>]*class="[^"]*(?:hmail-quote-container|gmail_quote_container)[^"]*"[^>]*>([\s\S]*?)(?=<div[^>]*class="[^"]*(?:hmail-quote-container|gmail_quote_container)|$)/gi;
    
    while ((match = containerPattern.exec(body)) !== null) {
      quoteMatches.push({
        fullMatch: match[0],
        content: match[1],
        index: match.index
      });
    }
    
    if (quoteMatches.length > 0) {
      foundQuotes = true;
      
      // Get the main message (before any quote containers)
      const firstQuoteIndex = quoteMatches[0].index;
      const mainContent = body.substring(0, firstQuoteIndex).trim();
      
      if (mainContent) {
        // Clean HTML and extract text
        const cleanMain = mainContent
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<\/div>/gi, '\n')
          .replace(/<div[^>]*>/gi, '')
          .replace(/<[^>]+>/g, '')
          .replace(/&nbsp;/g, ' ')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/\n{3,}/g, '\n\n')
          .trim();
        
        if (cleanMain) {
          messages.push({
            from: originalFrom,
            email: originalEmail,
            time: originalTime,
            body: cleanMain,
            isQuoted: false
          });
        }
      }
      
      // Process each quoted section
      for (const quoteMatch of quoteMatches) {
        const quoteContent = quoteMatch.content;
        
        // Extract the "On ... wrote:" attribution - handle both raw text and within div
        let attrText = '';
        const attrMatch = quoteContent.match(/<div[^>]*class="[^"]*(?:hmail-attr|gmail_attr)[^"]*"[^>]*>([\s\S]*?)<\/div>/i);
        if (attrMatch) {
          attrText = attrMatch[1]
            .replace(/<[^>]+>/g, '')
            .replace(/&nbsp;/g, ' ')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .trim();
        }
        
        let senderName = 'Previous message';
        let senderEmail = '';
        
        if (attrText) {
          // Extract sender name and email from "On Wed, Feb 4, 2026 at 4:22 AM CREATRVERSE <creatrhq@gmail.com> wrote:"
          // Pattern: everything after time and before < or "wrote:"
          const senderMatch = attrText.match(/(?:On\s+\w+,?\s+\w+\s+\d{1,2},?\s+\d{4}\s+at\s+\d{1,2}:\d{2}\s*(?:AM|PM)?\s+)?(.+?)(?:\s*<([^>]+)>)?\s*wrote:/i);
          if (senderMatch) {
            senderName = senderMatch[1]?.trim() || 'Previous message';
            senderEmail = senderMatch[2]?.trim() || '';
          }
        }
        
        // Extract the quoted message body (inside blockquote or gmail_quote div)
        // First get content after the attr div
        let quotedBodyHtml = quoteContent;
        if (attrMatch) {
          const attrEndIndex = quoteContent.indexOf(attrMatch[0]) + attrMatch[0].length;
          quotedBodyHtml = quoteContent.substring(attrEndIndex);
        }
        
        // Look for blockquote or extract remaining content
        const blockquoteMatch = quotedBodyHtml.match(/<blockquote[^>]*>([\s\S]*?)<\/blockquote>/i);
        let quotedBody = '';
        
        if (blockquoteMatch) {
          quotedBody = blockquoteMatch[1];
        } else {
          // Use the remaining content
          quotedBody = quotedBodyHtml;
        }
        
        // Clean the body - but check for nested quote containers first
        const hasNestedQuotes = quotedBody.includes('hmail-quote-container') || quotedBody.includes('gmail_quote_container');
        
        if (hasNestedQuotes) {
          // Split nested quotes into separate messages recursively
          const nestedParsed = parseEmailThread(quotedBody, senderName, senderEmail, '');
          messages.push(...nestedParsed.map(m => ({ ...m, isQuoted: true })));
        } else {
          // Clean and add as single quoted message
          quotedBody = quotedBody
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<\/div>/gi, '\n')
            .replace(/<div[^>]*>/gi, '')
            .replace(/<[^>]+>/g, '')
            .replace(/&nbsp;/g, ' ')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/\n{3,}/g, '\n\n')
            .trim();
          
          // Don't add if it's just another nested quote marker or empty
          if (quotedBody && quotedBody.length > 5 && !quotedBody.match(/^On\s+\w+,?\s+\w+\s+\d/)) {
            messages.push({
              from: senderName,
              email: senderEmail,
              time: '',
              body: quotedBody,
              isQuoted: true
            });
          }
        }
      }
    }
    
    // If no Gmail quote structure found, try plain text patterns
    if (!foundQuotes) {
      const plainTextBody = body
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/div>/gi, '\n')
        .replace(/<div[^>]*>/gi, '')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .trim();
      
      // Split by multiple quote patterns
      // This regex captures:
      // 1. "From: [Name] <email>\nDate/Sent: [date]\nTo: [name]" format (Outlook/Mac Mail)
      // 2. "On [Day], [Date] at [Time] [Name] <email> wrote:" format (Gmail)
      
      const combinedPattern = /(?:(?:^|\n)From:\s*([^\n<]+?)(?:\s*<([^>]+)>)?\s*\n\s*(?:Date|Sent):\s*[^\n]+\n\s*To:\s*[^\n]+(?:\n\s*Subject:\s*[^\n]+)?\s*\n)|(?:On\s+(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)(?:day)?,?\s+(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+\d{1,2},?\s+\d{4}\s+at\s+\d{1,2}:\d{2}\s*(?:AM|PM)?\s*,?\s*([^\n<]+?)(?:\s*<([^>\n]+)>)?\s*wrote:)/gi;
      
      // Find all matches and their positions
      const matches = [];
      let match;
      combinedPattern.lastIndex = 0;
      
      while ((match = combinedPattern.exec(plainTextBody)) !== null) {
        // Extract sender from either capture group (From: pattern or On wrote: pattern)
        const senderName = (match[1] || match[3] || 'Previous message').trim();
        const senderEmail = (match[2] || match[4] || '').trim();
        
        matches.push({
          index: match.index,
          length: match[0].length,
          senderName,
          senderEmail
        });
      }
      
      if (matches.length > 0) {
        // First section is the main message (before first quote)
        const firstQuoteIndex = matches[0].index;
        const mainContent = plainTextBody.substring(0, firstQuoteIndex).trim();
        
        if (mainContent && mainContent.length > 5) {
          messages.push({
            from: originalFrom,
            email: originalEmail,
            time: originalTime,
            body: mainContent,
            isQuoted: false
          });
        }
        
        // Process each quoted section
        for (let i = 0; i < matches.length; i++) {
          const currentMatch = matches[i];
          const nextMatch = matches[i + 1];
          
          // Get content after this quote header until next quote or end
          const startPos = currentMatch.index + currentMatch.length;
          const endPos = nextMatch ? nextMatch.index : plainTextBody.length;
          const quotedContent = plainTextBody.substring(startPos, endPos).trim();
          
          if (quotedContent && quotedContent.length > 5) {
            // Check if sender is "me" (CREATRVERSE/creatrhq)
            const isSentByMe = currentMatch.senderName.toLowerCase().includes('creatrverse') ||
                               currentMatch.senderEmail.toLowerCase().includes('creatrhq');
            
            messages.push({
              from: isSentByMe ? 'You' : currentMatch.senderName,
              email: currentMatch.senderEmail,
              time: '',
              body: quotedContent,
              isQuoted: true
            });
          }
        }
      }
    }
    
    // If still no messages parsed, return original as single message
    if (messages.length === 0) {
      const cleanBody = body
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/div>/gi, '\n')
        .replace(/<div[^>]*>/gi, '')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
      
      messages.push({
        from: originalFrom,
        email: originalEmail,
        time: originalTime,
        body: cleanBody || body,
        isQuoted: false
      });
    }
    
    return messages.filter(m => m.body && m.body.trim().length > 0);
  };
  
  // Helper function to detect if sender is a person vs company
  const isPersonalSender = (from, email) => {
    const fromLower = (from || '').toLowerCase();
    const emailLower = (email || '').toLowerCase();
    
    // Company indicators
    const companyIndicators = [
      'noreply', 'no-reply', 'donotreply', 'do-not-reply', 'notifications',
      'support', 'team', 'info@', 'hello@', 'contact@', 'sales@', 'marketing@',
      'newsletter', 'updates', 'billing@', 'accounts@', 'service@', 'help@',
      '.com>', 'inc', 'llc', 'corp', 'ltd', 'company', 'enterprise', 'solutions',
      'automated', 'system', 'alert', 'notification'
    ];
    
    // If email contains company indicators, it's not personal
    if (companyIndicators.some(ind => emailLower.includes(ind) || fromLower.includes(ind))) {
      return false;
    }
    
    // Check if sender name looks like a person (has first and last name pattern)
    const nameParts = fromLower.split(' ').filter(p => p.length > 1);
    if (nameParts.length >= 2 && !companyIndicators.some(ind => nameParts.some(p => p.includes(ind)))) {
      return true;
    }
    
    return false;
  };
  
  // Categorize email based on content
  const categorizeEmail = (email) => {
    const subject = (email.subject || '').toLowerCase();
    const from = (email.from || email.from_email || '').toLowerCase();
    const fromEmail = (email.email || email.from_email || '').toLowerCase();
    const body = (email.body || email.snippet || email.preview || '').toLowerCase();
    const to = (email.to || '').toLowerCase();
    
    // Combine all text for comprehensive scanning
    const allText = `${subject} ${from} ${body} ${to}`;
    
    // Check for dollar amounts (e.g., $10, $99.99, $1,000.00)
    const hasDollarAmount = /\$\d+(?:,\d{3})*(?:\.\d{2})?/.test(subject + body);
    
    // TRAVEL - Flights, bookings, itineraries, boarding passes
    const travelKeywords = [
      'flight', 'booking confirmation', 'itinerary', 'boarding pass', 'check-in',
      'airline', 'airport', 'departure', 'arrival', 'layover', 'gate',
      'seat assignment', 'e-ticket', 'travel confirmation', 'trip confirmation',
      'hotel reservation', 'hotel booking', 'car rental', 'rental confirmation',
      'united airlines', 'american airlines', 'delta', 'southwest', 'jetblue',
      'british airways', 'lufthansa', 'emirates', 'frontier', 'spirit airlines',
      'alaska airlines', 'hawaiian airlines', 'air france', 'klm',
      'booking.com', 'expedia', 'kayak', 'priceline', 'tripadvisor',
      'marriott', 'hilton', 'hyatt', 'airbnb', 'vrbo',
      'tsa precheck', 'global entry', 'passport', 'visa application',
      'fly', 'nonstop', 'round trip', 'one way', 'class of service',
      'baggage', 'carry-on', 'checked bag', 'travel insurance',
      'has shipped' // catches "Your Order Has Shipped" from airlines
    ];
    const travelSenders = [
      'airline', 'airlines', 'booking', 'expedia', 'kayak', 'travel',
      'marriott', 'hilton', 'hyatt', 'airbnb', 'hertz', 'avis', 'enterprise'
    ];
    if (travelKeywords.some(keyword => allText.includes(keyword)) ||
        travelSenders.some(sender => from.includes(sender) || fromEmail.includes(sender))) {
      return 'travel';
    }
    
    // ORDERS - Receipt/Invoice/Order related OR contains dollar amounts
    const orderKeywords = [
      'receipt', 'invoice', 'order', 'purchase', 'transaction', 'payment received',
      'order confirmation', 'order shipped', 'order delivered', 'tracking number',
      'your order', 'order #', 'invoice #', 'receipt #', 'payment confirmation',
      'billing statement', 'subscription renewed', 'charge', 'refund', 'paid',
      'total:', 'amount:', 'price', 'cost', 'fee', 'payment'
    ];
    if (hasDollarAmount || orderKeywords.some(keyword => allText.includes(keyword))) {
      return 'orders';
    }
    
    // MEETING - Tours, meetings, calls, scheduling
    const meetingKeywords = [
      'meeting', 'schedule', 'calendar', 'appointment', 'call', 'zoom', 'teams',
      'tour', 'showing', 'viewing', 'walkthrough', 'site visit', 'property tour',
      'phone call', 'video call', 'conference', 'webinar', 'demo', 'presentation',
      'reschedule', 'agenda', 'invite', 'invitation', 'rsvp', 'confirm attendance',
      'availability', 'book a time', 'schedule a call', 'let\'s meet', 'meet up',
      'catch up', 'touch base', 'check in', 'sync up', 'huddle', 'standup'
    ];
    if (meetingKeywords.some(keyword => allText.includes(keyword))) {
      return 'meeting';
    }
    
    // URGENT - Only from personal senders (not companies)
    const urgentKeywords = [
      'urgent', 'asap', 'immediately', 'critical', 'emergency', 'time-sensitive',
      'action required', 'immediate attention', 'priority', 'deadline', 'overdue',
      'need help', 'please respond', 'waiting for', 'as soon as possible'
    ];
    if (urgentKeywords.some(keyword => allText.includes(keyword))) {
      if (isPersonalSender(from, fromEmail)) {
        return 'urgent';
      }
    }
    
    // CLIENT - Business/client communications (not orders)
    const clientKeywords = [
      'contract', 'agreement', 'proposal', 'quote', 'estimate', 'project',
      'customer', 'client', 'deal', 'opportunity', 'partnership', 'collaboration',
      'account update', 'service update', 'renewal notice', 'subscription'
    ];
    if (clientKeywords.some(keyword => allText.includes(keyword))) {
      return 'client';
    }
    
    // REPORT - Automated/newsletter/notification emails
    const reportKeywords = [
      'report', 'analytics', 'summary', 'digest', 'newsletter', 'update',
      'notification', 'automated', 'weekly', 'monthly', 'daily report',
      'performance', 'metrics', 'statistics', 'insights', 'dashboard',
      'status update', 'changelog', 'release notes', 'security alert'
    ];
    if (reportKeywords.some(keyword => allText.includes(keyword))) {
      return 'report';
    }
    
    // CHECK CUSTOM CATEGORIES - scan for user-defined category keywords
    const savedKeywords = localStorage.getItem('custom_category_keywords');
    if (savedKeywords) {
      try {
        const customKeywords = JSON.parse(savedKeywords);
        for (const [category, keywords] of Object.entries(customKeywords)) {
          if (Array.isArray(keywords) && keywords.some(kw => allText.includes(kw.toLowerCase()))) {
            return category;
          }
        }
      } catch (e) {
        console.error('Error parsing custom keywords:', e);
      }
    }
    
    // Default to 'other' for uncategorized emails
    return 'other';
  };
  
  // Helper to check if active account is a Gmail account
  const isActiveAccountGmail = () => {
    const activeAccount = emailAccounts.find(acc => acc.id === activeEmailAccount);
    return activeAccount && activeAccount.type === 'gmail';
  };

  // Fetch Gmail emails when any Gmail account tab is selected
  useEffect(() => {
    const activeAccount = emailAccounts.find(acc => acc.id === activeEmailAccount);
    const isGmailAccount = activeAccount && activeAccount.type === 'gmail';
    const isNylasAccount = activeAccount && activeAccount.type === 'nylas';
    
    if (isGmailAccount) {
      // Check if account is connected
      const isConnected = activeAccount.connected || 
        (activeEmailAccount === 'gmail' && gmailConnected) ||
        (activeEmailAccount === 'wdjwood'); // wdjwood is a known connected account
      
      if (isConnected) {
        // Get the backend account ID for this Gmail account
        const backendAccountId = getGmailAccountId(activeAccount.email);
        
        // Check if we have cached emails for this folder and account
        const cacheKey = `${activeEmailAccount}_${activeFolder}`;
        if (gmailEmailsCache[cacheKey]) {
          setGmailEmails(gmailEmailsCache[cacheKey]);
          console.log(`📧 Using cached ${gmailEmailsCache[cacheKey].length} Gmail emails for ${activeEmailAccount}/${activeFolder}`);
        } else {
          console.log(`📧 Syncing Gmail emails for ${activeEmailAccount}/${activeFolder} (backend: ${backendAccountId})`);
          // Pass both backend accountId and frontend accountId to avoid stale closures
          syncGmailEmails(activeFolder, backendAccountId, activeEmailAccount);
        }
      }
    } else if (isNylasAccount && activeAccount.connected) {
      // Fetch Nylas emails
      const cacheKey = `${activeEmailAccount}_${activeFolder}`;
      if (nylasEmailsCache[cacheKey]) {
        setNylasEmails(nylasEmailsCache[cacheKey]);
        console.log(`📧 Using cached ${nylasEmailsCache[cacheKey].length} Nylas emails for ${activeEmailAccount}/${activeFolder}`);
      } else {
        console.log(`📧 Syncing Nylas emails for ${activeEmailAccount}/${activeFolder}`);
        syncNylasEmails(activeFolder, activeAccount.email, activeEmailAccount);
      }
    }
  }, [activeEmailAccount, activeFolder, gmailConnected, emailAccounts]);
  
  // Handle URL params for compose email (from Lead Generation page)
  useEffect(() => {
    const compose = searchParams.get('compose');
    const to = searchParams.get('to');
    const subject = searchParams.get('subject');
    const body = searchParams.get('body');
    
    if (compose === 'true') {
      // Switch to email tab and open compose modal
      setActiveTab('email');
      setEmailTo(to ? decodeURIComponent(to) : '');
      setEmailSubject(subject ? decodeURIComponent(subject) : '');
      setEmailBody(body ? decodeURIComponent(body) : '');
      setEmailCc('');
      setEmailBcc('');
      setShowEmailComposeModal(true);
      
      // Clear URL params after processing
      setSearchParams({});
    }
    
    // Handle SMS compose from Lead Generation page
    const sms = searchParams.get('sms');
    const phone = searchParams.get('phone');
    const message = searchParams.get('message');
    
    if (sms === 'true') {
      // Switch to messages tab and open compose modal
      setActiveTab('messages');
      setComposeRecipient(phone ? decodeURIComponent(phone) : '');
      setComposeMessage(message ? decodeURIComponent(message) : '');
      setShowComposeModal(true);
      
      // Clear URL params after processing
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);
  
  // Meeting modal state
  const [showAddToMeetingModal, setShowAddToMeetingModal] = useState(false);
  const [showNewMeetingModal, setShowNewMeetingModal] = useState(false);
  const [meetingConversation, setMeetingConversation] = useState(null);
  const [existingMeetings, setExistingMeetings] = useState([]);
  const [selectedMeetingId, setSelectedMeetingId] = useState('');
  const [newMeetingTitle, setNewMeetingTitle] = useState('');
  const [newMeetingDate, setNewMeetingDate] = useState('');
  const [newMeetingNotes, setNewMeetingNotes] = useState('');
  const [activeCallDropdown, setActiveCallDropdown] = useState(null);
  const [activeMeetingPlatform, setActiveMeetingPlatform] = useState(null);
  const [isMeetingActive, setIsMeetingActive] = useState(false);
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [mediaStream, setMediaStream] = useState(null);
  const [emailReplyInput, setEmailReplyInput] = useState('');
  const [deletedMessages, setDeletedMessages] = useState({});
  const [selectedContact, setSelectedContact] = useState('');
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [showCommWidgetModal, setShowCommWidgetModal] = useState(false);
  const [commWidgetModalType, setCommWidgetModalType] = useState('');
  const [composeRecipient, setComposeRecipient] = useState('');
  const [composeMessage, setComposeMessage] = useState('');
  const [composeSending, setComposeSending] = useState(false);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [showContactSuggestions, setShowContactSuggestions] = useState(false);
  
  // State for searching within a conversation
  const [conversationSearchQuery, setConversationSearchQuery] = useState('');
  const [showConversationSearch, setShowConversationSearch] = useState(false);
  
  // Helper function to format timestamp intelligently
  // - Within 24 hours: show time (e.g., "2:30 PM")
  // - Within past week: show day name (e.g., "Monday")
  // - Older than a week: show date (e.g., "01/12/26")
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    const now = new Date();
    let messageDate = null;
    
    // Check if it's already a time format like "08:45 AM" or "2:30 PM" - return as is (it's today)
    if (/^\d{1,2}:\d{2}\s*(AM|PM)$/i.test(timestamp.trim())) {
      return timestamp.trim();
    }
    
    // If already in MM/DD/YY format, check if it's today
    if (/^\d{2}\/\d{2}\/\d{2}$/.test(timestamp)) {
      const [month, day, year] = timestamp.split('/');
      const fullYear = 2000 + parseInt(year);
      
      // Check if it's today - if so, return "Today" or current time
      const today = new Date();
      if (parseInt(month) === today.getMonth() + 1 && 
          parseInt(day) === today.getDate() && 
          fullYear === today.getFullYear()) {
        // It's today but we don't have the time, so just return "Today"
        return 'Today';
      }
      
      messageDate = new Date(fullYear, parseInt(month) - 1, parseInt(day));
    }
    
    // Handle "Dec 20, 2025 at 1:08 AM" format
    if (!messageDate && timestamp.includes(' at ')) {
      const [datePart, timePart] = timestamp.split(' at ');
      const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
      const dateMatch = datePart.match(/(\w+)\s+(\d{1,2}),?\s+(\d{4})/i);
      if (dateMatch) {
        const monthStr = dateMatch[1].toLowerCase().slice(0, 3);
        const monthIndex = monthNames.indexOf(monthStr);
        if (monthIndex !== -1) {
          // Parse time part (e.g., "1:08 AM" or "2:30 PM")
          const timeMatch = timePart?.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
          let hours = 0, minutes = 0;
          if (timeMatch) {
            hours = parseInt(timeMatch[1]);
            minutes = parseInt(timeMatch[2]);
            const isPM = timeMatch[3].toUpperCase() === 'PM';
            if (isPM && hours !== 12) hours += 12;
            if (!isPM && hours === 12) hours = 0;
          }
          messageDate = new Date(parseInt(dateMatch[3]), monthIndex, parseInt(dateMatch[2]), hours, minutes);
        }
      }
    }
    
    // Try parsing "Dec 20, 2025" format without time
    if (!messageDate) {
      const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
      const dateMatch = timestamp.match(/(\w+)\s+(\d{1,2}),?\s+(\d{4})/i);
      if (dateMatch) {
        const monthStr = dateMatch[1].toLowerCase().slice(0, 3);
        const monthIndex = monthNames.indexOf(monthStr);
        if (monthIndex !== -1) {
          messageDate = new Date(parseInt(dateMatch[3]), monthIndex, parseInt(dateMatch[2]));
        }
      }
    }
    
    // Try standard Date parsing
    if (!messageDate) {
      const parsed = new Date(timestamp);
      if (!isNaN(parsed.getTime())) {
        messageDate = parsed;
      }
    }
    
    // Handle "Just now"
    if (!messageDate && timestamp.toLowerCase().includes('just now')) {
      return now.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
    
    // If we still couldn't parse, return the original timestamp
    if (!messageDate) {
      return timestamp;
    }
    
    // Calculate time difference
    const diffMs = now - messageDate;
    const diffHours = diffMs / (1000 * 60 * 60);
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    
    // Within 24 hours - show time
    if (diffHours < 24 && diffHours >= 0) {
      return messageDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
    
    // Within past week (1-7 days) - show day name
    if (diffDays < 7 && diffDays >= 1) {
      return messageDate.toLocaleDateString('en-US', { weekday: 'long' });
    }
    
    // Older than a week - show MM/DD/YY
    const month = String(messageDate.getMonth() + 1).padStart(2, '0');
    const day = String(messageDate.getDate()).padStart(2, '0');
    const year = String(messageDate.getFullYear()).slice(-2);
    return `${month}/${day}/${year}`;
  };
  
  // Helper function to get category badge styling - vibrant colors
  const getCategoryBadge = (category) => {
    const badges = {
      'business': { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Business' },
      'personal': { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Personal' },
      'urgent': { bg: 'bg-red-500', text: 'text-white', label: 'Urgent' },
      'follow-up': { bg: 'bg-yellow-500', text: 'text-black', label: 'Follow Up' },
      'follow_up': { bg: 'bg-yellow-500', text: 'text-black', label: 'Follow Up' },
      'client': { bg: 'bg-purple-500', text: 'text-white', label: 'Client' },
      'report': { bg: 'bg-cyan-500', text: 'text-white', label: 'Report' },
      'meeting': { bg: 'bg-blue-500', text: 'text-white', label: 'Meeting' },
      'meeting_request': { bg: 'bg-blue-500', text: 'text-white', label: 'Meeting' },
      'travel': { bg: 'bg-orange-500', text: 'text-white', label: 'Travel' },
      'general': { bg: 'bg-gray-600', text: 'text-gray-200', label: 'General' },
      'other': { bg: 'bg-gray-500', text: 'text-white', label: 'Other' },
    };
    return badges[category] || badges['general'];
  };
  
  // Keyword-based category detection for messages - only action-oriented tags
  const detectMessageCategory = (messageContent) => {
    if (!messageContent) return null;
    const content = messageContent.toLowerCase();
    
    // Urgent keywords - highest priority
    const urgentKeywords = ['urgent', 'asap', 'emergency', 'immediately', 'critical', 'deadline', 'today', 'now', 'important', 'priority', 'rush'];
    if (urgentKeywords.some(kw => content.includes(kw))) return 'urgent';
    
    // Meeting keywords
    const meetingKeywords = ['meeting', 'meet', 'schedule', 'appointment', 'call', 'zoom', 'teams', 'conference'];
    if (meetingKeywords.some(kw => content.includes(kw))) return 'meeting';
    
    // Travel keywords
    const travelKeywords = ['flight', 'boarding pass', 'itinerary', 'airline', 'airport', 'departure', 'arrival', 'hotel', 'booking confirmation', 'travel', 'trip', 'layover', 'gate', 'seat'];
    if (travelKeywords.some(kw => content.includes(kw))) return 'travel';
    
    // Follow-up keywords
    const followUpKeywords = ['follow up', 'follow-up', 'followup', 'checking in', 'reminder', 'pending', 'waiting', 'status', 'update on', 'any update', 'get back', 'circle back', 'touch base'];
    if (followUpKeywords.some(kw => content.includes(kw))) return 'follow-up';
    
    // Client keywords
    const clientKeywords = ['client', 'customer', 'buyer', 'seller', 'investor', 'prospect', 'lead', 'deal', 'property', 'listing', 'offer', 'contract', 'closing', 'escrow'];
    if (clientKeywords.some(kw => content.includes(kw))) return 'client';
    
    // Report keywords
    const reportKeywords = ['report', 'summary', 'analysis', 'numbers', 'stats', 'metrics', 'data', 'results', 'performance', 'quarterly', 'monthly', 'weekly', 'review'];
    if (reportKeywords.some(kw => content.includes(kw))) return 'report';
    
    // Return null for messages without action tags (no badge will show)
    return null;
  };
  
  // Add Contact Modal State
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [newContactName, setNewContactName] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');
  const [newContactCompany, setNewContactCompany] = useState('');
  const [newContactRole, setNewContactRole] = useState('');
  const [newContactCategory, setNewContactCategory] = useState('Personal');
  const [newContactNotes, setNewContactNotes] = useState('');
  const [contactAutoScanning, setContactAutoScanning] = useState(false);
  const [originalConversationName, setOriginalConversationName] = useState(''); // Track original name for updates

  // Add to Flight modal state
  const [showAddFlightModal, setShowAddFlightModal] = useState(false);
  const [flightForm, setFlightForm] = useState({ airline: '', flight_number: '', departure: '', arrival: '', date: '', time: '', notes: '' });

  // Add to Finances modal state
  const [showAddFinanceModal, setShowAddFinanceModal] = useState(false);
  const [financeForm, setFinanceForm] = useState({ description: '', amount: '', category: 'Other', type: 'expense', date: '' });

  // Compose email controls state
  const [composeAttachments, setComposeAttachments] = useState([]);
  const [generatingComposeDraft, setGeneratingComposeDraft] = useState(false);

  // Contact list for dropdown - will be populated from iCloud
  const [iCloudContacts, setICloudContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  
  // Fallback hardcoded contacts (used if iCloud fetch fails)
  const fallbackContacts = [
    { name: 'Sarah Mitchell', email: 'sarah.mitchell@realestate.com', phone: '+1 (555) 123-4567', type: 'business' },
    { name: 'John Martinez', email: 'john.martinez@realestate.com', phone: '+1 (555) 234-5678', type: 'business' },
    { name: 'Emily Chen', email: 'emily.chen@realestate.com', phone: '+1 (555) 345-6789', type: 'business' },
    { name: 'Alex Rivera', email: 'alex.rivera@realestate.com', phone: '+1 (555) 456-7890', type: 'business' },
  ];
  
  // Fetch iCloud contacts when compose modal opens
  const fetchICloudContacts = async () => {
    try {
      setLoadingContacts(true);
      console.log('📱 Fetching iCloud contacts...');
      const response = await apiCall('/contacts/icloud/all');
      if (response.contacts && response.contacts.length > 0) {
        setICloudContacts(response.contacts);
        console.log(`✅ Loaded ${response.contacts.length} iCloud contacts`);
      } else {
        console.log('⚠️ No iCloud contacts found, using fallback');
        setICloudContacts(fallbackContacts);
      }
    } catch (error) {
      console.error('Error fetching iCloud contacts:', error);
      setICloudContacts(fallbackContacts);
    } finally {
      setLoadingContacts(false);
    }
  };
  
  // Combined contacts list (iCloud + fallback)
  const contactsList = iCloudContacts.length > 0 ? iCloudContacts : fallbackContacts;

  
  // Auto-filter contacts when they're loaded and user has a search term
  useEffect(() => {
    if (contactsList.length > 0 && composeRecipient.trim() && showComposeModal) {
      const searchValue = composeRecipient.trim().toLowerCase();
      const filtered = contactsList.filter(contact => {
        const nameMatch = contact.name?.toLowerCase().includes(searchValue);
        const phoneMatch = contact.phone?.includes(composeRecipient.trim());
        return nameMatch || phoneMatch;
      });
      console.log(`🔍 Auto-filtering after contacts loaded: "${composeRecipient}" - Found ${filtered.length} matches`);
      setFilteredContacts(filtered);
      setShowContactSuggestions(true);
    }
  }, [contactsList.length, composeRecipient, showComposeModal]);

  // Swipe-to-delete handlers
  const handleTouchStart = (e, id) => {
    const touch = e.touches[0];
    setSwipeOffset({ ...swipeOffset, [id]: { startX: touch.clientX, currentX: 0 } });
  };

  const handleTouchMove = (e, id) => {
    const touch = e.touches[0];
    const startX = swipeOffset[id]?.startX || touch.clientX;
    const diff = touch.clientX - startX;
    // Only allow left swipe (negative diff)
    if (diff < 0) {
      setSwipeOffset({ ...swipeOffset, [id]: { startX, currentX: diff } });
    }
  };

  const handleTouchEnd = (id) => {
    const offset = swipeOffset[id]?.currentX || 0;
    // If swiped more than 100px, delete
    if (offset < -100) {
      handleDeleteConversation(id);
    }
    // Reset
    setSwipeOffset({ ...swipeOffset, [id]: { startX: 0, currentX: 0 } });
  };

  const handleDeleteConversation = async (phoneNumber) => {
    if (!phoneNumber) {
      console.error('No phone number provided for deletion');
      alert('Unable to delete: No phone number found for this conversation');
      return;
    }
    
    if (!window.confirm(`Delete entire conversation with ${phoneNumber}? This cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await apiCall(`/messages/conversation/${encodeURIComponent(phoneNumber)}`, {
        method: 'DELETE'
      });
      
      console.log('✅ Conversation deleted:', response);
      
      // Remove from conversations list by phone number
      setConversations(prev => prev.filter(c => c.phone !== phoneNumber));
      
      // Clear selected conversation if it was the deleted one
      if (selectedConversation?.phone === phoneNumber) {
        setSelectedConversation(null);
      }
      
      // Refresh conversations to ensure consistency
      const conversationsData = await apiCall('/conversations');
      if (conversationsData && Array.isArray(conversationsData)) {
        const sortedConversations = conversationsData.sort((a, b) => {
          const dateA = new Date(a.updated_at || a.timestamp || 0);
          const dateB = new Date(b.updated_at || b.timestamp || 0);
          return dateB - dateA;
        });
        setConversations(sortedConversations);
      }
      
    } catch (error) {
      console.error('Error deleting conversation:', error);
      alert('Failed to delete conversation: ' + error.message);
    }
  };

  const handleDeleteEmail = async (emailId) => {
    // Confirm deletion
    if (!window.confirm('Are you sure you want to delete this email? It will be moved to trash.')) {
      return;
    }
    
    try {
      // Only call Gmail API if it's a Gmail email
      if (activeEmailAccount === 'gmail') {
        const accountId = getCurrentGmailAccountId();
        const response = await fetch(`${window.location.origin}/api/gmail/messages/${emailId}?account_id=${accountId}`, {
          method: 'DELETE'
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to delete email');
        }
        
        // Remove from local state and cache
        setGmailEmails(prev => prev.filter(e => e.id !== emailId));
        setGmailEmailsCache(prev => {
          const newCache = { ...prev };
          Object.keys(newCache).forEach(folder => {
            newCache[folder] = newCache[folder].filter(e => e.id !== emailId);
          });
          return newCache;
        });
        
        // Clear selected email if it was the deleted one
        if (selectedEmail?.id === emailId) {
          setSelectedEmail(null);
        }
        
        alert('Email moved to trash successfully');
      } else {
        // For other email accounts, just remove from local state
        console.log('Deleting email:', emailId);
        setSelectedEmail(null);
      }
    } catch (error) {
      console.error('Error deleting email:', error);
      alert(`Failed to delete email: ${error.message}`);
    }
  };

  const handleReplyEmail = (email) => {
    // Set up compose modal for reply
    const replyTo = email.email || email.from;
    const replySubject = email.subject?.startsWith('Re:') ? email.subject : `Re: ${email.subject}`;
    const originalBody = email.body || email.preview || '';
    const replyBody = `\n\n\n--- Original Message ---\nFrom: ${email.from}\nDate: ${email.time}\nSubject: ${email.subject}\n\n${originalBody}`;
    
    setEmailTo(replyTo);
    setEmailSubject(replySubject);
    setEmailBody(replyBody);
    setEmailCc('');
    setEmailBcc('');
    setShowEmailComposeModal(true);
    setOpenEmailDropdown(null);
  };

  const handleDeleteMessage = async (convId, msgIdx) => {
    const message = selectedConversation?.messages?.[msgIdx];
    if (!message) {
      console.error('Message not found');
      return;
    }
    
    // Confirm deletion
    if (!window.confirm('Are you sure you want to delete this message?')) {
      setOpenMessageDropdown(null);
      return;
    }
    
    try {
      // Delete from backend if message has an ID
      if (message.id) {
        await apiCall(`/messages/${message.id}`, { method: 'DELETE' });
        console.log('✅ Message deleted from database:', message.id);
      }
      
      // Update local state - remove message from conversation
      setConversations(prevConvs => 
        prevConvs.map(conv => {
          if (conv.id === convId) {
            const updatedMessages = conv.messages.filter((_, idx) => idx !== msgIdx);
            return { ...conv, messages: updatedMessages };
          }
          return conv;
        })
      );
      
      // Update selected conversation
      if (selectedConversation && selectedConversation.id === convId) {
        setSelectedConversation(prev => ({
          ...prev,
          messages: prev.messages.filter((_, idx) => idx !== msgIdx)
        }));
      }
      
      // Also mark as deleted in UI state (for safety)
      const key = `${convId}-${msgIdx}`;
      setDeletedMessages({
        ...deletedMessages,
        [key]: true
      });
      
      console.log('✅ Message removed from UI');
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Failed to delete message: ' + error.message);
    }
    
    setOpenMessageDropdown(null);
  };

  // Emoji reaction handlers
  const handleAddReaction = (convId, msgIdx, emoji) => {
    const key = `${convId}-${msgIdx}`;
    setMessageReactions({
      ...messageReactions,
      [key]: emoji
    });
    setShowEmojiPicker(null);
  };

  const handleToggleFlag = (emailId) => {
    setFlaggedEmails({
      ...flaggedEmails,
      [emailId]: !flaggedEmails[emailId]
    });
  };

  const handleTogglePin = (convId) => {
    setPinnedConversations({
      ...pinnedConversations,
      [convId]: !pinnedConversations[convId]
    });
  };

  // Fetch existing meetings from the Meetings page Kanban
  const fetchExistingMeetings = async () => {
    try {
      const response = await apiCall('/excel/get-desktop-data');
      if (response && response.meetings && Array.isArray(response.meetings)) {
        setExistingMeetings(response.meetings);
      } else {
        setExistingMeetings([]);
      }
    } catch (error) {
      console.error('Error fetching meetings:', error);
      setExistingMeetings([]);
    }
  };

  // Handle opening "Add to Meeting" modal
  const handleAddToMeeting = async (conv) => {
    setMeetingConversation(conv);
    await fetchExistingMeetings();
    setShowAddToMeetingModal(true);
    setOpenMessageDropdown(null);
  };

  // Handle adding conversation to existing meeting on Meetings page
  const handleAddToExistingMeeting = async () => {
    if (!selectedMeetingId || !meetingConversation) return;
    
    try {
      // Find the selected meeting
      const selectedMeeting = existingMeetings.find(m => m.id === selectedMeetingId);
      
      // Build notes with attachment info if present
      let notesAddition = `Added from ${meetingConversation.emailSubject ? 'Email' : 'Messages'}: ${meetingConversation.name} (${meetingConversation.phone})`;
      if (meetingConversation.emailSubject) {
        notesAddition += `\nSubject: ${meetingConversation.emailSubject}`;
      }
      if (meetingConversation.attachments && meetingConversation.attachments.length > 0) {
        notesAddition += `\nAttachments: ${meetingConversation.attachments.map(a => a.name || a.filename).join(', ')}`;
      }
      
      // Update the meeting with contact info
      const updateData = {
        contact_name: meetingConversation.name,
        contact_phone: meetingConversation.phone,
        notes: selectedMeeting?.notes 
          ? `${selectedMeeting.notes}\n\n${notesAddition}`
          : `Contact ${notesAddition}`
      };
      
      await apiCall(`/excel/update-meeting/${selectedMeetingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      
      // Upload attachments to the meeting if present
      if (meetingConversation.attachments && meetingConversation.attachments.length > 0) {
        console.log('📎 Uploading', meetingConversation.attachments.length, 'attachments to existing meeting');
        
        for (const att of meetingConversation.attachments) {
          try {
            // Fetch attachment data
            let attachmentData = null;
            let filename = att.name || att.filename || 'attachment';
            let mimeType = att.mimeType || 'application/octet-stream';
            
            if (activeEmailAccount === 'gmail' && att.id) {
              const accountId = getCurrentGmailAccountId();
              const response = await fetch(`${window.location.origin}/api/gmail/messages/${meetingConversation.messageId}/attachments/${att.id}?account_id=${accountId}`);
              if (response.ok) {
                const attData = await response.json();
                attachmentData = attData.data;
              }
            } else if (att.id) {
              const { outlook2EmailAPI } = await import('../services/api');
              const attData = await outlook2EmailAPI.getAttachment(meetingConversation.messageId, att.id);
              attachmentData = attData.contentBytes;
              mimeType = attData.contentType || mimeType;
            }
            
            if (attachmentData) {
              // Convert base64 to blob
              const byteCharacters = atob(attachmentData);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              const blob = new Blob([byteArray], { type: mimeType });
              
              // Upload to meeting
              const formData = new FormData();
              formData.append('file', blob, filename);
              
              await fetch(`${window.location.origin}/api/meetings/${selectedMeetingId}/documents`, {
                method: 'POST',
                body: formData
              });
              console.log(`✅ Uploaded "${filename}" to meeting`);
            }
          } catch (attErr) {
            console.error('Error uploading attachment to meeting:', attErr);
          }
        }
      }
      
      const attachmentCount = meetingConversation.attachments?.length || 0;
      const toastMsg = attachmentCount > 0 
        ? `Added ${meetingConversation.name} to meeting with ${attachmentCount} attachment${attachmentCount > 1 ? 's' : ''}!`
        : `Added ${meetingConversation.name} to meeting!`;
      setShowAddContactToast(toastMsg);
      setTimeout(() => setShowAddContactToast(null), 3000);
      setShowAddToMeetingModal(false);
      setSelectedMeetingId('');
      setMeetingConversation(null);
    } catch (error) {
      console.error('Error adding to meeting:', error);
      alert('Failed to add to meeting: ' + error.message);
    }
  };

  // Handle opening "New Meeting" modal
  const handleNewMeeting = (conv) => {
    setMeetingConversation(conv);
    setNewMeetingTitle(`Meeting with ${conv.name}`);
    setNewMeetingNotes(`Contact: ${conv.name}\nPhone: ${conv.phone}`);
    setNewMeetingDate(new Date().toISOString().split('T')[0]);
    setShowNewMeetingModal(true);
    setOpenMessageDropdown(null);
  };

  // Handle creating new meeting - adds to the Meetings page Kanban board
  const handleCreateNewMeeting = async () => {
    if (!newMeetingTitle || !meetingConversation) return;
    
    try {
      const meetingData = {
        title: newMeetingTitle,
        name: newMeetingTitle,
        date: newMeetingDate || new Date().toISOString().split('T')[0],
        time: '10:00',
        notes: newMeetingNotes,
        contact_name: meetingConversation.name,
        contact_phone: meetingConversation.phone,
        phone: meetingConversation.phone,
        pipeline_stage: 'prospecting' // Default stage for Kanban
      };
      
      // Use the excel endpoint to add to Meetings page Kanban
      const meetingResponse = await apiCall('/excel/add-meeting', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(meetingData)
      });
      
      // If there are attachments from email, upload them to the meeting
      if (meetingConversation.attachments && meetingConversation.attachments.length > 0 && meetingResponse?.id) {
        console.log('📎 Uploading', meetingConversation.attachments.length, 'attachments to meeting');
        
        for (const att of meetingConversation.attachments) {
          try {
            // Fetch attachment data
            let attachmentData = null;
            let filename = att.name || att.filename || 'attachment';
            let mimeType = att.mimeType || 'application/octet-stream';
            
            if (activeEmailAccount === 'gmail' && att.id) {
              const accountId = getCurrentGmailAccountId();
              const response = await fetch(`${window.location.origin}/api/gmail/messages/${meetingConversation.messageId}/attachments/${att.id}?account_id=${accountId}`);
              if (response.ok) {
                const attData = await response.json();
                attachmentData = attData.data;
              }
            } else if (att.id) {
              const { outlook2EmailAPI } = await import('../services/api');
              const attData = await outlook2EmailAPI.getAttachment(meetingConversation.messageId, att.id);
              attachmentData = attData.contentBytes;
              mimeType = attData.contentType || mimeType;
            }
            
            if (attachmentData) {
              // Convert base64 to blob
              const byteCharacters = atob(attachmentData);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              const blob = new Blob([byteArray], { type: mimeType });
              
              // Upload to meeting
              const formData = new FormData();
              formData.append('file', blob, filename);
              
              await fetch(`${window.location.origin}/api/meetings/${meetingResponse.id}/documents`, {
                method: 'POST',
                body: formData
              });
              console.log(`✅ Uploaded "${filename}" to meeting`);
            }
          } catch (attErr) {
            console.error('Error uploading attachment to meeting:', attErr);
          }
        }
      }
      
      const attachmentCount = meetingConversation.attachments?.length || 0;
      const toastMsg = attachmentCount > 0 
        ? `Meeting "${newMeetingTitle}" added with ${attachmentCount} attachment${attachmentCount > 1 ? 's' : ''}!`
        : `Meeting "${newMeetingTitle}" added to Meetings page!`;
      setShowAddContactToast(toastMsg);
      setTimeout(() => setShowAddContactToast(null), 3000);
      setShowNewMeetingModal(false);
      setNewMeetingTitle('');
      setNewMeetingDate('');
      setNewMeetingNotes('');
      setMeetingConversation(null);
    } catch (error) {
      console.error('Error creating meeting:', error);
      alert('Failed to create meeting: ' + error.message);
    }
  };

  // Fetch calls separately for refreshing after updates
  const fetchCalls = async () => {
    try {
      const callsData = await apiCall('/calls');
      // Ensure callsData is an array
      setCalls(Array.isArray(callsData) ? callsData : []);
    } catch (err) {
      console.log('Error fetching calls:', err);
      setCalls([]);
    }
  };

  // Delete a call log
  const deleteCall = async (callSid) => {
    try {
      await apiCall(`/calls/${callSid}`, { method: 'DELETE' });
      // Remove from local state
      setCalls(prevCalls => prevCalls.filter(call => call.call_sid !== callSid));
      setActiveCallDropdown(null);
    } catch (err) {
      console.error('Error deleting call:', err);
      alert('Failed to delete call: ' + (err.message || 'Unknown error'));
    }
  };

  // Sync Outlook emails manually (refresh button)
  const syncOutlookEmails = async () => {
    setOutlookLoading(true);
    try {
      const { outlook2AuthAPI, outlook2EmailAPI } = await import('../services/api');
      const authStatus = await outlook2AuthAPI.status();
      
      if (authStatus.authenticated) {
        setOutlookConnected(true);
        
        try {
          // Fetch emails from current active folder
          const emailResult = await outlook2EmailAPI.syncEmails(activeFolder, 50);
          if (emailResult.emails) {
            // Transform to match expected format - use basic categorization initially
            const transformedEmails = emailResult.emails.map((email, idx) => {
              const fromEmail = email.sender || email.from || 'unknown@email.com';
              const fromName = email.sender_name || fromEmail.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
              
              // Filter out inline attachments (keep only downloadable ones)
              const downloadableAttachments = (email.attachments || []).filter(att => !att.isInline);
              
              return {
                id: email.id || `outlook-${idx}`,
                from: fromName,
                email: fromEmail,
                subject: email.subject || 'No Subject',
                preview: email.body_preview || '',
                time: formatEmailTime(email.received_datetime || email.received_time),
                category: categorizeEmail(email), // Initial basic categorization
                folder: activeFolder,
                isRead: email.is_read ?? true,
                hasAttachments: downloadableAttachments.length > 0 || email.has_attachments,
                attachments: downloadableAttachments,
                body: email.body || email.body_preview || '',
                bodyType: email.body_type || 'html',
                to: email.to || [],
                toNames: email.to_names || [],
                cc: email.cc || [],
                ccNames: email.cc_names || [],
                conversationId: email.conversationId,
                source: 'outlook2',
                thread: [{
                  id: email.id,
                  from: fromName,
                  email: fromEmail,
                  time: formatEmailTime(email.received_datetime || email.received_time),
                  body: email.body || email.body_preview || '',
                  bodyType: email.body_type || 'html',
                  to: email.to || [],
                  cc: email.cc || [],
                  attachments: downloadableAttachments
                }]
              };
            });
            setOutlookEmails(transformedEmails);
            // Cache the emails for this folder
            setOutlookEmailsCache(prev => ({ ...prev, [activeFolder]: transformedEmails }));
            setHasInitiallyFetched(true);
            console.log(`📧 Synced ${transformedEmails.length} Outlook emails from ${activeFolder}`);
            
            // AI Categorization - run in background to improve categorization
            try {
              const emailsForAI = emailResult.emails.slice(0, 20).map(email => ({
                email_id: email.id,
                subject: email.subject || '',
                sender: email.sender || email.from || '',
                sender_name: email.sender_name || '',
                body_preview: email.body_preview || ''
              }));
              
              console.log('🤖 Starting AI bulk categorization for', emailsForAI.length, 'emails...');
              const aiCategories = await aiEmailAPI.categorizeBulk(emailsForAI);
              
              if (aiCategories.categories && aiCategories.categories.length > 0) {
                // Update emails with AI categories
                setOutlookEmails(prev => {
                  const categoryMap = {};
                  aiCategories.categories.forEach(cat => {
                    categoryMap[cat.email_id] = cat.category;
                  });
                  
                  const updated = prev.map(email => {
                    if (categoryMap[email.id]) {
                      return { ...email, category: categoryMap[email.id] };
                    }
                    return email;
                  });
                  // Also update cache
                  setOutlookEmailsCache(cache => ({ ...cache, [activeFolder]: updated }));
                  return updated;
                });
                console.log('✅ AI categorization complete -', aiCategories.count, 'emails categorized');
              }
            } catch (aiErr) {
              console.warn('AI categorization failed, using basic categorization:', aiErr);
            }
          } else if (emailResult.error || emailResult.detail) {
            // API returned an error - likely token expired
            throw new Error(emailResult.detail || emailResult.error || 'Failed to fetch emails');
          }
        } catch (syncErr) {
          console.error('Email sync failed:', syncErr);
          // Token likely expired - disconnect and prompt re-auth
          setOutlookConnected(false);
          // Disconnect and re-auth
          try {
            await outlook2AuthAPI.disconnect();
          } catch (e) { /* ignore */ }
          
          // Open OAuth flow
          const authData = await outlook2AuthAPI.getAuthUrl();
          if (authData.authorization_url) {
            alert('Your Outlook session has expired. Please sign in again.');
            window.open(authData.authorization_url, '_blank', 'width=600,height=700');
          }
        }
      } else {
        // Not authenticated - initiate OAuth flow
        setOutlookConnected(false);
        console.log('Outlook not authenticated - redirecting to connect...');
        
        // Get authorization URL and redirect
        try {
          const authData = await outlook2AuthAPI.getAuthUrl();
          if (authData.authorization_url) {
            window.open(authData.authorization_url, '_blank', 'width=600,height=700');
          }
        } catch (authErr) {
          console.error('Failed to get auth URL:', authErr);
          alert('Please reconnect your Twenty8 Outlook account. Click the sync button again after connecting.');
        }
      }
    } catch (err) {
      console.error('Error syncing Outlook emails:', err);
      setOutlookConnected(false);
    } finally {
      setOutlookLoading(false);
    }
  };

  // Handle selecting an email - fetch thread if available
  const handleSelectEmail = async (email) => {
    console.log('📧 Selected email FULL:', JSON.stringify({
      id: email.id,
      from: email.from,
      source: email.source, 
      conversationId: email.conversationId,
      attachmentsCount: email.attachments?.length,
      threadAttachmentsCount: email.thread?.[0]?.attachments?.length,
      hasSource: 'source' in email
    }));
    
    // Set selected email immediately to show detail view
    setSelectedEmail({
      ...email,
      thread: email.thread || [{
        id: email.id,
        from: email.from,
        email: email.email,
        time: email.time,
        body: email.body,
        bodyType: email.bodyType || 'html',
        to: email.to || [],
        cc: email.cc || [],
        attachments: email.attachments || [],
        hasAttachments: email.hasAttachments
      }]
    });
    
    // For Gmail emails, fetch full message details
    if (email.source === 'gmail' && email.id) {
      try {
        console.log('📧 Fetching full Gmail message for id:', email.id);
        const accountId = getCurrentGmailAccountId();
        const response = await fetch(`${window.location.origin}/api/gmail/messages/${email.id}?account_id=${accountId}`);
        
        if (response.ok) {
          const fullMessage = await response.json();
          console.log('📧 Full Gmail message received, body length:', fullMessage.body?.length);
          
          // Parse from field
          const parseFromField = (fromStr) => {
            if (!fromStr) return { name: 'Unknown', email: '' };
            const match = fromStr.match(/^(.+?)\s*<(.+?)>$/);
            if (match) {
              return { name: match[1].trim(), email: match[2].trim() };
            }
            if (fromStr.includes('@')) {
              return { name: fromStr.split('@')[0], email: fromStr };
            }
            return { name: fromStr, email: '' };
          };
          
          const fromParsed = parseFromField(fullMessage.from);
          
          // Transform Gmail attachments to match expected format
          const transformedAttachments = (fullMessage.attachments || []).map(att => ({
            id: att.id,
            name: att.filename || att.name || 'Attachment',
            filename: att.filename || att.name || 'Attachment',
            mimeType: att.mimeType,
            size: att.size,
            partId: att.partId,
            isInline: false
          }));
          
          console.log('📎 Gmail attachments found:', transformedAttachments.length, transformedAttachments);
          
          // Update with full message body and attachments
          setSelectedEmail(prev => ({
            ...prev,
            body: fullMessage.body || prev.body,
            hasAttachments: fullMessage.has_attachments || transformedAttachments.length > 0,
            attachments: transformedAttachments,
            thread: [{
              id: fullMessage.id,
              from: fromParsed.name,
              email: fromParsed.email,
              time: prev.time,
              body: fullMessage.body || prev.body,
              bodyType: fullMessage.body?.startsWith('<!') || fullMessage.body?.includes('<html') ? 'html' : 'text',
              to: fullMessage.to ? [fullMessage.to] : [],
              cc: [],
              attachments: transformedAttachments,
              hasAttachments: fullMessage.has_attachments || transformedAttachments.length > 0
            }]
          }));
        }
      } catch (err) {
        console.error('Error fetching Gmail message details:', err);
      }
    }
    
    // For Outlook emails with conversationId, fetch the full thread in background
    if (email.conversationId && email.source === 'outlook2') {
      try {
        console.log('📧 Fetching thread for conversationId:', email.conversationId);
        const { outlook2EmailAPI } = await import('../services/api');
        const threadData = await outlook2EmailAPI.getThread(email.conversationId);
        console.log('📧 Thread data received:', threadData);
        
        if (threadData.messages && threadData.messages.length > 0) {
          // Transform thread messages
          const threadMessages = threadData.messages.map(msg => ({
            id: msg.id,
            from: msg.sender_name || msg.from,
            email: msg.from,
            time: formatEmailTime(msg.received_time),
            body: msg.body,
            bodyType: msg.body_type,
            to: msg.to || [],
            cc: msg.cc || [],
            attachments: msg.attachments || [],
            hasAttachments: msg.has_attachments || (msg.attachments && msg.attachments.length > 0)
          }));
          
          // Update with full thread data
          setSelectedEmail(prev => ({
            ...prev,
            thread: threadMessages,
            conversationId: email.conversationId
          }));
        }
      } catch (err) {
        console.error('Error fetching email thread:', err);
      }
    }
  };

  // Download attachment
  const handleDownloadAttachment = async (messageId, attachment) => {
    try {
      // Check if this is a Gmail attachment
      if (activeEmailAccount === 'gmail' && attachment.id) {
        // Fetch Gmail attachment
        const accountId = getCurrentGmailAccountId();
        const response = await fetch(`${window.location.origin}/api/gmail/messages/${messageId}/attachments/${attachment.id}?account_id=${accountId}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch attachment');
        }
        
        const attData = await response.json();
        
        if (attData.data) {
          // Create download link from base64 data
          const link = document.createElement('a');
          link.href = `data:${attachment.mimeType || 'application/octet-stream'};base64,${attData.data}`;
          link.download = attachment.filename || attachment.name || 'attachment';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        // Outlook attachment
        const { outlook2EmailAPI } = await import('../services/api');
        const attData = await outlook2EmailAPI.getAttachment(messageId, attachment.id);
        
        if (attData.contentBytes) {
          // Create download link from base64 data
          const link = document.createElement('a');
          link.href = `data:${attData.contentType};base64,${attData.contentBytes}`;
          link.download = attData.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    } catch (err) {
      console.error('Error downloading attachment:', err);
      alert('Failed to download attachment');
    }
  };

  // Add attachment to Documents page
  const handleAddAttachmentToDocuments = async (messageId, attachment) => {
    try {
      // First, fetch the attachment data
      let attachmentData = null;
      let filename = attachment.name || attachment.filename || 'attachment';
      let mimeType = attachment.mimeType || 'application/octet-stream';
      
      if (activeEmailAccount === 'gmail' && attachment.id) {
        const accountId = getCurrentGmailAccountId();
        const response = await fetch(`${window.location.origin}/api/gmail/messages/${messageId}/attachments/${attachment.id}?account_id=${accountId}`);
        if (!response.ok) throw new Error('Failed to fetch attachment');
        const attData = await response.json();
        attachmentData = attData.data; // base64 encoded
      } else {
        const { outlook2EmailAPI } = await import('../services/api');
        const attData = await outlook2EmailAPI.getAttachment(messageId, attachment.id);
        attachmentData = attData.contentBytes;
        mimeType = attData.contentType || mimeType;
      }
      
      if (!attachmentData) throw new Error('No attachment data');
      
      // Convert base64 to blob
      const byteCharacters = atob(attachmentData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      
      // Create FormData and upload to documents
      const formData = new FormData();
      formData.append('file', blob, filename);
      formData.append('folder', 'Email Attachments');
      formData.append('description', `Attachment from email - ${selectedEmail?.subject || 'No Subject'}`);
      
      const uploadResponse = await fetch(`${window.location.origin}/api/documents/upload`, {
        method: 'POST',
        body: formData
      });
      
      if (!uploadResponse.ok) throw new Error('Failed to upload document');
      
      setShowAddContactToast(`"${filename}" added to Documents!`);
      setTimeout(() => setShowAddContactToast(null), 3000);
    } catch (err) {
      console.error('Error adding attachment to documents:', err);
      alert('Failed to add attachment to documents: ' + err.message);
    }
  };

  // Handle Add to Flight from email message
  const handleAddToFlight = (msg) => {
    const body = msg.parsedBody || msg.body || '';
    const subject = selectedEmail?.subject || '';
    const from = msg.from || selectedEmail?.from || '';
    const today = new Date().toISOString().split('T')[0];
    setFlightForm({
      airline: from || '',
      flight_number: '',
      departure: '',
      arrival: '',
      date: today,
      time: '',
      notes: `From: ${from}\nSubject: ${subject}\n\n${body.substring(0, 300)}`
    });
    setShowAddFlightModal(true);
  };

  const handleSaveFlight = async () => {
    try {
      const res = await fetch(`${window.location.origin}/api/flights/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...flightForm, source: 'email' })
      });
      if (!res.ok) throw new Error('Failed to save flight');
      setShowAddFlightModal(false);
      setShowAddContactToast('Flight added to Travel!');
      setTimeout(() => setShowAddContactToast(null), 3000);
    } catch (err) {
      console.error('Error saving flight:', err);
      alert('Failed to add flight: ' + err.message);
    }
  };

  // Handle Add to Finances from email message
  const handleAddToFinances = (msg) => {
    const body = msg.parsedBody || msg.body || '';
    const subject = selectedEmail?.subject || '';
    const from = msg.from || selectedEmail?.from || '';
    const today = new Date().toISOString().split('T')[0];
    // Try to extract an amount from the email body
    const amountMatch = body.match(/\$[\d,]+\.?\d*/);
    setFinanceForm({
      description: subject || `Payment - ${from}`,
      amount: amountMatch ? amountMatch[0].replace(/[$,]/g, '') : '',
      category: 'Other',
      type: 'expense',
      date: today
    });
    setShowAddFinanceModal(true);
  };

  const handleSaveFinance = async () => {
    try {
      const res = await fetch(`${window.location.origin}/api/finances/transactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description: financeForm.description,
          amount: parseFloat(financeForm.amount) || 0,
          category: financeForm.category,
          type: financeForm.type,
          date: financeForm.date
        })
      });
      if (!res.ok) throw new Error('Failed to save transaction');
      setShowAddFinanceModal(false);
      setShowAddContactToast('Transaction added to Finances!');
      setTimeout(() => setShowAddContactToast(null), 3000);
    } catch (err) {
      console.error('Error saving transaction:', err);
      alert('Failed to add transaction: ' + err.message);
    }
  };

  // Handle New Meeting with attachments from email
  const handleNewMeetingWithAttachments = (msg, attachments = []) => {
    const senderName = msg.from || selectedEmail?.from || 'Unknown';
    const senderEmail = msg.email || selectedEmail?.email || '';
    
    // Build notes with attachment info
    let notes = `Contact: ${senderName}\nEmail: ${senderEmail}`;
    if (selectedEmail?.subject) {
      notes += `\nSubject: ${selectedEmail.subject}`;
    }
    if (attachments && attachments.length > 0) {
      notes += `\n\nAttachments (${attachments.length}):`;
      attachments.forEach(att => {
        notes += `\n- ${att.name || att.filename || 'Attachment'} (${formatFileSize(att.size)})`;
      });
    }
    
    setMeetingConversation({ 
      name: senderName, 
      phone: senderEmail, 
      attachments: attachments,
      messageId: msg.id
    });
    setNewMeetingTitle(`Meeting with ${senderName}`);
    setNewMeetingNotes(notes);
    setNewMeetingDate(new Date().toISOString().split('T')[0]);
    setShowNewMeetingModal(true);
    setOpenEmailDropdown(null);
  };

  // Handle Add to Meeting with attachments from email
  const handleAddToMeetingWithAttachments = async (msg, attachments = []) => {
    const senderName = msg.from || selectedEmail?.from || 'Unknown';
    const senderEmail = msg.email || selectedEmail?.email || '';
    
    setMeetingConversation({ 
      name: senderName, 
      phone: senderEmail, 
      attachments: attachments,
      messageId: msg.id,
      emailSubject: selectedEmail?.subject
    });
    await fetchExistingMeetings();
    setShowAddToMeetingModal(true);
    setOpenEmailDropdown(null);
  };

  // Handle Add Contact from email message
  const handleAddContactFromEmail = (msg) => {
    const senderName = msg.from || selectedEmail?.from || 'Unknown';
    const senderEmail = msg.email || selectedEmail?.email || '';
    
    setNewContactName(senderName);
    setNewContactPhone('');
    setNewContactEmail(senderEmail);
    setNewContactCompany('');
    setNewContactRole('');
    setNewContactCategory('Business');
    setNewContactNotes(`Added from email: ${selectedEmail?.subject || 'No Subject'}`);
    setOriginalConversationName(senderName);
    setShowAddContactModal(true);
    setOpenEmailDropdown(null);
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // State for email reply sending
  const [sendingEmailReply, setSendingEmailReply] = useState(false);

  // Send email reply
  const handleSendEmailReply = async () => {
    if (!emailReplyInput.trim() || !selectedEmail) return;
    
    setSendingEmailReply(true);
    try {
      const { outlook2EmailAPI } = await import('../services/api');
      
      // Get the first message in the thread to reply to
      const originalMessageId = selectedEmail.thread?.[0]?.id || selectedEmail.id;
      
      // Format reply body as HTML
      const replyBody = `<div style="font-family: Arial, sans-serif;">${emailReplyInput.replace(/\n/g, '<br>')}</div>`;
      
      await outlook2EmailAPI.replyToEmail(originalMessageId, replyBody, false);
      
      console.log('✅ Email reply sent successfully');
      setEmailReplyInput('');
      alert('Email sent successfully!');
      
      // Optionally refresh the thread
      if (selectedEmail.conversationId) {
        const threadData = await outlook2EmailAPI.getThread(selectedEmail.conversationId);
        if (threadData.messages && threadData.messages.length > 0) {
          const threadMessages = threadData.messages.map(msg => ({
            id: msg.id,
            from: msg.sender_name || msg.from,
            email: msg.from,
            time: formatEmailTime(msg.received_time),
            body: msg.body,
            bodyType: msg.body_type,
            to: msg.to || [],
            cc: msg.cc || [],
            attachments: msg.attachments || [],
            hasAttachments: msg.has_attachments || (msg.attachments && msg.attachments.length > 0)
          }));
          setSelectedEmail({
            ...selectedEmail,
            thread: threadMessages
          });
        }
      }
    } catch (err) {
      console.error('Error sending email reply:', err);
      alert('Failed to send email. Please try again.');
    } finally {
      setSendingEmailReply(false);
    }
  };

  // Handle folder change - use cached emails if available, don't auto-fetch
  const handleFolderChange = (folder) => {
    setActiveFolder(folder);
    setSelectedEmail(null); // Clear selected email when switching folders
    
    // If Outlook is the active account, use cached emails for this folder
    if (activeEmailAccount === 'outlook') {
      // Check if we have cached emails for this folder
      if (outlookEmailsCache[folder]) {
        setOutlookEmails(outlookEmailsCache[folder]);
        console.log(`📧 Using cached ${outlookEmailsCache[folder].length} emails from ${folder}`);
      } else {
        // No cached data for this folder - show empty state
        // User needs to click sync to fetch
        setOutlookEmails([]);
        console.log(`📧 No cached emails for ${folder} - click sync to fetch`);
      }
    }
  };

  // Handle opening "Add Contact" (uses existing Add Contact modal)
  const handleAddContactFromDropdown = (conv) => {
    setNewContactName(conv.name);
    setNewContactPhone(conv.phone);
    setNewContactEmail('');
    setNewContactCompany('');
    setNewContactRole('');
    setNewContactCategory('Personal');
    setNewContactNotes('Added from text message');
    setOriginalConversationName(conv.name);
    setShowAddContactModal(true);
    setOpenMessageDropdown(null);
  };

  const availableEmojis = ['❤️', '👍', '😂', '🔥', '🎉', '👏', '😍', '🙌'];

  // Favorites contacts for quick calling
  const favoriteContacts = [
    { id: 1, name: 'Sarah Mitchell', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop', phone: '+1 (555) 123-4567', category: 'family', isFaceTime: true },
    { id: 2, name: 'Michael Chen', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop', phone: '+1 (555) 234-5678', category: 'friends', isFaceTime: false },
    { id: 3, name: 'Emily Rodriguez', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop', phone: '+1 (555) 345-6789', category: 'business', isFaceTime: true },
    { id: 4, name: 'David Park', avatar: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=100&h=100&fit=crop', phone: '+1 (555) 567-8901', category: 'business', isFaceTime: false },
    { id: 5, name: 'Lisa Anderson', avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop', phone: '+1 (555) 678-9012', category: 'family', isFaceTime: true },
    { id: 6, name: 'James Rivera', avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop', phone: '+1 (555) 456-7890', category: 'business', isFaceTime: false },
    { id: 7, name: 'Rachel Kim', avatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=100&h=100&fit=crop', phone: '+1 (555) 890-1234', category: 'friends', isFaceTime: true },
    { id: 8, name: 'Tom Wilson', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop', phone: '+1 (555) 789-0123', category: 'friends', isFaceTime: false },
  ];

  // Mock group members data
  const groupMembers = {
    0: [ // Design Team - 23 members (showing as 5 in preview, but full roster available)
      { id: 1, name: 'You', avatar: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=100&h=100&fit=crop', role: 'Managing Partner', isOnline: true },
      { id: 2, name: 'Sarah Mitchell', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop', role: 'Senior Designer', isOnline: true },
      { id: 3, name: 'Michael Chen', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop', role: 'Lead Developer', isOnline: true },
      { id: 4, name: 'James Rivera', avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop', role: 'Financial Analyst', isOnline: true },
      { id: 5, name: 'Emily Rodriguez', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop', role: 'Project Manager', isOnline: false },
      { id: 6, name: 'Tom Wilson', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop', role: 'Architect', isOnline: true },
      { id: 7, name: 'Lisa Anderson', avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop', role: 'Interior Designer', isOnline: false },
      { id: 8, name: 'David Park', avatar: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=100&h=100&fit=crop', role: 'Construction Manager', isOnline: true },
      { id: 9, name: 'Rachel Kim', avatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=100&h=100&fit=crop', role: 'Design Coordinator', isOnline: false },
      { id: 10, name: 'Alex Turner', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop', role: 'Senior Designer', isOnline: true },
      { id: 11, name: 'Sophia Lee', avatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=100&h=100&fit=crop', role: 'UX Designer', isOnline: false },
      { id: 12, name: 'Chris Brown', avatar: 'https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?w=100&h=100&fit=crop', role: 'Developer', isOnline: false },
      { id: 13, name: 'Maya Patel', avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop', role: 'UX Researcher', isOnline: true },
      { id: 14, name: 'Kevin Zhang', avatar: 'https://images.unsplash.com/photo-1463453091185-61582044d556?w=100&h=100&fit=crop', role: 'Backend Developer', isOnline: false },
      { id: 15, name: 'Olivia Turner', avatar: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=100&h=100&fit=crop', role: 'Visual Designer', isOnline: true },
      { id: 16, name: 'Daniel Scott', avatar: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=100&h=100&fit=crop', role: 'Frontend Developer', isOnline: false },
      { id: 17, name: 'Ava Mitchell', avatar: 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=100&h=100&fit=crop', role: 'Graphic Designer', isOnline: true },
      { id: 18, name: 'Ryan Cooper', avatar: 'https://images.unsplash.com/photo-1507081323647-4d250478b919?w=100&h=100&fit=crop', role: 'Full Stack Developer', isOnline: false },
      { id: 19, name: 'Isabella Garcia', avatar: 'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=100&h=100&fit=crop', role: 'UI Designer', isOnline: true },
      { id: 20, name: 'Ethan Roberts', avatar: 'https://images.unsplash.com/photo-1531427186611-ecfd6d936c79?w=100&h=100&fit=crop', role: 'DevOps Engineer', isOnline: true },
      { id: 21, name: 'Mia Thompson', avatar: 'https://images.unsplash.com/photo-1502685104226-ee32379fefbe?w=100&h=100&fit=crop', role: 'Motion Designer', isOnline: false },
      { id: 22, name: 'Noah Williams', avatar: 'https://images.unsplash.com/photo-1506277886164-e25aa3f4ef7f?w=100&h=100&fit=crop', role: 'QA Engineer', isOnline: false },
      { id: 23, name: 'Emma Davis', avatar: 'https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?w=100&h=100&fit=crop', role: 'Scrum Master', isOnline: true }
    ],
    6: [ // Property Management Team - 8 members
      { id: 1, name: 'You', avatar: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=100&h=100&fit=crop', role: 'Managing Partner', isOnline: true },
      { id: 2, name: 'Tom Bradley', avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop', role: 'Property Manager', isOnline: true },
      { id: 3, name: 'Jessica Martinez', avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop', role: 'Maintenance Coordinator', isOnline: false },
      { id: 4, name: 'Robert Johnson', avatar: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=100&h=100&fit=crop', role: 'Facilities Manager', isOnline: true },
      { id: 5, name: 'Amanda White', avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100&h=100&fit=crop', role: 'Leasing Agent', isOnline: true },
      { id: 6, name: 'Carlos Rodriguez', avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop', role: 'Building Engineer', isOnline: false },
      { id: 7, name: 'Nicole Brown', avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop', role: 'Tenant Relations', isOnline: true },
      { id: 8, name: 'Steven Taylor', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop', role: 'Property Inspector', isOnline: true }
    ],
    9: [ // Investment Partners - 12 members
      { id: 1, name: 'You', avatar: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=100&h=100&fit=crop', role: 'Managing Partner', isOnline: true },
      { id: 2, name: 'Maria Rodriguez', avatar: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=100&h=100&fit=crop', role: 'Investment Director', isOnline: true },
      { id: 3, name: 'James Chen', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop', role: 'Portfolio Manager', isOnline: false },
      { id: 4, name: 'Victoria Martinez', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop', role: 'Financial Analyst', isOnline: true },
      { id: 5, name: 'Marcus Johnson', avatar: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=100&h=100&fit=crop', role: 'Deal Sourcing Lead', isOnline: true },
      { id: 6, name: 'Patricia Anderson', avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100&h=100&fit=crop', role: 'Asset Manager', isOnline: false },
      { id: 7, name: 'Richard Taylor', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop', role: 'Investment Analyst', isOnline: true },
      { id: 8, name: 'Jennifer Lee', avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop', role: 'Due Diligence Manager', isOnline: false },
      { id: 9, name: 'Daniel Kim', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop', role: 'Risk Analyst', isOnline: true },
      { id: 10, name: 'Rachel Wong', avatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=100&h=100&fit=crop', role: 'Acquisitions Manager', isOnline: true },
      { id: 11, name: 'Christopher Davis', avatar: 'https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?w=100&h=100&fit=crop', role: 'Market Researcher', isOnline: false },
      { id: 12, name: 'Sophia Martinez', avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop', role: 'Capital Markets', isOnline: false }
    ]
  };

  // Get members for selected conversation or return empty array if not a group
  const getCurrentGroupMembers = () => {
    if (!selectedConversation || !conversations[selectedConversation?.id]?.isGroup) return [];
    return groupMembers[selectedConversation?.id] || [];
  };

  // Send SMS message
  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedConversation) return;
    
    const messageText = messageInput.trim();
    const now = new Date();
    const timeStamp = now.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/Chicago'
    });
    const dateStamp = now.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      timeZone: 'America/Chicago'
    });
    
    try {
      const response = await apiCall('/messages/send-sms', {
        method: 'POST',
        body: JSON.stringify({
          phone_number: selectedConversation.phone,
          recipient_name: selectedConversation.name,
          message: messageText
        })
      });
      
      console.log('✅ Message sent:', response);
      
      // Clear input
      setMessageInput('');
      
      // Mark that we're not user scrolling so auto-scroll works
      setIsUserScrolling(false);
      
      // Add sent message to the current conversation locally (optimistic update)
      const newMessage = {
        id: `sent-${Date.now()}`,
        sender: 'You',
        text: messageText,
        time: timeStamp,
        sent: true
      };
      
      // Update the selected conversation with the new message
      setSelectedConversation(prev => ({
        ...prev,
        messages: [...(prev.messages || []), newMessage],
        lastMessage: messageText,
        timestamp: dateStamp
      }));
      
      // Update the conversation in the list
      setConversations(prev => prev.map(conv => {
        if (conv.phone === selectedConversation.phone) {
          return {
            ...conv,
            messages: [...(conv.messages || []), newMessage],
            lastMessage: messageText,
            timestamp: dateStamp
          };
        }
        return conv;
      }));
      
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message: ' + error.message);
    }
  };

  // Function to build conversations from messages
  const buildConversations = (messagesData) => {
    const conversationsMap = {};
    
    // Ensure messagesData is an array
    if (!Array.isArray(messagesData)) {
      console.warn('buildConversations: messagesData is not an array', messagesData);
      return [];
    }
    
    messagesData.forEach(msg => {
      let phone;
      let contactName;
      
      if (msg.from_name === 'You') {
        // Outgoing message - conversation is with the recipient
        phone = msg.to_phone;
        contactName = msg.subject?.replace('Text to ', '') || phone;
      } else {
        // Incoming message - conversation is with the sender
        if (msg.from_phone) {
          phone = msg.from_phone;
          contactName = msg.from_name;
        } else {
          // Old message without from_phone - skip it
          console.warn('Skipping message without from_phone:', msg.id);
          return;
        }
      }
      
      if (!conversationsMap[phone]) {
        conversationsMap[phone] = {
          id: phone,
          name: contactName,
          phone: phone,
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(contactName)}&size=128&background=random&color=fff&bold=true`,
          lastMessage: '',
          time: 'Now',
          unread: 0,
          isPinned: false,
          isOnline: false,
          category: msg.category || 'personal', // Get category from message
          isGroup: false,
          phoneType: msg.category === 'business' ? 'business' : 'personal',
          messages: []
        };
      }
      
      // Update category if message has one (use the most recent one)
      if (msg.category) {
        conversationsMap[phone].category = msg.category;
        conversationsMap[phone].phoneType = msg.category === 'business' ? 'business' : 'personal';
      }
      
      // Add message to conversation
      conversationsMap[phone].messages.push(msg);
      
      // Update unread count
      if (!msg.read && msg.from_name !== 'You') {
        conversationsMap[phone].unread = (conversationsMap[phone].unread || 0) + 1;
      }
    });
    
    // After all messages are added, update lastMessage to be the most recent one
    Object.values(conversationsMap).forEach(conversation => {
      // Sort messages by created_at
      conversation.messages.sort((a, b) => {
        return (a.created_at || '') > (b.created_at || '') ? 1 : -1;
      });
      
      // Set lastMessage to the most recent message
      const lastMsg = conversation.messages[conversation.messages.length - 1];
      if (lastMsg) {
        conversation.lastMessage = lastMsg.preview || lastMsg.content || '';
        conversation.time = lastMsg.timestamp || 'Now';
      }
      
      // Detect conversation category from message content
      // Only action categories: Urgent > Meeting > Follow-up > Client > Report
      const categoryPriority = ['urgent', 'meeting', 'follow-up', 'client', 'report'];
      let detectedCategories = {};
      
      conversation.messages.forEach(msg => {
        const msgContent = msg.content || msg.preview || '';
        const msgCategory = detectMessageCategory(msgContent);
        if (msgCategory) {
          detectedCategories[msgCategory] = (detectedCategories[msgCategory] || 0) + 1;
        }
      });
      
      // Set conversation category to the highest priority action category found
      conversation.category = null; // Default: no category badge
      for (const cat of categoryPriority) {
        if (detectedCategories[cat]) {
          conversation.category = cat;
          break;
        }
      }
    });
    
    // Convert to array and sort by most recent
    return Object.values(conversationsMap).sort((a, b) => {
      return (b.messages[b.messages.length - 1]?.created_at || '') > (a.messages[a.messages.length - 1]?.created_at || '') ? 1 : -1;
    });
  };

  // Fetch real messages and calls from API
  useEffect(() => {
    let isMounted = true;
    let pollInterval = null;
    let isTabVisible = true;
    
    const fetchData = async (isInitialLoad = false) => {
      // Skip polling if tab is not visible (save resources)
      if (!isInitialLoad && !isTabVisible) {
        console.log('⏸️ Skipping poll - tab not visible');
        return;
      }
      
      try {
        // Only show loading on initial load, not on background refreshes
        if (isInitialLoad) {
          setLoading(true);
        }
        
        // Fetch conversations directly from /api/conversations (contains SMS messages from Twilio webhook)
        const conversationsData = await apiCall('/conversations');
        
        if (!isMounted) return; // Don't update state if unmounted
        
        if (conversationsData && Array.isArray(conversationsData)) {
          // Sort by timestamp with most recent first
          const sortedConversations = conversationsData.sort((a, b) => {
            const dateA = new Date(a.updated_at || a.timestamp || 0);
            const dateB = new Date(b.updated_at || b.timestamp || 0);
            return dateB - dateA;
          });
          setConversations(sortedConversations);
          console.log(`🔄 Conversations refreshed: ${sortedConversations.length} conversations`);
          
          // Auto-select first conversation if none selected
          if (!selectedConversation && sortedConversations.length > 0) {
            isManualSelection.current = true;
            setSelectedConversation(sortedConversations[0]);
          }
        }
        
        // Fetch calls
        try {
          const callsData = await apiCall('/calls');
          if (isMounted) {
            setCalls(Array.isArray(callsData) ? callsData : []);
          }
        } catch (err) {
          console.log('Calls endpoint not available yet');
          if (isMounted) {
            setCalls([]);
          }
        }
        
        // Outlook2 emails are now fetched by the auto-fetch useEffect when Email tab is active
        // This prevents overwriting emails when switching folders
        
      } catch (error) {
        console.error('Error fetching data:', error);
        // Don't clear messages on error - keep showing existing data
        // Only clear on initial load failure
        if (isInitialLoad && isMounted) {
          setMessages([]);
        }
      } finally {
        if (isMounted && isInitialLoad) {
          setLoading(false);
        }
      }
    };
    
    // Handle tab visibility changes
    const handleVisibilityChange = () => {
      isTabVisible = !document.hidden;
      if (isTabVisible && isMounted) {
        console.log('👁️ Tab visible - refreshing messages');
        fetchData(false); // Immediately refresh when tab becomes visible
      }
    };
    
    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Initial fetch
    fetchData(true);
    
    // Auto-refresh every 10 seconds (reduced from 30 for more responsive updates)
    pollInterval = setInterval(() => {
      if (isMounted) {
        fetchData(false);
      }
    }, 10000);
    
    // Cleanup function
    return () => {
      isMounted = false;
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []); // Empty dependency array - only run on mount
  
  // Track if conversation was manually selected by user (vs auto-update)
  const isManualSelection = useRef(false);
  
  // Separate effect to update selectedConversation when conversations change (auto-refresh)
  useEffect(() => {
    if (selectedConversation && conversations.length > 0) {
      const updatedSelected = conversations.find(c => c.id === selectedConversation.id);
      if (updatedSelected) {
        const currentMessageCount = selectedConversation.messages?.length || 0;
        const newMessageCount = updatedSelected.messages?.length || 0;
        
        // CRITICAL FIX: Only update if message count actually changed
        // Don't update just because it's a new object reference
        // This prevents scroll position reset on auto-refresh
        if (currentMessageCount !== newMessageCount) {
          // Message count changed - update the conversation
          isManualSelection.current = false;
          setSelectedConversation(updatedSelected);
        }
        // If message count is the same, DON'T update - keep existing object reference
        // This preserves scroll position during auto-refresh
      }
    }
  }, [conversations]);

  // Track the last manually selected conversation ID to prevent re-scroll on auto-refresh
  const lastManualSelectionId = useRef(null);

  // Scroll to bottom ONLY when user manually opens a conversation
  useEffect(() => {
    // Only scroll if:
    // 1. We have a conversation
    // 2. It was manually selected (flag is true)
    // 3. The ID is different from last manual selection (prevents duplicate scrolls)
    if (selectedConversation && isManualSelection.current && 
        lastManualSelectionId.current !== selectedConversation.id) {
      
      lastManualSelectionId.current = selectedConversation.id;
      setJustOpenedConversation(true);
      setPreviousMessageCount(selectedConversation?.messages?.length || 0);
      
      // Force scroll to bottom - use setTimeout to ensure DOM is ready
      setTimeout(() => {
        if (messagesContainerRef.current) {
          const container = messagesContainerRef.current;
          container.scrollTop = container.scrollHeight;
        }
        
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
        }
      }, 50);
      
      // Reset flags after scroll
      const timer = setTimeout(() => {
        setJustOpenedConversation(false);
        isManualSelection.current = false;
      }, 500);
      
      return () => {
        clearTimeout(timer);
      };
    }
  }, [selectedConversation]); // Watch the whole object, but only act on ID change + manual flag

  // Smart auto-scroll: ONLY scroll on NEW messages when user is near bottom
  useEffect(() => {
    if (!messagesContainerRef.current || !messagesEndRef.current || !selectedConversation) {
      return;
    }

    // NEVER scroll if conversation was just opened (still in grace period)
    if (justOpenedConversation) {
      return;
    }

    const container = messagesContainerRef.current;
    const messageCount = selectedConversation?.messages?.length || 0;
    
    // Only proceed if message count actually increased (new message)
    if (messageCount <= previousMessageCount) {
      setPreviousMessageCount(messageCount);
      return;
    }
    
    // Check if user is near the bottom (within 100px)
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    
    // Only scroll if user was already at bottom AND not manually scrolling
    if (isNearBottom && !isUserScrolling) {
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
    }
    
    setPreviousMessageCount(messageCount);
  }, [selectedConversation?.messages?.length]);

  // Track when user manually scrolls
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
      setIsUserScrolling(!isNearBottom);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [selectedConversation]);

  // Persist Outlook email state to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem('outlookEmails', JSON.stringify(outlookEmails));
    } catch (e) { console.warn('Failed to save outlookEmails to sessionStorage:', e); }
  }, [outlookEmails]);

  useEffect(() => {
    try {
      sessionStorage.setItem('outlookEmailsCache', JSON.stringify(outlookEmailsCache));
    } catch (e) { console.warn('Failed to save outlookEmailsCache to sessionStorage:', e); }
  }, [outlookEmailsCache]);

  useEffect(() => {
    try {
      sessionStorage.setItem('outlookConnected', String(outlookConnected));
    } catch (e) { console.warn('Failed to save outlookConnected to sessionStorage:', e); }
  }, [outlookConnected]);

  useEffect(() => {
    try {
      sessionStorage.setItem('outlookHasInitiallyFetched', String(hasInitiallyFetched));
    } catch (e) { console.warn('Failed to save hasInitiallyFetched to sessionStorage:', e); }
  }, [hasInitiallyFetched]);

  // Auto-fetch Outlook emails ONLY ONCE per session, not when switching tabs/pages
  // Use a ref to track if we've already attempted a fetch this session
  const hasFetchAttempted = useRef((() => {
    try {
      return sessionStorage.getItem('outlookFetchAttempted') === 'true';
    } catch { return false; }
  })());

  useEffect(() => {
    const fetchOutlookEmails = async () => {
      // Skip if we've already attempted fetch OR if we have cached data
      if (hasFetchAttempted.current) {
        console.log('📧 Skipping fetch - already attempted this session');
        return;
      }
      
      // Only auto-fetch when first visiting email tab
      if (activeTab === 'email' && activeEmailAccount === 'outlook' && !outlookLoading && activeFolder === 'inbox') {
        // Check if we have any cached emails (from sessionStorage or state)
        const hasCachedEmails = Object.keys(outlookEmailsCache).length > 0 || outlookEmails.length > 0;
        if (hasCachedEmails) {
          console.log('📧 Using persisted emails from sessionStorage');
          hasFetchAttempted.current = true;
          sessionStorage.setItem('outlookFetchAttempted', 'true');
          setHasInitiallyFetched(true);
        } else if (!hasInitiallyFetched) {
          console.log('📧 Initial fetch of Outlook emails...');
          hasFetchAttempted.current = true;
          sessionStorage.setItem('outlookFetchAttempted', 'true');
          await syncOutlookEmails();
        }
      }
    };
    fetchOutlookEmails();
  }, [activeTab, activeEmailAccount]);

  // When switching back to email tab, restore cached emails for current folder
  useEffect(() => {
    if (activeTab === 'email' && activeEmailAccount === 'outlook') {
      if (outlookEmailsCache[activeFolder]) {
        setOutlookEmails(outlookEmailsCache[activeFolder]);
        console.log(`📧 Restored ${outlookEmailsCache[activeFolder].length} cached emails for ${activeFolder}`);
      }
    }
  }, [activeTab, activeEmailAccount, activeFolder]);

  // Filter conversations by category and phone type
  const filteredConversations = conversations.filter(conv => {
    // Get category from conversation or detect from last message
    const convCategory = conv.category || detectMessageCategory(conv.lastMessage);
    
    // Map backend categories to frontend category names for filtering
    const normalizedCategory = convCategory === 'meeting_request' ? 'meeting' 
      : convCategory === 'follow_up' ? 'follow-up' 
      : convCategory;
    
    // Filter by category (urgent, follow-up, client, etc.)
    const categoryMatch = activeCategory === 'all' || normalizedCategory === activeCategory || convCategory === activeCategory;
    
    // Filter by phone type (all, personal, business)
    const phoneTypeMatch = activeMessagesCategory === 'all' || conv.phoneType === activeMessagesCategory;
    
    // Search filter
    const searchMatch = !searchQuery || conv.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                       conv.lastMessage?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return categoryMatch && phoneTypeMatch && searchMatch;
  });

  // When a specific category is selected (not 'all'), show individual messages with that category
  const categorizedMessages = React.useMemo(() => {
    if (activeCategory === 'all') return [];
    
    const messagesWithCategory = [];
    conversations.forEach(conv => {
      conv.messages?.forEach(msg => {
        const msgContent = msg.text || msg.content || msg.preview || '';
        const msgCategory = detectMessageCategory(msgContent);
        
        if (msgCategory === activeCategory) {
          messagesWithCategory.push({
            id: `${conv.id}-${msg.id}`,
            messageId: msg.id,
            conversationId: conv.id,
            conversationName: conv.name,
            conversationPhone: conv.phone,
            avatar: conv.avatar,
            content: msgContent,
            time: msg.time || msg.timestamp || 'Now',
            category: msgCategory,
            isOwn: msg.from_name === 'You' || msg.sent === true,
            originalMessage: msg,
            originalConversation: conv
          });
        }
      });
    });
    
    // Sort by most recent first
    return messagesWithCategory.sort((a, b) => {
      const aTime = a.originalMessage?.created_at || '';
      const bTime = b.originalMessage?.created_at || '';
      return bTime > aTime ? 1 : -1;
    });
  }, [conversations, activeCategory]);

  // Messages for all conversations with real photos

  // Email list with EXPANDED threads and way more emails for all accounts
  const emailsByAccount = {
    gmail: [
      // INBOX - 15 emails with expanded threads
      { id: 1, from: 'John Martinez', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop', email: 'john@gmail.com', subject: 'RE: Contract Exception - Chelsea Property', preview: 'Client is requesting special terms for the Chelsea property deal...', time: '2h', category: 'urgent', folder: 'inbox', thread: [
        { from: 'John Martinez', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop', time: '2h ago', body: 'Hi,\n\nClient is requesting special terms for the Chelsea property. They want a 90-day closing and 5% price reduction.\n\nBest,\nJohn' },
        { from: 'You', time: '1h ago', body: 'Thanks John. The price reduction is steep. Let\'s schedule a call to discuss alternatives.' },
        { from: 'Sarah Mitchell', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop', time: '45m ago', body: 'I agree. We could be flexible on timeline but suggest 2.5% max on price.' },
        { from: 'John Martinez', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop', time: '30m ago', body: 'Perfect. I\'ll set up a call for 3 PM today with all stakeholders.' },
        { from: 'You', time: '20m ago', body: 'Sounds good. I\'ll prepare the counter-offer documents.' }
      ] },
      { id: 2, from: 'Sarah Chen', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop', email: 'sarah.chen@gmail.com', subject: 'Weekend Plans - Brooklyn Food Tour', preview: 'Hey! Want to check out that new Brooklyn food market this Saturday?...', time: '5h', category: 'client', folder: 'inbox', thread: [
        { from: 'Sarah Chen', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop', time: '5h ago', body: 'Hey! Want to check out that new Brooklyn food market this Saturday? I heard they have amazing tacos!' },
        { from: 'You', time: '4h ago', body: 'Yes! I\'ve been wanting to try that place. What time works for you?' },
        { from: 'Sarah Chen', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop', time: '3h ago', body: 'How about 11 AM? We can grab brunch there and explore the market.' },
        { from: 'You', time: '2h ago', body: 'Perfect! See you Saturday at 11. Should I bring anything?' },
        { from: 'Sarah Chen', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop', time: '1h ago', body: 'Just bring your appetite! 😊 This is going to be fun!' }
      ] },
      { id: 3, from: 'Netflix', avatar: 'https://images.unsplash.com/photo-1522869635100-9f4c5e86aa37?w=100&h=100&fit=crop', email: 'info@netflix.com', subject: 'New Releases This Week', preview: 'Your personalized recommendations are here! Check out these new shows...', time: '1d', category: 'report', folder: 'inbox', thread: [{ from: 'Netflix', avatar: 'https://images.unsplash.com/photo-1522869635100-9f4c5e86aa37?w=100&h=100&fit=crop', time: '1d ago', body: 'Your personalized recommendations are here! New shows: Stranger Things S5, The Crown Final Season, and more!' }] },
      { id: 4, from: 'Amazon', avatar: 'https://images.unsplash.com/photo-1523474253046-8cd2748b5fd2?w=100&h=100&fit=crop', email: 'shipment-tracking@amazon.com', subject: 'Your Package Has Shipped', preview: 'Good news! Your order will arrive by tomorrow...', time: '1d', category: 'follow-up', folder: 'inbox', thread: [{ from: 'Amazon', avatar: 'https://images.unsplash.com/photo-1523474253046-8cd2748b5fd2?w=100&h=100&fit=crop', time: '1d ago', body: 'Your package has shipped! Track your order: Estimated delivery tomorrow by 8 PM.' }] },
      { id: 16, from: 'Delta Air Lines', avatar: 'https://images.unsplash.com/photo-1436491865332-7a61a109db05?w=100&h=100&fit=crop', email: 'no-reply@delta.com', subject: 'Your Flight Itinerary - DFW to JFK', preview: 'Your upcoming flight details and check-in reminder...', time: '1d', category: 'travel', folder: 'inbox', thread: [
        { from: 'Delta Air Lines', avatar: 'https://images.unsplash.com/photo-1436491865332-7a61a109db05?w=100&h=100&fit=crop', time: '1d ago', body: 'Your Trip Summary\n\nDelta Flight DL 1892\nDallas/Fort Worth (DFW) → New York JFK\nFebruary 25, 2026\nDeparture: 6:00 AM CST\nArrival: 10:45 AM EST\n\nConfirmation: GH7K2M\nSeat: 2A (Delta One)\nBaggage: 2 checked bags included\n\nCheck in online 24 hours before departure.\nManage your trip at delta.com' },
        { from: 'You', time: '12h ago', body: 'Great, I need to remember to check in the night before. Adding a reminder.' }
      ] },
      { id: 5, from: 'Marcus Johnson', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop', email: 'marcus.j@gmail.com', subject: 'Gym Session Tomorrow?', preview: 'Are we still on for 6 AM workout tomorrow? Let me know!', time: '2d', category: 'client', folder: 'inbox', thread: [
        { from: 'Marcus Johnson', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop', time: '2d ago', body: 'Are we still on for 6 AM workout tomorrow? Want to do chest and shoulders.' },
        { from: 'You', time: '1d ago', body: 'Definitely! I\'ll be there. Let\'s crush it!' },
        { from: 'Marcus Johnson', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop', time: '1d ago', body: 'Perfect! See you bright and early. Don\'t forget your water bottle.' }
      ] },
      { id: 6, from: 'Spotify', avatar: 'https://images.unsplash.com/photo-1614680376573-df3480f0c6ff?w=100&h=100&fit=crop', email: 'noreply@spotify.com', subject: 'Your 2024 Wrapped is Ready!', preview: 'See your most played songs and artists of the year...', time: '2d', category: 'report', folder: 'inbox', thread: [{ from: 'Spotify', avatar: 'https://images.unsplash.com/photo-1614680376573-df3480f0c6ff?w=100&h=100&fit=crop', time: '2d ago', body: 'Your 2024 Wrapped is here! You listened to 45,230 minutes of music this year!' }] },
      { id: 7, from: 'Jessica Lee', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop', email: 'jessica.lee@gmail.com', subject: 'Happy New Year! 🎉', preview: 'Wishing you and your family a wonderful 2025!...', time: '3d', category: 'client', folder: 'inbox', thread: [
        { from: 'Jessica Lee', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop', time: '3d ago', body: 'Happy New Year! Wishing you and your family a wonderful 2025 full of success and happiness!' },
        { from: 'You', time: '2d ago', body: 'Thank you Jessica! Happy New Year to you too! Hope 2025 brings you everything you wish for.' }
      ] },
      { id: 8, from: 'Uber', avatar: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=100&h=100&fit=crop', email: 'receipts@uber.com', subject: 'Your Receipt for Trip to JFK Airport', preview: 'Thanks for riding with Uber. Your trip cost $45.30...', time: '3d', category: 'report', folder: 'inbox', thread: [{ from: 'Uber', avatar: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=100&h=100&fit=crop', time: '3d ago', body: 'Your trip receipt: Manhattan to JFK Airport - $45.30. Thanks for riding with Uber!' }] },
      { id: 9, from: 'DoorDash', avatar: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=100&h=100&fit=crop', email: 'no-reply@doordash.com', subject: 'Your Order is On The Way!', preview: 'Your order from Thai Kitchen is being delivered...', time: '4d', category: 'follow-up', folder: 'inbox', thread: [{ from: 'DoorDash', avatar: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=100&h=100&fit=crop', time: '4d ago', body: 'Your order from Thai Kitchen is on the way! ETA: 25 minutes.' }] },
      { id: 10, from: 'Ryan Cooper', avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop', email: 'ryan.cooper@gmail.com', subject: 'Fantasy Football League Update', preview: 'Your team moved up to 3rd place this week!...', time: '4d', category: 'client', folder: 'inbox', thread: [{ from: 'Ryan Cooper', avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop', time: '4d ago', body: 'Your team moved up to 3rd place! Great pickups this week!' }] },
      { id: 11, from: 'Airbnb', avatar: 'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=100&h=100&fit=crop', email: 'automated@airbnb.com', subject: 'Your Upcoming Trip to Miami', preview: 'Check-in details for your Miami Beach rental...', time: '5d', category: 'urgent', folder: 'inbox', thread: [{ from: 'Airbnb', avatar: 'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=100&h=100&fit=crop', time: '5d ago', body: 'Your Miami trip details: Check-in at 3 PM. Host contact: (305) 555-0123' }] },
      { id: 12, from: 'GitHub', avatar: 'https://images.unsplash.com/photo-1618401471353-b98afee0b2eb?w=100&h=100&fit=crop', email: 'noreply@github.com', subject: '[Security] New login from Chrome on Windows', preview: 'We noticed a new sign-in to your GitHub account...', time: '5d', category: 'urgent', folder: 'inbox', thread: [{ from: 'GitHub', avatar: 'https://images.unsplash.com/photo-1618401471353-b98afee0b2eb?w=100&h=100&fit=crop', time: '5d ago', body: 'New login detected from New York, NY on Chrome. If this wasn\'t you, secure your account immediately.' }] },
      { id: 13, from: 'Best Buy', avatar: 'https://images.unsplash.com/photo-1593642532400-2682810df593?w=100&h=100&fit=crop', email: 'deals@bestbuy.com', subject: 'Flash Sale: 50% Off Electronics', preview: 'Don\'t miss out on our biggest sale of the year!...', time: '6d', category: 'report', folder: 'inbox', thread: [{ from: 'Best Buy', avatar: 'https://images.unsplash.com/photo-1593642532400-2682810df593?w=100&h=100&fit=crop', time: '6d ago', body: 'Flash sale alert! 50% off laptops, TVs, and more. Today only!' }] },
      { id: 14, from: 'Rachel Martinez', avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop', email: 'rachel.m@gmail.com', subject: 'Book Club Meeting Next Tuesday', preview: 'We\'re discussing "The Midnight Library" next week...', time: '6d', category: 'follow-up', folder: 'inbox', thread: [{ from: 'Rachel Martinez', avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop', time: '6d ago', body: 'Book club next Tuesday at 7 PM! We\'re discussing "The Midnight Library". Don\'t forget to finish reading!' }] },
      { id: 15, from: 'Masterclass', avatar: 'https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=100&h=100&fit=crop', email: 'learn@masterclass.com', subject: 'New Class: Photography with Annie Leibovitz', preview: 'Learn photography techniques from the master...', time: '1w', category: 'report', folder: 'inbox', thread: [{ from: 'Masterclass', avatar: 'https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=100&h=100&fit=crop', time: '1w ago', body: 'New photography class available! Learn from Annie Leibovitz herself.' }] },
      
      // DRAFTS - 5 emails
      { id: 101, from: 'Draft', avatar: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=100&h=100&fit=crop', email: 'me@gmail.com', subject: 'RE: Vacation Plans Discussion', preview: 'Hi everyone, I wanted to share some thoughts about our summer vacation...', time: '3h', category: 'client', folder: 'drafts', thread: [{ from: 'You', time: '3h ago', body: 'Summer vacation ideas...' }] },
      { id: 102, from: 'Draft', avatar: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=100&h=100&fit=crop', email: 'me@gmail.com', subject: 'Birthday Party RSVP', preview: 'Thanks for the invitation! I would love to attend...', time: '1d', category: 'follow-up', folder: 'drafts', thread: [{ from: 'You', time: '1d ago', body: 'Thanks for invitation!' }] },
      { id: 103, from: 'Draft', avatar: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=100&h=100&fit=crop', email: 'me@gmail.com', subject: 'Recipe Request', preview: 'Could you share that amazing pasta recipe from last week?...', time: '2d', category: 'client', folder: 'drafts', thread: [{ from: 'You', time: '2d ago', body: 'Recipe request...' }] },
      { id: 104, from: 'Draft', avatar: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=100&h=100&fit=crop', email: 'me@gmail.com', subject: 'Thank You Note', preview: 'Thank you so much for your hospitality during my visit...', time: '3d', category: 'client', folder: 'drafts', thread: [{ from: 'You', time: '3d ago', body: 'Thank you!' }] },
      { id: 105, from: 'Draft', avatar: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=100&h=100&fit=crop', email: 'me@gmail.com', subject: 'Job Application Follow-up', preview: 'I wanted to follow up on my application submitted last week...', time: '4d', category: 'follow-up', folder: 'drafts', thread: [{ from: 'You', time: '4d ago', body: 'Job application follow-up...' }] },
      
      // ARCHIVE - 8 emails
      { id: 201, from: 'Bank of America', avatar: 'https://images.unsplash.com/photo-1541354329998-f4d9a9f9297f?w=100&h=100&fit=crop', email: 'alerts@bankofamerica.com', subject: 'Your Monthly Statement is Ready', preview: 'View your December statement online now...', time: '2w', category: 'report', folder: 'archive', thread: [{ from: 'Bank of America', avatar: 'https://images.unsplash.com/photo-1541354329998-f4d9a9f9297f?w=100&h=100&fit=crop', time: '2w ago', body: 'Statement ready.' }] },
      { id: 202, from: 'LinkedIn', avatar: 'https://images.unsplash.com/photo-1611944212129-29977ae1398c?w=100&h=100&fit=crop', email: 'messages-noreply@linkedin.com', subject: 'You have 5 new connection requests', preview: 'Expand your professional network today...', time: '3w', category: 'client', folder: 'archive', thread: [{ from: 'LinkedIn', avatar: 'https://images.unsplash.com/photo-1611944212129-29977ae1398c?w=100&h=100&fit=crop', time: '3w ago', body: 'New connections!' }] },
      { id: 203, from: 'Mint', avatar: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=100&h=100&fit=crop', email: 'noreply@mint.com', subject: 'Your November Budget Summary', preview: 'You stayed under budget in 4 out of 6 categories...', time: '1mo', category: 'report', folder: 'archive', thread: [{ from: 'Mint', avatar: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=100&h=100&fit=crop', time: '1mo ago', body: 'Budget summary.' }] },
      { id: 204, from: 'American Express', avatar: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=100&h=100&fit=crop', email: 'statements@americanexpress.com', subject: 'Credit Card Statement Available', preview: 'Your October statement is now available online...', time: '1mo', category: 'report', folder: 'archive', thread: [{ from: 'American Express', avatar: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=100&h=100&fit=crop', time: '1mo ago', body: 'Statement available.' }] },
      { id: 205, from: 'Verizon', avatar: 'https://images.unsplash.com/photo-1510557880182-3d4d3cba35a5?w=100&h=100&fit=crop', email: 'billing@verizon.com', subject: 'Your Bill is Ready', preview: 'Your September bill of $127.53 is now available...', time: '2mo', category: 'report', folder: 'archive', thread: [{ from: 'Verizon', avatar: 'https://images.unsplash.com/photo-1510557880182-3d4d3cba35a5?w=100&h=100&fit=crop', time: '2mo ago', body: 'Bill ready.' }] },
      { id: 206, from: 'Eventbrite', avatar: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=100&h=100&fit=crop', email: 'noreply@eventbrite.com', subject: 'Event Reminder: Tech Conference 2024', preview: 'Don\'t forget your ticket to the conference...', time: '2mo', category: 'follow-up', folder: 'archive', thread: [{ from: 'Eventbrite', avatar: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=100&h=100&fit=crop', time: '2mo ago', body: 'Event reminder.' }] },
      { id: 207, from: 'Zillow', avatar: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=100&h=100&fit=crop', email: 'alerts@zillow.com', subject: 'New Homes Matching Your Search', preview: '3 new properties in Brooklyn under $1M...', time: '3mo', category: 'client', folder: 'archive', thread: [{ from: 'Zillow', avatar: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=100&h=100&fit=crop', time: '3mo ago', body: 'New properties!' }] },
      { id: 208, from: 'Audible', avatar: 'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=100&h=100&fit=crop', email: 'hello@audible.com', subject: 'Your Monthly Credit Has Arrived', preview: 'Choose your next audiobook with your monthly credit...', time: '3mo', category: 'report', folder: 'archive', thread: [{ from: 'Audible', avatar: 'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=100&h=100&fit=crop', time: '3mo ago', body: 'Monthly credit.' }] },
      
      // TRASH - 4 emails
      { id: 301, from: 'Old Conversation', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', email: 'oldconvo@gmail.com', subject: 'Outdated Project Discussion', preview: 'This project has been cancelled...', time: '1mo', category: 'client', folder: 'trash', thread: [{ from: 'Old Conversation', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', time: '1mo ago', body: 'Project cancelled.' }] },
      { id: 302, from: 'Deleted Contact', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', email: 'deleted@email.com', subject: 'Old Event Invitation', preview: 'Event has passed...', time: '2mo', category: 'client', folder: 'trash', thread: [{ from: 'Deleted Contact', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', time: '2mo ago', body: 'Event passed.' }] },
      { id: 303, from: 'Expired Offer', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', email: 'offers@expired.com', subject: 'Limited Time Offer (Expired)', preview: 'This offer has expired...', time: '3mo', category: 'report', folder: 'trash', thread: [{ from: 'Expired Offer', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', time: '3mo ago', body: 'Offer expired.' }] },
      { id: 304, from: 'Unsubscribed', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', email: 'unsubbed@newsletter.com', subject: 'Old Newsletter', preview: 'No longer subscribed...', time: '4mo', category: 'report', folder: 'trash', thread: [{ from: 'Unsubscribed', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', time: '4mo ago', body: 'Unsubscribed.' }] },
      
      // FLAGGED - 5 important emails
      { id: 501, from: 'John Martinez', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop', email: 'john@gmail.com', subject: 'URGENT: Contract Deadline Tomorrow', preview: 'The Chelsea property contract must be signed by 5 PM tomorrow...', time: '1h', category: 'urgent', folder: 'flagged', isFlagged: true, thread: [{ from: 'John Martinez', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop', time: '1h ago', body: 'Contract deadline tomorrow at 5 PM. All parties ready to sign. Please confirm attendance.' }] },
      { id: 502, from: 'Sarah Chen', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop', email: 'sarah.chen@gmail.com', subject: 'Investment Opportunity - Brooklyn Development', preview: 'High-priority investment opportunity with 25% projected ROI...', time: '6h', category: 'urgent', folder: 'flagged', isFlagged: true, thread: [{ from: 'Sarah Chen', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop', time: '6h ago', body: 'Flagged for review: Brooklyn development project with 25% ROI projection. Prime location, strong demand. Decision needed by Friday.' }] },
      { id: 503, from: 'Legal Team', avatar: 'https://images.unsplash.com/photo-1556157382-97eda2d62296?w=100&h=100&fit=crop', email: 'legal@gmail.com', subject: 'Action Required: Lease Agreement Review', preview: 'Madison Avenue lease requires your approval before proceeding...', time: '1d', category: 'urgent', folder: 'flagged', isFlagged: true, thread: [{ from: 'Legal Team', avatar: 'https://images.unsplash.com/photo-1556157382-97eda2d62296?w=100&h=100&fit=crop', time: '1d ago', body: 'Lease agreement needs final approval. Minor revisions completed as requested. Ready for signature.' }] },
      { id: 504, from: 'Michael Chen', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop', email: 'mchen@outlook.com', subject: 'Meeting Notes - Q1 Strategy', preview: 'Important action items from today\'s strategy meeting...', time: '2d', category: 'follow-up', folder: 'flagged', isFlagged: true, thread: [{ from: 'Michael Chen', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop', time: '2d ago', body: 'Key action items from strategy meeting. Please review and confirm timeline for Q1 initiatives.' }] },
      { id: 505, from: 'Emily Rodriguez', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop', email: 'emily.rodriguez@gmail.com', subject: 'Follow Up: Client Presentation Feedback', preview: 'Client loved the presentation. Next steps outlined inside...', time: '3d', category: 'client', folder: 'flagged', isFlagged: true, thread: [{ from: 'Emily Rodriguez', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop', time: '3d ago', body: 'Flagged: Client presentation feedback. They want to move forward. Schedule follow-up call ASAP.' }] },
      
      // SPAM - 6 emails
      { id: 401, from: 'Suspicious Sender', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', email: 'noreply@suspicious.com', subject: 'You Won $1 Million! Claim Now!', preview: 'Congratulations! Click here to claim your prize...', time: '1w', category: 'urgent', folder: 'spam', thread: [{ from: 'Suspicious Sender', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', time: '1w ago', body: 'Claim prize!' }] },
      { id: 402, from: 'Fake Bank', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', email: 'security@fakebank.com', subject: 'Verify Your Account Immediately', preview: 'Your account will be closed unless you verify...', time: '2w', category: 'urgent', folder: 'spam', thread: [{ from: 'Fake Bank', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', time: '2w ago', body: 'Verify account!' }] },
      { id: 403, from: 'Unknown Sender', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', email: 'random@spam.com', subject: 'Meet Singles in Your Area!', preview: 'Hot singles are waiting to meet you...', time: '3w', category: 'client', folder: 'spam', thread: [{ from: 'Unknown Sender', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', time: '3w ago', body: 'Meet singles!' }] },
      { id: 404, from: 'Crypto Scam', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', email: 'invest@cryptoscam.com', subject: 'Bitcoin Investment Opportunity', preview: 'Double your Bitcoin in 48 hours guaranteed...', time: '1mo', category: 'urgent', folder: 'spam', thread: [{ from: 'Crypto Scam', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', time: '1mo ago', body: 'Crypto investment!' }] },
      { id: 405, from: 'Phishing Alert', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', email: 'help@phishing.com', subject: 'Update Your Payment Information', preview: 'We need to verify your payment details...', time: '1mo', category: 'urgent', folder: 'spam', thread: [{ from: 'Phishing Alert', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', time: '1mo ago', body: 'Update payment!' }] },
      { id: 406, from: 'Weight Loss Spam', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', email: 'offers@weightloss.com', subject: 'Lose 30 Pounds in 30 Days!', preview: 'Our revolutionary formula guarantees results...', time: '2mo', category: 'report', folder: 'spam', thread: [{ from: 'Weight Loss Spam', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', time: '2mo ago', body: 'Weight loss offer!' }] },
    ],
    
    outlook: [
      // INBOX - 15 emails with expanded threads
      { id: 11, from: 'Finance Team', avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop', email: 'finance@company.com', subject: 'Q4 Financial Report Ready for Review', preview: 'The quarterly financial report has been completed and requires your immediate review...', time: '2h', category: 'urgent', folder: 'inbox', thread: [
        { from: 'Finance Team', avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop', time: '2h ago', body: 'Dear Team,\n\nThe Q4 financial report is now ready for your review. Revenue increased 23% YoY with strong performance across all divisions.\n\nKey highlights:\n- Total revenue: $45.2M\n- Operating margin: 18.5%\n- Net profit: $8.4M\n\nPlease review and provide feedback by EOD Friday.\n\nBest regards,\nFinance Team' },
        { from: 'You', time: '1h ago', body: 'Excellent work! The numbers look very strong. I have a few questions about the EMEA region performance - let\'s schedule a call to discuss.' },
        { from: 'Finance Team', avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop', time: '30m ago', body: 'Absolutely! I\'m available tomorrow at 10 AM or 3 PM. Which works better for you?' }
      ] },
      { id: 12, from: 'Michael Wong', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop', email: 'michael@company.com', subject: 'Team Meeting Rescheduled - Property Acquisition Strategy', preview: 'Our weekly sync has been moved to Thursday at 2 PM to accommodate the board meeting...', time: '3h', category: 'follow-up', folder: 'inbox', thread: [
        { from: 'Michael Wong', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop', time: '3h ago', body: 'Hi everyone,\n\nOur weekly strategy meeting has been rescheduled to Thursday 2 PM. We\'ll be discussing:\n\n1. Downtown Manhattan acquisition opportunity\n2. Q1 2025 investment targets\n3. Team expansion plans\n\nSee you there!\nMichael' },
        { from: 'Sarah Chen', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop', time: '2h ago', body: 'Thanks Michael! I\'ll prepare the Manhattan property analysis for the meeting.' },
        { from: 'You', time: '1h ago', body: 'Perfect timing. I\'ll bring the budget projections and ROI analysis.' }
      ] },
      { id: 13, from: 'HR Department', avatar: 'https://images.unsplash.com/photo-1573496799652-408c2ac9fe98?w=100&h=100&fit=crop', email: 'hr@company.com', subject: 'Benefits Enrollment Deadline - Action Required', preview: 'Important: Benefits enrollment closes this Friday at 5 PM...', time: '5h', category: 'urgent', folder: 'inbox', thread: [
        { from: 'HR Department', avatar: 'https://images.unsplash.com/photo-1573496799652-408c2ac9fe98?w=100&h=100&fit=crop', time: '5h ago', body: 'Dear Team Members,\n\nThis is your final reminder that benefits enrollment closes this Friday, January 10th at 5:00 PM EST.\n\nNew for 2025:\n- Enhanced dental coverage\n- Mental health support program\n- Increased 401(k) matching (up to 6%)\n- Remote work stipend\n\nPlease complete your enrollment at: benefits.company.com\n\nIf you have questions, contact HR at ext. 2500.\n\nBest,\nHR Team' }
      ] },
      { id: 14, from: 'IT Support', avatar: 'https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?w=100&h=100&fit=crop', email: 'it@company.com', subject: 'System Maintenance Tonight - 11 PM to 2 AM', preview: 'Scheduled maintenance window for email servers and CRM system...', time: '1d', category: 'report', folder: 'inbox', thread: [
        { from: 'IT Support', avatar: 'https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?w=100&h=100&fit=crop', time: '1d ago', body: 'Dear All,\n\nWe will be performing critical system maintenance tonight from 11:00 PM to 2:00 AM EST.\n\nAffected systems:\n- Email servers\n- CRM platform\n- Document management system\n- VPN access\n\nPlease save all work and log out before 11 PM. If you need emergency access, contact the IT helpdesk.\n\nThank you for your cooperation.\nIT Support Team' }
      ] },
      { id: 15, from: 'Project Management', avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop', email: 'pm@company.com', subject: 'Brooklyn Development Project - Phase 2 Approval', preview: 'Great news! The Brooklyn project Phase 2 has been approved by the board...', time: '1d', category: 'urgent', folder: 'inbox', thread: [
        { from: 'Project Management', avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop', time: '1d ago', body: 'Team,\n\nExcellent news! The board has approved Phase 2 of the Brooklyn development project with a budget of $12.5M.\n\nNext steps:\n1. Finalize construction contracts\n2. Secure additional permits\n3. Begin tenant pre-leasing\n\nKickoff meeting: Monday 9 AM\n\nCongratulations to everyone involved!\nProject Management' }
      ] },
      { id: 16, from: 'Legal Department', avatar: 'https://images.unsplash.com/photo-1556157382-97eda2d62296?w=100&h=100&fit=crop', email: 'legal@company.com', subject: 'Contract Review Complete - Madison Avenue Lease', preview: 'The legal review of the Madison Avenue lease agreement has been completed...', time: '2d', category: 'follow-up', folder: 'inbox', thread: [
        { from: 'Legal Department', avatar: 'https://images.unsplash.com/photo-1556157382-97eda2d62296?w=100&h=100&fit=crop', time: '2d ago', body: 'Dear Team,\n\nWe\'ve completed our review of the Madison Avenue lease agreement. Overall, the terms are favorable with a few minor revisions needed in Section 7 (Maintenance Responsibilities).\n\nRecommendations:\n- Clarify HVAC maintenance obligations\n- Add force majeure clause\n- Adjust lease escalation terms (currently 3.5% annual)\n\nFull report attached.\n\nBest,\nLegal Team' },
        { from: 'You', time: '1d ago', body: 'Thanks for the thorough review. Let\'s incorporate these changes and schedule a call with the landlord\'s attorney next week.' }
      ] },
      { id: 17, from: 'Compliance Office', avatar: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=100&h=100&fit=crop', email: 'compliance@company.com', subject: 'Quarterly Compliance Training - Due January 31st', preview: 'All employees must complete Q1 compliance training modules by month end...', time: '2d', category: 'report', folder: 'inbox', thread: [
        { from: 'Compliance Office', avatar: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=100&h=100&fit=crop', time: '2d ago', body: 'Dear Colleagues,\n\nPlease complete the following compliance training modules by January 31st:\n\n1. Anti-Money Laundering (AML) - 45 minutes\n2. Data Privacy & GDPR - 30 minutes  \n3. Workplace Safety - 20 minutes\n\nAccess the training portal at: training.company.com\n\nCompletion is mandatory and will be monitored.\n\nThank you,\nCompliance Office' }
      ] },
      { id: 18, from: 'Corporate Events', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop', email: 'events@company.com', subject: 'Annual Company Retreat - Save the Date!', preview: 'Mark your calendars! Our 2025 company retreat will be held in Miami...', time: '3d', category: 'client', folder: 'inbox', thread: [
        { from: 'Corporate Events', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop', time: '3d ago', body: 'Exciting announcement!\n\nOur 2025 Annual Company Retreat is scheduled for March 15-18 in Miami Beach, Florida!\n\nAgenda highlights:\n- Strategic planning sessions\n- Team building activities\n- Awards dinner & celebration\n- Beachfront networking events\n\nMore details coming soon. Please mark your calendars!\n\nEvents Team' }
      ] },
      { id: 19, from: 'Training & Development', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop', email: 'training@company.com', subject: 'Leadership Workshop - Registration Open', preview: 'New leadership development workshop series starting February...', time: '4d', category: 'follow-up', folder: 'inbox', thread: [
        { from: 'Training & Development', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop', time: '4d ago', body: 'Dear Leaders,\n\nWe\'re excited to announce our new Leadership Excellence Workshop series starting in February!\n\nTopics:\n- Strategic Decision Making\n- Team Performance Management  \n- Conflict Resolution\n- Executive Communication\n\nLimited to 20 participants. Register at: training.company.com/leadership\n\nBest,\nTraining Team' }
      ] },
      { id: 110, from: 'Facilities Management', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop', email: 'facilities@company.com', subject: 'Office Renovation Update', preview: 'The 5th floor renovation is ahead of schedule and will be completed by January 20th...', time: '5d', category: 'report', folder: 'inbox', thread: [
        { from: 'Facilities Management', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop', time: '5d ago', body: 'Good news everyone!\n\nThe 5th floor renovation is progressing ahead of schedule. Completion is now expected by January 20th (originally January 31st).\n\nNew features:\n- Modern open workspace layout\n- 4 new conference rooms\n- Updated kitchen & break areas\n- Soundproof phone booths\n\nWe\'ll begin moving teams back starting January 22nd.\n\nFacilities Team' }
      ] },
      { id: 120, from: 'Security Team', avatar: 'https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?w=100&h=100&fit=crop', email: 'security@company.com', subject: 'New Security Badge System Activation', preview: 'Important: New biometric security badges will be activated next Monday...', time: '5d', category: 'urgent', folder: 'inbox', thread: [
        { from: 'Security Team', avatar: 'https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?w=100&h=100&fit=crop', time: '5d ago', body: 'Attention All Employees,\n\nOur new biometric security badge system goes live on Monday, January 13th.\n\nWhat you need to do:\n1. Register your fingerprint at the security desk (Lobby A)\n2. Pick up your new badge  \n3. Return your old badge\n\nRegistration hours: 8 AM - 6 PM daily\nPlease complete registration by Friday.\n\nSecurity Team' }
      ] },
      { id: 121, from: 'Procurement', avatar: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=100&h=100&fit=crop', email: 'procurement@company.com', subject: 'Q1 Office Supplies Order Reminder', preview: 'Submit your Q1 office supplies requests by January 15th...', time: '1w', category: 'report', folder: 'inbox', thread: [
        { from: 'Procurement', avatar: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=100&h=100&fit=crop', time: '1w ago', body: 'Dear Team,\n\nPlease submit Q1 office supplies requests through the procurement portal by January 15th.\n\nItems available:\n- Stationery & office supplies\n- Technology accessories\n- Ergonomic equipment\n- Cleaning supplies\n\nBudget: $500 per department\nPortal: procurement.company.com\n\nThank you,\nProcurement Team' }
      ] },
      { id: 122, from: 'Employee Wellness', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop', email: 'wellness@company.com', subject: 'January Wellness Challenge - Join Us!', preview: 'Kick off the new year with our January wellness challenge and win prizes...', time: '1w', category: 'client', folder: 'inbox', thread: [
        { from: 'Employee Wellness', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop', time: '1w ago', body: 'Hello Healthy Team!\n\nJoin our January Wellness Challenge: "Step Into Success"\n\nGoal: 10,000 steps daily for 30 days\n\nPrizes:\n1st place: $500 wellness voucher\n2nd place: $300 voucher  \n3rd place: $200 voucher\n\nSign up at: wellness.company.com/challenge\n\nLet\'s get moving!\nWellness Committee' }
      ] },
      { id: 123, from: 'Communications Team', avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop', email: 'comms@company.com', subject: 'Company Newsletter - January Edition', preview: 'Read the latest company news, achievements, and announcements...', time: '1w', category: 'report', folder: 'inbox', thread: [
        { from: 'Communications Team', avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop', time: '1w ago', body: 'Dear Colleagues,\n\nOur January newsletter is now available!\n\nHighlights:\n- Record-breaking Q4 results\n- New hires & promotions  \n- Employee spotlights\n- Upcoming events\n- Office updates\n\nRead online: newsletter.company.com/jan-2025\n\nCommunications Team' }
      ] },
      { id: 124, from: 'Executive Team', avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop', email: 'exec@company.com', subject: 'CEO Town Hall - Thursday January 16th', preview: 'Join our quarterly CEO town hall to discuss 2025 strategy and priorities...', time: '1w', category: 'urgent', folder: 'inbox', thread: [
        { from: 'Executive Team', avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop', time: '1w ago', body: 'All Team Members,\n\nPlease join our Q1 CEO Town Hall on Thursday, January 16th at 3:00 PM EST.\n\nAgenda:\n- 2024 Year in Review\n- 2025 Strategic Priorities\n- Q&A Session\n\nLocation: Main Auditorium (also via Zoom)\nZoom link: company.zoom.us/ceo-townhall\n\nYour participation is highly encouraged!\n\nExecutive Team' }
      ] },
      
      // DRAFTS - 5 emails
      { id: 111, from: 'Draft', avatar: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=100&h=100&fit=crop', email: 'me@company.com', subject: 'RE: Q1 Budget Proposal', preview: 'Thank you for the detailed budget breakdown. I have reviewed the numbers and have a few questions...', time: '5h', category: 'report', folder: 'drafts', thread: [{ from: 'You', time: '5h ago', body: 'Thank you for the detailed budget breakdown. I have reviewed the numbers and have a few questions about the marketing spend allocation...' }] },
      { id: 112, from: 'Draft', avatar: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=100&h=100&fit=crop', email: 'me@company.com', subject: 'Team Performance Review Notes', preview: 'Draft notes for upcoming performance reviews...', time: '1d', category: 'report', folder: 'drafts', thread: [{ from: 'You', time: '1d ago', body: 'Performance review notes for Q4...' }] },
      { id: 113, from: 'Draft', avatar: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=100&h=100&fit=crop', email: 'me@company.com', subject: 'Client Proposal - Downtown Project', preview: 'Drafting proposal for the downtown Manhattan project...', time: '2d', category: 'client', folder: 'drafts', thread: [{ from: 'You', time: '2d ago', body: 'Project proposal draft...' }] },
      { id: 114, from: 'Draft', avatar: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=100&h=100&fit=crop', email: 'me@company.com', subject: 'Meeting Agenda - Board Presentation', preview: 'Preparing agenda for next week\'s board meeting...', time: '3d', category: 'urgent', folder: 'drafts', thread: [{ from: 'You', time: '3d ago', body: 'Board meeting agenda draft...' }] },
      { id: 115, from: 'Draft', avatar: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=100&h=100&fit=crop', email: 'me@company.com', subject: 'Employee Recognition Email', preview: 'Draft email recognizing outstanding team performance...', time: '4d', category: 'follow-up', folder: 'drafts', thread: [{ from: 'You', time: '4d ago', body: 'Team recognition message...' }] },
      
      // ARCHIVE - 8 emails
      { id: 211, from: 'Sales Team', avatar: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=100&h=100&fit=crop', email: 'sales@company.com', subject: 'Monthly Sales Report - December', preview: 'December sales exceeded targets by 18% with strong holiday performance...', time: '3w', category: 'report', folder: 'archive', thread: [{ from: 'Sales Team', avatar: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=100&h=100&fit=crop', time: '3w ago', body: 'December was our best month yet! Sales exceeded targets by 18% with record-breaking deals closed in the final week.' }] },
      { id: 212, from: 'Marketing Team', avatar: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=100&h=100&fit=crop', email: 'marketing@company.com', subject: 'Q4 Campaign Results - Outstanding Performance', preview: 'Our Q4 marketing campaign exceeded all KPIs with 45% increase in qualified leads...', time: '1mo', category: 'report', folder: 'archive', thread: [{ from: 'Marketing Team', avatar: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=100&h=100&fit=crop', time: '1mo ago', body: 'Q4 campaign results are in! We exceeded expectations with 45% increase in qualified leads and 32% conversion rate improvement.' }] },
      { id: 213, from: 'Operations Team', avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop', email: 'ops@company.com', subject: 'Year End Operations Review', preview: 'Comprehensive review of 2024 operational performance and achievements...', time: '1mo', category: 'report', folder: 'archive', thread: [{ from: 'Operations Team', avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop', time: '1mo ago', body: '2024 operations review complete. Key achievement: 99.7% uptime across all systems!' }] },
      { id: 214, from: 'Customer Success', avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop', email: 'success@company.com', subject: 'Client Satisfaction Survey Results', preview: 'Our annual client satisfaction score reached an all-time high of 94%...', time: '1mo', category: 'report', folder: 'archive', thread: [{ from: 'Customer Success', avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop', time: '1mo ago', body: 'Fantastic news! Our client satisfaction score hit 94%, the highest in company history!' }] },
      { id: 215, from: 'Product Team', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop', email: 'product@company.com', subject: 'Q4 Product Roadmap Complete', preview: 'All Q4 product milestones achieved ahead of schedule...', time: '2mo', category: 'report', folder: 'archive', thread: [{ from: 'Product Team', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop', time: '2mo ago', body: 'Proud to announce we completed 100% of Q4 roadmap items, with 3 features delivered ahead of schedule!' }] },
      { id: 216, from: 'Engineering Team', avatar: 'https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?w=100&h=100&fit=crop', email: 'engineering@company.com', subject: 'Platform Performance Optimization Complete', preview: 'Major performance improvements deployed resulting in 40% faster load times...', time: '2mo', category: 'report', folder: 'archive', thread: [{ from: 'Engineering Team', avatar: 'https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?w=100&h=100&fit=crop', time: '2mo ago', body: 'Performance optimization project complete! Users will experience 40% faster page loads and improved responsiveness.' }] },
      { id: 217, from: 'Talent Acquisition', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop', email: 'recruiting@company.com', subject: 'Q4 Hiring Goals Exceeded', preview: 'Successfully hired 25 new team members in Q4, exceeding our target by 15%...', time: '3mo', category: 'report', folder: 'archive', thread: [{ from: 'Talent Acquisition', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop', time: '3mo ago', body: 'Q4 hiring exceeded goals! Welcomed 25 amazing new team members across engineering, sales, and operations.' }] },
      { id: 218, from: 'Learning & Development', avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop', email: 'learning@company.com', subject: 'Annual Training Completion Report', preview: '98% of employees completed mandatory training programs in 2024...', time: '3mo', category: 'report', folder: 'archive', thread: [{ from: 'Learning & Development', avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop', time: '3mo ago', body: 'Outstanding! 98% training completion rate with over 5,000 hours of professional development completed.' }] },
      
      // TRASH - 4 emails
      { id: 311, from: 'Old Meeting', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', email: 'calendar@company.com', subject: 'Cancelled: Team Offsite December', preview: 'This meeting has been cancelled due to schedule conflicts...', time: '2mo', category: 'client', folder: 'trash', thread: [{ from: 'Old Meeting', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', time: '2mo ago', body: 'The December team offsite has been cancelled. We\'ll reschedule for Q1 2025.' }] },
      { id: 312, from: 'Expired Promotion', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', email: 'promos@vendor.com', subject: 'Year-End Sale - Now Expired', preview: 'This promotional offer has expired...', time: '2mo', category: 'report', folder: 'trash', thread: [{ from: 'Expired Promotion', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', time: '2mo ago', body: 'This year-end promotion has expired.' }] },
      { id: 313, from: 'Old Webinar', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', email: 'webinars@training.com', subject: 'Past Webinar Invitation', preview: 'This webinar took place on November 15th...', time: '3mo', category: 'report', folder: 'trash', thread: [{ from: 'Old Webinar', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', time: '3mo ago', body: 'This webinar has concluded.' }] },
      { id: 314, from: 'Outdated Newsletter', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', email: 'news@company.com', subject: 'October Newsletter', preview: 'Old company newsletter from October...', time: '3mo', category: 'report', folder: 'trash', thread: [{ from: 'Outdated Newsletter', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', time: '3mo ago', body: 'October newsletter archive.' }] },
      
      // SPAM - 6 emails
      { id: 411, from: 'Phishing Attempt', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', email: 'admin@fake-company.com', subject: 'URGENT: Verify Your Account Now!', preview: 'Your account will be suspended unless you verify within 24 hours...', time: '1w', category: 'urgent', folder: 'spam', thread: [{ from: 'Phishing Attempt', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', time: '1w ago', body: 'Suspicious email - verify your account immediately!' }] },
      { id: 412, from: 'Fake IT Support', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', email: 'it-support@scam.com', subject: 'Password Reset Required', preview: 'Your password has expired and must be reset...', time: '2w', category: 'urgent', folder: 'spam', thread: [{ from: 'Fake IT Support', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', time: '2w ago', body: 'Phishing attempt - reset your password!' }] },
      { id: 413, from: 'Investment Scam', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', email: 'invest@fraud.com', subject: 'Guaranteed 500% Returns!', preview: 'Limited time investment opportunity with guaranteed returns...', time: '3w', category: 'urgent', folder: 'spam', thread: [{ from: 'Investment Scam', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', time: '3w ago', body: 'Too good to be true investment scam!' }] },
      { id: 414, from: 'Lottery Scam', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', email: 'winner@lottery-scam.com', subject: 'YOU WON $5 MILLION!', preview: 'Congratulations! You are the winner of our international lottery...', time: '1mo', category: 'urgent', folder: 'spam', thread: [{ from: 'Lottery Scam', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', time: '1mo ago', body: 'Lottery scam email!' }] },
      { id: 415, from: 'Fake Package', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', email: 'delivery@fake-shipping.com', subject: 'Package Delivery Failed', preview: 'Your package could not be delivered. Click here to reschedule...', time: '1mo', category: 'follow-up', folder: 'spam', thread: [{ from: 'Fake Package', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', time: '1mo ago', body: 'Fake delivery notification!' }] },
      { id: 416, from: 'Tax Scam', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', email: 'irs@fake-gov.com', subject: 'IRS Tax Refund Pending', preview: 'You have a pending tax refund. Claim it now...', time: '2mo', category: 'urgent', folder: 'spam', thread: [{ from: 'Tax Scam', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', time: '2mo ago', body: 'IRS impersonation scam!' }] },
    ],
    
    icloud: [
      // INBOX - 15 emails with expanded threads
      { id: 21, from: 'Alex Rivera', avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop', email: 'alex@icloud.com', subject: 'Market Analysis Update - Manhattan Real Estate', preview: 'The latest market trends show increased demand in downtown Manhattan...', time: '3h', category: 'urgent', folder: 'inbox', thread: [
        { from: 'Alex Rivera', avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop', time: '3h ago', body: 'Hi,\n\nMy latest market analysis for Manhattan shows strong demand growth in Q1 2025.\n\nKey findings:\n- Luxury apartments up 15% YoY\n- Commercial spaces seeing 8% price increase\n- Foreign investment returning post-pandemic\n\nFull report attached. Let\'s discuss this week.\n\nAlex' },
        { from: 'You', time: '2h ago', body: 'Excellent analysis Alex! The numbers confirm our investment thesis. Let\'s schedule a call Thursday afternoon to discuss our acquisition strategy.' },
        { from: 'Alex Rivera', avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop', time: '1h ago', body: 'Perfect! I\'m free after 2 PM on Thursday. I\'ll bring additional comps for the properties we\'re targeting.' }
      ] },
      { id: 22, from: 'Apple Store', avatar: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=100&h=100&fit=crop', email: 'no-reply@apple.com', subject: 'Your Order Has Shipped - MacBook Pro M4', preview: 'Good news! Your MacBook Pro order is on its way and will arrive tomorrow...', time: '1d', category: 'follow-up', folder: 'inbox', thread: [
        { from: 'Apple Store', avatar: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=100&h=100&fit=crop', time: '1d ago', body: 'Your order has shipped!\n\nMacBook Pro 16" M4 Pro\nSpace Black, 1TB\n\nTracking: 1Z999AA10123456789\nEstimated delivery: Tomorrow by 7 PM\n\nTrack your shipment: apple.co/track\n\nApple Store Team' }
      ] },
      { id: 23, from: 'Photos', avatar: 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=100&h=100&fit=crop', email: 'noreply@icloud.com', subject: 'Your Memories from January 2024', preview: 'Relive your favorite moments from one year ago with this special memory collection...', time: '2d', category: 'client', folder: 'inbox', thread: [{ from: 'Photos', avatar: 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=100&h=100&fit=crop', time: '2d ago', body: 'Remember January 2024? We found 127 photos and 18 videos from this time last year. Tap to relive these moments!\n\niCloud Photos' }] },
      { id: 24, from: 'Lisa Anderson', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop', email: 'lisa.a@icloud.com', subject: 'Coffee Next Week? Catch Up!', preview: 'It\'s been way too long! Want to grab coffee next Tuesday and catch up?', time: '2d', category: 'follow-up', folder: 'inbox', thread: [
        { from: 'Lisa Anderson', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop', time: '2d ago', body: 'Hey!\n\nIt\'s been way too long since we last caught up! Want to grab coffee next Tuesday? There\'s this great new place in SoHo I\'ve been wanting to try.\n\nLet me know!\nLisa' },
        { from: 'You', time: '1d ago', body: 'I\'d love to! Tuesday works perfectly. How about 10 AM at that SoHo spot? Send me the address!' },
        { from: 'Lisa Anderson', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop', time: '1d ago', body: 'Perfect! It\'s called Birch Coffee on Prince St. See you Tuesday at 10!' }
      ] },
      { id: 25, from: 'Apple Music', avatar: 'https://images.unsplash.com/photo-1614680376573-df3480f0c6ff?w=100&h=100&fit=crop', email: 'noreply@applemusic.com', subject: 'Your Replay 2024 is Ready!', preview: 'Discover your most played songs, artists, and albums of 2024...', time: '3d', category: 'client', folder: 'inbox', thread: [{ from: 'Apple Music', avatar: 'https://images.unsplash.com/photo-1614680376573-df3480f0c6ff?w=100&h=100&fit=crop', time: '3d ago', body: 'Your Apple Music Replay 2024\n\nYou listened to:\n- 1,247 hours of music\n- 892 different artists\n- 4,563 songs\n\nTop artist: The Weeknd\nTop song: "Starboy"\n\nView your full replay: music.apple.com/replay' }] },
      { id: 26, from: 'FaceTime', avatar: 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=100&h=100&fit=crop', email: 'noreply@icloud.com', subject: 'Missed FaceTime Call from Mom', preview: 'You missed a FaceTime audio call at 8:42 AM...', time: '4d', category: 'urgent', folder: 'inbox', thread: [{ from: 'FaceTime', avatar: 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=100&h=100&fit=crop', time: '4d ago', body: 'You missed a FaceTime audio call from Mom at 8:42 AM on January 6, 2025.' }] },
      { id: 27, from: 'Find My', avatar: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=100&h=100&fit=crop', email: 'noreply@icloud.com', subject: 'AirPods Pro Found Nearby', preview: 'Your AirPods Pro were detected at Home - Living Room...', time: '5d', category: 'report', folder: 'inbox', thread: [{ from: 'Find My', avatar: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=100&h=100&fit=crop', time: '5d ago', body: 'Your AirPods Pro were detected at:\nHome - Living Room\n10:23 AM\n\nPlay sound or get directions in the Find My app.' }] },
      { id: 28, from: 'Screen Time', avatar: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=100&h=100&fit=crop', email: 'noreply@icloud.com', subject: 'Weekly Screen Time Report', preview: 'Your screen time was down 12% this week compared to last week...', time: '1w', category: 'report', folder: 'inbox', thread: [{ from: 'Screen Time', avatar: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=100&h=100&fit=crop', time: '1w ago', body: 'Weekly Screen Time Summary:\n\nTotal: 4h 32m daily average (↓12%)\n\nMost used:\n1. Safari - 1h 45m\n2. Messages - 58m\n3. Mail - 42m\n\nPickups: 89 per day' }] },
      { id: 29, from: 'Apple Fitness+', avatar: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=100&h=100&fit=crop', email: 'noreply@apple.com', subject: 'You\'re on a 7-Day Streak! Keep Going!', preview: 'Amazing work! You\'ve closed your Activity Rings for 7 days in a row...', time: '1w', category: 'client', folder: 'inbox', thread: [{ from: 'Apple Fitness+', avatar: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=100&h=100&fit=crop', time: '1w ago', body: 'Congratulations!\n\nYou\'ve closed your rings for 7 days straight! You\'re crushing your fitness goals.\n\nThis week:\n- 3,247 calories burned\n- 142 minutes of exercise\n- 54 hours standing\n\nKeep up the momentum!' }] },
      { id: 210, from: 'iWork', avatar: 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=100&h=100&fit=crop', email: 'noreply@icloud.com', subject: 'Pages Document Shared With You', preview: 'Sarah Mitchell shared "Q1 Project Proposal" with you...', time: '1w', category: 'follow-up', folder: 'inbox', thread: [{ from: 'iWork', avatar: 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=100&h=100&fit=crop', time: '1w ago', body: 'Sarah Mitchell shared a Pages document with you:\n\n"Q1 Project Proposal"\n\nYou can view and edit this document at iCloud.com/pages' }] },
      { id: 220, from: 'Apple News', avatar: 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=100&h=100&fit=crop', email: 'noreply@apple.com', subject: 'Your Daily Briefing - Top Stories', preview: 'Here are today\'s top stories personalized for you...', time: '1w', category: 'report', folder: 'inbox', thread: [{ from: 'Apple News', avatar: 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=100&h=100&fit=crop', time: '1w ago', body: 'Good morning! Here are today\'s top stories:\n\n1. Tech sector rallies on strong earnings\n2. NYC real estate market shows growth\n3. New infrastructure bill passes Senate\n\nRead more in Apple News' }] },
      { id: 230, from: 'Reminders', avatar: 'https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=100&h=100&fit=crop', email: 'noreply@icloud.com', subject: 'Reminder: Dentist Appointment Tomorrow', preview: 'You have a reminder for tomorrow at 2:00 PM...', time: '1w', category: 'urgent', folder: 'inbox', thread: [{ from: 'Reminders', avatar: 'https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=100&h=100&fit=crop', time: '1w ago', body: 'Reminder for tomorrow:\n\nDentist Appointment\n2:00 PM\nDr. Smith\'s Office\n450 Park Avenue' }] },
      { id: 240, from: 'Apple Wallet', avatar: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=100&h=100&fit=crop', email: 'noreply@apple.com', subject: 'Boarding Pass Added - Flight to Miami', preview: 'Your boarding pass for AA1234 has been added to Wallet...', time: '1w', category: 'travel', folder: 'inbox', thread: [{ from: 'Apple Wallet', avatar: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=100&h=100&fit=crop', time: '1w ago', body: 'Boarding pass added:\n\nAmerican Airlines Flight 1234\nJFK → MIA\nJanuary 20, 2025\nDeparture: 9:45 AM\nGate: B12\nSeat: 3A' }] },
      { id: 241, from: 'American Airlines', avatar: 'https://images.unsplash.com/photo-1436491865332-7a61a109db05?w=100&h=100&fit=crop', email: 'reservations@aa.com', subject: 'Flight Confirmation - DFW to LAX Feb 10', preview: 'Your flight has been booked! Confirmation code: ABCDEF...', time: '2d', category: 'travel', folder: 'inbox', thread: [{ from: 'American Airlines', avatar: 'https://images.unsplash.com/photo-1436491865332-7a61a109db05?w=100&h=100&fit=crop', time: '2d ago', body: 'Booking Confirmation\n\nAmerican Airlines Flight AA 2847\nDallas/Fort Worth (DFW) → Los Angeles (LAX)\nFebruary 10, 2026\nDeparture: 7:15 AM CST\nArrival: 9:30 AM PST\n\nConfirmation Code: ABCDEF\nPassenger: Darren Woodson\nSeat: 4C (First Class)\n\nManage your reservation at aa.com' }] },
      { id: 242, from: 'Marriott Bonvoy', avatar: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=100&h=100&fit=crop', email: 'reservations@marriott.com', subject: 'Hotel Reservation Confirmed - JW Marriott LA', preview: 'Your stay at JW Marriott Los Angeles is confirmed for Feb 10-12...', time: '2d', category: 'travel', folder: 'inbox', thread: [{ from: 'Marriott Bonvoy', avatar: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=100&h=100&fit=crop', time: '2d ago', body: 'Reservation Confirmed\n\nJW Marriott Los Angeles L.A. LIVE\n900 W Olympic Blvd, Los Angeles, CA\n\nCheck-in: February 10, 2026 (3:00 PM)\nCheck-out: February 12, 2026 (11:00 AM)\nRoom: Executive Suite\n\nConfirmation #: 89234567\nBonvoy Points Earned: 4,250\n\nView or modify your reservation at marriott.com' }] },
      { id: 250, from: 'Books', avatar: 'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=100&h=100&fit=crop', email: 'noreply@apple.com', subject: 'Your Book is Ready! "The Innovation Stack"', preview: 'The book you pre-ordered is now available to read...', time: '2w', category: 'client', folder: 'inbox', thread: [{ from: 'Books', avatar: 'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=100&h=100&fit=crop', time: '2w ago', body: 'Your pre-ordered book is ready!\n\n"The Innovation Stack"\nby Jim McKelvey\n\nStart reading now in Apple Books.' }] },
      { id: 260, from: 'Podcasts', avatar: 'https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=100&h=100&fit=crop', email: 'noreply@apple.com', subject: 'New Episode: How I Built This', preview: 'A new episode of your favorite podcast is available...', time: '2w', category: 'report', folder: 'inbox', thread: [{ from: 'Podcasts', avatar: 'https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=100&h=100&fit=crop', time: '2w ago', body: 'New episode available:\n\n"How I Built This" with Guy Raz\nEpisode: Airbnb - Joe Gebbia and Brian Chesky\n\nListen now in Apple Podcasts' }] },
      
      // DRAFTS - 5 emails
      { id: 121, from: 'Draft', avatar: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=100&h=100&fit=crop', email: 'me@icloud.com', subject: 'RE: Photo Sharing Album - Family Vacation', preview: 'Thanks for sharing the photos from our amazing trip to Hawaii...', time: '1d', category: 'client', folder: 'drafts', thread: [{ from: 'You', time: '1d ago', body: 'Thanks for sharing the Hawaii photos! The sunset shots are incredible...' }] },
      { id: 122, from: 'Draft', avatar: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=100&h=100&fit=crop', email: 'me@icloud.com', subject: 'Notes App Collaboration Request', preview: 'Would love to collaborate on the project planning notes...', time: '2d', category: 'follow-up', folder: 'drafts', thread: [{ from: 'You', time: '2d ago', body: 'Draft note about project collaboration...' }] },
      { id: 123, from: 'Draft', avatar: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=100&h=100&fit=crop', email: 'me@icloud.com', subject: 'Calendar Invite Response', preview: 'Thanks for the meeting invitation, I\'ll be there...', time: '3d', category: 'client', folder: 'drafts', thread: [{ from: 'You', time: '3d ago', body: 'Calendar response draft...' }] },
      { id: 124, from: 'Draft', avatar: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=100&h=100&fit=crop', email: 'me@icloud.com', subject: 'Device Setup Question', preview: 'Quick question about setting up my new iPad...', time: '4d', category: 'report', folder: 'drafts', thread: [{ from: 'You', time: '4d ago', body: 'iPad setup question...' }] },
      { id: 125, from: 'Draft', avatar: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=100&h=100&fit=crop', email: 'me@icloud.com', subject: 'Keynote Presentation Feedback', preview: 'Great presentation! Just a few minor suggestions...', time: '5d', category: 'follow-up', folder: 'drafts', thread: [{ from: 'You', time: '5d ago', body: 'Presentation feedback draft...' }] },
      
      // ARCHIVE - 8 emails
      { id: 221, from: 'App Store', avatar: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=100&h=100&fit=crop', email: 'noreply@appstore.com', subject: 'Your Subscription Receipt - iCloud+ 2TB', preview: 'Thank you for your App Store purchase of iCloud+ storage...', time: '1mo', category: 'report', folder: 'archive', thread: [{ from: 'App Store', avatar: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=100&h=100&fit=crop', time: '1mo ago', body: 'Receipt for iCloud+ 2TB subscription - $9.99/month. Next billing: February 1, 2025.' }] },
      { id: 222, from: 'iCloud Storage', avatar: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=100&h=100&fit=crop', email: 'noreply@icloud.com', subject: 'Storage Upgraded Successfully', preview: 'Your iCloud storage has been upgraded to 2TB...', time: '1mo', category: 'report', folder: 'archive', thread: [{ from: 'iCloud Storage', avatar: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=100&h=100&fit=crop', time: '1mo ago', body: 'iCloud storage successfully upgraded to 2TB. All your photos, documents, and backups are now syncing.' }] },
      { id: 223, from: 'Apple Support', avatar: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=100&h=100&fit=crop', email: 'support@apple.com', subject: 'Your Support Case is Resolved', preview: 'The issue you reported has been resolved by our team...', time: '2mo', category: 'follow-up', folder: 'archive', thread: [{ from: 'Apple Support', avatar: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=100&h=100&fit=crop', time: '2mo ago', body: 'Your support case #12345678 regarding iCloud sync issues has been resolved. Thank you for your patience!' }] },
      { id: 224, from: 'Apple Card', avatar: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=100&h=100&fit=crop', email: 'noreply@apple.com', subject: 'November Statement Available', preview: 'Your Apple Card statement for November is ready...', time: '2mo', category: 'report', folder: 'archive', thread: [{ from: 'Apple Card', avatar: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=100&h=100&fit=crop', time: '2mo ago', body: 'November Apple Card Statement\nTotal: $2,347.89\nDue date: December 15\n2% Daily Cash earned: $46.96' }] },
      { id: 225, from: 'Apple TV+', avatar: 'https://images.unsplash.com/photo-1522869635100-9f4c5e86aa37?w=100&h=100&fit=crop', email: 'noreply@apple.com', subject: 'New Season Alert: Ted Lasso', preview: 'The show you love has a new season available...', time: '3mo', category: 'client', folder: 'archive', thread: [{ from: 'Apple TV+', avatar: 'https://images.unsplash.com/photo-1522869635100-9f4c5e86aa37?w=100&h=100&fit=crop', time: '3mo ago', body: 'New season of Ted Lasso is now streaming on Apple TV+!' }] },
      { id: 226, from: 'Photos', avatar: 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=100&h=100&fit=crop', email: 'noreply@icloud.com', subject: 'Your Year in Review 2024', preview: 'Relive your year with this special photo collection...', time: '3mo', category: 'report', folder: 'archive', thread: [{ from: 'Photos', avatar: 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=100&h=100&fit=crop', time: '3mo ago', body: '2024 Year in Review: 4,892 photos, 326 videos, visits to 12 cities!' }] },
      { id: 227, from: 'Health App', avatar: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=100&h=100&fit=crop', email: 'noreply@icloud.com', subject: 'Annual Health Summary 2024', preview: 'Your health and fitness achievements for 2024...', time: '3mo', category: 'report', folder: 'archive', thread: [{ from: 'Health App', avatar: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=100&h=100&fit=crop', time: '3mo ago', body: '2024 Health Summary:\n- 2.1M steps\n- 892 miles walked\n- 234 workouts\n- 8,542 calories burned' }] },
      { id: 228, from: 'Apple Arcade', avatar: 'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=100&h=100&fit=crop', email: 'noreply@apple.com', subject: 'New Games Added This Month', preview: '15 new games have been added to Apple Arcade...', time: '3mo', category: 'client', folder: 'archive', thread: [{ from: 'Apple Arcade', avatar: 'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=100&h=100&fit=crop', time: '3mo ago', body: 'Check out 15 new games on Apple Arcade including exclusive titles from top developers!' }] },
      
      // TRASH - 4 emails
      { id: 321, from: 'Old Newsletter', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', email: 'newsletter@random.com', subject: 'Weekly Update from October', preview: 'Here\'s what happened this week in October...', time: '3mo', category: 'report', folder: 'trash', thread: [{ from: 'Old Newsletter', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', time: '3mo ago', body: 'Old October newsletter.' }] },
      { id: 322, from: 'Expired Offer', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', email: 'offers@expired.com', subject: 'Black Friday Sale - Ended', preview: 'This Black Friday promotion has ended...', time: '3mo', category: 'report', folder: 'trash', thread: [{ from: 'Expired Offer', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', time: '3mo ago', body: 'Black Friday sale ended.' }] },
      { id: 323, from: 'Old Event', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', email: 'events@past.com', subject: 'Past Event Reminder', preview: 'This event took place last month...', time: '4mo', category: 'client', folder: 'trash', thread: [{ from: 'Old Event', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', time: '4mo ago', body: 'Event has concluded.' }] },
      { id: 324, from: 'Canceled Service', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', email: 'service@canceled.com', subject: 'Subscription Canceled', preview: 'Your subscription has been cancelled...', time: '4mo', category: 'report', folder: 'trash', thread: [{ from: 'Canceled Service', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', time: '4mo ago', body: 'Subscription canceled.' }] },
      
      // SPAM - 6 emails
      { id: 421, from: 'Scam Alert', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', email: 'fake@scam.com', subject: 'Claim Your Free iPhone 16 Pro!', preview: 'You\'ve been selected to receive a free iPhone 16 Pro Max...', time: '1w', category: 'urgent', folder: 'spam', thread: [{ from: 'Scam Alert', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', time: '1w ago', body: 'Free iPhone scam!' }] },
      { id: 422, from: 'Fake Apple', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', email: 'security@fake-apple.com', subject: 'Apple ID Suspended - Verify Now', preview: 'Your Apple ID has been suspended due to suspicious activity...', time: '2w', category: 'urgent', folder: 'spam', thread: [{ from: 'Fake Apple', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', time: '2w ago', body: 'Phishing attempt!' }] },
      { id: 423, from: 'Prize Scam', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', email: 'winner@prizescam.com', subject: 'You Won an iPad!', preview: 'Congratulations! You are the winner of our iPad giveaway...', time: '3w', category: 'urgent', folder: 'spam', thread: [{ from: 'Prize Scam', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', time: '3w ago', body: 'iPad prize scam!' }] },
      { id: 424, from: 'Fake iCloud', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', email: 'storage@fake-icloud.com', subject: 'iCloud Storage Full - Upgrade Required', preview: 'Your iCloud storage is full. Click here to upgrade...', time: '1mo', category: 'urgent', folder: 'spam', thread: [{ from: 'Fake iCloud', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', time: '1mo ago', body: 'Fake storage warning!' }] },
      { id: 425, from: 'Survey Spam', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', email: 'survey@spam.com', subject: 'Take Survey, Win $1000 Gift Card', preview: 'Complete our 2-minute survey for a chance to win...', time: '1mo', category: 'client', folder: 'spam', thread: [{ from: 'Survey Spam', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', time: '1mo ago', body: 'Survey scam!' }] },
      { id: 426, from: 'Virus Warning', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', email: 'alert@fakesecurity.com', subject: 'URGENT: Virus Detected on Your Device!', preview: 'Your iPhone has been infected with a virus. Click to remove...', time: '2mo', category: 'urgent', folder: 'spam', thread: [{ from: 'Virus Warning', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', time: '2mo ago', body: 'Fake virus warning!' }] },
    ],
    
    business: [
      // INBOX - 15 emails with expanded threads
      { id: 31, from: 'Emily Chen', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop', email: 'emily@premierrealty.com', subject: 'New Property Acquisition Opportunity - Brooklyn Heights', preview: 'I\'ve identified a high-potential acquisition target in Brooklyn Heights with strong upside...', time: '3h', category: 'urgent', folder: 'inbox', thread: [
        { from: 'Emily Chen', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop', time: '3h ago', body: 'Hi Team,\n\nI\'ve identified an exceptional acquisition opportunity in Brooklyn Heights:\n\n- Property: 245 Columbia Heights\n- Type: Mixed-use building (retail + residential)\n- Price: $18.5M\n- Cap rate: 6.2%\n- Strong tenant base with long-term leases\n\nThis could be a cornerstone investment for our portfolio. Full analysis attached.\n\nLet\'s discuss this week.\n\nEmily' },
        { from: 'You', time: '2h ago', body: 'Excellent find Emily! The location is prime and the cap rate is attractive. I\'ll review the full analysis and let\'s schedule a site visit Friday afternoon.' },
        { from: 'Emily Chen', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop', time: '1h ago', body: 'Perfect! I\'ll coordinate with the listing agent and set up the tour for 2 PM Friday. I\'ll send calendar invites.' }
      ] },
      { id: 32, from: 'Legal Department', avatar: 'https://images.unsplash.com/photo-1556157382-97eda2d62296?w=100&h=100&fit=crop', email: 'legal@premierrealty.com', subject: 'Contract Review Completed - Madison Avenue Lease', preview: 'The legal team has completed the comprehensive review of the Madison Avenue lease agreement...', time: '5h', category: 'urgent', folder: 'inbox', thread: [
        { from: 'Legal Department', avatar: 'https://images.unsplash.com/photo-1556157382-97eda2d62296?w=100&h=100&fit=crop', time: '5h ago', body: 'Dear Managing Partner,\n\nWe\'ve completed our detailed review of the Madison Avenue 20-year lease agreement.\n\nKey findings:\n- Overall terms are favorable\n- Recommend revisions to Section 7 (Maintenance)\n- Add force majeure clause\n- Negotiate lease escalation from 3.5% to 3% annually\n\nEstimated savings: $420K over lease term\n\nFull report and redlines attached.\n\nBest regards,\nLegal Team' },
        { from: 'You', time: '3h ago', body: 'Thank you for the thorough review. The savings identified are significant. Let\'s incorporate these changes and I\'ll reach out to the landlord\'s counsel next week to negotiate.' },
        { from: 'Legal Department', avatar: 'https://images.unsplash.com/photo-1556157382-97eda2d62296?w=100&h=100&fit=crop', time: '2h ago', body: 'Sounds good. We\'ll prepare the revised documents and talking points for your negotiation. Happy to join the call if helpful.' }
      ] },
      { id: 33, from: 'David Park', avatar: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=100&h=100&fit=crop', email: 'david@premierrealty.com', subject: 'Client Presentation Success - Next Steps', preview: 'Following up on yesterday\'s outstanding presentation to the Riverside Group...', time: '1d', category: 'follow-up', folder: 'inbox', thread: [
        { from: 'David Park', avatar: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=100&h=100&fit=crop', time: '1d ago', body: 'Hi,\n\nGreat news from yesterday\'s presentation! The Riverside Group was extremely impressed and wants to move forward.\n\nThey\'re ready to:\n- Sign LOI next week\n- Proceed with due diligence  \n- Target closing in 60 days\n\nDeal size: $24M\n\nShould I send over the LOI draft?\n\nDavid' },
        { from: 'You', time: '18h ago', body: 'Fantastic work David! Yes, please send the LOI immediately. Let\'s schedule a closing timeline meeting with our transaction team for Tuesday morning.' }
      ] },
      { id: 34, from: 'Accounting', avatar: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=100&h=100&fit=crop', email: 'accounting@premierrealty.com', subject: 'Invoice #2024-891 Payment Received - $125,000', preview: 'Payment of $125,000 has been received and processed for the Chelsea property transaction...', time: '1d', category: 'report', folder: 'inbox', thread: [
        { from: 'Accounting', avatar: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=100&h=100&fit=crop', time: '1d ago', body: 'Payment confirmation:\n\nInvoice: #2024-891\nClient: Chelsea Property LLC\nAmount: $125,000\nDate received: January 8, 2025\nStatus: Cleared\n\nFunds have been deposited to operating account.\n\nAccounting Department' }
      ] },
      { id: 35, from: 'Property Inspector', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop', email: 'inspector@propertyservices.com', subject: 'Inspection Report - Brooklyn Property - URGENT ISSUES', preview: 'Attached is the complete inspection report for the Brooklyn property with several urgent items requiring attention...', time: '2d', category: 'urgent', folder: 'inbox', thread: [
        { from: 'Property Inspector', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop', time: '2d ago', body: 'Inspection completed for 432 Atlantic Avenue, Brooklyn.\n\nURGENT ISSUES:\n- HVAC system needs replacement (est. $85K)\n- Roof repairs required (est. $35K)\n- Electrical panel upgrade needed (est. $18K)\n\nTotal estimated repairs: $138K\n\nRecommendation: Negotiate price reduction or request seller credits.\n\nFull report attached.\n\nJohn Mitchell\nCertified Property Inspector' },
        { from: 'You', time: '1d ago', body: 'Thank you for the detailed inspection. These are significant findings. I\'ll renegotiate with the seller for a $150K price reduction to cover repairs plus contingency.' }
      ] },
      { id: 36, from: 'Sarah Mitchell', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop', email: 'sarah@premierrealty.com', subject: 'Q1 Investment Strategy Presentation', preview: 'I\'ve finalized the Q1 strategy presentation for Thursday\'s partners meeting...', time: '2d', category: 'report', folder: 'inbox', thread: [
        { from: 'Sarah Mitchell', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop', time: '2d ago', body: 'The Q1 strategy presentation is ready for Thursday.\n\nKey themes:\n- Geographic expansion into Boston\n- Portfolio diversification\n- 18% target ROI\n- $50M capital deployment\n\nPresentation attached. Any feedback?\n\nSarah' },
        { from: 'You', time: '1d ago', body: 'Excellent work Sarah! The Boston expansion strategy is compelling. Can you add a slide on competitive landscape analysis? Otherwise looks great!' }
      ] },
      { id: 37, from: 'Asset Management', avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop', email: 'assetmgmt@premierrealty.com', subject: 'Portfolio Performance Report - December 2024', preview: 'Attached is the comprehensive monthly performance report for all properties...', time: '3d', category: 'report', folder: 'inbox', thread: [
        { from: 'Asset Management', avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop', time: '3d ago', body: 'December Portfolio Performance:\n\n- Total portfolio value: $287M\n- Monthly NOI: $1.8M\n- Occupancy rate: 96.2%\n- Same-store growth: +4.8%\n\nStrong performance across all asset classes.\n\nFull report attached.\n\nAsset Management Team' }
      ] },
      { id: 38, from: 'Tenant Relations', avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop', email: 'tenants@premierrealty.com', subject: 'Lease Renewal - Fortune 500 Tenant', preview: 'Excellent news! Our Fortune 500 tenant has agreed to renew for 10 years...', time: '4d', category: 'urgent', folder: 'inbox', thread: [
        { from: 'Tenant Relations', avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop', time: '4d ago', body: 'Major win!\n\nOur Fortune 500 tenant at 1 World Trade Center has agreed to:\n- 10-year lease renewal\n- 20% rent increase\n- Expansion to additional 5,000 sq ft\n\nAnnual rent: $2.4M\n\nDocs ready for signature.\n\nTenant Relations' }
      ] },
      { id: 39, from: 'Construction Manager', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop', email: 'construction@premierrealty.com', subject: 'Tribeca Project - Phase 1 Complete', preview: 'Phase 1 of the Tribeca renovation project has been completed on time and under budget...', time: '5d', category: 'follow-up', folder: 'inbox', thread: [
        { from: 'Construction Manager', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop', time: '5d ago', body: 'Phase 1 completion update:\n\nTribeca Renovation - COMPLETE\n- Finished 2 weeks early\n- Under budget by $47K\n- All inspections passed\n\nPhase 2 begins Monday.\n\nPhotos attached.\n\nConstruction Team' }
      ] },
      { id: 310, from: 'Market Research', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop', email: 'research@premierrealty.com', subject: 'NYC Real Estate Market Trends - January 2025', preview: 'Comprehensive market analysis showing strong momentum across all boroughs...', time: '1w', category: 'report', folder: 'inbox', thread: [{ from: 'Market Research', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop', time: '1w ago', body: 'January market trends:\n- Manhattan prices up 12% YoY\n- Brooklyn demand at record high\n- Office vacancies declining\n- Investment activity accelerating\n\nDetailed report attached.' }] },
      { id: 320, from: 'Financing Team', avatar: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=100&h=100&fit=crop', email: 'finance@premierrealty.com', subject: 'Debt Refinancing Approved - $35M', preview: 'Great news! The $35M refinancing package has been approved at favorable terms...', time: '1w', category: 'urgent', folder: 'inbox', thread: [{ from: 'Financing Team', avatar: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=100&h=100&fit=crop', time: '1w ago', body: 'Refinancing approved!\n\nAmount: $35M\nRate: 5.25% (down from 6.5%)\nTerm: 10 years fixed\nAnnual savings: $437K\n\nClosing: February 1st' }] },
      { id: 330, from: 'Insurance Broker', avatar: 'https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?w=100&h=100&fit=crop', email: 'broker@insurance.com', subject: 'Portfolio Insurance Renewal - Action Required', preview: 'Your portfolio insurance policy is up for renewal on February 1st...', time: '1w', category: 'urgent', folder: 'inbox', thread: [{ from: 'Insurance Broker', avatar: 'https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?w=100&h=100&fit=crop', time: '1w ago', body: 'Annual insurance renewal due:\n\nPremium: $487K\nCoverage: $300M property + $50M liability\nExpiration: January 31st\n\nPlease review and approve by January 20th.' }] },
      { id: 340, from: 'Environmental Consultant', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop', email: 'enviro@consultants.com', subject: 'Phase I Environmental Report - Queens Site', preview: 'Environmental assessment completed for the Queens development site...', time: '1w', category: 'report', folder: 'inbox', thread: [{ from: 'Environmental Consultant', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop', time: '1w ago', body: 'Phase I Environmental Assessment:\n\nSite: 89-12 Queens Blvd\nResult: CLEAR - No contamination found\nRecommendation: Proceed with acquisition\n\nReport attached.' }] },
      { id: 350, from: 'Tax Advisor', avatar: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=100&h=100&fit=crop', email: 'tax@advisors.com', subject: '2024 Tax Planning Strategy', preview: 'Year-end tax planning recommendations and 1031 exchange opportunities...', time: '2w', category: 'report', folder: 'inbox', thread: [{ from: 'Tax Advisor', avatar: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=100&h=100&fit=crop', time: '2w ago', body: '2024 tax planning:\n\n- Estimated tax savings: $1.2M\n- 1031 exchange opportunities identified\n- Depreciation optimization\n- Entity restructuring benefits\n\nSchedule call to review?\n\nTax Advisory Team' }] },
      { id: 360, from: 'Security Services', avatar: 'https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?w=100&h=100&fit=crop', email: 'security@services.com', subject: 'Annual Security Audit Complete', preview: 'Comprehensive security audit of all properties completed with recommendations...', time: '2w', category: 'report', folder: 'inbox', thread: [{ from: 'Security Services', avatar: 'https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?w=100&h=100&fit=crop', time: '2w ago', body: 'Annual security audit results:\n\n- 15 properties audited\n- Overall rating: Excellent\n- Minor upgrades recommended at 3 locations\n- Estimated cost: $42K\n\nFull report attached.' }] },
      
      // DRAFTS - 5 emails
      { id: 131, from: 'Draft', avatar: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=100&h=100&fit=crop', email: 'me@premierrealty.com', subject: 'RE: Partnership Proposal - Joint Venture Opportunity', preview: 'Thank you for the comprehensive partnership proposal. We are very interested in exploring this opportunity...', time: '1d', category: 'urgent', folder: 'drafts', thread: [{ from: 'You', time: '1d ago', body: 'Thank you for the partnership proposal. We are very interested in exploring this joint venture opportunity for the downtown development project. Let\'s schedule a meeting next week to discuss terms...' }] },
      { id: 132, from: 'Draft', avatar: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=100&h=100&fit=crop', email: 'me@premierrealty.com', subject: 'Q1 2025 Goals and Strategic Objectives', preview: 'Team, here are our ambitious goals and key priorities for Q1 2025...', time: '2d', category: 'report', folder: 'drafts', thread: [{ from: 'You', time: '2d ago', body: 'Q1 2025 strategic objectives draft:\n1. Close $75M in acquisitions\n2. Achieve 97% occupancy\n3. Launch Boston expansion...' }] },
      { id: 133, from: 'Draft', avatar: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=100&h=100&fit=crop', email: 'me@premierrealty.com', subject: 'Board of Directors - Quarterly Update', preview: 'Preparing quarterly update for board meeting next month...', time: '3d', category: 'report', folder: 'drafts', thread: [{ from: 'You', time: '3d ago', body: 'Board update draft in progress...' }] },
      { id: 134, from: 'Draft', avatar: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=100&h=100&fit=crop', email: 'me@premierrealty.com', subject: 'Investor Relations Update', preview: 'Quarterly investor newsletter draft...', time: '4d', category: 'client', folder: 'drafts', thread: [{ from: 'You', time: '4d ago', body: 'Investor newsletter Q4 2024...' }] },
      { id: 135, from: 'Draft', avatar: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=100&h=100&fit=crop', email: 'me@premierrealty.com', subject: 'Team Expansion Proposal', preview: 'Proposal to expand the acquisitions team...', time: '5d', category: 'report', folder: 'drafts', thread: [{ from: 'You', time: '5d ago', body: 'Team expansion proposal draft...' }] },
      
      // ARCHIVE - 8 emails
      { id: 231, from: 'Board of Directors', avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop', email: 'board@premierrealty.com', subject: 'Board Meeting Minutes - December 2024', preview: 'Attached are the detailed minutes from the December quarterly board meeting...', time: '1mo', category: 'report', folder: 'archive', thread: [{ from: 'Board of Directors', avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop', time: '1mo ago', body: 'December Board Meeting Minutes:\n\nKey decisions:\n- Approved $50M capital raise\n- Boston expansion green-lit\n- Executive bonus structure revised\n\nFull minutes attached.' }] },
      { id: 232, from: 'Vendor Relations', avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop', email: 'vendors@premierrealty.com', subject: 'All Vendor Contracts Renewed for 2025', preview: 'All vendor contracts have been successfully renewed with favorable 2025 pricing...', time: '1mo', category: 'follow-up', folder: 'archive', thread: [{ from: 'Vendor Relations', avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop', time: '1mo ago', body: '2025 vendor contract renewals complete:\n\n- HVAC maintenance: 5% cost reduction\n- Cleaning services: 3-year agreement\n- Security: Enhanced services, flat pricing\n\nTotal annual savings: $127K' }] },
      { id: 233, from: 'Investor Relations', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop', email: 'ir@premierrealty.com', subject: 'Q4 Investor Newsletter Published', preview: 'The Q4 investor newsletter has been distributed to all stakeholders...', time: '1mo', category: 'report', folder: 'archive', thread: [{ from: 'Investor Relations', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop', time: '1mo ago', body: 'Q4 newsletter sent to 247 investors highlighting record performance and 2025 outlook.' }] },
      { id: 234, from: 'Property Manager', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop', email: 'pm@premierrealty.com', subject: 'Year-End Property Inspections Complete', preview: 'All properties have completed annual inspections with excellent results...', time: '2mo', category: 'report', folder: 'archive', thread: [{ from: 'Property Manager', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop', time: '2mo ago', body: 'Annual inspections complete:\n- 23 properties inspected\n- Average condition: 9.2/10\n- Maintenance backlog minimal\n- No major capital needs identified' }] },
      { id: 235, from: 'Marketing Team', avatar: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=100&h=100&fit=crop', email: 'marketing@premierrealty.com', subject: 'Website Redesign Launched Successfully', preview: 'Our new website has launched with modern design and enhanced functionality...', time: '2mo', category: 'follow-up', folder: 'archive', thread: [{ from: 'Marketing Team', avatar: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=100&h=100&fit=crop', time: '2mo ago', body: 'New website launched!\n- Modern responsive design\n- Property showcase portal\n- Investor login area\n- 40% faster page loads\n\nVisit: premierrealty.com' }] },
      { id: 236, from: 'Technology Team', avatar: 'https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?w=100&h=100&fit=crop', email: 'tech@premierrealty.com', subject: 'CRM Migration Complete', preview: 'The CRM system migration has been completed successfully...', time: '3mo', category: 'report', folder: 'archive', thread: [{ from: 'Technology Team', avatar: 'https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?w=100&h=100&fit=crop', time: '3mo ago', body: 'Salesforce CRM migration:\n- All data migrated successfully\n- Zero downtime\n- Enhanced reporting capabilities\n- Team training completed' }] },
      { id: 237, from: 'HR Department', avatar: 'https://images.unsplash.com/photo-1573496799652-408c2ac9fe98?w=100&h=100&fit=crop', email: 'hr@premierrealty.com', subject: 'Annual Performance Reviews Completed', preview: 'All employee performance reviews for 2024 have been completed...', time: '3mo', category: 'report', folder: 'archive', thread: [{ from: 'HR Department', avatar: 'https://images.unsplash.com/photo-1573496799652-408c2ac9fe98?w=100&h=100&fit=crop', time: '3mo ago', body: '2024 performance reviews complete:\n- 100% completion rate\n- Average rating: 4.3/5\n- 15 promotions\n- Compensation adjustments processed' }] },
      { id: 238, from: 'Compliance Officer', avatar: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=100&h=100&fit=crop', email: 'compliance@premierrealty.com', subject: 'Annual Compliance Audit - All Clear', preview: 'The annual compliance audit has been completed with no findings...', time: '3mo', category: 'report', folder: 'archive', thread: [{ from: 'Compliance Officer', avatar: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=100&h=100&fit=crop', time: '3mo ago', body: 'Annual audit results:\n- Zero compliance violations\n- All policies current\n- Training requirements met\n- Excellent marks from auditors' }] },
      
      // TRASH - 4 emails
      { id: 331, from: 'Old Project', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', email: 'oldproject@premierrealty.com', subject: 'Cancelled Development Project - Queens', preview: 'This development project has been cancelled due to zoning issues...', time: '3mo', category: 'client', folder: 'trash', thread: [{ from: 'Old Project', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', time: '3mo ago', body: 'Queens project cancelled due to zoning complications.' }] },
      { id: 332, from: 'Withdrawn Offer', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', email: 'offers@premierrealty.com', subject: 'Offer Withdrawn - Bronx Property', preview: 'Our offer on the Bronx property has been withdrawn...', time: '3mo', category: 'report', folder: 'trash', thread: [{ from: 'Withdrawn Offer', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', time: '3mo ago', body: 'Offer withdrawn after inspection.' }] },
      { id: 333, from: 'Expired Listing', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', email: 'listings@premierrealty.com', subject: 'Listing Expired', preview: 'Property listing has expired...', time: '4mo', category: 'client', folder: 'trash', thread: [{ from: 'Expired Listing', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', time: '4mo ago', body: 'Listing expired.' }] },
      { id: 334, from: 'Terminated Contract', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', email: 'contracts@premierrealty.com', subject: 'Vendor Contract Terminated', preview: 'Contract with previous maintenance vendor has been terminated...', time: '4mo', category: 'report', folder: 'trash', thread: [{ from: 'Terminated Contract', avatar: 'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=100&h=100&fit=crop', time: '4mo ago', body: 'Vendor contract terminated.' }] },
      
      // SPAM - 6 emails
      { id: 431, from: 'Fake Solicitation', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', email: 'spam@fakebusiness.com', subject: 'Exclusive Business Opportunity - Act Now!', preview: 'Invest now and triple your money in 30 days guaranteed...', time: '1w', category: 'urgent', folder: 'spam', thread: [{ from: 'Fake Solicitation', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', time: '1w ago', body: 'Investment scam!' }] },
      { id: 432, from: 'Phishing Email', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', email: 'security@fake-bank.com', subject: 'Urgent: Verify Your Business Account', preview: 'Your business account will be locked unless you verify immediately...', time: '2w', category: 'urgent', folder: 'spam', thread: [{ from: 'Phishing Email', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', time: '2w ago', body: 'Banking phishing attempt!' }] },
      { id: 433, from: 'Fake Vendor', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', email: 'invoice@fakeven dor.com', subject: 'Overdue Invoice - Pay Immediately', preview: 'You have an overdue invoice that must be paid within 24 hours...', time: '3w', category: 'urgent', folder: 'spam', thread: [{ from: 'Fake Vendor', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', time: '3w ago', body: 'Fake invoice scam!' }] },
      { id: 434, from: 'Property Scam', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', email: 'deals@property-scam.com', subject: 'Amazing Property Deal - Below Market', preview: 'Property for sale 80% below market value...', time: '1mo', category: 'client', folder: 'spam', thread: [{ from: 'Property Scam', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', time: '1mo ago', body: 'Property deal scam!' }] },
      { id: 435, from: 'Fake Legal Notice', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', email: 'legal@fake-law.com', subject: 'Legal Action Required', preview: 'You are being sued. Click here for details...', time: '1mo', category: 'urgent', folder: 'spam', thread: [{ from: 'Fake Legal Notice', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', time: '1mo ago', body: 'Fake legal threat!' }] },
      { id: 436, from: 'Tax Scam', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', email: 'irs@fake-irs.gov', subject: 'IRS Audit Notice', preview: 'Your business is being audited by the IRS...', time: '2mo', category: 'urgent', folder: 'spam', thread: [{ from: 'Tax Scam', avatar: 'https://images.unsplash.com/photo-1516542076529-1ea3854896f2?w=100&h=100&fit=crop', time: '2mo ago', body: 'IRS scam email!' }] },
    ]
  };

  // Get emails for active account - use REAL emails for Outlook, Gmail, and Nylas
  const emailsForAccount = (() => {
    if (activeEmailAccount === 'outlook') {
      // Always use Outlook emails when Outlook tab is active
      console.log('📧 Using outlookEmails, count:', outlookEmails.length, 'first email source:', outlookEmails[0]?.source);
      return outlookEmails;
    }
    
    // Check if active account is a Gmail-type account
    const activeAccount = emailAccounts.find(acc => acc.id === activeEmailAccount);
    if (activeAccount && activeAccount.type === 'gmail') {
      // Use real Gmail emails when any Gmail account tab is active
      console.log(`📧 Using gmailEmails for ${activeEmailAccount}, count:`, gmailEmails.length);
      return gmailEmails;
    }
    
    // Check if active account is a Nylas-connected account
    if (activeAccount && activeAccount.type === 'nylas') {
      // Use Nylas emails for connected accounts
      console.log(`📧 Using nylasEmails for ${activeEmailAccount}, count:`, nylasEmails.length);
      return nylasEmails;
    }
    
    return emailsByAccount[activeEmailAccount] || [];
  })();

  // Filter emails by category, folder, and search query
  const filteredEmails = emailsForAccount.filter(email => {
    const categoryMatch = activeCategory === 'all' || email.category === activeCategory;
    // For flagged folder: show emails that are flagged (via state or isFlagged prop)
    // For other folders: match by folder
    const isFlagged = flaggedEmails[email.id] || email.isFlagged;
    const folderMatch = activeFolder === 'flagged'
      ? isFlagged
      : (!email.folder || email.folder === activeFolder || email.folder === 'flagged');
    
    // Search filter - check subject, from, preview/body
    const searchMatch = !emailSearchQuery || (() => {
      const query = emailSearchQuery.toLowerCase();
      const subject = (email.subject || '').toLowerCase();
      const from = (email.from || '').toLowerCase();
      const emailAddr = (email.email || '').toLowerCase();
      const preview = (email.preview || email.body || '').toLowerCase();
      return subject.includes(query) || from.includes(query) || emailAddr.includes(query) || preview.includes(query);
    })();
    
    return categoryMatch && folderMatch && searchMatch;
  });

  // Pagination for emails
  const totalEmailPages = Math.ceil(filteredEmails.length / emailsPerPage);
  const paginatedEmails = filteredEmails.slice(
    (emailCurrentPage - 1) * emailsPerPage,
    emailCurrentPage * emailsPerPage
  );

  // Reset to page 1 when changing folders/accounts or search query
  useEffect(() => {
    setEmailCurrentPage(1);
  }, [activeFolder, activeEmailAccount, activeCategory, emailSearchQuery]);

  return (
    <div className="h-screen bg-black text-white p-3 md:p-6 overflow-hidden flex flex-col">
      {/* Incoming Call Popup - renders on top of everything */}
      <IncomingCallPopup
        incomingCall={incomingCall}
        activeCall={voiceActiveCall}
        isConnected={voiceConnected}
        isMuted={isMuted}
        onAccept={acceptCall}
        onReject={rejectCall}
        onIgnore={ignoreCall}
        onEndCall={endVoiceCall}
        onToggleMute={toggleMute}
      />
      
      {/* Page Header */}
      <div className="mb-2 md:mb-4 flex-shrink-0">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1 md:gap-2">
          <div>
            <h1 className="text-lg sm:text-2xl md:text-3xl font-bold mokoto-font">COMMUNICATIONS</h1>
            <p className="text-gray-400 text-[10px] md:text-sm">Managing Partner Overview</p>
          </div>
          {/* Voice status indicator */}
          {voiceReady && (
            <div className="flex items-center gap-2 px-2 md:px-3 py-1 md:py-1.5 bg-green-500/20 border border-green-500/30 rounded-full">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-[10px] md:text-xs text-green-400 font-medium">Voice Ready</span>
            </div>
          )}
        </div>
      </div>

      {/* Main Chat Widget - Dark Theme */}
      <div className="bg-black border border-gray-400 rounded-xl overflow-hidden hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 flex flex-col flex-1 min-h-0">
        {/* Tab Selector - White styling */}
        <div className="flex border-b border-gray-400/20 bg-black overflow-x-auto">
          <button
            onClick={() => setActiveTab('messages')}
            className={`flex-1 min-w-[80px] py-3 md:py-4 px-2 md:px-6 text-xs md:text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === 'messages'
                ? 'text-white border-b-2 border-gray-300'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            <MessageCircle className="inline mr-1 md:mr-2" size={16} />
            <span className="hidden sm:inline">Messages</span>
            <span className="sm:hidden">Msgs</span>
          </button>
          <button
            onClick={() => setActiveTab('email')}
            className={`flex-1 min-w-[80px] py-3 md:py-4 px-2 md:px-6 text-xs md:text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === 'email'
                ? 'text-white border-b-2 border-gray-300'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            <Mail className="inline mr-1 md:mr-2" size={16} />
            Email
          </button>
          <button
            onClick={() => setActiveTab('calls')}
            className={`flex-1 min-w-[80px] py-3 md:py-4 px-2 md:px-6 text-xs md:text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === 'calls'
                ? 'text-white border-b-2 border-gray-300'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            <Phone className="inline mr-1 md:mr-2" size={16} />
            Calls
          </button>
          <button
            onClick={() => setActiveTab('meeting')}
            className={`flex-1 min-w-[80px] py-3 md:py-4 px-2 md:px-6 text-xs md:text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === 'meeting'
                ? 'text-white border-b-2 border-gray-300'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            <Video className="inline mr-1 md:mr-2" size={16} />
            <span className="hidden sm:inline">Meeting</span>
            <span className="sm:hidden">Meet</span>
          </button>
        </div>

        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* Messages Tab Content */}
          {activeTab === 'messages' && (
            <>
              {/* Left Sidebar - Conversation List - Hidden on mobile when chat is open */}
              <div className={`${mobileShowChat ? 'hidden md:flex' : 'flex'} w-full md:w-96 bg-black border-b md:border-b-0 md:border-r border-gray-400/20 flex-col flex-1 md:flex-initial overflow-hidden`}>
                {/* Messaging Connection Panel - Show when not connected */}
                {!isMessagingConnected && (
                  <div className="m-3 mb-0 bg-gradient-to-r from-green-900/50 to-blue-900/50 border border-green-500/30 rounded-xl p-3">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <MessageCircle className="text-green-400" size={18} />
                        <h3 className="text-sm font-bold text-white">Connect Your Messages</h3>
                      </div>
                      <p className="text-gray-300 text-xs">
                        Sync iPhone messages or connect Twilio for SMS.
                      </p>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => setShowMessagingConnectionModal(true)}
                          className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-3 py-2 rounded-lg font-semibold flex items-center justify-center gap-1.5 transition-all shadow-lg text-xs"
                          data-testid="connect-iphone-messages-btn"
                        >
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                          </svg>
                          iPhone
                        </button>
                        <button 
                          onClick={() => setShowMessagingConnectionModal(true)}
                          className="flex-1 bg-[#F22F46] hover:bg-[#d62a3e] text-white px-3 py-2 rounded-lg font-semibold flex items-center justify-center gap-1.5 transition-all shadow-lg text-xs"
                          data-testid="connect-twilio-btn"
                        >
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 0C5.381 0 0 5.381 0 12s5.381 12 12 12 12-5.381 12-12S18.619 0 12 0zm0 20.4c-4.639 0-8.4-3.761-8.4-8.4S7.361 3.6 12 3.6s8.4 3.761 8.4 8.4-3.761 8.4-8.4 8.4zm3.6-8.4c0 1.988-1.612 3.6-3.6 3.6s-3.6-1.612-3.6-3.6 1.612-3.6 3.6-3.6 3.6 1.612 3.6 3.6z"/>
                          </svg>
                          Twilio
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Connected Status Badge - Show when connected */}
                {isMessagingConnected && (
                  <div className="mx-3 mt-3 mb-0 flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-xs text-green-400 font-medium truncate">
                      {messagingConnectionType === 'shortcuts' ? 'iPhone Connected' : 'Twilio Connected'}
                    </span>
                    <button
                      onClick={() => {
                        localStorage.removeItem('messaging_connected');
                        localStorage.removeItem('messaging_connection_type');
                        setIsMessagingConnected(false);
                        setMessagingConnectionType(null);
                      }}
                      className="ml-auto text-gray-400 hover:text-white text-xs flex-shrink-0"
                    >
                      Disconnect
                    </button>
                  </div>
                )}
                
                {/* Search Bar + Compose Button */}
                <div className="p-3 md:p-4 flex-shrink-0 border-b border-gray-400/10">
                  <div className="flex gap-2 mb-2 md:mb-3">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                      <input
                        type="text"
                        placeholder="Search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-9 md:pl-10 pr-3 md:pr-4 py-2 md:py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-xs md:text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400"
                      />
                    </div>
                    <button
                      onClick={() => {
                        // Don't fetch contacts immediately - wait for user to search
                        setFilteredContacts([]);
                        setShowContactSuggestions(false);
                        setComposeRecipient('');
                        setComposeMessage('');
                        setShowComposeModal(true);
                      }}
                      className="px-3 md:px-4 py-2 md:py-2.5 bg-white hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-1 md:gap-2 text-black font-medium text-xs md:text-sm"
                      title="Compose new message"
                    >
                      <MessageCircle size={16} />
                      <span className="hidden sm:inline">Compose</span>
                      <span className="sm:hidden">New</span>
                    </button>
                  </div>
                  
                  {/* Phone Type Tabs */}
                  <div className="flex gap-1 md:gap-2">
                    <button
                      onClick={() => setActiveMessagesCategory('all')}
                      className={`flex-1 px-2 md:px-3 py-1.5 md:py-2 rounded-lg text-[10px] md:text-xs font-medium transition-colors ${
                        activeMessagesCategory === 'all'
                          ? 'bg-white text-black'
                          : 'bg-gray-900 text-gray-400 hover:bg-gray-800'
                      }`}
                    >
                      All
                    </button>
                    <button
                      onClick={() => setActiveMessagesCategory('personal')}
                      className={`flex-1 px-2 md:px-3 py-1.5 md:py-2 rounded-lg text-[10px] md:text-xs font-medium transition-colors ${
                        activeMessagesCategory === 'personal'
                          ? 'bg-white text-black'
                          : 'bg-gray-900 text-gray-400 hover:bg-gray-800'
                      }`}
                    >
                      Personal
                    </button>
                    <button
                      onClick={() => setActiveMessagesCategory('business')}
                      className={`flex-1 px-2 md:px-3 py-1.5 md:py-2 rounded-lg text-[10px] md:text-xs font-medium transition-colors ${
                        activeMessagesCategory === 'business'
                          ? 'bg-white text-black'
                          : 'bg-gray-900 text-gray-400 hover:bg-gray-800'
                      }`}
                    >
                      Business
                    </button>
                  </div>
                </div>

                {/* Category Tabs */}
                <div className="flex-shrink-0 px-2 md:px-3 py-2 border-b border-gray-400/10 overflow-x-auto">
                  <div className="flex gap-2 items-center">
                    <button
                      onClick={() => setActiveCategory('all')}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                        activeCategory === 'all'
                          ? 'bg-white text-black'
                          : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                      }`}
                    >
                      All
                    </button>
                    {messageCategories.map((cat) => (
                      <button
                        key={cat}
                        onClick={() => setActiveCategory(cat)}
                        data-testid={`category-${cat}-btn`}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                          activeCategory === cat
                            ? 'bg-white text-black'
                            : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                        }`}
                      >
                        {formatCategoryName(cat)}
                      </button>
                    ))}
                    {/* Edit Categories Button */}
                    <button
                      onClick={() => openCategoryModal('messages')}
                      data-testid="edit-message-categories-btn"
                      className="px-2 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap bg-gray-800/50 text-gray-500 hover:bg-gray-700 hover:text-gray-300 border border-dashed border-gray-600"
                      title="Edit Categories"
                    >
                      <Pencil size={12} className="inline mr-1" />
                      Edit
                    </button>
                  </div>
                </div>

                {/* Conversation List - Shows conversations for 'All' tab, individual messages for category tabs */}
                <div className="flex-1 overflow-y-auto">
                  {/* Show individual categorized messages when a specific category is selected */}
                  {activeCategory !== 'all' && categorizedMessages.length > 0 && categorizedMessages.map((catMsg) => (
                    <div
                      key={catMsg.id}
                      className="relative"
                    >
                      <div
                        onClick={() => {
                          isManualSelection.current = true;
                          setSelectedConversation(catMsg.originalConversation);
                          // Mark conversation as read when clicked
                          if (catMsg.originalConversation.unread > 0) {
                            setConversations(prevConvs => 
                              prevConvs.map(c => 
                                c.id === catMsg.originalConversation.id ? { ...c, unread: 0 } : c
                              )
                            );
                            // Also mark messages as read in the backend
                            apiCall(`/messages/mark-read/${encodeURIComponent(catMsg.originalConversation.phone)}`, {
                              method: 'POST'
                            }).catch(err => console.log('Could not mark as read:', err));
                          }
                        }}
                        className={`group relative px-4 py-3 cursor-pointer transition-all hover:bg-gray-900 ${
                          selectedConversation?.id === catMsg.conversationId ? 'bg-gray-900 border-l-4 border-purple-500' : ''
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Avatar */}
                          <div className="relative flex-shrink-0">
                            <img
                              src={catMsg.avatar}
                              alt={catMsg.conversationName}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2 min-w-0">
                                <h4 className="text-sm font-semibold text-white truncate">
                                  {catMsg.conversationName}
                                </h4>
                                {/* Category Badge */}
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${getCategoryBadge(catMsg.category).bg} ${getCategoryBadge(catMsg.category).text}`}>
                                  {getCategoryBadge(catMsg.category).label}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500 flex-shrink-0">{catMsg.time}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-gray-400 truncate flex-1">
                                {catMsg.isOwn ? 'You: ' : ''}{catMsg.content}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Show empty state for category tabs with no messages */}
                  {activeCategory !== 'all' && categorizedMessages.length === 0 && (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500 p-8">
                      <p className="text-sm">No messages in this category</p>
                    </div>
                  )}
                  
                  {/* Show conversations for 'All' tab */}
                  {activeCategory === 'all' && filteredConversations.map((conv) => (
                    <div
                      key={conv.id}
                      className="relative"
                    >
                      <div
                        onClick={() => {
                          isManualSelection.current = true;
                          setSelectedConversation(conv);
                          setMobileShowChat(true); // Show chat on mobile when conversation selected
                          // Reset conversation search when switching conversations
                          setConversationSearchQuery('');
                          setShowConversationSearch(false);
                          // Mark conversation as read when clicked
                          if (conv.unread > 0) {
                            // Update locally first (optimistic)
                            setConversations(prevConvs => 
                              prevConvs.map(c => 
                                c.phone === conv.phone ? { ...c, unread: 0 } : c
                              )
                            );
                            // Also mark messages as read in the backend
                            apiCall(`/messages/mark-read/${encodeURIComponent(conv.phone)}`, {
                              method: 'POST'
                            }).catch(err => console.log('Could not mark as read:', err));
                          }
                        }}
                        className={`group relative px-4 py-3 cursor-pointer transition-all hover:bg-gray-900 ${
                          selectedConversation?.id === conv.id ? 'bg-gray-900 border-l-4 border-purple-500' : ''
                        }`}
                      >
                      <div className="flex items-start gap-3">
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2 min-w-0">
                              <h4 className="text-sm font-semibold text-white truncate">
                                {conv.name}
                                {conv.isGroup && <span className="ml-1 text-xs text-gray-500">(Group)</span>}
                              </h4>
                              {/* Category Badge - detect from last message if not set */}
                              {(() => {
                                const category = conv.category || detectMessageCategory(conv.lastMessage);
                                if (category && category !== 'all') {
                                  const badge = getCategoryBadge(category);
                                  return (
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${badge.bg} ${badge.text}`}>
                                      {badge.label}
                                    </span>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500 flex-shrink-0">{formatTimestamp(conv.timestamp || conv.time)}</span>
                              
                              {/* 3-dot dropdown button for conversation */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMessageDropdown(openMessageDropdown === `conv-${conv.id}` ? null : `conv-${conv.id}`);
                                }}
                                className="p-1 hover:bg-gray-700 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                              >
                                <MoreVertical size={16} className="text-gray-400" />
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-gray-400 truncate flex-1">
                              {conv.hasVoiceMessage ? (
                                <span className="italic text-purple-400">🎤 Voice message</span>
                              ) : (
                                conv.lastMessage
                              )}
                            </p>
                            {conv.unread > 0 && (
                              <span className="ml-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                                {conv.unread}
                              </span>
                            )}
                            {pinnedConversations[conv.id] && conv.unread === 0 && (
                              <Pin size={14} className="ml-2 text-purple-400 fill-purple-400" />
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Dropdown Menu for Conversation */}
                      {openMessageDropdown === `conv-${conv.id}` && (
                        <div className="absolute right-4 top-12 w-48 bg-gray-900 border border-gray-400/20 rounded-lg shadow-xl z-20">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTogglePin(conv.id);
                              setOpenMessageDropdown(null);
                            }}
                            className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                          >
                            <Pin size={16} className="text-purple-400" />
                            {pinnedConversations[conv.id] ? 'Unpin Conversation' : 'Pin Conversation'}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddToMeeting(conv);
                            }}
                            className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                          >
                            <Calendar size={16} className="text-purple-400" />
                            Add to Meeting
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNewMeeting(conv);
                            }}
                            className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                          >
                            <CalendarPlus size={16} className="text-purple-400" />
                            New Meeting
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddContactFromDropdown(conv);
                            }}
                            className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                          >
                            <UserPlus size={16} className="text-purple-400" />
                            Add/Edit Contact
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteConversation(conv.phone || conv.id);
                              setOpenMessageDropdown(null);
                            }}
                            className="w-full text-left px-4 py-2.5 hover:bg-red-900 text-sm text-red-400 flex items-center gap-2"
                          >
                            <Trash2 size={16} className="text-red-400" />
                            Delete Conversation
                          </button>
                        </div>
                      )}
                    </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Side - Chat View - Hidden on mobile when list is shown */}
              <div className={`${mobileShowChat ? 'flex' : 'hidden md:flex'} flex-1 flex-col bg-black overflow-hidden`}>
                {!selectedConversation ? (
                  <div className="flex-1 flex items-center justify-center text-gray-400">
                    <div className="text-center">
                      <MessageCircle size={48} className="mx-auto mb-4 opacity-50" />
                      <p>Select a conversation to view messages</p>
                    </div>
                  </div>
                ) : (
                  <>
                {/* Chat Header */}
                <div className="px-3 md:px-6 py-3 md:py-4 border-b border-gray-400/20 bg-black flex-shrink-0">
                  <div className="flex items-center justify-between gap-2">
                    {/* Left side - Back button, avatar, name */}
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {/* Back Button - Mobile Only */}
                      <button
                        onClick={() => setMobileShowChat(false)}
                        className="md:hidden p-1.5 hover:bg-gray-800 rounded-lg transition-colors flex-shrink-0"
                      >
                        <ChevronLeft size={20} className="text-white" />
                      </button>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1 md:gap-2 flex-wrap">
                          <h3 className="text-sm md:text-lg font-bold text-white truncate">
                            {selectedConversation.name}
                          </h3>
                          {/* Category Badge in Header - Hidden on mobile */}
                          {selectedConversation.category && selectedConversation.category !== 'all' && (
                            <span className={`hidden sm:inline-block px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium ${getCategoryBadge(selectedConversation.category).bg} ${getCategoryBadge(selectedConversation.category).text}`}>
                              {getCategoryBadge(selectedConversation.category).label}
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] md:text-xs text-gray-400 truncate">
                          {selectedConversation.phone}
                        </p>
                      </div>
                    </div>
                    
                    {/* Right side - Action buttons */}
                    <div className="flex items-center gap-1 md:gap-3 flex-shrink-0">
                      {/* Add Contact Button - Icon only on mobile */}
                      {!selectedConversation.isGroup && (
                        <button
                          onClick={async () => {
                            // Pre-fill the form with current conversation data
                            setNewContactName(selectedConversation.name);
                            setNewContactPhone(selectedConversation.phone);
                            setNewContactEmail('');
                            setNewContactCompany('');
                            setNewContactRole('');
                            setNewContactCategory(selectedConversation.category || 'Personal');
                            setNewContactNotes('Added from text message');
                            setOriginalConversationName(selectedConversation.name);
                            setShowAddContactModal(true);
                            
                            // Auto-scan if the name looks like a phone number (not a real name)
                            const nameIsPhoneNumber = /^[\+\d\s\-\(\)]+$/.test(selectedConversation.name);
                            if (nameIsPhoneNumber && selectedConversation.phone) {
                              try {
                                setContactAutoScanning(true);
                                const response = await apiCall(`/contacts/scan-icloud/${encodeURIComponent(selectedConversation.phone)}`);
                                
                                if (response.found) {
                                  setNewContactName(response.name || selectedConversation.name);
                                  setNewContactEmail(response.email || '');
                                  setNewContactCompany(response.company || '');
                                  console.log(`✅ Auto-populated contact: ${response.name}`);
                                }
                              } catch (error) {
                                console.error('Auto scan error:', error);
                              } finally {
                                setContactAutoScanning(false);
                              }
                            }
                          }}
                          className="p-1.5 md:px-3 md:py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-1 md:gap-2"
                          title="Add to contacts"
                        >
                          <UserPlus size={16} />
                          <span className="hidden md:inline">Add Contact</span>
                        </button>
                      )}
                      <button 
                        onClick={() => setShowConversationSearch(!showConversationSearch)}
                        className={`hidden sm:block p-1.5 md:p-2 hover:bg-gray-900 rounded-full transition-colors ${showConversationSearch ? 'bg-gray-800' : ''}`}
                        title="Search in conversation"
                      >
                        <Search size={18} className={showConversationSearch ? 'text-purple-400' : 'text-gray-400'} />
                      </button>
                      <button className="p-1.5 md:p-2 hover:bg-gray-900 rounded-full transition-colors">
                        <Phone size={18} className="text-gray-400" />
                      </button>
                      <button 
                        onClick={() => handleDeleteConversation(selectedConversation.phone || selectedConversation.id)}
                        className="hidden sm:block p-1.5 md:p-2 hover:bg-red-900 rounded-full transition-colors"
                        title="Delete conversation"
                      >
                        <Trash2 size={18} className="text-red-400 hover:text-red-300" />
                      </button>
                      <button className="p-1.5 md:p-2 hover:bg-gray-900 rounded-full transition-colors">
                        <MoreVertical size={18} className="text-gray-400" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Conversation Search Bar */}
                  {showConversationSearch && (
                    <div className="px-4 py-2 bg-gray-900 border-b border-gray-700">
                      <div className="relative">
                        <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search messages in this conversation..."
                          value={conversationSearchQuery}
                          onChange={(e) => setConversationSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-10 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 text-sm"
                          autoFocus
                        />
                        {conversationSearchQuery && (
                          <button
                            onClick={() => setConversationSearchQuery('')}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                      {conversationSearchQuery && (
                        <p className="text-xs text-gray-400 mt-1">
                          {selectedConversation?.messages?.filter(m => 
                            (m.text || m.content || '').toLowerCase().includes(conversationSearchQuery.toLowerCase())
                          ).length || 0} matches found
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Messages Area */}
                <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-3 md:p-6 space-y-4">
                  {loading && (
                    <div className="text-center text-gray-400">Loading messages...</div>
                  )}
                  {!loading && !selectedConversation && (
                    <div className="text-center text-gray-400">Select a conversation to view messages</div>
                  )}
                  {!loading && selectedConversation && selectedConversation.messages?.length === 0 && (
                    <div className="text-center text-gray-400">No messages yet. Start the conversation!</div>
                  )}
                  {!loading && selectedConversation && selectedConversation.messages?.length > 0 && selectedConversation.messages
                    .filter(msg => {
                      // If no search query, show all messages
                      if (!conversationSearchQuery) return true;
                      // Filter messages that match the search query
                      const msgText = (msg.text || msg.content || msg.preview || '').toLowerCase();
                      return msgText.includes(conversationSearchQuery.toLowerCase());
                    })
                    .map((msg, idx) => {
                    const messageKey = `${selectedConversation.id}-${idx}`;
                    if (deletedMessages[messageKey]) return null;
                    
                    const isOwn = msg.from_name === 'You' || msg.sent === true;
                    // Detect category from message content - only shows tag if action keywords found
                    const messageCategory = detectMessageCategory(msg.text || msg.content || msg.preview);
                    const categoryBadge = messageCategory ? getCategoryBadge(messageCategory) : null;
                    
                    // Helper to highlight search matches
                    const highlightText = (text) => {
                      if (!conversationSearchQuery || !text) return text;
                      const regex = new RegExp(`(${conversationSearchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                      const parts = text.split(regex);
                      return parts.map((part, i) => 
                        regex.test(part) ? <mark key={i} className="bg-yellow-500 text-black rounded px-0.5">{part}</mark> : part
                      );
                    };
                    
                    return (
                    <div key={msg.id || idx} className={`flex gap-3 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                      <div className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'} max-w-md relative`}>
                        
                        {/* Message Container */}
                        <div className="relative group">
                          {/* Category Tag - styled like iMessage (gray pill, inside message bubble) */}
                          {categoryBadge && (
                            <div className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-2 ${categoryBadge.bg} ${categoryBadge.text}`}>
                              {categoryBadge.label}
                            </div>
                          )}
                          
                          {/* Text Message */}
                          <div
                            onClick={() => setShowEmojiPicker(showEmojiPicker === `${selectedConversation.id}-${idx}` ? null : `${selectedConversation.id}-${idx}`)}
                            className={`rounded-2xl px-4 py-2.5 cursor-pointer ${
                              isOwn
                                ? 'bg-purple-600 text-white rounded-br-sm'
                                : 'bg-gray-800 text-white rounded-bl-sm'
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap">{highlightText(msg.text || msg.content || msg.preview)}</p>
                            {/* Show reaction if exists */}
                            {messageReactions[`${selectedConversation.id}-${idx}`] && (
                              <div className="mt-1 inline-block bg-black/30 rounded-full px-2 py-0.5 text-base">
                                {messageReactions[`${selectedConversation.id}-${idx}`]}
                              </div>
                            )}
                          </div>

                          {/* Emoji Picker */}
                          {showEmojiPicker === `${selectedConversation.id}-${idx}` && (
                            <div className={`absolute ${isOwn ? 'right-0' : 'left-0'} -bottom-12 bg-gray-900 border border-gray-400/20 rounded-lg shadow-xl p-2 flex gap-1 z-20`}>
                              {availableEmojis.map((emoji) => (
                                <button
                                  key={emoji}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddReaction(selectedConversation.id, idx, emoji);
                                  }}
                                  className="text-xl hover:bg-gray-800 rounded p-1 transition-colors"
                                >
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Voice Message */}
                          {msg.hasVoiceMessage && (
                            <div className={`rounded-2xl px-4 py-3 flex items-center gap-3 min-w-[200px] ${
                              isOwn
                                ? 'bg-purple-600'
                                : 'bg-gray-800'
                            }`}>
                              <button className="w-8 h-8 bg-black rounded-full flex items-center justify-center flex-shrink-0 hover:bg-gray-900 transition-colors">
                                <div className="w-0 h-0 border-t-4 border-t-transparent border-l-6 border-l-white border-b-4 border-b-transparent ml-0.5"></div>
                              </button>
                              <div className="flex-1 flex items-center gap-1">
                                {[2,4,3,5,4,6,5,7,6,8,7,6,5,4,3,2].map((height, i) => (
                                  <div key={i} className="w-1 bg-white rounded-full" style={{height: `${height * 2}px`}}></div>
                                ))}
                              </div>
                              <span className="text-xs text-white font-medium">{msg.voiceDuration}</span>
                            </div>
                          )}

                          {/* Dropdown Button - Appears on Hover */}
                          <button
                            onClick={() => setOpenMessageDropdown(openMessageDropdown === idx ? null : idx)}
                            className={`absolute ${isOwn ? 'left-0 -translate-x-8' : 'right-0 translate-x-8'} top-0 p-1 bg-gray-800 hover:bg-gray-700 rounded-full opacity-0 group-hover:opacity-100 transition-opacity`}
                          >
                            <MoreVertical size={16} className="text-gray-300" />
                          </button>

                          {/* Dropdown Menu */}
                          {openMessageDropdown === idx && (
                            <div className={`absolute ${isOwn ? 'left-0' : 'right-0'} top-8 w-48 bg-gray-900 border border-gray-400/20 rounded-lg shadow-xl z-10`}>
                              <button
                                onClick={() => {
                                  setOpenMessageDropdown(null);
                                }}
                                className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                              >
                                <Reply size={16} className="text-purple-400" />
                                Reply
                              </button>
                              <button
                                onClick={() => {
                                  handleAddToMeeting(selectedConversation);
                                }}
                                className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                              >
                                <Calendar size={16} className="text-purple-400" />
                                Add Meeting
                              </button>
                              <button
                                onClick={() => {
                                  handleNewMeeting(selectedConversation);
                                }}
                                className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                              >
                                <CalendarPlus size={16} className="text-purple-400" />
                                New Meeting
                              </button>
                              <button
                                onClick={() => {
                                  setOpenMessageDropdown(null);
                                }}
                                className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                              >
                                <UserCheck size={16} className="text-purple-400" />
                                Add Contact
                              </button>
                              <button
                                onClick={() => {
                                  setOpenMessageDropdown(null);
                                }}
                                className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                              >
                                <UserPlus size={16} className="text-purple-400" />
                                New Contact
                              </button>
                              <button
                                onClick={() => {
                                  handleDeleteMessage(selectedConversation.id, idx);
                                }}
                                className="w-full text-left px-4 py-2.5 hover:bg-red-900 text-sm text-red-400 flex items-center gap-2"
                              >
                                <Trash2 size={16} className="text-red-400" />
                                Delete Message
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Message Footer */}
                        <div className="flex items-center gap-2 mt-1">
                          {msg.reactions && (
                            <div className="flex gap-1">
                              {msg.reactions.map((reaction, i) => (
                                <span key={i} className="text-xs bg-gray-800 border border-gray-400/20 rounded-full px-2 py-0.5 flex items-center gap-1 text-white">
                                  {reaction.emoji} {reaction.count}
                                </span>
                              ))}
                            </div>
                          )}
                          {msg.readBy && (
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <CheckCircle size={12} /> {msg.readBy}
                            </span>
                          )}
                          <span className="text-xs text-gray-500">{msg.time}</span>
                        </div>
                      </div>

                      {isOwn && (
                        <div className="w-10"></div>
                      )}
                    </div>
                    );
                  })}
                  {/* Scroll anchor */}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="px-3 md:px-6 py-3 md:py-4 border-t border-gray-400/20 bg-black flex-shrink-0">
                  {showAIDraftMessage && aiDraftMessageText && (
                    <div className="bg-purple-900/30 border border-purple-500/50 rounded-lg p-3 mb-3">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Sparkles size={16} className="text-purple-400" />
                          <span className="text-xs font-semibold text-purple-300">AI Suggested Message</span>
                        </div>
                        <button onClick={() => { setShowAIDraftMessage(false); setAiDraftMessageText(''); }} className="text-gray-400 hover:text-white">
                          <X size={14} />
                        </button>
                      </div>
                      <p className="text-sm text-white mb-2 whitespace-pre-wrap">{aiDraftMessageText}</p>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => {
                            setMessageInput(aiDraftMessageText);
                            setShowAIDraftMessage(false);
                            setAiDraftMessageText('');
                          }}
                          className="text-xs bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded"
                        >
                          Use This Draft
                        </button>
                        <button 
                          onClick={async () => {
                            if (!selectedConversation || generatingAIReply) return;
                            setGeneratingAIReply(true);
                            try {
                              const conversationMessages = selectedConversation.messages || [];
                              const lastContactMessage = conversationMessages
                                .filter(m => m.from_name !== 'You')
                                .slice(-1)[0];
                              
                              if (lastContactMessage) {
                                const messageText = lastContactMessage.content || lastContactMessage.preview || lastContactMessage.text || '';
                                const result = await aiEmailAPI.generateMessageReply({
                                  conversation_id: selectedConversation.id,
                                  sender_name: selectedConversation.name,
                                  message_content: messageText,
                                  context: conversationMessages.slice(-5).map(m => `${m.from_name === 'You' ? 'You' : selectedConversation.name}: ${m.content || m.preview || m.text || ''}`).join('\n'),
                                  reply_tone: 'professional'
                                });
                                if (result.reply) {
                                  setAiDraftMessageText(result.reply);
                                }
                              }
                            } catch (err) {
                              console.error('Failed to regenerate AI reply:', err);
                            } finally {
                              setGeneratingAIReply(false);
                            }
                          }}
                          disabled={generatingAIReply}
                          className="text-xs bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded flex items-center gap-1"
                          title="Generate new draft"
                        >
                          <RefreshCw size={12} className={generatingAIReply ? 'animate-spin' : ''} />
                          {generatingAIReply ? 'Generating...' : 'Refresh'}
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start gap-1 md:gap-3">
                    {/* Hidden file input for attachments */}
                    <input
                      type="file"
                      ref={messageAttachmentInputRef}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          // Check file size (max 10MB)
                          if (file.size > 10 * 1024 * 1024) {
                            alert('File too large. Maximum size is 10MB.');
                            return;
                          }
                          setMessageAttachment(file);
                          console.log('📎 Attached file:', file.name);
                        }
                      }}
                      className="hidden"
                      accept="image/*,.pdf,.doc,.docx,.txt"
                    />
                    <button 
                      onClick={() => messageAttachmentInputRef.current?.click()}
                      className={`p-1.5 md:p-2 hover:bg-gray-900 rounded-full transition-colors ${messageAttachment ? 'bg-purple-600/20' : ''}`}
                      title={messageAttachment ? `Attached: ${messageAttachment.name}` : "Attach file"}
                    >
                      <Paperclip size={18} className={messageAttachment ? 'text-purple-400' : 'text-gray-400'} />
                    </button>
                    <button 
                      onClick={() => setShowScheduleModal(true)}
                      className="hidden sm:block p-1.5 md:p-2 hover:bg-gray-900 rounded-full transition-colors"
                      title="Schedule message"
                    >
                      <Clock size={18} className="text-gray-400" />
                    </button>
                    <button 
                      onClick={async () => {
                        if (!selectedConversation) return;
                        setGeneratingAIReply(true);
                        try {
                          // Get the conversation messages
                          const conversationMessages = selectedConversation.messages || [];
                          // Get the last message from the contact (not sent by user)
                          // Messages from API have from_name, not sent property
                          const lastContactMessage = conversationMessages
                            .filter(m => m.from_name !== 'You')
                            .slice(-1)[0];
                          
                          if (lastContactMessage) {
                            const messageText = lastContactMessage.content || lastContactMessage.preview || lastContactMessage.text || '';
                            const result = await aiEmailAPI.generateMessageReply({
                              conversation_id: selectedConversation.id,
                              sender_name: selectedConversation.name,
                              message_content: messageText,
                              context: conversationMessages.slice(-5).map(m => `${m.from_name === 'You' ? 'You' : selectedConversation.name}: ${m.content || m.preview || m.text || ''}`).join('\n'),
                              reply_tone: 'professional'
                            });
                            if (result.reply) {
                              setAiDraftMessageText(result.reply);
                              setShowAIDraftMessage(true);
                            }
                          } else {
                            // No messages from contact, use generic
                            setAiDraftMessageText('Thank you for reaching out. I\'d be happy to help. What can I assist you with?');
                            setShowAIDraftMessage(true);
                          }
                        } catch (err) {
                          console.error('Failed to generate AI reply:', err);
                          // Fallback
                          setAiDraftMessageText('Thank you for reaching out. I\'ve reviewed your request and would be happy to discuss this further. Let\'s schedule a meeting at your earliest convenience.');
                          setShowAIDraftMessage(true);
                        } finally {
                          setGeneratingAIReply(false);
                        }
                      }}
                      disabled={generatingAIReply}
                      className="hidden sm:block p-1.5 md:p-2 hover:bg-gray-900 rounded-full transition-colors"
                      title="Generate AI Reply"
                      data-testid="message-ai-reply-btn"
                    >
                      {generatingAIReply ? (
                        <Loader2 size={18} className="text-purple-400 animate-spin" />
                      ) : (
                        <Sparkles size={18} className="text-purple-400" />
                      )}
                    </button>
                    <div className="flex-1 flex flex-col gap-2">
                      {/* Attachment Preview */}
                      {messageAttachment && (
                        <div className="flex items-center gap-2 px-2 md:px-3 py-1.5 md:py-2 bg-gray-800 border border-gray-600 rounded-lg">
                          <FileText size={14} className="text-purple-400" />
                          <span className="text-xs md:text-sm text-gray-300 truncate flex-1">{messageAttachment.name}</span>
                          <span className="hidden sm:inline text-xs text-gray-500">({(messageAttachment.size / 1024).toFixed(1)} KB)</span>
                          <button
                            onClick={() => setMessageAttachment(null)}
                            className="p-1 hover:bg-gray-700 rounded-full"
                          >
                            <X size={14} className="text-gray-400" />
                          </button>
                        </div>
                      )}
                      <textarea
                        value={messageInput}
                        onChange={(e) => setMessageInput(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        placeholder="Your message"
                        rows="1"
                        onInput={(e) => {
                          e.target.style.height = 'auto';
                          e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
                        }}
                        className="flex-1 px-3 md:px-4 py-2 md:py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none overflow-y-auto"
                        style={{ minHeight: '40px', maxHeight: '150px' }}
                      />
                    </div>
                    <button className="hidden sm:block p-1.5 md:p-2.5 hover:bg-gray-900 rounded-full transition-colors self-end">
                      <Mic size={18} className="text-gray-400" />
                    </button>
                    <button 
                      onClick={handleSendMessage}
                      disabled={!messageInput.trim()}
                      className="p-2 md:p-2.5 bg-purple-600 hover:bg-purple-700 rounded-full transition-colors self-end disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send size={16} className="text-white" />
                    </button>
                  </div>
                </div>
              </>
              )}
              </div>
          </>
          )}

          {/* Calls Tab Content */}
          {activeTab === 'calls' && (
            <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
              {/* Left Sidebar - Call Filters - Horizontal on mobile */}
              <div className="w-full md:w-48 bg-black border-b md:border-b-0 md:border-r border-gray-400/20 flex-shrink-0 p-3 md:p-4">
                <h3 className="text-xs md:text-sm font-bold text-white mb-2 md:mb-4 hidden md:block">FILTERS</h3>
                <div className="flex md:flex-col gap-1 md:gap-1 overflow-x-auto md:overflow-visible">
                  <button
                    onClick={() => setActiveCallFilter('all')}
                    className={`flex-shrink-0 px-3 py-1.5 md:py-2 rounded-lg transition-colors flex items-center gap-1.5 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
                      activeCallFilter === 'all'
                        ? 'bg-white text-black'
                        : 'text-gray-400 hover:bg-gray-900'
                    }`}
                  >
                    <Phone size={14} />
                    All Calls
                  </button>
                  <button
                    onClick={() => setActiveCallFilter('missed')}
                    className={`flex-shrink-0 px-3 py-1.5 md:py-2 rounded-lg transition-colors flex items-center gap-1.5 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
                      activeCallFilter === 'missed'
                        ? 'bg-white text-black'
                        : 'text-gray-400 hover:bg-gray-900'
                    }`}
                  >
                    <AlertTriangle size={14} />
                    Missed
                  </button>
                  <button
                    onClick={() => setActiveCallFilter('voicemail')}
                    className={`flex-shrink-0 px-3 py-1.5 md:py-2 rounded-lg transition-colors flex items-center gap-1.5 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
                      activeCallFilter === 'voicemail'
                        ? 'bg-white text-black'
                        : 'text-gray-400 hover:bg-gray-900'
                    }`}
                  >
                    <Mic size={14} />
                    Voicemails
                  </button>
                  <button
                    onClick={() => setActiveCallFilter('facetime')}
                    className={`flex-shrink-0 px-3 py-1.5 md:py-2 rounded-lg transition-colors flex items-center gap-1.5 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
                      activeCallFilter === 'facetime'
                        ? 'bg-white text-black'
                        : 'text-gray-400 hover:bg-gray-900'
                    }`}
                  >
                    <Video size={14} />
                    FaceTime
                  </button>
                </div>
              </div>

              {/* Call List */}
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Calls Connection Panel - Show when not connected */}
                {!isCallsConnected && (
                  <div className="m-3 mb-0 bg-gradient-to-r from-blue-900/50 to-purple-900/50 border border-blue-500/30 rounded-xl p-3">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                      <div className="flex items-center gap-2 flex-1">
                        <Phone className="text-blue-400" size={18} />
                        <div>
                          <h3 className="text-sm font-bold text-white">Connect Your Calls</h3>
                          <p className="text-gray-300 text-xs">Sync iPhone call logs or connect Twilio for voice calls.</p>
                        </div>
                      </div>
                      <div className="flex gap-2 w-full sm:w-auto">
                        <button 
                          onClick={() => setShowCallsConnectionModal(true)}
                          className="flex-1 sm:flex-initial bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-1.5 transition-all shadow-lg text-xs"
                          data-testid="connect-iphone-calls-btn"
                        >
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                          </svg>
                          iPhone
                        </button>
                        <button 
                          onClick={() => setShowCallsConnectionModal(true)}
                          className="flex-1 sm:flex-initial bg-[#F22F46] hover:bg-[#d62a3e] text-white px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-1.5 transition-all shadow-lg text-xs"
                          data-testid="connect-twilio-calls-btn"
                        >
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 0C5.381 0 0 5.381 0 12s5.381 12 12 12 12-5.381 12-12S18.619 0 12 0zm0 20.4c-4.639 0-8.4-3.761-8.4-8.4S7.361 3.6 12 3.6s8.4 3.761 8.4 8.4-3.761 8.4-8.4 8.4zm3.6-8.4c0 1.988-1.612 3.6-3.6 3.6s-3.6-1.612-3.6-3.6 1.612-3.6 3.6-3.6 3.6 1.612 3.6 3.6z"/>
                          </svg>
                          Twilio
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Connected Status Badge - Show when connected */}
                {isCallsConnected && (
                  <div className="mx-3 mt-3 mb-0 flex items-center gap-2 px-3 py-2 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                    <span className="text-xs text-blue-400 font-medium">
                      {callsConnectionType === 'shortcuts' ? 'iPhone Calls Connected' : 'Twilio Voice Connected'}
                    </span>
                    <button
                      onClick={() => {
                        localStorage.removeItem('calls_connected');
                        localStorage.removeItem('calls_connection_type');
                        setIsCallsConnected(false);
                        setCallsConnectionType(null);
                      }}
                      className="ml-auto text-gray-400 hover:text-white text-xs"
                    >
                      Disconnect
                    </button>
                  </div>
                )}
                
                {/* Category Tabs + New Call Button */}
                <div className="flex flex-col sm:flex-row gap-2 p-3 md:p-4 bg-black border-b border-gray-400/20 items-start sm:items-center justify-between">
                  <div className="flex gap-1 md:gap-2 overflow-x-auto w-full sm:w-auto pb-2 sm:pb-0 items-center">
                    <button
                      onClick={() => setActiveCallCategory('all')}
                      className={`flex-shrink-0 px-2 md:px-4 py-1.5 md:py-2 rounded-lg transition-colors text-xs md:text-sm font-medium whitespace-nowrap ${
                        activeCallCategory === 'all'
                          ? 'bg-white text-black'
                          : 'text-gray-400 hover:bg-gray-900'
                      }`}
                    >
                      All
                    </button>
                    {callCategories.map((cat) => (
                      <button
                        key={cat}
                        onClick={() => setActiveCallCategory(cat)}
                        data-testid={`call-category-${cat}-btn`}
                        className={`flex-shrink-0 px-2 md:px-4 py-1.5 md:py-2 rounded-lg transition-colors text-xs md:text-sm font-medium whitespace-nowrap ${
                          activeCallCategory === cat
                            ? 'bg-white text-black'
                            : 'text-gray-400 hover:bg-gray-900'
                        }`}
                      >
                        {formatCategoryName(cat)}
                      </button>
                    ))}
                    {/* Edit Categories Button */}
                    <button
                      onClick={() => openCategoryModal('calls')}
                      data-testid="edit-call-categories-btn"
                      className="flex-shrink-0 px-2 py-1.5 md:py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap text-gray-500 hover:bg-gray-900 hover:text-gray-300 border border-dashed border-gray-700"
                      title="Edit Categories"
                    >
                      <Pencil size={12} className="inline mr-1" />
                      Edit
                    </button>
                  </div>
                  
                  {/* New Call Button */}
                  <button
                    onClick={() => setShowNewCallModal(true)}
                    className="px-3 md:px-4 py-1.5 md:py-2 bg-white hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-1.5 md:gap-2 text-black font-medium text-xs md:text-sm flex-shrink-0"
                    title="New call"
                  >
                    <Phone size={14} />
                    <span className="hidden sm:inline">New Call</span>
                    <span className="sm:hidden">Call</span>
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-3 md:p-4 space-y-2" style={{ paddingBottom: '5rem' }}>
                  {calls.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 md:py-12 text-center">
                      <Phone size={40} className="text-gray-600 mb-3 md:mb-4" />
                      <h3 className="text-base md:text-lg font-semibold text-gray-400 mb-2">No Call History</h3>
                      <p className="text-xs md:text-sm text-gray-500">Your Twilio call logs will appear here</p>
                    </div>
                  ) : (
                    calls
                      .filter(call => {
                        // Call type filter
                        if (activeCallFilter === 'all') return true;
                        if (activeCallFilter === 'missed') return call.status === 'no-answer' || call.status === 'busy' || call.status === 'failed';
                        if (activeCallFilter === 'voicemail') return call.recording_url;
                        return true;
                      })
                      .map((call) => {
                        // Determine phone number to display (the other party)
                        const displayPhone = call.direction === 'inbound' ? call.from_phone : call.to_phone;
                        const displayName = call.contact_name || displayPhone;
                        
                        // Format the timestamp
                        const callDate = call.created_at ? new Date(call.created_at) : new Date();
                        const formattedDate = callDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        const formattedTime = callDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                        
                        // Format duration
                        const formatDuration = (seconds) => {
                          if (!seconds) return null;
                          const mins = Math.floor(seconds / 60);
                          const secs = seconds % 60;
                          return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
                        };
                        
                        // Determine status display
                        const isMissed = call.status === 'no-answer' || call.status === 'busy' || call.status === 'failed';
                        const isCompleted = call.status === 'completed';
                        const outcomeInfo = callOutcomes[call.call_sid];
                        
                        return (
                          <div
                            key={call.id}
                            onClick={(e) => {
                              // Don't open detail if clicking on action buttons
                              if (e.target.closest('button')) return;
                              setSelectedCallDetail(call);
                            }}
                            className="bg-gray-900 border border-gray-400/20 rounded-lg p-3 md:p-4 hover:bg-gray-800 hover:border-gray-500 transition-all cursor-pointer"
                            data-testid={`call-log-${call.id}`}
                          >
                            <div className="flex items-start gap-2 md:gap-4">
                              {/* Avatar */}
                              <div className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
                                <Phone size={24} className="text-gray-400" />
                              </div>

                              {/* Call Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className="text-sm font-semibold text-white">{displayName}</h4>
                                    {/* Direction Badge */}
                                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${
                                      call.direction === 'inbound' 
                                        ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                                        : 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                                    }`}>
                                      {call.direction === 'inbound' ? '↓ Inbound' : '↑ Outbound'}
                                    </span>
                                    {/* VAPI AI Badge */}
                                    {(call.vapi_handled || call.status === 'vapi_completed' || call.status === 'forwarded_to_vapi') && (
                                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-500/20 text-purple-400 border border-purple-500/30 flex items-center gap-1">
                                        <Bot size={10} /> AI Handled
                                      </span>
                                    )}
                                    {/* Outcome Badge */}
                                    {(call.outcome || outcomeInfo) && (
                                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                        (call.outcome || outcomeInfo) === 'booked' ? 'bg-green-500 text-white' :
                                        (call.outcome || outcomeInfo) === 'hot_lead' ? 'bg-orange-500 text-white' :
                                        (call.outcome || outcomeInfo) === 'follow_up' ? 'bg-blue-500 text-white' :
                                        (call.outcome || outcomeInfo) === 'qualified' ? 'bg-purple-500 text-white' :
                                        'bg-gray-600 text-gray-200'
                                      }`}>
                                        {(call.outcome || outcomeInfo) === 'booked' ? '📅 Booked' :
                                         (call.outcome || outcomeInfo) === 'hot_lead' ? '🔥 Hot Lead' :
                                         (call.outcome || outcomeInfo) === 'follow_up' ? '🔄 Follow-up' :
                                         (call.outcome || outcomeInfo) === 'qualified' ? '✓ Qualified' :
                                         (call.outcome || outcomeInfo) === 'not_interested' ? '✗ Not Interested' :
                                         '📧 Voicemail'}
                                      </span>
                                    )}
                                    {/* Sentiment Badge - Show if AI summary exists */}
                                    {call.sentiment && (
                                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                        call.sentiment === 'positive' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                                        call.sentiment === 'negative' ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                                        'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                                      }`}>
                                        {call.sentiment === 'positive' ? '😊' : call.sentiment === 'negative' ? '😟' : '😐'} {call.sentiment}
                                      </span>
                                    )}
                                    {/* Lead Quality Badge */}
                                    {call.lead_quality && (
                                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                        call.lead_quality === 'hot' ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' :
                                        call.lead_quality === 'warm' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                                        'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                      }`}>
                                        {call.lead_quality === 'hot' ? '🔥 Hot' : call.lead_quality === 'warm' ? '☀️ Warm' : '❄️ Cold'}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {/* Call Actions */}
                                    <button 
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        
                                        // Use browser calling if voice is ready
                                        if (voiceReady && voiceMakeCall) {
                                          try {
                                            console.log('📞 Initiating browser call to:', displayPhone);
                                            await voiceMakeCall(displayPhone);
                                            // Don't call REST API - browser call handles everything
                                          } catch (error) {
                                            console.error('Failed to initiate call:', error);
                                            alert('Failed to initiate call: ' + error.message);
                                          }
                                        } else {
                                          // Fallback to old modal if voice not ready
                                          setActiveCall({ ...call, phone: displayPhone, name: displayName });
                                          setShowCallModal(true);
                                          setIsCalling(true);
                                          setCallDuration(0);
                                        }
                                      }}
                                      className="p-2 bg-green-600 hover:bg-green-700 rounded-full transition-colors"
                                      title={voiceReady ? "Call via browser" : "Call back"}
                                    >
                                      <Phone size={16} className="text-white" />
                                    </button>
                                    
                                    {/* 3-dot menu */}
                                    <div className="relative">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setActiveCallDropdown(activeCallDropdown === call.id ? null : call.id);
                                        }}
                                        className="p-2 hover:bg-gray-700 rounded-full transition-colors"
                                        title="More options"
                                      >
                                        <MoreVertical size={16} className="text-gray-400" />
                                      </button>
                                      
                                      {/* Dropdown Menu */}
                                      {activeCallDropdown === call.id && (
                                        <div className="absolute right-0 top-full mt-1 w-48 bg-gray-800 border border-gray-400/20 rounded-lg shadow-xl z-50">
                                          <div className="py-1">
                                            <button
                                              onClick={() => {
                                                setActiveCallDropdown(null);
                                                handleNewMeeting({ name: displayName, phone: displayPhone });
                                              }}
                                              className="w-full px-4 py-2 text-left text-sm text-white hover:bg-gray-700 transition-colors flex items-center gap-2"
                                            >
                                              <CalendarPlus size={16} />
                                              New Meeting
                                            </button>
                                            <button
                                              onClick={() => {
                                                setActiveCallDropdown(null);
                                                handleAddContactFromDropdown({ name: displayName, phone: displayPhone });
                                              }}
                                              className="w-full px-4 py-2 text-left text-sm text-white hover:bg-gray-700 transition-colors flex items-center gap-2"
                                            >
                                              <UserPlus size={16} />
                                              Add Contact
                                            </button>
                                            <div className="border-t border-gray-400/10 my-1"></div>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (window.confirm('Are you sure you want to delete this call?')) {
                                                  deleteCall(call.call_sid);
                                                }
                                                setActiveCallDropdown(null);
                                              }}
                                              className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-gray-700 transition-colors flex items-center gap-2"
                                              data-testid={`delete-call-${call.id}`}
                                            >
                                              <Trash2 size={16} />
                                              Delete Call
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
                                  <span>{displayPhone}</span>
                                  <span>•</span>
                                  <span>{formattedDate} at {formattedTime}</span>
                                </div>

                                {/* Call Duration or Status */}
                                <div className="flex items-center gap-2 text-xs">
                                  {isCompleted && call.duration && (
                                    <span className="text-gray-400">
                                      Duration: {formatDuration(call.duration)}
                                    </span>
                                  )}
                                  {isCompleted && !call.duration && (
                                    <span className="text-green-400 font-medium">
                                      Completed
                                    </span>
                                  )}
                                  {isMissed && (
                                    <span className="text-red-400 font-medium">
                                      {call.status === 'no-answer' ? 'No Answer' : call.status === 'busy' ? 'Busy' : 'Failed'}
                                    </span>
                                  )}
                                  {call.status === 'ringing' && (
                                    <span className="text-yellow-400 font-medium">
                                      Ringing...
                                    </span>
                                  )}
                                  {call.status === 'in-progress' && (
                                    <span className="text-green-400 font-medium animate-pulse">
                                      In Progress
                                    </span>
                                  )}
                                </div>

                                {/* Recording Link */}
                                {call.recording_url && (
                                  <div className="mt-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Mic size={14} className="text-purple-400" />
                                      <span className="text-xs font-semibold text-purple-400">CALL RECORDING</span>
                                    </div>
                                    <a 
                                      href={call.recording_url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1"
                                    >
                                      <Mic size={12} />
                                      Play Recording
                                    </a>
                                  </div>
                                )}

                                {/* AI Summary Section - Show if call has summary */}
                                {call.ai_summary && (
                                  <div className="mt-3 p-3 bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-lg">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Sparkles size={14} className="text-purple-400" />
                                      <span className="text-xs font-semibold text-purple-400">AI SUMMARY</span>
                                      {/* Sentiment & Lead Quality Badges */}
                                      <div className="flex items-center gap-1.5 ml-auto">
                                        {call.sentiment && (
                                          <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                                            call.sentiment === 'positive' ? 'bg-green-500/20 text-green-400' :
                                            call.sentiment === 'negative' ? 'bg-red-500/20 text-red-400' :
                                            'bg-gray-500/20 text-gray-400'
                                          }`}>
                                            {call.sentiment === 'positive' ? '😊' : call.sentiment === 'negative' ? '😟' : '😐'} {call.sentiment}
                                          </span>
                                        )}
                                        {call.lead_quality && (
                                          <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                                            call.lead_quality === 'hot' ? 'bg-orange-500/20 text-orange-400' :
                                            call.lead_quality === 'warm' ? 'bg-yellow-500/20 text-yellow-400' :
                                            'bg-blue-500/20 text-blue-400'
                                          }`}>
                                            {call.lead_quality === 'hot' ? '🔥' : call.lead_quality === 'warm' ? '☀️' : '❄️'} {call.lead_quality}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Summary Text */}
                                    <p className="text-xs text-gray-300 leading-relaxed line-clamp-2 mb-2">
                                      {call.ai_summary}
                                    </p>
                                    
                                    {/* Action Items */}
                                    {call.action_items && call.action_items.length > 0 && (
                                      <div className="mt-2 pt-2 border-t border-purple-500/20">
                                        <p className="text-[10px] font-semibold text-purple-400 uppercase mb-1">Action Items</p>
                                        <ul className="space-y-0.5">
                                          {call.action_items.slice(0, 2).map((item, idx) => (
                                            <li key={idx} className="text-[11px] text-gray-400 flex items-start gap-1.5">
                                              <span className="text-purple-400 mt-0.5">•</span>
                                              <span className="line-clamp-1">{item}</span>
                                            </li>
                                          ))}
                                          {call.action_items.length > 2 && (
                                            <li className="text-[10px] text-purple-400">
                                              +{call.action_items.length - 2} more...
                                            </li>
                                          )}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Group Members Modal */}
          {showGroupMembers && conversations[selectedConversation]?.isGroup && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowGroupMembers(false)}>
              <div className="bg-gray-900 border border-gray-400/20 rounded-lg w-[500px] max-h-[600px] flex flex-col" onClick={(e) => e.stopPropagation()}>
                {/* Modal Header */}
                <div className="px-6 py-4 border-b border-gray-400/20 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-white">{conversations[selectedConversation].name}</h3>
                    <p className="text-xs text-gray-400">
                      {getCurrentGroupMembers().length} members • {getCurrentGroupMembers().filter(m => m.isOnline).length} online
                    </p>
                  </div>
                  <button
                    onClick={() => setShowGroupMembers(false)}
                    className="p-2 hover:bg-gray-800 rounded-full transition-colors"
                  >
                    <X size={20} className="text-gray-400" />
                  </button>
                </div>

                {/* Members List */}
                <div className="flex-1 overflow-y-auto p-4">
                  {getCurrentGroupMembers().length > 0 ? (
                    <div className="space-y-2">
                      {getCurrentGroupMembers().map((member) => (
                        <div
                          key={member.id}
                          className="flex items-center gap-3 p-3 hover:bg-gray-800 rounded-lg transition-colors"
                        >
                          {/* Avatar with online status */}
                          <div className="relative flex-shrink-0">
                            <img
                              src={member.avatar}
                              alt={member.name}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                            {member.isOnline && (
                              <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-gray-900 rounded-full"></div>
                            )}
                          </div>

                          {/* Member Info */}
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="text-sm font-semibold text-white">{member.name}</h4>
                              {member.role === 'Admin' && (
                                <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs font-medium rounded-full border border-purple-500/30">
                                  Admin
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400">{member.role}</p>
                          </div>

                          {/* Online Status */}
                          <div className="flex-shrink-0">
                            <span className={`text-xs font-medium ${member.isOnline ? 'text-green-400' : 'text-gray-500'}`}>
                              {member.isOnline ? 'Online' : 'Offline'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-400">
                      No member data available for this group
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="px-6 py-4 border-t border-gray-400/20">
                  <button
                    className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors"
                    onClick={() => setShowGroupMembers(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Phone Call Modal */}
          {showCallModal && activeCall && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => {
              setShowCallModal(false);
              setActiveCall(null);
              setIsCalling(false);
              setCallDuration(0);
            }}>
              <div className="bg-gray-900 border border-gray-400/20 rounded-2xl w-[400px] p-8 flex flex-col items-center gap-6" onClick={(e) => e.stopPropagation()}>
                {/* Contact Avatar */}
                <div className="relative">
                  {activeCall.avatar ? (
                    <img
                      src={activeCall.avatar}
                      alt={activeCall.name}
                      className="w-32 h-32 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-full bg-gray-700 flex items-center justify-center">
                      <Phone size={48} className="text-gray-400" />
                    </div>
                  )}
                  {isCalling && (
                    <div className="absolute inset-0 rounded-full border-4 border-green-500 animate-ping"></div>
                  )}
                </div>

                {/* Contact Info */}
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-white mb-2">{activeCall.name}</h3>
                  <p className="text-gray-400 mb-1">{activeCall.phone}</p>
                  <p className="text-green-400 font-medium">
                    {isCalling ? 'Calling...' : `${Math.floor(callDuration / 60)}:${String(callDuration % 60).padStart(2, '0')}`}
                  </p>
                </div>

                {/* Call Controls */}
                <div className="flex gap-4 mt-4">
                  <button
                    onClick={() => setIsCalling(!isCalling)}
                    className="p-4 bg-gray-800 hover:bg-gray-700 rounded-full transition-colors"
                    title={isCalling ? "Mute" : "Unmute"}
                  >
                    <Mic size={24} className="text-white" />
                  </button>
                  <button
                    onClick={() => {
                      setShowCallModal(false);
                      setActiveCall(null);
                      setIsCalling(false);
                      setCallDuration(0);
                    }}
                    className="p-4 bg-red-600 hover:bg-red-700 rounded-full transition-colors"
                    title="End Call"
                  >
                    <Phone size={24} className="text-white rotate-[135deg]" />
                  </button>
                  <button
                    onClick={() => {}}
                    className="p-4 bg-gray-800 hover:bg-gray-700 rounded-full transition-colors"
                    title="Speaker"
                  >
                    <ThumbsUp size={24} className="text-white rotate-90" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* FaceTime Modal */}
          {showFaceTimeModal && activeCall && (
            <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50" onClick={() => {
              setShowFaceTimeModal(false);
              setActiveCall(null);
              setIsCalling(false);
              setCallDuration(0);
            }}>
              <div className="bg-black border border-gray-400 rounded-2xl w-[800px] h-[600px] relative overflow-hidden hover:border-gray-300 transition-all duration-300" onClick={(e) => e.stopPropagation()}>
                {/* Main Video Area (Simulated with dark background) */}
                <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
                  {/* Contact Avatar as placeholder for video */}
                  <div className="flex flex-col items-center gap-4">
                    {activeCall.avatar ? (
                      <img
                        src={activeCall.avatar}
                        alt={activeCall.name}
                        className="w-48 h-48 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-48 h-48 rounded-full bg-gray-700 flex items-center justify-center">
                        <Video size={64} className="text-gray-400" />
                      </div>
                    )}
                    <div className="text-center">
                      <h3 className="text-3xl font-bold text-white mb-2">{activeCall.name}</h3>
                      <p className="text-green-400 font-medium text-lg">
                        {isCalling ? 'Connecting...' : `${Math.floor(callDuration / 60)}:${String(callDuration % 60).padStart(2, '0')}`}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Self Video Preview (Picture-in-Picture) */}
                <div className="absolute top-4 right-4 w-48 h-36 bg-gray-800 border-2 border-gray-400/30 rounded-xl overflow-hidden">
                  <video
                    autoPlay
                    muted
                    playsInline
                    ref={(video) => {
                      if (video && !video.srcObject) {
                        navigator.mediaDevices.getUserMedia({ video: true, audio: false })
                          .then(stream => {
                            video.srcObject = stream;
                          })
                          .catch(err => {
                            console.log('Camera access denied:', err);
                          });
                      }
                    }}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Call Info Overlay */}
                <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <p className="text-white font-medium">{activeCall.name}</p>
                  <p className="text-sm text-gray-300">FaceTime Video</p>
                </div>

                {/* Bottom Controls */}
                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-6">
                  <button
                    onClick={() => setIsCalling(!isCalling)}
                    className="p-4 bg-gray-800/80 backdrop-blur-sm hover:bg-gray-700 rounded-full transition-colors border border-gray-400/20"
                    title="Mute"
                  >
                    <Mic size={24} className="text-white" />
                  </button>
                  <button
                    onClick={() => {}}
                    className="p-4 bg-gray-800/80 backdrop-blur-sm hover:bg-gray-700 rounded-full transition-colors border border-gray-400/20"
                    title="Turn off camera"
                  >
                    <Video size={24} className="text-white" />
                  </button>
                  <button
                    onClick={() => {
                      setShowFaceTimeModal(false);
                      setActiveCall(null);
                      setIsCalling(false);
                      setCallDuration(0);
                    }}
                    className="p-5 bg-red-600 hover:bg-red-700 rounded-full transition-colors shadow-lg"
                    title="End Call"
                  >
                    <Phone size={28} className="text-white rotate-[135deg]" />
                  </button>
                  <button
                    onClick={() => {}}
                    className="p-4 bg-gray-800/80 backdrop-blur-sm hover:bg-gray-700 rounded-full transition-colors border border-gray-400/20"
                    title="Switch camera"
                  >
                    <X size={24} className="text-white rotate-180" />
                  </button>
                  <button
                    onClick={() => {}}
                    className="p-4 bg-gray-800/80 backdrop-blur-sm hover:bg-gray-700 rounded-full transition-colors border border-gray-400/20"
                    title="Full screen"
                  >
                    <Sparkles size={24} className="text-white" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* New Call Modal */}
          {showNewCallModal && (
            <NewCallModal
              isOpen={showNewCallModal}
              onClose={() => setShowNewCallModal(false)}
              contactsList={contactsList}
              loadingContacts={loadingContacts}
              fetchICloudContacts={fetchICloudContacts}
              voiceMakeCall={voiceMakeCall}
              voiceReady={voiceReady}
              onCallInitiated={(callData) => {
                setActiveCall(callData);
                setShowNewCallModal(false);
                // Don't show the old call modal for browser calls - the IncomingCallPopup handles active calls
                if (!callData.isBrowserCall) {
                  setShowCallModal(true);
                  setIsCalling(true);
                  setCallDuration(0);
                }
              }}
            />
          )}

          {/* Call Detail Modal - Shows transcript, summary, lead capture, outcome */}
          {selectedCallDetail && (
            <CallDetailModal
              call={selectedCallDetail}
              isOpen={!!selectedCallDetail}
              onClose={() => setSelectedCallDetail(null)}
              onOutcomeUpdate={(callSid, outcome) => {
                setCallOutcomes(prev => ({ ...prev, [callSid]: outcome }));
                // Refresh calls list
                fetchCalls();
              }}
              onLeadCapture={(result) => {
                console.log('Lead captured:', result);
                // Refresh calls list
                fetchCalls();
              }}
            />
          )}

          {/* Meeting Tab Content */}
          {activeTab === 'meeting' && (
            <div className="flex-1 flex flex-col bg-black" style={{ height: 'calc(100% - 57px)' }}>
              {/* Meeting Platform Selection or Active Meeting */}
              {!isMeetingActive ? (
                <div className="flex-1 overflow-y-auto">
                  <div className="max-w-4xl mx-auto p-6">
                    <div className="text-center mb-4">
                      <Video size={40} className="text-purple-400 mx-auto mb-2" />
                      <h2 className="text-xl font-bold text-white mb-1">Start a Meeting</h2>
                      <p className="text-sm text-gray-400">Connect to your favorite meeting platform</p>
                    </div>

                    {/* Platform Grid */}
                    <div className="grid grid-cols-2 gap-3 pb-4">
                      {/* Zoom */}
                      <div
                        onClick={() => {
                          setActiveMeetingPlatform('zoom');
                          setIsMeetingActive(true);
                        }}
                        className="bg-gray-900 border-2 border-gray-400/40 rounded-xl p-4 hover:border-gray-300 hover:bg-gray-800 transition-all cursor-pointer group"
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Video size={24} className="text-black" />
                          </div>
                          <h3 className="text-lg font-bold text-white">Zoom</h3>
                          <p className="text-xs text-gray-400 text-center">Connect to Zoom Meeting</p>
                          <button className="mt-1 px-3 py-1 bg-white hover:bg-gray-200 text-black text-xs rounded-lg font-medium transition-colors">
                            Connect
                          </button>
                        </div>
                      </div>

                      {/* Microsoft Teams */}
                      <div
                        onClick={() => {
                          setActiveMeetingPlatform('teams');
                          setIsMeetingActive(true);
                        }}
                        className="bg-gray-900 border-2 border-gray-400/40 rounded-xl p-4 hover:border-purple-500 hover:bg-gray-800 transition-all cursor-pointer group"
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Video size={24} className="text-white" />
                          </div>
                          <h3 className="text-lg font-bold text-white">Microsoft Teams</h3>
                          <p className="text-xs text-gray-400 text-center">Connect to Teams Meeting</p>
                          <button className="mt-1 px-3 py-1 bg-white hover:bg-gray-200 text-black text-xs rounded-lg font-medium transition-colors">
                            Connect
                          </button>
                        </div>
                      </div>

                      {/* Google Meet */}
                      <div
                        onClick={() => {
                          setActiveMeetingPlatform('meet');
                          setIsMeetingActive(true);
                        }}
                        className="bg-gray-900 border-2 border-gray-400/40 rounded-xl p-4 hover:border-green-500 hover:bg-gray-800 transition-all cursor-pointer group"
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Video size={24} className="text-white" />
                          </div>
                          <h3 className="text-lg font-bold text-white">Google Meet</h3>
                          <p className="text-xs text-gray-400 text-center">Connect to Google Meet</p>
                          <button className="mt-1 px-3 py-1 bg-white hover:bg-gray-200 text-black text-xs rounded-lg font-medium transition-colors">
                            Connect
                          </button>
                        </div>
                      </div>

                      {/* Zoho Meeting */}
                      <div
                        onClick={() => {
                          setActiveMeetingPlatform('zoho');
                          setIsMeetingActive(true);
                        }}
                        className="bg-gray-900 border-2 border-gray-400/40 rounded-xl p-4 hover:border-orange-500 hover:bg-gray-800 transition-all cursor-pointer group"
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Video size={24} className="text-white" />
                          </div>
                          <h3 className="text-lg font-bold text-white">Zoho Meeting</h3>
                          <p className="text-xs text-gray-400 text-center">Connect to Zoho Meeting</p>
                          <button className="mt-1 px-3 py-1 bg-white hover:bg-gray-200 text-black text-xs rounded-lg font-medium transition-colors">
                            Connect
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Active Meeting View */
                <div className="flex flex-col bg-black h-full overflow-hidden">
                  {/* Meeting Header */}
                  <div className="bg-gray-900 border-b border-gray-400/20 p-3 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${
                        activeMeetingPlatform === 'zoom' ? 'bg-blue-500' :
                        activeMeetingPlatform === 'teams' ? 'bg-purple-600' :
                        activeMeetingPlatform === 'meet' ? 'bg-green-500' :
                        'bg-orange-500'
                      } animate-pulse`}></div>
                      <span className="text-white font-semibold text-sm">
                        {activeMeetingPlatform === 'zoom' ? 'Zoom Meeting' :
                         activeMeetingPlatform === 'teams' ? 'Microsoft Teams Meeting' :
                         activeMeetingPlatform === 'meet' ? 'Google Meet' :
                         'Zoho Meeting'}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const meetingId = prompt("Enter Meeting ID:");
                          if (meetingId) {
                            console.log("Meeting ID:", meetingId);
                          }
                        }}
                        className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-white text-sm font-medium transition-colors flex items-center gap-2"
                      >
                        <UserPlus size={16} />
                        Meeting ID
                      </button>
                      <button
                        onClick={() => {
                          setIsMeetingActive(false);
                          setActiveMeetingPlatform(null);
                        }}
                        className="px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm font-medium transition-colors"
                      >
                        Leave Meeting
                      </button>
                    </div>
                  </div>

                  {/* Main Video Area */}
                  <div className="flex-1 relative bg-gradient-to-br from-gray-900 via-gray-800 to-black overflow-hidden">
                    {/* Large Camera Feed */}
                    <div className="absolute inset-0">
                      {!isCameraOff ? (
                        <video
                          autoPlay
                          muted
                          playsInline
                          ref={(video) => {
                            if (video && !video.srcObject) {
                              navigator.mediaDevices.getUserMedia({ video: true, audio: true })
                                .then(stream => {
                                  video.srcObject = stream;
                                  setMediaStream(stream);
                                  
                                  // Set initial audio track state
                                  const audioTrack = stream.getAudioTracks()[0];
                                  if (audioTrack) {
                                    audioTrack.enabled = !isMicMuted;
                                  }
                                })
                                .catch(err => {
                                  console.log('Camera access denied:', err);
                                });
                            }
                          }}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-900">
                          <div className="text-center">
                            <Video size={64} className="text-gray-600 mx-auto mb-4" />
                            <p className="text-gray-400">Camera is off</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Meeting Info Overlay */}
                    <div className="absolute top-3 left-3 bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-lg">
                      <p className="text-white font-medium text-sm">Meeting in Progress</p>
                      <p className="text-xs text-gray-300">
                        {isMicMuted ? '🔇 Mic Muted' : '🎤 Mic Active'} • {isCameraOff ? '📷 Camera Off' : '📹 Camera Active'}
                      </p>
                    </div>

                    {/* Participant Video Windows - Multiple People on Call */}
                    <div className="absolute top-3 right-3 grid grid-cols-2 gap-2">
                      {/* Participant 1 */}
                      <div className="w-32 h-24 bg-gray-700 border-2 border-gray-400/30 rounded-lg overflow-hidden">
                        <div className="w-full h-full relative">
                          <img
                            src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=150&fit=crop"
                            alt="Sarah Mitchell"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute bottom-1 left-1 bg-black/70 px-2 py-0.5 rounded text-xs text-white">
                            Sarah M.
                          </div>
                        </div>
                      </div>
                      
                      {/* Participant 2 */}
                      <div className="w-32 h-24 bg-gray-700 border-2 border-gray-400/30 rounded-lg overflow-hidden">
                        <div className="w-full h-full relative">
                          <img
                            src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=150&fit=crop"
                            alt="Michael Chen"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute bottom-1 left-1 bg-black/70 px-2 py-0.5 rounded text-xs text-white">
                            Michael C.
                          </div>
                        </div>
                      </div>
                      
                      {/* Participant 3 */}
                      <div className="w-32 h-24 bg-gray-700 border-2 border-gray-400/30 rounded-lg overflow-hidden">
                        <div className="w-full h-full relative">
                          <img
                            src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=150&fit=crop"
                            alt="Emily Rodriguez"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute bottom-1 left-1 bg-black/70 px-2 py-0.5 rounded text-xs text-white">
                            Emily R.
                          </div>
                        </div>
                      </div>
                      
                      {/* Participant 4 */}
                      <div className="w-32 h-24 bg-gray-700 border-2 border-gray-400/30 rounded-lg overflow-hidden">
                        <div className="w-full h-full relative">
                          <img
                            src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=150&fit=crop"
                            alt="David Park"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute bottom-1 left-1 bg-black/70 px-2 py-0.5 rounded text-xs text-white">
                            David P.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Meeting Controls */}
                  <div className="bg-gray-900 border-t border-gray-400/20 p-3 flex-shrink-0">
                    <div className="flex items-center justify-center gap-3">
                      <button 
                        onClick={() => {
                          setIsMicMuted(!isMicMuted);
                          if (mediaStream) {
                            const audioTrack = mediaStream.getAudioTracks()[0];
                            if (audioTrack) {
                              audioTrack.enabled = isMicMuted; // Toggle (opposite of current state)
                            }
                          }
                        }}
                        className={`p-3 rounded-full transition-colors ${
                          isMicMuted 
                            ? 'bg-red-600 hover:bg-red-700' 
                            : 'bg-gray-800 hover:bg-gray-700'
                        }`}
                        title={isMicMuted ? "Unmute Microphone" : "Mute Microphone"}
                      >
                        {isMicMuted ? (
                          <MicOff size={20} className="text-white" />
                        ) : (
                          <Mic size={20} className="text-white" />
                        )}
                      </button>
                      <button 
                        onClick={() => {
                          setIsCameraOff(!isCameraOff);
                          if (mediaStream) {
                            const videoTrack = mediaStream.getVideoTracks()[0];
                            if (videoTrack) {
                              videoTrack.enabled = isCameraOff; // Toggle (opposite of current state)
                            }
                          }
                        }}
                        className={`p-3 rounded-full transition-colors ${
                          isCameraOff 
                            ? 'bg-red-600 hover:bg-red-700' 
                            : 'bg-gray-800 hover:bg-gray-700'
                        }`}
                        title={isCameraOff ? "Turn Camera On" : "Turn Camera Off"}
                      >
                        {isCameraOff ? (
                          <VideoOff size={20} className="text-white" />
                        ) : (
                          <Video size={20} className="text-white" />
                        )}
                      </button>
                      <button className="p-3 bg-gray-800 hover:bg-gray-700 rounded-full transition-colors" title="Chat">
                        <MessageCircle size={20} className="text-white" />
                      </button>
                      <button className="p-3 bg-gray-800 hover:bg-gray-700 rounded-full transition-colors" title="Invite">
                        <UserPlus size={20} className="text-white" />
                      </button>
                      <button
                        onClick={() => {
                          // Stop all media tracks when leaving
                          if (mediaStream) {
                            mediaStream.getTracks().forEach(track => track.stop());
                            setMediaStream(null);
                          }
                          setIsMeetingActive(false);
                          setActiveMeetingPlatform(null);
                          setIsMicMuted(false);
                          setIsCameraOff(false);
                        }}
                        className="p-3 bg-red-600 hover:bg-red-700 rounded-full transition-colors"
                        title="End Call"
                      >
                        <Phone size={20} className="text-white rotate-[135deg]" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Compose Message Modal */}
          {showComposeModal && activeTab === 'messages' && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowComposeModal(false)}>
              <div className="bg-gray-900 border border-gray-400/20 rounded-2xl w-[600px] flex flex-col" onClick={(e) => e.stopPropagation()}>
                {/* Modal Header */}
                <div className="p-6 border-b border-gray-400/20">
                  <h2 className="text-2xl font-bold text-white">New Message</h2>
                  <p className="text-gray-400 text-sm mt-1">Compose a new text message</p>
                </div>

                {/* Compose Form */}
                <div className="p-6 space-y-4">
                  {/* To Field with Contact Search */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      To: {loadingContacts && <span className="text-purple-400 text-xs ml-2">(Loading contacts...)</span>}
                    </label>
                    <input
                      type="text"
                      value={composeRecipient}
                      onChange={async (e) => {
                        const searchValue = e.target.value;
                        setComposeRecipient(searchValue);
                        
                        // Only fetch iCloud contacts when user starts typing and they haven't been loaded yet
                        if (searchValue.trim().length >= 1 && iCloudContacts.length === 0 && !loadingContacts) {
                          console.log('🔄 Loading iCloud contacts for search...');
                          await fetchICloudContacts();
                          // After loading, the useEffect will handle filtering
                          return;
                        }
                        
                        // Show suggestions when typing and filter contacts
                        if (searchValue.trim()) {
                          setShowContactSuggestions(true);
                          
                          // Filter contacts based on search input
                          const filtered = contactsList.filter(contact => {
                            const searchLower = searchValue.toLowerCase();
                            const nameMatch = contact.name?.toLowerCase().includes(searchLower);
                            const phoneMatch = contact.phone?.includes(searchValue);
                            return nameMatch || phoneMatch;
                          });
                          
                          console.log(`🔍 Searching for "${searchValue}" - Found ${filtered.length} contacts`);
                          setFilteredContacts(filtered);
                        } else {
                          // If user clears the input, show top contacts if already loaded
                          if (contactsList.length > 0) {
                            setShowContactSuggestions(true);
                            setFilteredContacts(contactsList.slice(0, 20));
                          } else {
                            setShowContactSuggestions(false);
                            setFilteredContacts([]);
                          }
                        }
                      }}
                      onFocus={() => {
                        // Only show contacts on focus if they're already loaded and input is not empty
                        if (contactsList.length > 0 && !composeRecipient.trim()) {
                          setShowContactSuggestions(true);
                          setFilteredContacts(contactsList.slice(0, 20));
                        }
                      }}
                      placeholder="Enter contact name or phone number (e.g., +1234567890)"
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400"
                    />
                    
                    {/* Contact Suggestions Dropdown */}
                    {showContactSuggestions && filteredContacts.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-400/20 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                        {filteredContacts.map((contact, idx) => (
                          <button
                            key={idx}
                            onClick={() => {
                              setComposeRecipient(contact.phone);
                              setShowContactSuggestions(false);
                              setFilteredContacts([]);
                            }}
                            className="w-full px-4 py-3 text-left hover:bg-gray-700 transition-colors border-b border-gray-400/10 last:border-b-0"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold">
                                {contact.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '??'}
                              </div>
                              <div>
                                <div className="text-white font-medium">{contact.name}</div>
                                <div className="text-xs text-gray-400">{contact.phone}</div>
                                {contact.source === 'icloud' && (
                                  <div className="text-xs text-purple-400">iCloud Contact</div>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {/* Loading state */}
                    {showContactSuggestions && loadingContacts && (
                      <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-400/20 rounded-lg shadow-xl p-4 text-center">
                        <div className="text-gray-400">Loading iCloud contacts...</div>
                      </div>
                    )}
                    
                    {/* No contacts found */}
                    {showContactSuggestions && !loadingContacts && filteredContacts.length === 0 && composeRecipient.trim() && (
                      <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-400/20 rounded-lg shadow-xl p-4 text-center">
                        <div className="text-gray-400">No contacts found. Enter a phone number directly.</div>
                      </div>
                    )}
                  </div>

                  {/* Message Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Message:</label>
                    <textarea
                      value={composeMessage}
                      onChange={(e) => setComposeMessage(e.target.value)}
                      rows="6"
                      placeholder="Type your message..."
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
                    ></textarea>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="p-6 border-t border-gray-400/20 flex gap-3">
                  <button
                    onClick={() => {
                      setShowComposeModal(false);
                      setComposeRecipient('');
                      setComposeMessage('');
                      setFilteredContacts([]);
                      setShowContactSuggestions(false);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors text-white font-medium"
                    disabled={composeSending}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      // Validate inputs
                      if (!composeRecipient.trim()) {
                        alert('Please enter a recipient phone number');
                        return;
                      }
                      
                      if (!composeMessage.trim()) {
                        alert('Please enter a message');
                        return;
                      }
                      
                      try {
                        setComposeSending(true);
                        
                        // Send SMS
                        await apiCall('/messages/send-sms', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            phone_number: composeRecipient,
                            message: composeMessage,
                            recipient_name: composeRecipient
                          })
                        });
                        
                        console.log('✅ Message sent successfully');
                        
                        // Reset form and close modal
                        setComposeRecipient('');
                        setComposeMessage('');
                        setFilteredContacts([]);
                        setShowContactSuggestions(false);
                        setShowComposeModal(false);
                        
                        // Simple refresh - fetch messages and let the main useEffect handle grouping
                        // This prevents duplication and maintains conversation state
                        const messagesData = await apiCall('/messages');
                        setMessages(messagesData);
                        
                      } catch (error) {
                        console.error('Failed to send message:', error);
                        alert('Failed to send message: ' + error.message);
                      } finally {
                        setComposeSending(false);
                      }
                    }}
                    className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={composeSending}
                  >
                    {composeSending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        Send Message
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Email Tab Content */}
          {activeTab === 'email' && !selectedEmail && (
            <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
              {/* Email Folders Sidebar - Horizontal scroll on mobile, vertical on desktop */}
              <div className="w-full md:w-48 bg-black border-b md:border-b-0 md:border-r border-gray-400/20 flex-shrink-0">
                <div className="p-3 md:p-4">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase mb-2 md:mb-3 hidden md:block">Folders</h3>
                  <div className="flex md:flex-col gap-1 md:gap-1 overflow-x-auto md:overflow-visible pb-2 md:pb-0">
                    {emailFolders.map((folder) => {
                      // Get icon for folder
                      const getFolderIcon = (f) => {
                        const icons = {
                          'inbox': Mail,
                          'sent': Send,
                          'drafts': FileText,
                          'flagged': Flag,
                          'archive': Mail,
                          'trash': Trash2,
                          'spam': AlertTriangle
                        };
                        return icons[f] || Mail;
                      };
                      const FolderIcon = getFolderIcon(folder);
                      return (
                        <button
                          key={folder}
                          onClick={() => handleFolderChange(folder)}
                          data-testid={`email-folder-${folder}-btn`}
                          className={`flex-shrink-0 px-3 py-1.5 md:py-2 rounded-lg transition-colors flex items-center gap-1.5 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
                            activeFolder === folder
                              ? 'bg-white text-black'
                              : 'text-gray-400 hover:bg-gray-900'
                          }`}
                        >
                          <FolderIcon size={14} />
                          {formatCategoryName(folder)}
                        </button>
                      );
                    })}
                    {/* Edit Folders Button */}
                    <button
                      onClick={() => openCategoryModal('emails')}
                      data-testid="edit-email-folders-btn"
                      className="flex-shrink-0 px-2 py-1.5 md:py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap text-gray-500 hover:bg-gray-900 hover:text-gray-300 border border-dashed border-gray-700"
                      title="Edit Folders"
                    >
                      <Pencil size={12} className="inline mr-1" />
                      Edit
                    </button>
                  </div>
                </div>
              </div>

              {/* Email List and Content */}
              <div className="flex-1 flex flex-col bg-black overflow-hidden">
                {/* Email Connection Panel */}
                <div className="m-3 mb-0 bg-gradient-to-r from-purple-900/50 to-pink-900/50 border border-purple-500/30 rounded-xl p-3">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                    <div className="flex items-center gap-2 flex-1">
                      <Mail className="text-purple-400" size={18} />
                      <div>
                        <h3 className="text-sm font-bold text-white">Connect Email Accounts</h3>
                        <p className="text-gray-300 text-xs">Add multiple email accounts from Outlook, Gmail, or iCloud.</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => setShowAddEmailModal(true)}
                      className="flex-shrink-0 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-1.5 transition-all shadow-lg text-xs"
                      data-testid="add-email-account-btn"
                    >
                      <Plus size={14} />
                      Add Email
                    </button>
                  </div>
                </div>
                
                {/* Email Header with Account Tabs */}
                <div className="px-3 md:px-6 py-3 md:py-4 border-b border-gray-400/20 bg-black flex-shrink-0">
                  <div className="flex items-center justify-between mb-2 md:mb-3">
                    <div>
                      <h3 className="text-base md:text-lg font-bold text-white capitalize">{activeFolder}</h3>
                      <p className="text-[10px] md:text-xs text-gray-400">{filteredEmails.length} emails</p>
                    </div>
                    <button 
                      onClick={() => {
                        setEmailTo('');
                        setEmailCc('');
                        setEmailBcc('');
                        setEmailSubject('');
                        setEmailBody('');
                        setShowEmailComposeModal(true);
                      }}
                      className="px-3 md:px-4 py-1.5 md:py-2 bg-white hover:bg-gray-200 text-black rounded-lg text-xs md:text-sm font-medium transition-colors"
                      data-testid="email-compose-btn"
                    >
                      <Mail className="inline mr-1 md:mr-2" size={14} />
                      <span className="hidden sm:inline">Compose</span>
                      <span className="sm:hidden">New</span>
                    </button>
                  </div>
                  
                  {/* Email Account Tabs - Dynamic based on emailAccounts */}
                  <div className="flex gap-1 md:gap-2 overflow-x-auto items-center">
                    {emailAccounts.map((account) => (
                      <div key={account.id} className="flex items-center gap-0.5 group">
                        {renamingAccountId === account.id ? (
                          /* Inline rename input */
                          <div className="flex items-center gap-1 bg-gray-800 rounded-lg px-2 py-1">
                            <input
                              type="text"
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleRenameEmailAccount(account.id, renameValue);
                                } else if (e.key === 'Escape') {
                                  setRenamingAccountId(null);
                                  setRenameValue('');
                                }
                              }}
                              onBlur={() => {
                                if (renameValue.trim()) {
                                  handleRenameEmailAccount(account.id, renameValue);
                                } else {
                                  setRenamingAccountId(null);
                                  setRenameValue('');
                                }
                              }}
                              className="bg-transparent border-none outline-none text-white text-xs md:text-sm w-20 md:w-24"
                              autoFocus
                              data-testid={`rename-input-${account.id}`}
                            />
                            <button
                              onClick={() => handleRenameEmailAccount(account.id, renameValue)}
                              className="text-green-400 hover:text-green-300"
                              title="Save"
                            >
                              <Check size={12} />
                            </button>
                            <button
                              onClick={() => {
                                setRenamingAccountId(null);
                                setRenameValue('');
                              }}
                              className="text-red-400 hover:text-red-300"
                              title="Cancel"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ) : (
                          /* Normal account tab button */
                          <button
                            onClick={() => setActiveEmailAccount(account.id)}
                            onDoubleClick={() => startRenamingAccount(account.id, account.name)}
                            className={`px-2 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all whitespace-nowrap flex items-center gap-1 md:gap-2 ${
                              activeEmailAccount === account.id
                                ? account.type === 'outlook' 
                                  ? 'bg-blue-600 text-white' 
                                  : account.type === 'gmail'
                                    ? 'bg-white text-black'
                                    : account.type === 'nylas'
                                      ? 'bg-purple-600 text-white'
                                      : 'bg-gray-600 text-white'
                                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                            }`}
                            title="Double-click to rename"
                          >
                            {account.type === 'outlook' && <Mail size={12} />}
                            {account.type === 'gmail' && (
                              <svg className="w-3 h-3" viewBox="0 0 24 24">
                                <path fill="currentColor" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                              </svg>
                            )}
                            {account.type === 'nylas' && <Mail size={12} />}
                            {account.type === 'icloud' && (
                              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                              </svg>
                            )}
                            {account.name}
                            {((account.id === 'outlook' && outlookConnected) || (account.id === 'gmail' && gmailConnected) || account.connected) && (
                              <span className="w-2 h-2 bg-green-500 rounded-full" title="Connected"></span>
                            )}
                          </button>
                        )}
                        {/* Edit button - visible on hover for active account */}
                        {activeEmailAccount === account.id && renamingAccountId !== account.id && (
                          <button
                            onClick={() => startRenamingAccount(account.id, account.name)}
                            className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white"
                            title="Rename account"
                            data-testid={`rename-btn-${account.id}`}
                          >
                            <Pencil size={12} />
                          </button>
                        )}
                        {/* Sync/Connect buttons for active account */}
                        {activeEmailAccount === account.id && account.id === 'outlook' && (
                          <button
                            onClick={syncOutlookEmails}
                            disabled={outlookLoading}
                            className={`p-1.5 md:p-2 rounded-lg transition-all flex items-center justify-center ${
                              outlookLoading
                                ? 'bg-gray-700 cursor-not-allowed'
                                : outlookConnected 
                                  ? 'bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white'
                                  : 'bg-blue-600 hover:bg-blue-700 text-white'
                            }`}
                            title={outlookConnected ? "Sync emails" : "Connect account"}
                            data-testid="outlook-sync-btn"
                          >
                            {outlookLoading ? (
                              <Loader2 size={14} className="animate-spin text-blue-400" />
                            ) : outlookConnected ? (
                              <RefreshCw size={14} />
                            ) : (
                              <Link size={14} />
                            )}
                          </button>
                        )}
                        {activeEmailAccount === account.id && account.id === 'gmail' && (
                          <>
                            {gmailConnected && (
                              <button
                                onClick={() => syncGmailEmails(activeFolder)}
                                disabled={gmailLoading}
                                className={`p-1.5 md:p-2 rounded-lg transition-all ${
                                  gmailLoading 
                                    ? 'bg-gray-800 cursor-wait'
                                    : 'bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white'
                                }`}
                                title={`Sync Gmail - ${gmailEmail}`}
                                data-testid="gmail-sync-btn"
                              >
                                {gmailLoading ? (
                                  <Loader2 size={14} className="animate-spin text-blue-400" />
                                ) : (
                                  <RefreshCw size={14} />
                                )}
                              </button>
                            )}
                            <button
                              onClick={connectGmail}
                              disabled={gmailLoading}
                              className={`p-1.5 md:p-2 rounded-lg transition-all ${
                                gmailLoading 
                                  ? 'bg-gray-800 cursor-wait'
                                  : gmailConnected 
                                    ? 'bg-green-600/20 hover:bg-green-600/30 text-green-400 border border-green-600/50'
                                    : 'bg-orange-600 hover:bg-orange-700 text-white'
                              }`}
                              title={gmailConnected ? `Connected: ${gmailEmail} - Click to reconnect` : "Connect Gmail account"}
                              data-testid="gmail-connect-btn"
                            >
                              {gmailLoading ? (
                                <Loader2 size={14} className="animate-spin text-orange-400" />
                              ) : gmailConnected ? (
                                <Check size={14} />
                              ) : (
                                <Link size={14} />
                              )}
                            </button>
                          </>
                        )}
                        {/* Sync/Connect buttons for custom accounts */}
                        {activeEmailAccount === account.id && account.id !== 'outlook' && account.id !== 'gmail' && (
                          <>
                            {/* Sync button - show when connected */}
                            {account.connected && (
                              <button
                                onClick={() => {
                                  // Trigger sync for this account based on type
                                  if (account.type === 'outlook') {
                                    syncOutlookEmails();
                                  } else if (account.type === 'gmail') {
                                    syncGmailEmails(activeFolder);
                                  } else if (account.type === 'icloud') {
                                    // For iCloud, we'd need to implement iCloud email sync
                                    alert('Syncing iCloud emails...');
                                  }
                                }}
                                className="p-1.5 md:p-2 rounded-lg transition-all bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white"
                                title={`Sync ${account.name}`}
                              >
                                <RefreshCw size={14} />
                              </button>
                            )}
                            {/* Connect button - show based on type */}
                            <button
                              onClick={() => {
                                if (account.type === 'gmail') {
                                  connectGmail(account.email);
                                } else if (account.type === 'outlook') {
                                  syncOutlookEmails();
                                } else if (account.type === 'icloud') {
                                  // For iCloud with stored credentials, mark as connected
                                  const creds = localStorage.getItem(`email_creds_${account.id}`);
                                  if (creds) {
                                    setEmailAccounts(prev => prev.map(acc => 
                                      acc.id === account.id ? { ...acc, connected: true } : acc
                                    ));
                                  } else {
                                    alert('Please add credentials for this account');
                                  }
                                }
                              }}
                              className={`p-1.5 md:p-2 rounded-lg transition-all ${
                                account.connected 
                                  ? 'bg-green-600/20 hover:bg-green-600/30 text-green-400 border border-green-600/50'
                                  : account.type === 'gmail' 
                                    ? 'bg-orange-600 hover:bg-orange-700 text-white'
                                    : account.type === 'outlook'
                                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                              }`}
                              title={account.connected ? `Connected: ${account.email}` : "Connect account"}
                            >
                              {account.connected ? (
                                <Check size={14} />
                              ) : (
                                <Link size={14} />
                              )}
                            </button>
                          </>
                        )}
                        {/* Remove button for custom accounts (not default outlook/gmail) */}
                        {account.id !== 'outlook' && account.id !== 'gmail' && activeEmailAccount === account.id && (
                          <button
                            onClick={() => handleRemoveEmailAccount(account.id)}
                            className="p-1.5 rounded-lg bg-red-600/20 hover:bg-red-600/30 text-red-400 transition-all"
                            title="Remove this account"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Category Tabs */}
                <div className="flex-shrink-0 px-6 py-3 border-b border-gray-400/10 overflow-x-auto">
                  <div className="flex gap-2 items-center">
                    <button
                      onClick={() => setActiveCategory('all')}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                        activeCategory === 'all'
                          ? 'bg-white text-black'
                          : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                      }`}
                    >
                      All
                    </button>
                    {emailCategories.map((cat) => (
                      <button
                        key={cat}
                        onClick={() => setActiveCategory(cat)}
                        data-testid={`email-category-${cat}-btn`}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                          activeCategory === cat
                            ? 'bg-white text-black'
                            : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                        }`}
                      >
                        {formatCategoryName(cat)}
                      </button>
                    ))}
                    {/* Edit Categories Button */}
                    <button
                      onClick={() => openCategoryModal('emailCategories')}
                      data-testid="edit-email-categories-btn"
                      className="px-2 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap bg-gray-800/50 text-gray-500 hover:bg-gray-700 hover:text-gray-300 border border-dashed border-gray-600"
                      title="Edit Categories"
                    >
                      <Pencil size={12} className="inline mr-1" />
                      Edit
                    </button>
                  </div>
                </div>

                {/* Email Search Bar */}
                <div className="px-4 py-2 border-b border-gray-400/20">
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                    <input
                      type="text"
                      placeholder="Search emails by subject, sender, or content..."
                      value={emailSearchQuery}
                      onChange={(e) => setEmailSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-10 py-2 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                      data-testid="email-search-input"
                    />
                    {emailSearchQuery && (
                      <button
                        onClick={() => setEmailSearchQuery('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  {emailSearchQuery && (
                    <p className="text-xs text-gray-500 mt-1">
                      Found {filteredEmails.length} email{filteredEmails.length !== 1 ? 's' : ''} matching "{emailSearchQuery}"
                    </p>
                  )}
                </div>

                {/* Email List - Shows 8 rows before scrolling */}
                <div className="overflow-y-auto px-4 py-3 space-y-3" style={{ height: '720px', maxHeight: '720px' }}>
                  {paginatedEmails.map((email) => (
                    <div key={email.id} className="relative">
                      <div 
                        className="group relative bg-gray-900/50 border border-gray-700/50 rounded-xl px-5 py-4 hover:bg-gray-800/70 transition-all cursor-pointer"
                        onClick={() => handleSelectEmail(email)}
                        data-testid={`email-card-${email.id}`}
                      >
                        {/* Row 1: Sender Name + Email on left, Badges + Time + Flag on right */}
                        <div className="flex items-start justify-between mb-2">
                          <div className="min-w-0 flex-1">
                            <h4 className="text-base font-bold text-white truncate">{email.from}</h4>
                            <p className="text-sm text-gray-400 truncate">{email.email || 'no-reply@email.com'}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                            {email.hasAttachments && (
                              <Paperclip size={14} className="text-gray-400" title="Has attachments" />
                            )}
                            {email.category === 'urgent' && (
                              <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs font-semibold rounded">URGENT</span>
                            )}
                            {email.category === 'orders' && (
                              <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs font-semibold rounded">ORDERS</span>
                            )}
                            {email.category === 'meeting' && (
                              <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs font-semibold rounded">MEETING</span>
                            )}
                            {email.category === 'client' && (
                              <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-semibold rounded">CLIENT</span>
                            )}
                            {email.category === 'report' && (
                              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs font-semibold rounded">REPORT</span>
                            )}
                            {email.category === 'travel' && (
                              <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs font-semibold rounded">TRAVEL</span>
                            )}
                            {email.category === 'other' && (
                              <span className="px-2 py-0.5 bg-gray-500/20 text-gray-400 text-xs font-semibold rounded">OTHER</span>
                            )}
                            <span className="text-sm text-white">{email.time}</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleFlag(email.id);
                              }}
                              className="p-1 hover:bg-gray-700 rounded transition-colors"
                              title={flaggedEmails[email.id] || email.isFlagged ? "Unflag email" : "Flag email"}
                            >
                              <Flag 
                                size={16} 
                                className={`${flaggedEmails[email.id] || email.isFlagged ? 'text-yellow-500 fill-yellow-500' : 'text-gray-400'}`}
                              />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenEmailDropdown(openEmailDropdown === `list-${email.id}` ? null : `list-${email.id}`);
                              }}
                              className="p-1 hover:bg-gray-700 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                            >
                              <MoreVertical size={16} className="text-gray-400" />
                            </button>
                          </div>
                        </div>
                        
                        {/* Row 2: Subject Line (bold, white, larger) */}
                        <h3 className="text-lg font-bold text-white truncate mb-1">{email.subject}</h3>
                        
                        {/* Row 3: Preview Text (gray, truncated) */}
                        <p className="text-sm text-gray-400 truncate">{email.preview}</p>
                      </div>
                      
                      {/* Dropdown Menu for Email List Item */}
                      {openEmailDropdown === `list-${email.id}` && (
                        <div className="absolute right-4 top-16 w-48 bg-gray-900 border border-gray-400/20 rounded-lg shadow-xl z-10">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReplyEmail(email);
                            }}
                            className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                          >
                            <Reply size={16} className="text-purple-400" />
                            Reply
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddToMeeting({ name: email.from, phone: email.email, id: email.id });
                            }}
                            className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                          >
                            <Calendar size={16} className="text-purple-400" />
                            Add Meeting
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNewMeeting({ name: email.from, phone: email.email, id: email.id });
                            }}
                            className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                          >
                            <CalendarPlus size={16} className="text-purple-400" />
                            New Meeting
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenEmailDropdown(null);
                            }}
                            className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                          >
                            <UserCheck size={16} className="text-purple-400" />
                            Add Contact
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenEmailDropdown(null);
                            }}
                            className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                          >
                            <UserPlus size={16} className="text-purple-400" />
                            New Contact
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteEmail(email.id);
                              setOpenEmailDropdown(null);
                            }}
                            className="w-full text-left px-4 py-2.5 hover:bg-red-900 text-sm text-red-400 flex items-center gap-2"
                          >
                            <Trash2 size={16} className="text-red-400" />
                            Delete Email
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Email Pagination */}
                {totalEmailPages > 1 && (
                  <div className="flex items-center justify-between px-6 py-3 border-t border-gray-400/20 bg-gray-900/50">
                    <div className="text-sm text-gray-400">
                      Showing {((emailCurrentPage - 1) * emailsPerPage) + 1}-{Math.min(emailCurrentPage * emailsPerPage, filteredEmails.length)} of {filteredEmails.length} emails
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setEmailCurrentPage(1)}
                        disabled={emailCurrentPage === 1}
                        className={`px-2 py-1 rounded text-sm ${emailCurrentPage === 1 ? 'text-gray-600 cursor-not-allowed' : 'text-gray-400 hover:bg-gray-800'}`}
                      >
                        First
                      </button>
                      <button
                        onClick={() => setEmailCurrentPage(p => Math.max(1, p - 1))}
                        disabled={emailCurrentPage === 1}
                        className={`px-3 py-1 rounded text-sm ${emailCurrentPage === 1 ? 'text-gray-600 cursor-not-allowed' : 'text-gray-400 hover:bg-gray-800'}`}
                      >
                        ← Prev
                      </button>
                      <span className="px-3 py-1 bg-gray-800 rounded text-sm text-white">
                        {emailCurrentPage} / {totalEmailPages}
                      </span>
                      <button
                        onClick={() => setEmailCurrentPage(p => Math.min(totalEmailPages, p + 1))}
                        disabled={emailCurrentPage === totalEmailPages}
                        className={`px-3 py-1 rounded text-sm ${emailCurrentPage === totalEmailPages ? 'text-gray-600 cursor-not-allowed' : 'text-gray-400 hover:bg-gray-800'}`}
                      >
                        Next →
                      </button>
                      <button
                        onClick={() => setEmailCurrentPage(totalEmailPages)}
                        disabled={emailCurrentPage === totalEmailPages}
                        className={`px-2 py-1 rounded text-sm ${emailCurrentPage === totalEmailPages ? 'text-gray-600 cursor-not-allowed' : 'text-gray-400 hover:bg-gray-800'}`}
                      >
                        Last
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Email Thread View */}
          {activeTab === 'email' && selectedEmail && (
            <div className="flex-1 flex flex-col bg-black overflow-hidden">
              {/* Thread Header */}
              <div className="px-6 py-4 border-b border-gray-400/20 bg-black flex-shrink-0">
                <div className="flex items-center gap-4">
                  <button 
                    onClick={() => setSelectedEmail(null)}
                    className="p-2 hover:bg-gray-900 rounded-full transition-colors"
                  >
                    <X size={20} className="text-gray-400" />
                  </button>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white">{selectedEmail.subject}</h3>
                    <p className="text-xs text-gray-400">{selectedEmail.thread?.length || 1} message{(selectedEmail.thread?.length || 1) > 1 ? 's' : ''} in thread</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => {
                        const lastMsg = selectedEmail.thread?.[selectedEmail.thread.length - 1];
                        const senderEmail = selectedEmail.email || lastMsg?.email || '';
                        const senderName = selectedEmail.from || lastMsg?.from || '';
                        const originalBody = lastMsg?.body || lastMsg?.parsedBody || '';
                        setEmailTo(senderEmail);
                        setEmailCc('');
                        setEmailBcc('');
                        setEmailSubject(selectedEmail.subject?.startsWith('RE:') ? selectedEmail.subject : `RE: ${selectedEmail.subject}`);
                        setEmailBody(`\n\n--- Original Message ---\nFrom: ${senderName} <${senderEmail}>\nSubject: ${selectedEmail.subject}\n\n${originalBody}`);
                        setComposeAttachments([]);
                        setShowEmailComposeModal(true);
                      }}
                      className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
                      data-testid="email-reply-btn"
                    >
                      <Reply size={14} />
                      Reply
                    </button>
                    <button 
                      onClick={() => {
                        const lastMsg = selectedEmail.thread?.[selectedEmail.thread.length - 1];
                        const senderEmail = selectedEmail.email || lastMsg?.email || '';
                        const senderName = selectedEmail.from || lastMsg?.from || '';
                        const originalBody = lastMsg?.body || lastMsg?.parsedBody || '';
                        // Collect all unique email addresses from the thread
                        const allEmails = new Set();
                        selectedEmail.thread?.forEach(msg => {
                          if (msg.email) allEmails.add(msg.email);
                        });
                        if (selectedEmail.email) allEmails.add(selectedEmail.email);
                        // Remove own email and set To as sender, CC as others
                        allEmails.delete(senderEmail);
                        const ccList = Array.from(allEmails).filter(e => 
                          !e.toLowerCase().includes('creatrhq') && !e.toLowerCase().includes('you')
                        );
                        setEmailTo(senderEmail);
                        setEmailCc(ccList.join(', '));
                        setEmailBcc('');
                        setEmailSubject(selectedEmail.subject?.startsWith('RE:') ? selectedEmail.subject : `RE: ${selectedEmail.subject}`);
                        setEmailBody(`\n\n--- Original Message ---\nFrom: ${senderName} <${senderEmail}>\nSubject: ${selectedEmail.subject}\n\n${originalBody}`);
                        setComposeAttachments([]);
                        setShowEmailComposeModal(true);
                      }}
                      className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors"
                      data-testid="email-reply-all-btn"
                    >
                      Reply All
                    </button>
                    <button 
                      onClick={() => {
                        const lastMsg = selectedEmail.thread?.[selectedEmail.thread.length - 1];
                        const senderEmail = selectedEmail.email || lastMsg?.email || '';
                        const senderName = selectedEmail.from || lastMsg?.from || '';
                        const originalBody = lastMsg?.body || lastMsg?.parsedBody || '';
                        setEmailTo('');
                        setEmailCc('');
                        setEmailBcc('');
                        setEmailSubject(selectedEmail.subject?.startsWith('FWD:') ? selectedEmail.subject : `FWD: ${selectedEmail.subject}`);
                        setEmailBody(`\n\n--- Forwarded Message ---\nFrom: ${senderName} <${senderEmail}>\nSubject: ${selectedEmail.subject}\n\n${originalBody}`);
                        setComposeAttachments([]);
                        setShowEmailComposeModal(true);
                      }}
                      className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors"
                      data-testid="email-forward-btn"
                    >
                      Forward
                    </button>
                  </div>
                </div>
              </div>

              {/* Thread Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {selectedEmail.thread?.flatMap((msg, msgIdx) => {
                  // Parse the message body to extract quoted messages
                  const parsedMessages = parseEmailThread(msg.body, msg.from, msg.email, msg.time);
                  
                  return parsedMessages.map((parsedMsg, partIdx) => {
                    // Determine if this is a sent message (from "You")
                    const isSentByMe = parsedMsg.from?.toLowerCase() === 'you' || 
                                       parsedMsg.from?.toLowerCase()?.includes('creatrhq') ||
                                       parsedMsg.email?.toLowerCase()?.includes('creatrhq') ||
                                       (partIdx === 0 && (msg.from?.toLowerCase()?.includes('creatrhq') || msg.email?.toLowerCase()?.includes('creatrhq')));
                    
                    const uniqueKey = `${msg.id || msgIdx}-${partIdx}`;
                    
                    return (
                      <div 
                        key={uniqueKey} 
                        className={`rounded-xl p-5 relative group ${
                          isSentByMe 
                            ? 'bg-purple-900/20 border border-purple-500/30' 
                            : parsedMsg.isQuoted
                              ? 'bg-gray-800/30 border border-gray-600/30'
                              : 'bg-gray-900/50 border border-gray-700/50'
                        }`}
                      >
                        {/* Quoted indicator */}
                        {parsedMsg.isQuoted && (
                          <div className="absolute left-0 top-0 bottom-0 w-1 bg-gray-500 rounded-l-xl"></div>
                        )}
                        
                        {/* Message Header - Sender and Time */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <h4 className={`text-base font-bold ${isSentByMe ? 'text-purple-300' : 'text-white'}`}>
                              {isSentByMe ? 'You' : (parsedMsg.from || msg.from)}
                            </h4>
                            {!isSentByMe && (parsedMsg.email || msg.email) && (
                              <span className="text-sm text-gray-500">{parsedMsg.email || msg.email}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-400">{parsedMsg.time || msg.time}</span>
                            {partIdx === 0 && (
                              <button
                                onClick={() => setOpenEmailDropdown(openEmailDropdown === uniqueKey ? null : uniqueKey)}
                                className="p-1 hover:bg-gray-700 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <MoreVertical size={16} className="text-gray-400" />
                              </button>
                            )}
                            
                            {/* Dropdown Menu for Email Thread Message */}
                            {partIdx === 0 && openEmailDropdown === uniqueKey && (
                              <div className="absolute right-4 top-12 w-52 bg-gray-900 border border-gray-400/20 rounded-lg shadow-xl z-20">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddToMeetingWithAttachments(msg, msg.attachments?.filter(a => !a.isInline) || []);
                                  }}
                                  className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10 rounded-t-lg"
                                >
                                  <Calendar size={16} className="text-purple-400" />
                                  Add to Meeting
                                  {msg.attachments?.filter(a => !a.isInline).length > 0 && (
                                    <span className="ml-auto text-xs text-gray-500">+{msg.attachments.filter(a => !a.isInline).length} file{msg.attachments.filter(a => !a.isInline).length > 1 ? 's' : ''}</span>
                                  )}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleNewMeetingWithAttachments(msg, msg.attachments?.filter(a => !a.isInline) || []);
                                  }}
                                  className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                                >
                                  <CalendarPlus size={16} className="text-purple-400" />
                                  New Meeting
                                  {msg.attachments?.filter(a => !a.isInline).length > 0 && (
                                    <span className="ml-auto text-xs text-gray-500">+{msg.attachments.filter(a => !a.isInline).length} file{msg.attachments.filter(a => !a.isInline).length > 1 ? 's' : ''}</span>
                                  )}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddContactFromEmail(msg);
                                  }}
                                  className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                                >
                                  <UserCheck size={16} className="text-purple-400" />
                                  Add Contact
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Open add contact modal with empty fields
                                    setNewContactName('');
                                    setNewContactPhone('');
                                    setNewContactEmail(msg.email || '');
                                    setNewContactCompany('');
                                    setNewContactRole('');
                                    setNewContactCategory('Personal');
                                    setNewContactNotes('');
                                    setOriginalConversationName('');
                                    setShowAddContactModal(true);
                                    setOpenEmailDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                                >
                                  <UserPlus size={16} className="text-purple-400" />
                                  New Contact
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddToFlight(msg);
                                    setOpenEmailDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                                  data-testid="add-to-flight-btn"
                                >
                                  <Plane size={16} className="text-cyan-400" />
                                  Add to Flights
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddToFinances(msg);
                                    setOpenEmailDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                                  data-testid="add-to-finances-btn"
                                >
                                  <DollarSign size={16} className="text-green-400" />
                                  Add to Finances
                                </button>
                                {/* Add to Documents - only show if there are attachments */}
                                {msg.attachments?.filter(a => !a.isInline).length > 0 && (
                                  <>
                                    <div className="border-t border-gray-600 my-1"></div>
                                    {msg.attachments.filter(a => !a.isInline).map((att, attIdx) => (
                                      <button
                                        key={att.id || attIdx}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleAddAttachmentToDocuments(msg.id, att);
                                          setOpenEmailDropdown(null);
                                        }}
                                        className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2"
                                      >
                                        <FolderPlus size={16} className="text-blue-400" />
                                        <span className="truncate flex-1">Add &quot;{att.name || att.filename}&quot; to Docs</span>
                                      </button>
                                    ))}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      
                      {/* To/Cc Recipients - Only show for sent messages on first part */}
                      {partIdx === 0 && isSentByMe && msg.to && msg.to.length > 0 && (
                        <p className="text-sm text-gray-500 mb-3">
                          <span className="text-gray-400">To:</span> {Array.isArray(msg.to) ? msg.to.join(', ') : msg.to}
                        </p>
                      )}
                      
                      {/* Attachments Section - Only show on first part */}
                      {partIdx === 0 && msg.attachments && msg.attachments.filter(a => !a.isInline).length > 0 && (
                        <div className="mb-4 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-gray-400">
                              {msg.attachments.filter(a => !a.isInline).length} Attachment{msg.attachments.filter(a => !a.isInline).length > 1 ? 's' : ''}
                            </span>
                            {msg.attachments.filter(a => !a.isInline).length > 1 && (
                              <button 
                                onClick={() => msg.attachments.filter(a => !a.isInline).forEach(att => handleDownloadAttachment(msg.id, att))}
                                className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                              >
                                <Download size={12} />
                                Download all
                              </button>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {msg.attachments.filter(a => !a.isInline).map((att, attIdx) => (
                              <button
                                key={att.id || attIdx}
                                onClick={() => handleDownloadAttachment(msg.id, att)}
                                className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                              >
                                <FileText size={16} className="text-blue-400" />
                                <div className="text-left">
                                  <p className="text-xs font-medium text-white truncate max-w-[150px]">{att.name || att.filename || 'Attachment'}</p>
                                  <p className="text-xs text-gray-400">{formatFileSize(att.size)}</p>
                                </div>
                                <Download size={14} className="text-gray-400" />
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Email Body */}
                      <div className="email-body-content">
                        {(parsedMsg.body && parsedMsg.body.includes('<')) ? (
                          <div 
                            className="text-sm text-gray-200 prose prose-invert prose-sm max-w-none 
                            [&_a]:text-blue-400 [&_a]:underline 
                            [&_table]:border-collapse [&_table]:w-full
                            [&_td]:border [&_td]:border-gray-700 [&_td]:p-2 [&_td]:text-white
                            [&_th]:border [&_th]:border-gray-700 [&_th]:p-2 [&_th]:bg-gray-800 [&_th]:text-white
                            [&_img]:max-w-full [&_img]:h-auto
                            [&_p]:mb-2 [&_p]:text-white [&_br]:block
                            [&_div]:mb-1 [&_div]:text-white
                            [&_span]:text-white [&_li]:text-white [&_ul]:text-white [&_ol]:text-white
                            [&_b]:text-white [&_strong]:text-white [&_i]:text-white [&_em]:text-white
                            [&_h1]:text-white [&_h2]:text-white [&_h3]:text-white [&_h4]:text-white [&_h5]:text-white [&_h6]:text-white"
                          dangerouslySetInnerHTML={{ 
                            __html: parsedMsg.body
                              // Remove style tags and their content
                              .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
                              // Remove potentially dangerous scripts
                              .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
                              // Clean up common email artifacts
                              .replace(/<!--[\s\S]*?-->/g, '')
                              // Remove head and html tags but keep body content
                              .replace(/<html[^>]*>/gi, '')
                              .replace(/<\/html>/gi, '')
                              .replace(/<head>[\s\S]*?<\/head>/gi, '')
                              .replace(/<body[^>]*>/gi, '')
                              .replace(/<\/body>/gi, '')
                              .replace(/<meta[^>]*>/gi, '')
                              .replace(/<title[^>]*>[\s\S]*?<\/title>/gi, '')
                              // Override inline color styles to white
                              .replace(/color:\s*rgb\s*\(\s*0\s*,\s*0\s*,\s*0\s*\)/gi, 'color: rgb(255, 255, 255)')
                              .replace(/color:\s*#000000/gi, 'color: #ffffff')
                              .replace(/color:\s*#000/gi, 'color: #fff')
                              .replace(/color:\s*black/gi, 'color: white')
                              .replace(/color:\s*rgb\s*\(\s*\d{1,2}\s*,\s*\d{1,2}\s*,\s*\d{1,2}\s*\)/gi, 'color: rgb(255, 255, 255)')
                          }} 
                        />
                      ) : (
                        <p className="text-sm text-gray-200 whitespace-pre-wrap leading-relaxed">{parsedMsg.body}</p>
                      )}
                    </div>

                    {/* Dropdown Menu - only show on first part */}
                    {partIdx === 0 && openEmailDropdown === uniqueKey && (
                      <div className="absolute right-4 top-12 w-48 bg-gray-900 border border-gray-400/20 rounded-lg shadow-xl z-10">
                        <button
                          onClick={() => {
                            handleReplyEmail(msg);
                          }}
                          className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                        >
                          <Reply size={16} className="text-purple-400" />
                          Reply
                        </button>
                        <button
                          onClick={() => {
                            handleAddToMeeting({ name: selectedEmail.from, phone: selectedEmail.email, id: selectedEmail.id });
                          }}
                          className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                        >
                          <Calendar size={16} className="text-purple-400" />
                          Add Meeting
                        </button>
                        <button
                          onClick={() => {
                            setOpenEmailDropdown(null);
                          }}
                          className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
                        >
                          <UserCheck size={16} className="text-purple-400" />
                          Add Contact
                        </button>
                        <button
                          onClick={() => {
                            setOpenEmailDropdown(null);
                          }}
                          className="w-full text-left px-4 py-2.5 hover:bg-red-900 text-sm text-red-400 flex items-center gap-2"
                        >
                          <Trash2 size={16} className="text-red-400" />
                          Delete Message
                        </button>
                      </div>
                    )}
                  </div>
                    );
                  });
                })}
              </div>

              {/* Reply Input */}
              <div className="px-6 py-4 border-t border-gray-400/20 bg-black flex-shrink-0">
                {showAIDraftEmail && aiDraftEmailText && (
                  <div className="bg-purple-900/30 border border-purple-500/50 rounded-lg p-3 mb-3">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Sparkles size={16} className="text-purple-400" />
                        <span className="text-xs font-semibold text-purple-300">AI Suggested Reply</span>
                      </div>
                      <button onClick={() => { setShowAIDraftEmail(false); setAiDraftEmailText(''); }} className="text-gray-400 hover:text-white">
                        <X size={14} />
                      </button>
                    </div>
                    <p className="text-sm text-white mb-2 whitespace-pre-wrap">{aiDraftEmailText}</p>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => {
                          setEmailReplyInput(aiDraftEmailText);
                          setShowAIDraftEmail(false);
                          setAiDraftEmailText('');
                        }}
                        className="text-xs bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded"
                      >
                        Use This Draft
                      </button>
                      <button 
                        onClick={async () => {
                          if (!selectedEmail || generatingAIReply) return;
                          setGeneratingAIReply(true);
                          try {
                            const lastMessage = selectedEmail.thread?.[selectedEmail.thread.length - 1] || selectedEmail;
                            const emailBody = lastMessage.body || selectedEmail.body || selectedEmail.preview || '';
                            const result = await aiEmailAPI.generateEmailReply({
                              email_id: String(lastMessage.id || selectedEmail.id || 'unknown'),
                              subject: selectedEmail.subject || 'No Subject',
                              sender: lastMessage.email || selectedEmail.email || 'unknown@email.com',
                              sender_name: lastMessage.from || selectedEmail.from || 'Unknown Sender',
                              body: emailBody,
                              reply_tone: 'professional'
                            });
                            if (result.reply) {
                              setAiDraftEmailText(result.reply);
                            }
                          } catch (err) {
                            console.error('Failed to regenerate AI reply:', err);
                          } finally {
                            setGeneratingAIReply(false);
                          }
                        }}
                        disabled={generatingAIReply}
                        className="text-xs bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded flex items-center gap-1"
                        title="Generate new draft"
                      >
                        <RefreshCw size={12} className={generatingAIReply ? 'animate-spin' : ''} />
                        {generatingAIReply ? 'Generating...' : 'Refresh'}
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <button className="p-2 hover:bg-gray-900 rounded-full transition-colors mt-1">
                    <Paperclip size={20} className="text-gray-400" />
                  </button>
                  <button 
                    onClick={() => setShowScheduleModal(true)}
                    className="p-2 hover:bg-gray-900 rounded-full transition-colors mt-1"
                    title="Schedule email"
                  >
                    <Clock size={20} className="text-gray-400" />
                  </button>
                  <button 
                    onClick={async () => {
                      if (!selectedEmail) return;
                      setGeneratingAIReply(true);
                      try {
                        // Get the last message in the thread to reply to
                        const lastMessage = selectedEmail.thread?.[selectedEmail.thread.length - 1] || selectedEmail;
                        const emailBody = lastMessage.body || selectedEmail.body || selectedEmail.preview || '';
                        const result = await aiEmailAPI.generateEmailReply({
                          email_id: String(lastMessage.id || selectedEmail.id || 'unknown'),
                          subject: selectedEmail.subject || 'No Subject',
                          sender: lastMessage.email || selectedEmail.email || 'unknown@email.com',
                          sender_name: lastMessage.from || selectedEmail.from || 'Unknown Sender',
                          body: emailBody,
                          reply_tone: 'professional'
                        });
                        if (result.reply) {
                          setAiDraftEmailText(result.reply);
                          setShowAIDraftEmail(true);
                        }
                      } catch (err) {
                        console.error('Failed to generate AI reply:', err);
                        // Fallback to static text
                        setAiDraftEmailText('Thank you for your email. I appreciate the detailed information. I\'d like to schedule a meeting to discuss this further. Would Tuesday at 2 PM work for your team?');
                        setShowAIDraftEmail(true);
                      } finally {
                        setGeneratingAIReply(false);
                      }
                    }}
                    disabled={generatingAIReply}
                    className="p-2 hover:bg-gray-900 rounded-full transition-colors mt-1"
                    title="Generate AI Reply"
                    data-testid="email-ai-reply-btn"
                  >
                    {generatingAIReply ? (
                      <Loader2 size={20} className="text-purple-400 animate-spin" />
                    ) : (
                      <Sparkles size={20} className="text-purple-400" />
                    )}
                  </button>
                  <textarea
                    value={emailReplyInput}
                    onChange={(e) => setEmailReplyInput(e.target.value)}
                    placeholder="Type your reply..."
                    rows="4"
                    onInput={(e) => {
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 300) + 'px';
                    }}
                    className="flex-1 px-4 py-3 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none overflow-y-auto"
                    style={{ minHeight: '120px', maxHeight: '300px' }}
                  />
                  <button 
                    onClick={handleSendEmailReply}
                    disabled={sendingEmailReply || !emailReplyInput.trim()}
                    className={`p-2.5 rounded-full transition-colors self-end ${
                      sendingEmailReply || !emailReplyInput.trim()
                        ? 'bg-gray-700 cursor-not-allowed'
                        : 'bg-purple-600 hover:bg-purple-700'
                    }`}
                    title="Send reply"
                    data-testid="email-send-reply-btn"
                  >
                    {sendingEmailReply ? (
                      <Loader2 size={18} className="text-white animate-spin" />
                    ) : (
                      <Send size={18} className="text-white" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

 

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-black border border-gray-400 rounded-xl w-full max-w-md mx-4 overflow-hidden hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
            <div className="px-6 py-4 border-b border-gray-400/20 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Schedule {activeTab === 'messages' ? 'Message' : 'Email'}</h3>
              <button 
                onClick={() => setShowScheduleModal(false)}
                className="p-2 hover:bg-gray-900 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Message Preview */}
              {messageInput && (
                <div className="p-3 bg-gray-800 rounded-lg border border-gray-600">
                  <p className="text-xs text-gray-400 mb-1">Message to schedule:</p>
                  <p className="text-sm text-white">{messageInput.substring(0, 100)}{messageInput.length > 100 ? '...' : ''}</p>
                </div>
              )}
              
              {/* Recipient */}
              {selectedConversation && (
                <div className="flex items-center gap-2 p-3 bg-gray-800/50 rounded-lg">
                  <img src={selectedConversation.avatar} alt="" className="w-8 h-8 rounded-full" />
                  <div>
                    <p className="text-sm text-white font-medium">{selectedConversation.name}</p>
                    <p className="text-xs text-gray-400">{selectedConversation.phone}</p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Date</label>
                <input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Time</label>
                <input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="w-full px-4 py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
              </div>
              
              {/* Scheduled Messages List */}
              {scheduledMessages.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs text-gray-400 mb-2">Scheduled Messages ({scheduledMessages.length})</p>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {scheduledMessages.map((sm, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-800/50 rounded text-xs">
                        <div>
                          <p className="text-white">{sm.to}</p>
                          <p className="text-gray-400">{new Date(sm.scheduledFor).toLocaleString()}</p>
                        </div>
                        <button 
                          onClick={() => setScheduledMessages(prev => prev.filter((_, i) => i !== idx))}
                          className="text-red-400 hover:text-red-300"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-400/20 flex items-center justify-end gap-3">
              <button 
                onClick={() => setShowScheduleModal(false)}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={async () => {
                  if (!scheduleDate || !scheduleTime) {
                    alert('Please select both date and time');
                    return;
                  }
                  if (!messageInput.trim()) {
                    alert('Please enter a message to schedule');
                    return;
                  }
                  if (!selectedConversation) {
                    alert('Please select a conversation first');
                    return;
                  }
                  
                  const scheduledFor = new Date(`${scheduleDate}T${scheduleTime}`);
                  if (scheduledFor <= new Date()) {
                    alert('Please select a future date and time');
                    return;
                  }
                  
                  // Create scheduled message object
                  const newScheduledMessage = {
                    id: `scheduled-${Date.now()}`,
                    to: selectedConversation.name,
                    phone: selectedConversation.phone,
                    message: messageInput,
                    scheduledFor: scheduledFor.toISOString(),
                    status: 'pending'
                  };
                  
                  // Save to backend
                  try {
                    await apiCall('/messages/schedule', {
                      method: 'POST',
                      body: JSON.stringify({
                        to_phone: selectedConversation.phone,
                        message: messageInput,
                        scheduled_for: scheduledFor.toISOString(),
                        conversation_id: selectedConversation.id
                      })
                    });
                    
                    // Add to local state
                    setScheduledMessages(prev => [...prev, newScheduledMessage]);
                    
                    // Show the scheduled messages tracker popup
                    setShowScheduledTracker(true);
                    
                    // Clear inputs
                    setShowScheduleModal(false);
                    setScheduleDate('');
                    setScheduleTime('');
                    setMessageInput('');
                  } catch (error) {
                    console.error('Failed to schedule message:', error);
                    // Still add locally even if backend fails
                    setScheduledMessages(prev => [...prev, newScheduledMessage]);
                    
                    // Show the scheduled messages tracker popup
                    setShowScheduledTracker(true);
                    
                    setShowScheduleModal(false);
                    setScheduleDate('');
                    setScheduleTime('');
                    setMessageInput('');
                  }
                }}
                disabled={!scheduleDate || !scheduleTime || !messageInput.trim()}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Clock size={16} />
                Schedule
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scheduled Messages Tracker Popup */}
      {showScheduledTracker && scheduledMessages.length > 0 && (
        <div className="fixed bottom-4 right-4 z-50 w-80 bg-black border border-purple-500/50 rounded-xl shadow-[0_0_30px_rgba(168,85,247,0.3)] overflow-hidden">
          <div className="px-4 py-3 bg-purple-600/20 border-b border-purple-500/30 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-purple-400" />
              <h3 className="text-sm font-bold text-white">Scheduled Messages</h3>
              <span className="px-1.5 py-0.5 bg-purple-600 text-white text-[10px] font-bold rounded-full">
                {scheduledMessages.filter(m => m.status === 'pending').length}
              </span>
            </div>
            <button 
              onClick={() => setShowScheduledTracker(false)}
              className="p-1 hover:bg-white/10 rounded-full transition-colors"
            >
              <X size={16} className="text-gray-400" />
            </button>
          </div>
          
          <div className="max-h-64 overflow-y-auto">
            {scheduledMessages.filter(m => m.status === 'pending').map((msg, idx) => {
              const scheduledTime = new Date(msg.scheduledFor);
              const now = new Date();
              const timeDiff = scheduledTime - now;
              const isExpired = timeDiff <= 0;
              
              // Calculate countdown
              const hours = Math.floor(timeDiff / (1000 * 60 * 60));
              const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
              const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
              
              return (
                <div key={msg.id || idx} className="p-3 border-b border-gray-800 last:border-b-0 hover:bg-white/5">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">{msg.to}</p>
                      <p className="text-xs text-gray-400 truncate">{msg.message?.substring(0, 50)}{msg.message?.length > 50 ? '...' : ''}</p>
                    </div>
                    <button 
                      onClick={() => {
                        setScheduledMessages(prev => prev.filter(m => m.id !== msg.id));
                        if (scheduledMessages.filter(m => m.status === 'pending').length <= 1) {
                          setShowScheduledTracker(false);
                        }
                      }}
                      className="p-1 text-gray-500 hover:text-red-400 transition-colors"
                      title="Cancel scheduled message"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  
                  {/* Countdown Timer */}
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] text-gray-500">
                      {scheduledTime.toLocaleString()}
                    </p>
                    {isExpired ? (
                      <span className="px-2 py-1 bg-green-600/20 text-green-400 text-[10px] font-semibold rounded flex items-center gap-1">
                        <CheckCircle size={10} />
                        Sending...
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-purple-600/20 text-purple-300 text-[10px] font-mono font-semibold rounded">
                        {hours > 0 ? `${hours}h ` : ''}{minutes}m {seconds}s
                      </span>
                    )}
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mt-2 w-full bg-gray-800 rounded-full h-1 overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-1000 ${isExpired ? 'bg-green-500' : 'bg-purple-500'}`}
                      style={{ 
                        width: isExpired ? '100%' : `${Math.max(0, 100 - (timeDiff / (1000 * 60 * 60 * 24)) * 100)}%`
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          
          {scheduledMessages.filter(m => m.status === 'pending').length === 0 && (
            <div className="p-4 text-center">
              <CheckCircle size={24} className="mx-auto mb-2 text-green-400" />
              <p className="text-sm text-gray-400">All messages sent!</p>
            </div>
          )}
        </div>
      )}

      {/* Email Compose Modal */}
      {showEmailComposeModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowEmailComposeModal(false)}>
          <div 
            className="bg-black border border-gray-400 rounded-xl w-full max-w-2xl mx-4 overflow-hidden hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-400/20 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">New Email</h3>
                <p className="text-gray-400 text-sm mt-1">
                  Compose a new email from {activeEmailAccount === 'gmail' ? gmailEmail : (outlookConnected ? 'darren@twenty8holdings.com' : 'your account')}
                </p>
              </div>
              <button 
                onClick={() => setShowEmailComposeModal(false)}
                className="p-2 hover:bg-gray-900 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* To Field */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">To:</label>
                <input
                  type="email"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="recipient@example.com"
                  className="w-full px-4 py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400"
                  data-testid="email-compose-to"
                />
              </div>

              {/* Cc Field */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Cc: (optional)</label>
                <input
                  type="email"
                  value={emailCc}
                  onChange={(e) => setEmailCc(e.target.value)}
                  placeholder="cc@example.com"
                  className="w-full px-4 py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400"
                  data-testid="email-compose-cc"
                />
              </div>

              {/* Bcc Field */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Bcc: (optional)</label>
                <input
                  type="email"
                  value={emailBcc}
                  onChange={(e) => setEmailBcc(e.target.value)}
                  placeholder="bcc@example.com"
                  className="w-full px-4 py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400"
                  data-testid="email-compose-bcc"
                />
              </div>

              {/* Subject Field */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Subject:</label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Email subject"
                  className="w-full px-4 py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400"
                  data-testid="email-compose-subject"
                />
              </div>

              {/* Body Field */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Message:</label>
                <textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  rows="8"
                  placeholder="Type your message..."
                  className="w-full px-4 py-2.5 bg-gray-900 border border-gray-400/20 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
                  data-testid="email-compose-body"
                />
              </div>

              {/* Attached files */}
              {composeAttachments.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {composeAttachments.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm">
                      <Paperclip size={14} className="text-purple-400" />
                      <span className="text-gray-300 max-w-[150px] truncate">{file.name}</span>
                      <button
                        onClick={() => setComposeAttachments(prev => prev.filter((_, i) => i !== idx))}
                        className="text-gray-500 hover:text-red-400 transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-400/20 flex items-center justify-between">
              <div className="flex items-center gap-1">
                <label className="p-2 hover:bg-gray-900 rounded-full transition-colors cursor-pointer" title="Attach file">
                  <Paperclip size={20} className={composeAttachments.length > 0 ? 'text-purple-400' : 'text-gray-400'} />
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    data-testid="compose-attach-input"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setComposeAttachments(prev => [...prev, ...files]);
                      e.target.value = '';
                    }}
                  />
                </label>
                <button
                  onClick={() => setShowScheduleModal(true)}
                  className="p-2 hover:bg-gray-900 rounded-full transition-colors"
                  title="Schedule email"
                  data-testid="compose-schedule-btn"
                >
                  <Clock size={20} className="text-gray-400" />
                </button>
                <button
                  onClick={async () => {
                    if (!emailTo.trim() || !emailSubject.trim()) {
                      alert('Please enter recipient and subject first for AI to generate a draft.');
                      return;
                    }
                    setGeneratingComposeDraft(true);
                    try {
                      const result = await aiEmailAPI.generateEmailReply({
                        email_id: 'compose-new',
                        subject: emailSubject,
                        sender: emailTo,
                        sender_name: emailTo.split('@')[0],
                        body: `Write a professional email about: ${emailSubject}`,
                        reply_tone: 'professional'
                      });
                      if (result.reply) {
                        setEmailBody(result.reply);
                      }
                    } catch (err) {
                      console.error('Failed to generate AI draft:', err);
                      setEmailBody(`Dear ${emailTo.split('@')[0]},\n\nRegarding: ${emailSubject}\n\nI wanted to reach out to discuss this matter further. Please let me know a convenient time to connect.\n\nBest regards`);
                    } finally {
                      setGeneratingComposeDraft(false);
                    }
                  }}
                  disabled={generatingComposeDraft}
                  className="p-2 hover:bg-gray-900 rounded-full transition-colors"
                  title="AI Draft"
                  data-testid="compose-ai-btn"
                >
                  {generatingComposeDraft ? (
                    <Loader2 size={20} className="text-purple-400 animate-spin" />
                  ) : (
                    <Sparkles size={20} className="text-purple-400" />
                  )}
                </button>
              </div>
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => {
                    setShowEmailComposeModal(false);
                    setEmailTo('');
                    setEmailCc('');
                    setEmailBcc('');
                    setEmailSubject('');
                    setEmailBody('');
                    setComposeAttachments([]);
                  }}
                  className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors"
                  disabled={sendingEmail}
                >
                  Cancel
                </button>
              <button 
                onClick={async () => {
                  if (!emailTo.trim() || !emailSubject.trim()) {
                    alert('Please enter recipient and subject');
                    return;
                  }
                  setSendingEmail(true);
                  try {
                    // Convert file attachments to base64
                    const attachmentData = [];
                    if (composeAttachments && composeAttachments.length > 0) {
                      for (const file of composeAttachments) {
                        const base64 = await new Promise((resolve, reject) => {
                          const reader = new FileReader();
                          reader.onload = () => resolve(reader.result.split(',')[1]); // strip data:...;base64,
                          reader.onerror = reject;
                          reader.readAsDataURL(file);
                        });
                        attachmentData.push({
                          name: file.name,
                          contentType: file.type || 'application/octet-stream',
                          contentBytes: base64,
                          size: file.size
                        });
                      }
                    }

                    if (activeEmailAccount === 'gmail') {
                      // Send via Gmail API with attachments
                      const response = await fetch(`${window.location.origin}/api/gmail/send`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          to: emailTo.split(',').map(e => e.trim()).filter(e => e),
                          cc: emailCc ? emailCc.split(',').map(e => e.trim()).filter(e => e) : [],
                          bcc: emailBcc ? emailBcc.split(',').map(e => e.trim()).filter(e => e) : [],
                          subject: emailSubject,
                          body: emailBody,
                          account_id: 'creatrhq',
                          attachments: attachmentData
                        })
                      });
                      if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(errorData.detail || 'Failed to send email');
                      }
                      alert('Email sent successfully via Gmail!');
                    } else {
                      // Send via Outlook API with attachments
                      const { outlook2EmailAPI } = await import('../services/api');
                      await outlook2EmailAPI.sendEmail({
                        to: [emailTo.trim()],
                        cc: emailCc.trim() ? [emailCc.trim()] : [],
                        subject: emailSubject,
                        body: `<div style="font-family: Arial, sans-serif;">${emailBody.replace(/\n/g, '<br>')}</div>`,
                        body_type: 'html',
                        attachments: attachmentData
                      });
                      alert('Email sent successfully!');
                    }
                    setShowEmailComposeModal(false);
                    setEmailTo('');
                    setEmailCc('');
                    setEmailBcc('');
                    setEmailSubject('');
                    setEmailBody('');
                    setComposeAttachments([]);
                    // Refresh sent folder if we're viewing it
                    if (activeFolder === 'sent' && activeEmailAccount === 'gmail') {
                      syncGmailEmails('sent');
                    }
                  } catch (err) {
                    console.error('Error sending email:', err);
                    alert(`Failed to send email: ${err.message || 'Please try again.'}`);
                  } finally {
                    setSendingEmail(false);
                  }
                }}
                disabled={sendingEmail || !emailTo.trim() || !emailSubject.trim()}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                  sendingEmail || !emailTo.trim() || !emailSubject.trim()
                    ? 'bg-gray-700 cursor-not-allowed text-gray-400'
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
                data-testid="email-compose-send-btn"
              >
                {sendingEmail ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Send Email
                  </>
                )}
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-black border border-gray-400 rounded-xl w-full max-w-md mx-4 overflow-hidden hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
            <div className="px-6 py-4 border-b border-gray-400/20">
              <h3 className="text-lg font-bold text-white">Delete Conversation?</h3>
            </div>

            <div className="p-6">
              <p className="text-gray-300">Are you sure you want to delete this conversation? This action cannot be undone.</p>
            </div>

            <div className="px-6 py-4 border-t border-gray-400/20 flex items-center justify-end gap-3">
              <button 
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setConversationToDelete(null);
                }}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={() => {
                  // Delete logic here
                  setShowDeleteConfirm(false);
                  setConversationToDelete(null);
                }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}


      
      {/* Add Contact Toast Notification */}
      {/* Add to Flight Modal */}
      {showAddFlightModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowAddFlightModal(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white flex items-center gap-2"><Plane size={20} className="text-cyan-400" /> Add to Flights</h3>
              <button onClick={() => setShowAddFlightModal(false)} className="p-1 hover:bg-gray-800 rounded-full"><X size={18} className="text-gray-400" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Airline</label>
                  <input data-testid="flight-airline" value={flightForm.airline} onChange={e => setFlightForm({...flightForm, airline: e.target.value})} className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 text-sm border border-gray-700 focus:border-cyan-500 focus:outline-none" placeholder="e.g. American Airlines" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Flight #</label>
                  <input data-testid="flight-number" value={flightForm.flight_number} onChange={e => setFlightForm({...flightForm, flight_number: e.target.value})} className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 text-sm border border-gray-700 focus:border-cyan-500 focus:outline-none" placeholder="e.g. AA1234" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Departure</label>
                  <input data-testid="flight-departure" value={flightForm.departure} onChange={e => setFlightForm({...flightForm, departure: e.target.value})} className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 text-sm border border-gray-700 focus:border-cyan-500 focus:outline-none" placeholder="e.g. Dallas, TX" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Arrival</label>
                  <input data-testid="flight-arrival" value={flightForm.arrival} onChange={e => setFlightForm({...flightForm, arrival: e.target.value})} className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 text-sm border border-gray-700 focus:border-cyan-500 focus:outline-none" placeholder="e.g. New York, NY" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Date</label>
                  <input data-testid="flight-date" type="date" value={flightForm.date} onChange={e => setFlightForm({...flightForm, date: e.target.value})} className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 text-sm border border-gray-700 focus:border-cyan-500 focus:outline-none" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Time</label>
                  <input data-testid="flight-time" type="time" value={flightForm.time} onChange={e => setFlightForm({...flightForm, time: e.target.value})} className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 text-sm border border-gray-700 focus:border-cyan-500 focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Notes</label>
                <textarea data-testid="flight-notes" value={flightForm.notes} onChange={e => setFlightForm({...flightForm, notes: e.target.value})} rows={3} className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 text-sm border border-gray-700 focus:border-cyan-500 focus:outline-none resize-none" placeholder="Additional details..." />
              </div>
              <button data-testid="save-flight-btn" onClick={handleSaveFlight} className="w-full py-2.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2">
                <Plane size={16} /> Add Flight
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add to Finances Modal */}
      {showAddFinanceModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowAddFinanceModal(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white flex items-center gap-2"><DollarSign size={20} className="text-green-400" /> Add to Finances</h3>
              <button onClick={() => setShowAddFinanceModal(false)} className="p-1 hover:bg-gray-800 rounded-full"><X size={18} className="text-gray-400" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Description</label>
                <input data-testid="finance-description" value={financeForm.description} onChange={e => setFinanceForm({...financeForm, description: e.target.value})} className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 text-sm border border-gray-700 focus:border-green-500 focus:outline-none" placeholder="e.g. Software Subscription" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Amount ($)</label>
                  <input data-testid="finance-amount" type="number" step="0.01" value={financeForm.amount} onChange={e => setFinanceForm({...financeForm, amount: e.target.value})} className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 text-sm border border-gray-700 focus:border-green-500 focus:outline-none" placeholder="0.00" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Type</label>
                  <select data-testid="finance-type" value={financeForm.type} onChange={e => setFinanceForm({...financeForm, type: e.target.value})} className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 text-sm border border-gray-700 focus:border-green-500 focus:outline-none">
                    <option value="expense">Expense</option>
                    <option value="income">Income</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Category</label>
                  <select data-testid="finance-category" value={financeForm.category} onChange={e => setFinanceForm({...financeForm, category: e.target.value})} className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 text-sm border border-gray-700 focus:border-green-500 focus:outline-none">
                    <option value="Travel">Travel</option>
                    <option value="Food">Food</option>
                    <option value="Shopping">Shopping</option>
                    <option value="Utilities">Utilities</option>
                    <option value="Entertainment">Entertainment</option>
                    <option value="Subscription">Subscription</option>
                    <option value="Business">Business</option>
                    <option value="Real Estate">Real Estate</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Date</label>
                  <input data-testid="finance-date" type="date" value={financeForm.date} onChange={e => setFinanceForm({...financeForm, date: e.target.value})} className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 text-sm border border-gray-700 focus:border-green-500 focus:outline-none" />
                </div>
              </div>
              <button data-testid="save-finance-btn" onClick={handleSaveFinance} className="w-full py-2.5 bg-green-600 hover:bg-green-500 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2">
                <DollarSign size={16} /> Add Transaction
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddContactToast && (
        <div className="fixed bottom-6 right-6 bg-green-600 text-white px-6 py-3 rounded-lg shadow-xl flex items-center gap-3 z-50 animate-slide-up">
          <Check size={20} />
          <span className="font-medium">{showAddContactToast}</span>
        </div>
      )}

      {/* Communications Widget Modal */}
      {showCommWidgetModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowCommWidgetModal(false)}>
          <div className="bg-black border border-gray-400 rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-y-auto hover:border-gray-300 transition-all duration-300" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-black border-b border-gray-400 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
              <h2 className="text-2xl font-bold text-white mokoto-font">
                {commWidgetModalType === 'priorityMessages' && 'All Priority Messages & Follow-ups'}
                {commWidgetModalType === 'recentActivity' && 'Complete Recent Activity Log'}
                {commWidgetModalType === 'teamCommunications' && 'Team Communications - Detailed View'}
              </h2>
              <button
                onClick={() => setShowCommWidgetModal(false)}
                className="w-8 h-8 bg-white hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
              >
                <X size={16} className="text-black" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {commWidgetModalType === 'priorityMessages' && (
                <div className="space-y-4">
                  <p className="text-gray-400 mb-4">All messages requiring urgent attention and follow-up</p>
                  {[
                    { title: 'Empire State Lease - Urgent Review', priority: 'URGENT', color: 'red', desc: 'Tech Corp requesting contract revision for $2.5M lease agreement', time: '10 days no reply', contact: 'Sarah Mitchell', value: '$2.5M' },
                    { title: 'Blackstone Group - Investment Query', priority: 'HIGH', color: 'orange', desc: '$50M portfolio opportunity discussion - requires immediate response', time: '5 days pending', contact: 'John Martinez', value: '$50M' },
                    { title: 'Brooklyn Warehouse - Client Concern', priority: 'MEDIUM', color: 'yellow', desc: 'Buyer raising inspection issues on $4.2M property', time: '3 days ago', contact: 'Emily Chen', value: '$4.2M' },
                    { title: 'Madison Ave Office - Tenant Request', priority: 'HIGH', color: 'orange', desc: 'Existing tenant requesting lease extension terms', time: '2 days ago', contact: 'Alex Rivera', value: '$6.2M' },
                    { title: 'SoHo Retail Space - Negotiation', priority: 'URGENT', color: 'red', desc: 'Landlord counter-offer requires immediate response', time: '1 day ago', contact: 'Michael Wong', value: '$3.8M' },
                    { title: 'Tribeca Development - Investor Meeting', priority: 'HIGH', color: 'orange', desc: 'Schedule follow-up meeting with potential investors', time: '4 hours ago', contact: 'Sarah Mitchell', value: '$18M' },
                    { title: 'Chelsea Tower - Documentation', priority: 'MEDIUM', color: 'yellow', desc: 'Missing documents for closing scheduled next week', time: '6 hours ago', contact: 'John Martinez', value: '$15M' },
                    { title: 'Upper East Side - Showing Feedback', priority: 'MEDIUM', color: 'yellow', desc: 'VIP client feedback pending on luxury penthouse', time: '12 hours ago', contact: 'Emily Chen', value: '$9.8M' }
                  ].map((msg, idx) => (
                    <div key={idx} className="bg-black border border-gray-400 rounded-lg p-5 hover:border-gray-300 transition-all duration-300">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white mb-2">{msg.title}</h3>
                          <p className="text-sm text-gray-400 mb-3">{msg.desc}</p>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-gray-400 flex items-center gap-1">
                              <Clock size={14} />
                              {msg.time}
                            </span>
                            <span className="text-gray-400 flex items-center gap-1">
                              <UserPlus size={14} />
                              {msg.contact}
                            </span>
                            <span className="text-green-400 font-semibold">{msg.value}</span>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          msg.color === 'red' ? 'bg-red-500/20 text-red-400' :
                          msg.color === 'orange' ? 'bg-orange-500/20 text-orange-400' :
                          'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {msg.priority}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {commWidgetModalType === 'recentActivity' && (
                <div className="space-y-4">
                  <p className="text-gray-400 mb-4">Complete log of all recent communication activities and updates</p>
                  {[
                    { title: 'Chelsea Tower Deal - Closed', status: 'RESOLVED', color: 'green', desc: 'Contract signed and finalized, $15M luxury development deal completed', time: '2 hours ago', contact: 'Sarah Mitchell', type: 'Deal Closing' },
                    { title: 'Madison Ave - Proposal Sent', status: 'ACTIVE', color: 'blue', desc: '$6.2M commercial property proposal delivered to potential buyer', time: '5 hours ago', contact: 'John Martinez', type: 'Proposal' },
                    { title: 'SoHo Retail - Follow-up Complete', status: 'RESOLVED', color: 'green', desc: 'All client questions answered regarding $3.8M retail space', time: '1 day ago', contact: 'Emily Chen', type: 'Follow-up' },
                    { title: 'Brooklyn Warehouse - Site Visit', status: 'COMPLETED', color: 'green', desc: 'Conducted property tour for $4.2M industrial space buyer', time: '1 day ago', contact: 'Alex Rivera', type: 'Site Visit' },
                    { title: 'Tribeca Loft - Negotiation Round 2', status: 'IN PROGRESS', color: 'blue', desc: 'Second round of negotiations for $4.5M luxury loft underway', time: '2 days ago', contact: 'Michael Wong', type: 'Negotiation' },
                    { title: 'Upper East Side - Marketing Launch', status: 'COMPLETED', color: 'green', desc: 'Successfully launched marketing campaign for $9.8M penthouse', time: '2 days ago', contact: 'Emily Chen', type: 'Marketing' },
                    { title: 'Downtown Office - Lease Renewal', status: 'ACTIVE', color: 'blue', desc: 'Processing $7.5M office lease renewal with existing tenant', time: '3 days ago', contact: 'Sarah Mitchell', type: 'Lease' },
                    { title: 'Midtown Plaza - Inspection Complete', status: 'RESOLVED', color: 'green', desc: 'Building inspection completed for $22M mixed-use development', time: '3 days ago', contact: 'John Martinez', type: 'Inspection' }
                  ].map((activity, idx) => (
                    <div key={idx} className="bg-black border border-gray-400 rounded-lg p-5 hover:border-gray-300 transition-all duration-300">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-semibold text-white">{activity.title}</h3>
                            <span className="px-2 py-1 bg-gray-800 text-gray-400 rounded text-xs">{activity.type}</span>
                          </div>
                          <p className="text-sm text-gray-400 mb-3">{activity.desc}</p>
                          <div className="flex items-center gap-4 text-sm text-gray-400">
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {activity.time}
                            </span>
                            <span className="flex items-center gap-1">
                              <UserPlus size={14} />
                              {activity.contact}
                            </span>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          activity.color === 'green' ? 'bg-green-500/20 text-green-400' :
                          'bg-blue-500/20 text-blue-400'
                        }`}>
                          {activity.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {commWidgetModalType === 'teamCommunications' && (
                <div className="space-y-6">
                  <p className="text-gray-400 mb-4">Detailed communication activity breakdown for each team member</p>
                  {[
                    { name: 'Sarah Mitchell', role: 'Senior Broker', threads: 45, emails: 127, messages: 89, calls: 34, meetings: 18, response: '2.3 hrs' },
                    { name: 'John Martinez', role: 'Deal Manager', threads: 38, emails: 98, messages: 72, calls: 41, meetings: 22, response: '3.1 hrs' },
                    { name: 'Alex Rivera', role: 'Property Manager', threads: 52, emails: 156, messages: 103, calls: 28, meetings: 31, response: '1.8 hrs' },
                    { name: 'Emily Chen', role: 'Marketing Director', threads: 31, emails: 84, messages: 61, calls: 19, meetings: 15, response: '4.2 hrs' },
                    { name: 'Michael Wong', role: 'Financial Analyst', threads: 29, emails: 76, messages: 54, calls: 23, meetings: 19, response: '2.9 hrs' }
                  ].map((member, idx) => (
                    <div key={idx} className="bg-black border border-gray-400 rounded-lg p-6 hover:border-gray-300 transition-all duration-300">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-semibold text-white mb-1">{member.name}</h3>
                          <p className="text-sm text-gray-400">{member.role}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-bold text-white">{member.threads}</div>
                          <div className="text-xs text-gray-400">Active Threads</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div className="text-center p-3 bg-black border border-gray-400 rounded">
                          <div className="text-xl font-bold text-blue-400">{member.emails}</div>
                          <div className="text-xs text-gray-400 mt-1">Emails</div>
                        </div>
                        <div className="text-center p-3 bg-black border border-gray-400 rounded">
                          <div className="text-xl font-bold text-green-400">{member.messages}</div>
                          <div className="text-xs text-gray-400 mt-1">Messages</div>
                        </div>
                        <div className="text-center p-3 bg-black border border-gray-400 rounded">
                          <div className="text-xl font-bold text-purple-400">{member.calls}</div>
                          <div className="text-xs text-gray-400 mt-1">Calls</div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-3 border-t border-gray-400">
                        <span className="text-sm text-gray-400">Meetings This Week</span>
                        <span className="text-lg font-semibold text-white">{member.meetings}</span>
                        <span className="text-sm text-gray-400">Avg Response Time</span>
                        <span className="text-lg font-semibold text-cyan-400">{member.response}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add to Meeting Modal */}
      {showAddToMeetingModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowAddToMeetingModal(false)}>
          <div className="bg-gray-900 border border-gray-400/20 rounded-2xl w-[500px] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-400/20">
              <h2 className="text-xl font-bold text-white">Add to Existing Meeting</h2>
              <p className="text-gray-400 text-sm mt-1">Link {meetingConversation?.name} to a meeting card</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Select Meeting</label>
                <select
                  value={selectedMeetingId}
                  onChange={(e) => setSelectedMeetingId(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-400/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                >
                  <option value="">-- Select a meeting --</option>
                  {existingMeetings.map((meeting, idx) => (
                    <option key={idx} value={meeting.id || idx}>
                      {meeting.title || meeting.name || `Meeting ${idx + 1}`} - {meeting.status || 'N/A'}
                    </option>
                  ))}
                </select>
              </div>
              
              {existingMeetings.length === 0 && (
                <p className="text-gray-400 text-sm text-center py-4">No meetings found. Create a new meeting instead.</p>
              )}
            </div>
            
            <div className="p-6 border-t border-gray-400/20 flex justify-end gap-3">
              <button
                onClick={() => setShowAddToMeetingModal(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddToExistingMeeting}
                disabled={!selectedMeetingId}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                Add to Meeting
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Meeting Modal */}
      {showNewMeetingModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowNewMeetingModal(false)}>
          <div className="bg-gray-900 border border-gray-400/20 rounded-2xl w-[500px] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-400/20">
              <h2 className="text-xl font-bold text-white">Create New Meeting</h2>
              <p className="text-gray-400 text-sm mt-1">Add {meetingConversation?.name} to a new meeting card</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Meeting Title</label>
                <input
                  type="text"
                  value={newMeetingTitle}
                  onChange={(e) => setNewMeetingTitle(e.target.value)}
                  placeholder="e.g., Property Viewing with John"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Date</label>
                <input
                  type="date"
                  value={newMeetingDate}
                  onChange={(e) => setNewMeetingDate(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-400/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Notes</label>
                <textarea
                  value={newMeetingNotes}
                  onChange={(e) => setNewMeetingNotes(e.target.value)}
                  placeholder="Add meeting notes..."
                  rows={4}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
                />
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-400/20 flex justify-end gap-3">
              <button
                onClick={() => setShowNewMeetingModal(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateNewMeeting}
                disabled={!newMeetingTitle}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                Create Meeting
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Contact Modal - Top Level */}
      {showAddContactModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowAddContactModal(false)}>
          <div className="bg-gray-900 border border-gray-400/20 rounded-2xl w-[600px] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-400/20">
              <h2 className="text-2xl font-bold text-white">Add Contact</h2>
              <p className="text-gray-400 text-sm mt-1">Create a new contact from this conversation</p>
            </div>

            {/* Contact Form */}
            <div className="p-6 space-y-4 max-h-[500px] overflow-y-auto">
              {/* Name Field */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
                <input
                  type="text"
                  value={newContactName}
                  onChange={(e) => setNewContactName(e.target.value)}
                  placeholder="Enter contact name"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
              </div>

              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                <input
                  type="email"
                  value={newContactEmail}
                  onChange={(e) => setNewContactEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
              </div>

              {/* Phone Field with Auto Scan */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    value={newContactPhone}
                    onChange={(e) => setNewContactPhone(e.target.value)}
                    placeholder="+1234567890"
                    className="flex-1 px-4 py-2 bg-gray-800 border border-gray-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400"
                  />
                  <button
                    onClick={async () => {
                      if (!newContactPhone.trim()) {
                        alert('Please enter a phone number first');
                        return;
                      }
                      
                      try {
                        setContactAutoScanning(true);
                        
                        // Call backend to scan iCloud
                        const response = await apiCall(`/contacts/scan-icloud/${encodeURIComponent(newContactPhone)}`);
                        
                        if (response.found) {
                          setNewContactName(response.name || newContactName);
                          setNewContactEmail(response.email || newContactEmail);
                          setNewContactCompany(response.company || newContactCompany);
                          alert(`Found in iCloud: ${response.name}`);
                        } else {
                          alert('No match found in iCloud contacts');
                        }
                      } catch (error) {
                        console.error('Auto scan error:', error);
                        alert('Auto scan failed: ' + error.message);
                      } finally {
                        setContactAutoScanning(false);
                      }
                    }}
                    disabled={contactAutoScanning}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {contactAutoScanning ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Scanning...
                      </>
                    ) : (
                      'Auto Scan'
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Enter phone number, then click &apos;Auto Scan&apos; to find match from iCloud contacts</p>
              </div>

              {/* Company Field */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Company</label>
                <input
                  type="text"
                  value={newContactCompany}
                  onChange={(e) => setNewContactCompany(e.target.value)}
                  placeholder="Company name"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
              </div>

              {/* Role Field */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Role</label>
                <input
                  type="text"
                  value={newContactRole}
                  onChange={(e) => setNewContactRole(e.target.value)}
                  placeholder="Job title or role"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
              </div>

              {/* Category Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                <select
                  value={newContactCategory}
                  onChange={(e) => setNewContactCategory(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-400/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                >
                  <option value="Personal">Personal</option>
                  <option value="Business">Business</option>
                  <option value="Family">Family</option>
                  <option value="Friends">Friends</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Notes Field */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Notes</label>
                <textarea
                  value={newContactNotes}
                  onChange={(e) => setNewContactNotes(e.target.value)}
                  rows="3"
                  placeholder="Additional notes..."
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
                ></textarea>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="p-6 border-t border-gray-400/20 flex gap-3">
              <button
                onClick={() => {
                  setShowAddContactModal(false);
                  // Reset form
                  setNewContactName('');
                  setNewContactEmail('');
                  setNewContactPhone('');
                  setNewContactCompany('');
                  setNewContactRole('');
                  setNewContactCategory('Personal');
                  setNewContactNotes('');
                }}
                className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors text-white font-medium"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  // Validate
                  if (!newContactName.trim()) {
                    alert('Please enter a contact name');
                    return;
                  }
                  
                  if (!newContactPhone.trim()) {
                    alert('Please enter a phone number');
                    return;
                  }
                  
                  try {
                    // Save contact to backend - include original name for message updates
                    const contactData = {
                      name: newContactName,
                      email: newContactEmail,
                      phone: newContactPhone,
                      company: newContactCompany,
                      role: newContactRole,
                      category: newContactCategory.toLowerCase(),
                      notes: newContactNotes,
                      source: 'manual',
                      original_name: originalConversationName // Pass original name so backend can update messages
                    };
                    
                    await apiCall('/contacts', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(contactData)
                    });
                    
                    console.log('✅ Contact saved successfully');
                    
                    // Show success toast
                    setShowAddContactToast(newContactName);
                    setTimeout(() => setShowAddContactToast(null), 3000);
                    
                    // Close modal and reset
                    setShowAddContactModal(false);
                    setNewContactName('');
                    setNewContactEmail('');
                    setNewContactPhone('');
                    setNewContactCompany('');
                    setNewContactRole('');
                    setNewContactCategory('Personal');
                    setNewContactNotes('');
                    setOriginalConversationName('');
                    
                    // Refresh conversations to update contact name
                    const messagesData = await apiCall('/messages');
                    setMessages(messagesData);
                    
                  } catch (error) {
                    console.error('Failed to save contact:', error);
                    alert('Failed to save contact: ' + error.message);
                  }
                }}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors text-white font-medium flex items-center justify-center gap-2"
              >
                <UserPlus size={18} />
                Save Contact
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Messaging Connection Modal */}
      {showMessagingConnectionModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowMessagingConnectionModal(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-700">
              <h3 className="text-xl font-bold text-white mb-1">Connect Your Messages</h3>
              <p className="text-sm text-gray-400">Choose how you want to sync your messages</p>
            </div>
            
            <div className="p-6 space-y-4">
              {messagingConnectionError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {messagingConnectionError}
                </div>
              )}
              
              {/* iPhone Shortcuts Option */}
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 hover:border-green-500/50 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-500/20 rounded-xl">
                    <svg className="w-8 h-8 text-green-400" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-white font-semibold mb-1">iPhone via Shortcuts App</h4>
                    <p className="text-gray-400 text-sm mb-3">
                      Sync your iMessage and SMS using Apple Shortcuts automation. Your messages stay on your device.
                    </p>
                    <div className="space-y-2 text-xs text-gray-500 mb-4">
                      <p>1. Download the Shortcut from our setup page</p>
                      <p>2. Configure it with your dashboard URL</p>
                      <p>3. Messages sync automatically in background</p>
                    </div>
                    <button
                      onClick={handleShortcutsConnect}
                      className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                      data-testid="shortcuts-connect-btn"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                      </svg>
                      Connect iPhone
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-700"></div>
                <span className="text-gray-500 text-sm">or</span>
                <div className="flex-1 h-px bg-gray-700"></div>
              </div>
              
              {/* Twilio Option */}
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 hover:border-red-500/50 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-red-500/20 rounded-xl">
                    <svg className="w-8 h-8 text-red-400" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0C5.381 0 0 5.381 0 12s5.381 12 12 12 12-5.381 12-12S18.619 0 12 0zm0 20.4c-4.639 0-8.4-3.761-8.4-8.4S7.361 3.6 12 3.6s8.4 3.761 8.4 8.4-3.761 8.4-8.4 8.4zm3.6-8.4c0 1.988-1.612 3.6-3.6 3.6s-3.6-1.612-3.6-3.6 1.612-3.6 3.6-3.6 3.6 1.612 3.6 3.6z"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-white font-semibold mb-1">Twilio SMS</h4>
                    <p className="text-gray-400 text-sm mb-3">
                      Use a Twilio phone number for cloud-based SMS messaging. Great for business communications.
                    </p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Account SID</label>
                        <input
                          type="text"
                          value={twilioAccountSid}
                          onChange={(e) => setTwilioAccountSid(e.target.value)}
                          placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                          className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none text-sm"
                          data-testid="twilio-account-sid-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Auth Token</label>
                        <input
                          type="password"
                          value={twilioAuthToken}
                          onChange={(e) => setTwilioAuthToken(e.target.value)}
                          placeholder="Your auth token"
                          className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none text-sm"
                          data-testid="twilio-auth-token-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Phone Number</label>
                        <input
                          type="text"
                          value={twilioPhoneNumber}
                          onChange={(e) => setTwilioPhoneNumber(e.target.value)}
                          placeholder="+1234567890"
                          className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none text-sm"
                          data-testid="twilio-phone-number-input"
                        />
                      </div>
                      <p className="text-xs text-gray-500">
                        Get your credentials at{' '}
                        <a 
                          href="https://console.twilio.com" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-red-400 hover:underline"
                        >
                          console.twilio.com
                        </a>
                      </p>
                      <button
                        onClick={handleTwilioConnect}
                        disabled={connectingMessaging}
                        className="w-full bg-[#F22F46] hover:bg-[#d62a3e] disabled:bg-gray-600 text-white px-4 py-2.5 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                        data-testid="twilio-connect-btn"
                      >
                        {connectingMessaging ? (
                          <>
                            <RefreshCw size={18} className="animate-spin" />
                            Connecting...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 0C5.381 0 0 5.381 0 12s5.381 12 12 12 12-5.381 12-12S18.619 0 12 0zm0 20.4c-4.639 0-8.4-3.761-8.4-8.4S7.361 3.6 12 3.6s8.4 3.761 8.4 8.4-3.761 8.4-8.4 8.4zm3.6-8.4c0 1.988-1.612 3.6-3.6 3.6s-3.6-1.612-3.6-3.6 1.612-3.6 3.6-3.6 3.6 1.612 3.6 3.6z"/>
                            </svg>
                            Connect Twilio
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-700">
              <button
                onClick={() => {
                  setShowMessagingConnectionModal(false);
                  setMessagingConnectionError('');
                }}
                className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calls Connection Modal */}
      {showCallsConnectionModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowCallsConnectionModal(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-700">
              <h3 className="text-xl font-bold text-white mb-1">Connect Your Calls</h3>
              <p className="text-sm text-gray-400">Choose how you want to sync your call history</p>
            </div>
            
            <div className="p-6 space-y-4">
              {callsConnectionError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {callsConnectionError}
                </div>
              )}
              
              {/* iPhone Shortcuts Option */}
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 hover:border-blue-500/50 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-500/20 rounded-xl">
                    <svg className="w-8 h-8 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-white font-semibold mb-1">iPhone via Shortcuts App</h4>
                    <p className="text-gray-400 text-sm mb-3">
                      Sync your call history using Apple Shortcuts automation. Your data stays private.
                    </p>
                    <div className="space-y-2 text-xs text-gray-500 mb-4">
                      <p>1. Download the Call Log Shortcut</p>
                      <p>2. Run it to sync your recent calls</p>
                      <p>3. Automate it for continuous sync</p>
                    </div>
                    <button
                      onClick={handleCallsShortcutsConnect}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                      data-testid="calls-shortcuts-connect-btn"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                      </svg>
                      Connect iPhone
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-700"></div>
                <span className="text-gray-500 text-sm">or</span>
                <div className="flex-1 h-px bg-gray-700"></div>
              </div>
              
              {/* Twilio Option */}
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 hover:border-red-500/50 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-red-500/20 rounded-xl">
                    <svg className="w-8 h-8 text-red-400" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0C5.381 0 0 5.381 0 12s5.381 12 12 12 12-5.381 12-12S18.619 0 12 0zm0 20.4c-4.639 0-8.4-3.761-8.4-8.4S7.361 3.6 12 3.6s8.4 3.761 8.4 8.4-3.761 8.4-8.4 8.4zm3.6-8.4c0 1.988-1.612 3.6-3.6 3.6s-3.6-1.612-3.6-3.6 1.612-3.6 3.6-3.6 3.6 1.612 3.6 3.6z"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-white font-semibold mb-1">Twilio Voice</h4>
                    <p className="text-gray-400 text-sm mb-3">
                      Make and receive calls using a Twilio phone number. Great for business lines.
                    </p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Account SID</label>
                        <input
                          type="text"
                          value={twilioCallsAccountSid}
                          onChange={(e) => setTwilioCallsAccountSid(e.target.value)}
                          placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                          className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none text-sm"
                          data-testid="twilio-calls-account-sid-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Auth Token</label>
                        <input
                          type="password"
                          value={twilioCallsAuthToken}
                          onChange={(e) => setTwilioCallsAuthToken(e.target.value)}
                          placeholder="Your auth token"
                          className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none text-sm"
                          data-testid="twilio-calls-auth-token-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Phone Number</label>
                        <input
                          type="text"
                          value={twilioCallsPhoneNumber}
                          onChange={(e) => setTwilioCallsPhoneNumber(e.target.value)}
                          placeholder="+1234567890"
                          className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none text-sm"
                          data-testid="twilio-calls-phone-number-input"
                        />
                      </div>
                      <p className="text-xs text-gray-500">
                        Get your credentials at{' '}
                        <a 
                          href="https://console.twilio.com" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-red-400 hover:underline"
                        >
                          console.twilio.com
                        </a>
                      </p>
                      <button
                        onClick={handleTwilioCallsConnect}
                        disabled={connectingCalls}
                        className="w-full bg-[#F22F46] hover:bg-[#d62a3e] disabled:bg-gray-600 text-white px-4 py-2.5 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                        data-testid="twilio-calls-connect-btn"
                      >
                        {connectingCalls ? (
                          <>
                            <RefreshCw size={18} className="animate-spin" />
                            Connecting...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 0C5.381 0 0 5.381 0 12s5.381 12 12 12 12-5.381 12-12S18.619 0 12 0zm0 20.4c-4.639 0-8.4-3.761-8.4-8.4S7.361 3.6 12 3.6s8.4 3.761 8.4 8.4-3.761 8.4-8.4 8.4zm3.6-8.4c0 1.988-1.612 3.6-3.6 3.6s-3.6-1.612-3.6-3.6 1.612-3.6 3.6-3.6 3.6 1.612 3.6 3.6z"/>
                            </svg>
                            Connect Twilio
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-700">
              <button
                onClick={() => {
                  setShowCallsConnectionModal(false);
                  setCallsConnectionError('');
                }}
                className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Email Account Modal - Nylas Universal Connect */}
      {showAddEmailModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowAddEmailModal(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-700">
              <h3 className="text-xl font-bold text-white mb-1">Connect Email Account</h3>
              <p className="text-sm text-gray-400">Connect any email provider in seconds</p>
            </div>
            
            <div className="p-6 space-y-4">
              {addEmailError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {addEmailError}
                </div>
              )}
              
              {/* Account Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Account Name</label>
                <input
                  type="text"
                  value={newEmailName}
                  onChange={(e) => setNewEmailName(e.target.value)}
                  placeholder="e.g., Work, Personal, Business"
                  className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                  data-testid="new-email-name-input"
                />
                <p className="mt-1 text-xs text-gray-500">This name will appear as the tab label</p>
              </div>
              
              {/* Supported Providers */}
              <div className="bg-gray-800/50 rounded-xl p-4">
                <p className="text-xs text-gray-400 mb-3">Supported Email Providers</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1.5 bg-gray-700 rounded-full text-xs text-gray-300 flex items-center gap-1.5">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#EA4335">
                      <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                    </svg>
                    Gmail
                  </span>
                  <span className="px-3 py-1.5 bg-gray-700 rounded-full text-xs text-gray-300 flex items-center gap-1.5">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#0078D4">
                      <path d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.578.23h-8.547v-6.959l1.6 1.229c.102.086.221.127.358.127.142 0 .262-.043.363-.127l6.81-5.25c.088-.064.153-.14.193-.23.043-.088.039-.168-.008-.238v-.012c-.03-.058-.072-.11-.128-.155-.055-.047-.12-.074-.19-.086h-.012c-.063-.013-.122-.013-.175 0-.053.012-.104.035-.153.067l-6.81 5.25-1.848-1.42v-3.48h8.547c.226 0 .42.076.578.23.158.152.238.346.238.576z"/>
                    </svg>
                    Outlook
                  </span>
                  <span className="px-3 py-1.5 bg-gray-700 rounded-full text-xs text-gray-300 flex items-center gap-1.5">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#6001D2">
                      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 2.4c5.302 0 9.6 4.298 9.6 9.6s-4.298 9.6-9.6 9.6S2.4 17.302 2.4 12 6.698 2.4 12 2.4z"/>
                    </svg>
                    Yahoo
                  </span>
                  <span className="px-3 py-1.5 bg-gray-700 rounded-full text-xs text-gray-300 flex items-center gap-1.5">
                    <Mail className="w-4 h-4" />
                    IMAP
                  </span>
                </div>
              </div>
              
              {/* Info Box */}
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm text-green-400 font-medium">Secure One-Click Connect</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Click connect and sign in with your email provider. No passwords stored on our servers.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-700 flex gap-3">
              <button
                onClick={() => setShowAddEmailModal(false)}
                className="flex-1 px-4 py-3 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!newEmailName.trim()) {
                    setAddEmailError('Please enter an account name');
                    return;
                  }
                  // Redirect to Nylas OAuth
                  const userId = 'dwoodson'; // Use actual user ID
                  const accountName = encodeURIComponent(newEmailName.trim());
                  window.location.href = `${window.location.origin}/api/nylas/auth/connect?user_id=${userId}&account_name=${accountName}`;
                }}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-500 hover:to-pink-500 transition-all font-medium flex items-center justify-center gap-2"
                data-testid="connect-email-btn"
              >
                <Link className="w-4 h-4" />
                Connect Email
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Category Management Modal */}
      {showCategoryModal && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowCategoryModal(false)}
        >
          <div 
            className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">
                {categoryModalType === 'messages' && 'Edit Message Categories'}
                {categoryModalType === 'calls' && 'Edit Call Categories'}
                {categoryModalType === 'emails' && 'Edit Email Folders'}
                {categoryModalType === 'emailCategories' && 'Edit Email Categories'}
              </h3>
              <button
                onClick={() => setShowCategoryModal(false)}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X size={18} className="text-gray-400" />
              </button>
            </div>
            
            {/* Add New Category */}
            <div className="p-4 border-b border-gray-700">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addCategory()}
                  placeholder={categoryModalType === 'emails' ? 'New folder name...' : 'New category name...'}
                  className="flex-1 px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                  data-testid="new-category-input"
                />
                <button
                  onClick={addCategory}
                  disabled={!newCategoryName.trim()}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg transition-colors text-sm font-medium flex items-center gap-1.5"
                  data-testid="add-category-btn"
                >
                  <Plus size={14} />
                  Add
                </button>
              </div>
            </div>
            
            {/* Category List */}
            <div className="p-4 max-h-80 overflow-y-auto space-y-2">
              {(categoryModalType === 'messages' ? messageCategories : 
                categoryModalType === 'calls' ? callCategories : 
                categoryModalType === 'emailCategories' ? emailCategories : emailFolders
              ).map((cat) => {
                const defaultMessageCats = ['urgent', 'follow-up', 'client'];
                const defaultCallCats = ['family', 'friends', 'business'];
                const defaultEmailFolders = ['inbox', 'sent', 'drafts', 'trash'];
                const defaultEmailCategories = ['urgent', 'orders', 'meeting'];
                
                const isDefault = categoryModalType === 'messages' ? defaultMessageCats.includes(cat) :
                                  categoryModalType === 'calls' ? defaultCallCats.includes(cat) :
                                  categoryModalType === 'emailCategories' ? defaultEmailCategories.includes(cat) :
                                  defaultEmailFolders.includes(cat);
                
                return (
                  <div 
                    key={cat}
                    className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg group"
                  >
                    {editingCategory === cat ? (
                      <>
                        <input
                          type="text"
                          value={editCategoryValue}
                          onChange={(e) => setEditCategoryValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') updateCategory(cat, editCategoryValue);
                            if (e.key === 'Escape') {
                              setEditingCategory(null);
                              setEditCategoryValue('');
                            }
                          }}
                          className="flex-1 px-2 py-1 bg-black border border-gray-600 rounded text-white text-sm focus:border-purple-500 focus:outline-none"
                          autoFocus
                          data-testid={`edit-category-input-${cat}`}
                        />
                        <button
                          onClick={() => updateCategory(cat, editCategoryValue)}
                          className="p-1.5 text-green-400 hover:bg-green-500/20 rounded"
                          title="Save"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          onClick={() => {
                            setEditingCategory(null);
                            setEditCategoryValue('');
                          }}
                          className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"
                          title="Cancel"
                        >
                          <X size={14} />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-white text-sm">
                          {formatCategoryName(cat)}
                          {isDefault && (
                            <span className="ml-2 text-xs text-gray-500">(default)</span>
                          )}
                        </span>
                        <button
                          onClick={() => {
                            setEditingCategory(cat);
                            setEditCategoryValue(formatCategoryName(cat));
                          }}
                          className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Edit"
                          data-testid={`edit-btn-${cat}`}
                        >
                          <Pencil size={14} />
                        </button>
                        {!isDefault && (
                          <button
                            onClick={() => deleteCategory(cat)}
                            className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Delete"
                            data-testid={`delete-btn-${cat}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-700">
              <button
                onClick={() => setShowCategoryModal(false)}
                className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm font-medium"
                data-testid="close-category-modal-btn"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RealEstateManagingPartnerCommunications;
